﻿// ─────────────────────────────────────────────────────────────────────────────
// canvas_app.ts — Main canvas application
//
// Tap & invocation pipeline (one-stop overview)
// ─────────────────────────────────────────────
// PointerEvent flow per stroke:
//
//   pointerdown
//     ├─ compute _tapEligibleAtDown (idle long enough since last writing?)
//     ├─ cadence.pause()  — chain timer paused while pen is down so
//     │                     onSingleTap/onChainEnd can't fire mid-stroke
//     └─ if wand mode: clearTimeout(_wandTimeout), start tracing, etc.
//
//   pointermove
//     └─ feed ink / spell layer with coalesced samples
//
//   pointerup
//     ├─ if drag completion (handle/vertex/CP) and barely moved:
//     │     _feedTapToCadence(...)
//     ├─ if wand mode + isTap(): tap in cast → cancel wand, feed cadence
//     ├─ if wand mode + multi-stroke: spell flux resolution path
//     ├─ if pen mode + tap-eligible + isTap():
//     │     one-shot ring? consume; else _feedTapToCadence(...)
//     ├─ if pen mode + writing: commit ink (and refresh ink cooldown
//     │     unless the stroke was tap-shaped)
//     ├─ cadence.resume()  — unpauses the chain timer if it wasn't
//     │     already re-armed by feedTap
//     └─ if cast still up: _armWandIdleTimeout() — extend the cast
//           one more WAND_IDLE_MS while user is active
//
// Cadence engine
// ──────────────
// Buffer of taps + S/L letter string (see cadence_engine.ts). Single
// 300 ms threshold separates short from long. Patterns are exact
// strings; matches fire onResolved without resetting the buffer, so
// 'SSL' can fire stylus menu after 'S' already fired cast.
//
// Cast (wand) lifecycle
// ─────────────────────
//   Enter   — onResolved('thrice-knock') → _invokeWand()
//   Exit    — any non-cast resolve cancels first (e.g. SSL → menu)
//           — onSingleTap cancels  (lone tap dismisses cast)
//           — onChainEnd cancels   (chain ended without follow-up)
//           — _wandTimeout fires after WAND_IDLE_MS of pen idle
//           — multi-stroke spell resolves / fizzles
// ─────────────────────────────────────────────────────────────────────────────

import type { GrimoireAction } from '@shared/grimoire_action';
import type { StrokePoint, PenStyle } from '@shared/notebook_model';
import { DEFAULT_INK_STYLE, SPELL_INK_STYLE, makeStrokeId } from '@shared/notebook_model';
import type {
  InteractionContext, PageInteractionPolicy,
} from '@shared/interaction_context';
import {
  JOURNAL_CONTEXT,
  ADVENTURE_ACTION_CONTEXT, ADVENTURE_INVENTORY_CONTEXT,
  ADVENTURE_READONLY_CONTEXT, ADVENTURE_BOOKMARK_CONTEXT,
  resolveContext,
} from '@shared/interaction_context';
import { InkLayer } from './ink_layer';
import { SpellLayer } from './spell_layer';
import { PageRenderer, type PageLayout, type PageSidebarTab } from './page_renderer';
import { BlockRenderer, type WvRelationArrow } from './block_renderer';
import { getBodyJustification } from './body_justification';
import { getBodyGraphic, listBodyGraphics } from './body_graphics';
import { getMarginUtility, listMarginUtilities } from './margin_utility';
import { getMarginGraphic, listMarginGraphics } from './margin_graphics';
import { getFrameDecoration, listFrameDecorations } from './frame_decorations';
import { listBodyJustifications as _listBodyJustifications } from './body_justification';
import { DebugDialog, type DebugTab, type DebugSubtab, type DebugAction } from './debug_dialog';
import { SpellEngine, registerBuiltinSpells, type EngineContext as NewSpellEngineCtx } from './spell';
import type { Point as PModelPoint, RegionBounds } from '@shared/notebook_model';
import { getLatexOverlay } from './latex_overlay';
import { WebviewNotebookState, computeHandles, pointInPolygon } from './notebook_state';
import { isTap, tapFromStroke } from '../../src/core/tap_cadence';
import { classifyDwell } from '../../src/core/stroke_classifier';
import { resampleAlong, pathSamplerForEdges, pathSamplerForEllipse } from '../../src/core/stroke_resample';
import { ToolRegistry } from './tool_registry';
import { buildSidebarLayout, drawStylusSidebar, hitTestSidebar, type SidebarHitItem, STYLUS_SIDEBAR_WIDTH } from './stylus_sidebar';
import { MarginWipeLayer, clipPolylineAtVerticalLine, clipPolylineAtCircle, softenOutsideRuns, makeFragment, pickWipeAnim } from './margin_wipe';

/** Page-local x below which pointer-down events are reserved for the
 *  stylus sidebar (no ink starts there, regardless of whether the
 *  sidebar is visible at the moment). Slightly wider than the
 *  sidebar's visible footprint so tap targets stay generous. */
const LEFT_MARGIN_RESERVED_PX = STYLUS_SIDEBAR_WIDTH + 8;

/** Eraser trail visual — a single stroked path per drag. Width and
 *  edge softness come from the eraser tool's size + opacity so the
 *  ghost matches the actual erase footprint exactly. Pen-up flips
 *  fadeStart and the path rolls off with an ease-out over
 *  ERASER_TRAIL_FADE_MS. One ghost per drag = no overlap-induced
 *  alpha stacking and one ctx.stroke() call per ghost (cheap). */
const ERASER_TRAIL_FADE_MS    = 800;
const ERASER_TRAIL_PEAK_ALPHA = 0.30;
import { CadenceEngine, SL_THRESHOLD_MS } from './cadence_engine';
import type { CadencePattern } from './cadence_engine';
import { TapRingLayer } from './tap_ring';
import { TapAudio } from './tap_audio';
import { audioEngine } from './audio_engine';
import { PopDialogLayer, type PopOption } from './pop_dialog_v2';
import { TextInputLayer } from './text_input';
import { TOOL_DEFS, THICKNESS_OPTIONS, INK_COLORS, SECTION_PALETTE, PAGE_PALETTE } from './drawn_ui';
import {
  SelectionWidget,
  setSelectionAnimationsEnabled,
  type WidgetItem,
  type GestureKind,
} from './selection_widget';
import {
  TapEffectLayer, setTapEffectsEnabled,
  TAP_NEUTRAL_COLOR,
  INVOKE_GLOW_CAST_COLOR, INVOKE_GLOW_STYLUS_COLOR,
} from './tap_effects';
import {
  StylusBindingEngine, STYLUS_ACTIONS, STYLUS_TRIGGERS,
  type StylusActionId, type StylusTrigger,
} from './stylus_bindings';
import {
  SlimPenBridge,
  type BridgeStatus, type BridgePenInfo, type BridgeCapabilities,
} from './slim_pen_bridge';
import { DissolveManager } from './dissolve_anims';
import { ChargeEngine, computeStrokeLength } from '../../src/core/charge_engine';
import { createSpellRegistry, type SpellRegistry } from '../../src/core/spell_registry';
import type { RecognisedShape } from '../../src/core/stroke_geometry';
import { createStrandGraph, SegmentHash, addStroke, isRectangularLoop, clearGraph, beginLiveStroke, feedLivePoint, clearLiveStrand, type StrandGraph, type Loop } from '../../src/core/strand_graph';
import { createFluxState, tickFlux, activateLoop, resetFlux, readFluxSignature, beginWandCast, addWandSource, addWandSink, updateLiveCoSource, resolveWand, type FluxState } from '../../src/core/flux_engine';
import { classifyFromFlux, type BlockHitTest as FluxBlockHitTest } from '../../src/core/flux_classifier';
import { matchGesture } from './gesture_matcher';
import { ALL_TEMPLATES as GESTURE_TEMPLATES } from './gesture_templates';
// dissolve_anims constants are imported lazily where needed; the wind cast
// now erases immediately and lets dissolves manage their own lifetime.
import { drawStrandGraph } from './strand_renderer';
import { ShapeBridge, SpellBridge, type SerializedBlock } from './recognition_bridge';
import { TextureLayer } from './texture_layer';
import { WizardScroll } from './wizard_scroll';
import { CodexRenderer, type CodexEntry, type InvocationEntry, type ArcaneEntry, type ContentsEntry } from './codex_renderer';
import { SnapManager, type SnapResult } from './snap_manager';
import { drawAdventureEnv, drawBookmarksPage, drawCharacterSheet, drawDialogHistory } from './adventure_renderer';
import { drawActionScroll, hitTestScroll, type ScrollHitRect } from './action_scroll';
import { HandwritingRecognizer } from './handwriting_recognizer';
import { TextComposition } from './text_composition';
import type { AgentOrchestrator } from './agent/agent_orchestrator';
// NPC presence is now unified with arcane block activation (no separate overlay)

// ── Settings model ───────────────────────────────────────────────────────────

export type ShapeMode = 'off' | 'hint' | 'full';

export interface GrimoireSettings {
  /** Auto-recognize LaTeX from handwriting. */
  autoLatex: boolean;
  /** Auto-detect arrow relations between blocks. */
  autoArrows: boolean;
  /** Tap sound feedback. */
  tapSound: boolean;
  /** Spell detection sensitivity: 'low' | 'medium' | 'high'. */
  spellSensitivity: 'low' | 'medium' | 'high';
  /** Shape auto-detection mode: off (manual evaluate), hint (tap ring), full (auto-replace). */
  shapeMode: ShapeMode;
  /** Grain overlay intensity 0–1 (0 = off, 1 = maximum). */
  grainIntensity: number;
  /** Visual FX over drawn strokes (diffusion waviness). */
  visualFx: boolean;
  /** Show debug overlay for arcane detection stages. */
  debugOverlay: boolean;
  /** Master toggle for non-essential animations: stroke fade-outs,
   *  selection effects, tap rings. Off = silent updates for performance.
   *  Selection still happens; only the visual flourish is suppressed. */
  animations: boolean;
  /** Which visual fires at every confirmed tap. Each kind uses the
   *  same animation for both neutral taps and invocation-resolving
   *  ("climax") taps — only the colour shifts:
   *    'glow'  — radial gradient pulse (default; ghostly bloom)
   *    'ring'  — outline expanding circle
   *    'burst' — solid filled disc
   *    'off'   — no tap visual (audio + action still fire)
   *  Neutral colour: faint white. Climax tap colour: purple for cast,
   *  blue for stylus menu. */
  tapEffect: 'glow' | 'ring' | 'burst' | 'off';
  /** Side-button shortcut bindings, keyed by `${context}/${tip}/${trigger}`.
   *  The stylus_bindings engine owns the schema; this slot exists for
   *  persistence only. Empty = use built-in defaults. */
  stylusBindings: Record<string, string>;
  /** Per-tool params (color / size / opacity) keyed by tool id, plus
   *  the last active tool. The ToolRegistry owns the schema; this
   *  slot exists for persistence only. Undefined = factory defaults. */
  tools?: { active?: PenStyle; params?: Partial<Record<PenStyle, { color: string; size: number; opacity: number }>> };
  /** Permanent left-margin stylus sidebar — visible vs hidden.
   *  Toggled by the menu-invocation routes (cadence summon, side-
   *  button shortcut). Default off so existing users see the
   *  familiar canvas before invoking. */
  stylusSidebarVisible?: boolean;
}

const DEFAULT_SETTINGS: GrimoireSettings = {
  autoLatex: true,
  autoArrows: true,
  tapSound: true,
  spellSensitivity: 'medium',
  shapeMode: 'off',
  grainIntensity: 1,
  visualFx: true,
  debugOverlay: false,
  animations: true,
  tapEffect: 'glow',
  stylusBindings: {},
};

// VS Code webview API (injected globally)
declare function acquireVsCodeApi(): {
  postMessage(msg: unknown): void;
  getState(): unknown;
  setState(state: unknown): void;
};

interface SerialBlock {
  id: string;
  kind: string;
  bbox: { x: number; y: number; width: number; height: number };
  rotation?: number;
  content?: string;
  fontSize?: number;
  textStyle?: Record<string, unknown>;
  arcaneType?: string;
  arcaneState?: string;
  language?: string;
  fileRef?: string;
  // Figure block fields
  shapeKind?: string;
  vertices?: Array<{ x: number; y: number }>;
  edges?: Array<{ from: number; to: number; type: string; controlPoints?: Array<{ x: number; y: number }> }>;
  center?: { x: number; y: number };
  rx?: number; ry?: number;
  penSize?: number;
  lineStyle?: string;
  replacedStrokeIds?: string[];
}

interface SerialArrow {
  id: string; fromId: string; toId: string; type: string; confirmed: boolean;
}

// ── Undo / Redo types ─────────────────────────────────────────────────────────

const MAX_UNDO = 80;

/** Wand cast auto-cancel after this much idle time (no pen activity).
 *  Re-armed at every pen-up while cast is up, so a steady stream of
 *  taps or strokes keeps the cast alive. */
const WAND_IDLE_MS = 2200;

/** Hint affordance colour — used uniformly for shape-hint rings, the
 *  shape-ghost preview, the text-hint ring, and the predicted-text
 *  overlay. Warm parchment-white so the glow reads as "magic" on the
 *  page without competing with the ink. */
const HINT_GLOW_COLOR = '#f0e8d0';
/** Hint ring drawing radius (formerly the hintRingRadius setting,
 *  retired now that the surface is on-theme). */
const HINT_RING_RADIUS = 16;

/** A single undoable entry. `reverse` / `forward` are GrimoireAction arrays. */
interface UndoEntry {
  /** Actions to dispatch to reverse this entry. */
  reverse: GrimoireAction[];
  /** Actions to dispatch to re-apply this entry. */
  forward: GrimoireAction[];
}

interface SerialSection {
  id: string; title: string; description: string; color: string;
  pageIds: string[]; headerPageId: string; kind?: string;
}

interface NotebookInit {
  meta: { title: string; coverSeed: number };
  currentPageIndex: number;
  currentPageId?: string;
  pageCount: number;
  sections?: SerialSection[];
  pages: Array<{
    id: string; index: number; title: string; densityLevel: number;
    color?: string; sectionId?: string; isSectionHeader?: boolean;
    decorations?: {
      body?: { justification?: string; graphics?: string };
      margins?: { utility?: string; graphics?: string };
      frame?: string;
    };
    blocks?: SerialBlock[];
    arrows?: SerialArrow[];
  }>;
}

// ── Page groups ───────────────────────────────────────────────────────────────
// A tab represents a group of ordered pages.  The journal group holds the
// user's pages; the codex group is auto-generated from discovered spells;
// index and settings are single-page system groups.

export type PageGroupKind = 'journal' | 'codex' | 'settings';

export interface PageGroup {
  kind: PageGroupKind;
  label: string;
  /** Number of sub-pages within this group. */
  pageCount: number;
  /** Page interaction policy applied to all pages in this group. */
  policy: PageInteractionPolicy;
}

// ─────────────────────────────────────────────────────────────────────────────

/** Short toast label for a new-engine spell id. Explicit map keeps the
 *  on-canvas label intuitive ("select", "erase") regardless of how the
 *  spell id is namespaced internally. */
const SPELL_TOAST_LABELS: Record<string, string> = {
  'select.point':     'select',
  'select.region':    'select',
  'selection.erase':  'erase',
  'selection.clear':  'clear',
};
function shortSpellLabel(spellId: string): string {
  return SPELL_TOAST_LABELS[spellId] ?? spellId;
}

/** True if line segment (x1,y1)→(x2,y2) intersects the axis-aligned rectangle. */
function lineIntersectsRect(
  x1: number, y1: number, x2: number, y2: number,
  rx: number, ry: number, rw: number, rh: number,
): boolean {
  // Cohen–Sutherland-style: check if the segment definitely misses the rect
  const left = rx, right = rx + rw, top = ry, bottom = ry + rh;
  // Quick AABB overlap test of the line's own bbox against the rect
  const lx0 = Math.min(x1, x2), lx1 = Math.max(x1, x2);
  const ly0 = Math.min(y1, y2), ly1 = Math.max(y1, y2);
  if (lx1 < left || lx0 > right || ly1 < top || ly0 > bottom) return false;
  // Check if any corner of the rect is on each side of the line
  const dx = x2 - x1, dy = y2 - y1;
  const cross = (px: number, py: number) => dx * (py - y1) - dy * (px - x1);
  const c1 = cross(left, top), c2 = cross(right, top);
  const c3 = cross(right, bottom), c4 = cross(left, bottom);
  if (c1 > 0 && c2 > 0 && c3 > 0 && c4 > 0) return false;
  if (c1 < 0 && c2 < 0 && c3 < 0 && c4 < 0) return false;
  return true;
}

// ── Arcane analysis animation state ───────────────────────────────────────────

interface ArcaneAnim {
  /** Phase: box-pulse → rune-pulse → resolve. */
  phase: 'box-pulse' | 'rune-pulse' | 'resolve';
  startMs: number;
  /** Box and rune stroke IDs. */
  boxStrokeId: string;
  runeStrokeIds: string[];
  /** Aggregate bounding box of all consumed strokes. */
  bbox: { x: number; y: number; width: number; height: number };
  /** Classification result (null = invalid / no arcane). */
  blockKind: import('@shared/notebook_model').BlockKind | null;
  /** Resolved block ID (set when block is created). */
  blockId: string | null;
  pageId: string;
  /** For arcane blocks: which persona the drawn rune maps to. */
  detectedPersona?: 'ireth' | 'laura';
}

const ARCANE_BOX_PULSE_MS  = 420;   // box brightness oscillation
const ARCANE_RUNE_PULSE_MS = 380;   // rune brightness oscillation
const ARCANE_RESOLVE_MS    = 500;   // rune inflate + fade out

// ── Pointer frame — compute-once coordinate context per pointer event ────────

interface PointerFrame {
  absX: number;           // canvas-absolute x
  absY: number;           // canvas-absolute y
  pageX: number;          // page-local x
  pageY: number;          // page-local y
  pressure: number;
  tiltX: number;
  tiltY: number;
  twist: number;
  timestamp: number;      // e.timeStamp
  pointerId: number;
  pointerType: string;    // 'pen' | 'touch' | 'mouse'
  button: number;
  buttons: number;
}

// ── Unified UI hit rect — flat array covers tabs, nav, sidebar, scroll ───────

type UiHitTarget =
  | { kind: 'tab';          data: { index: number; kind?: string } }
  | { kind: 'codexNav';     data: { dir: 'back' | 'forward' } }
  | { kind: 'sidebar';      data: { index: number; kind?: string; pageId?: string; nodePath?: string } }
  | { kind: 'scroll';       data: ScrollHitRect }
  | { kind: 'contentsRow';  data: { pageId: string } }
  | { kind: 'subpageArrow'; data: { blockId: string; dir: -1 | 1 } };

interface UiHitRect {
  x: number; y: number; w: number; h: number;
  target: UiHitTarget;
}

export class CanvasApp {
  private _canvas: HTMLCanvasElement;
  private _ctx: CanvasRenderingContext2D;
  private _vscode: ReturnType<typeof acquireVsCodeApi>;

  // Layers
  private _ink!: InkLayer;
  private _spell!: SpellLayer;
  private _page!: PageRenderer;
  private _texture!: TextureLayer;
  private _blockRenderer!: BlockRenderer;
  private _wizardScroll!: WizardScroll;
  private _codex!: CodexRenderer;

  // State
  private _state!: WebviewNotebookState;
  private _chargeEngine!: ChargeEngine;

  private _layout!: PageLayout;

  // ── Page stroke cache ───────────────────────────────────────────────────
  // Cached ink strokes per page so switching is instant (no host round-trip).
  private _pageStrokeCache = new Map<string, Map<string, import('@shared/notebook_model').InkStroke>>();

  // Active pen
  private _activeStrokeId: string | null = null;
  private _isEraser = false;
  /** Snapshot of the active tool's effective style. Reads stay
   *  cheap (synchronous field) but the *source of truth* is the
   *  ToolRegistry below — `pen.style` actions route through it so
   *  per-tool customisations (color, size, opacity) persist across
   *  tool switches and across reloads. */
  private _penStyle = { ...DEFAULT_INK_STYLE };
  /** Owns one Tool per id with its own params. Picking a tool loads
   *  its params into `_penStyle`; updating a param writes back to the
   *  active tool. Persisted via `settings.tools`. */
  private _toolRegistry = new ToolRegistry();
  /** Set at pen-down when a hint ring resolved on contact, so pen-up
   *  bails out instead of running its tap/stroke pipeline (the gesture
   *  was already consumed, no follow-on tap should register). */
  private _consumedAtPenDown = false;

  // ── Slim Pen bridge (optional native helper) ──────────────────────────
  // Auto-connects to the local slim-pen-bridge service if it's running.
  // The journal works fine without it; the bridge is an additive layer
  // for features the browser can't reach (top click, haptics, raw HID).
  private _slimPenBridge = new SlimPenBridge();
  private _slimPenStatus: BridgeStatus = 'disconnected';
  private _slimPenInfo: BridgePenInfo | null = null;
  private _slimPenCaps: BridgeCapabilities = {};
  /** When the bridge last reported battery, in performance.now() time.
   *  Lets the settings panel show "battery 81% (read 12 s ago)" so the
   *  reading actually feels live rather than just a static flag. */
  private _slimPenBatteryReadAt = 0;
  /** Ring buffer of the last few pen-events forwarded by the bridge.
   *  Shown in the Slim Pen panel — useful for identifying which
   *  proprietary GATT characteristic carries the top click while
   *  iterating with probe_buttons.py. */
  private _slimPenEvents: Array<{ ts: number; source: string; preview: string }> = [];
  /** Browser-side capabilities, derived directly from PointerEvents.
   *  Independent of the bridge — these reflect what THIS browser is
   *  actually receiving from the hardware via PointerEvents. */
  private _browserPenCaps = {
    tilt: false,         // any non-zero tiltX/tiltY seen
    pressure: false,     // any 0 < pressure < 1 seen
    eraserSeen: false,   // eraser-typed pointerdown observed
  };
  /** Latest tilt + pressure samples from a pen-typed event. The Slim
   *  Pen settings panel shows these live so the user can validate
   *  readings are happening (rather than just a static "yes" flag). */
  private _liveTilt = { tiltX: 0, tiltY: 0, pressure: 0, ts: 0 };

  // ── Stylus side-button shortcuts ───────────────────────────────────────
  private _stylusBindings = new StylusBindingEngine();
  /** Recent ink tools, head = current. The 'tool-swap' action toggles
   *  between the two most recent. */
  private _recentTools: string[] = [];
  /** True when the permanent left-margin stylus sidebar is showing.
   *  Toggled by the same routes that previously opened the floating
   *  stylus menu (cadence summon, side-button shortcut). Persists
   *  via settings.stylusSidebarVisible so the user's preference
   *  survives reloads. */
  private _stylusSidebarVisible = false;
  /** Cached sidebar layout for hit testing — rebuilt every render
   *  from the active tool's params so pre-selections track. */
  private _stylusSidebarLayout: ReturnType<typeof buildSidebarLayout> | null = null;
  /** Animated content-eviction fragments. Populated when the sidebar
   *  toggles on and clips ink from its column; the render loop draws
   *  the live ones on top of normal ink each frame and drops them as
   *  their animations finish. */
  private _marginWipe = new MarginWipeLayer();
  /** One trail ghost per eraser drag. Active ghost (fadeStart=null)
   *  collects points as the user moves; on pen-up its fadeStart is
   *  set and the ghost dies over ERASER_TRAIL_FADE_MS. Each ghost
   *  carries the size + opacity it was started with so a mid-drag
   *  param change (rare) doesn't visually warp the trail, and so
   *  the render width tracks whatever the user picked when the
   *  drag began. */
  private _eraserGhosts: Array<{
    points: Array<{ x: number; y: number }>;
    size: number;
    opacity: number;
    fadeStart: number | null;
  }> = [];
  private _eraserActiveGhost: {
    points: Array<{ x: number; y: number }>;
    size: number;
    opacity: number;
    fadeStart: number | null;
  } | null = null;
  /** Tool id to restore when the hardware eraser-end lifts. The
   *  back-tip switches the registry to 'eraser' on pen-down so the
   *  sidebar visually highlights it; pen-up flips back so the user
   *  resumes whichever tool they had picked. Null when the back-tip
   *  isn't currently in use. */
  private _hardwareEraserPrevTool: PenStyle | null = null;
  /** True while a tool-swap is currently being held by an active
   *  draw-while-pressed gesture. Reverted at pen-up via onStrokeEnd
   *  so the swap is scoped to the side-held stroke. UNLESS press-
   *  and-tap is also bound to tool-swap and fires, in which case the
   *  flag is cleared without reverting (the user asked for the swap
   *  to stick on tap). */
  private _stylusSwapHeld = false;
  /** True while draw-while-pressed is bound to 'shape-boost' and the
   *  side button is held. Forces shape recognition to 'full' mode
   *  with a permissive confidence threshold for the duration of the
   *  stroke; reverted via onStrokeEnd so the boost is scoped to the
   *  gesture and never affects subsequent strokes. */
  private _shapeBoostHeld = false;
  /** True when any pointer (pen in proximity OR mouse cursor) is over
   *  the canvas. Drives the 'hovering' vs 'idle' context split. Mouse
   *  and pen are treated identically — a pointer over the canvas
   *  without an active stroke is "hovering". */
  private _pointerPresent = false;
  /** Last seen pointer position in page-local coords. Used as the
   *  fallback action point when a button event comes from a non-canvas
   *  source (e.g. system right-click that doesn't hit the canvas). */
  private _lastPointerX = 0;
  private _lastPointerY = 0;
  /** Pointer type and button bitmask of the most recent event — for
   *  the debug HUD. */
  private _lastPointerType: string = '';
  private _lastPointerButtons = 0;
  private _lastButtonEvent = '';

  // ── Undo / Redo ─────────────────────────────────────────────────────────
  private _undoStack: UndoEntry[] = [];
  private _redoStack: UndoEntry[] = [];
  /** When > 0, incoming pushUndo calls are batched into one composite entry. */
  private _groupDepth = 0;
  private _groupBuffer: UndoEntry[] = [];

  // Spell pipeline
  private _spellRegistry!: SpellRegistry;
  private _spellAllPoints: StrokePoint[] = [];   // full stroke points (for classification)

  // ── Invocation system (Tap Rings) ──────────────────────────────────────
  // Self-managing ring interactors handle cadence, visuals, and interaction.
  private _tapRingLayer = new TapRingLayer();
  private _tapAudio = new TapAudio();
  private _cadence = new CadenceEngine();
  /** Set at pen-down when peekResolve says this tap will resolve a
   *  pattern — the bright `playInvokeStrike` already played, so the
   *  pen-up onResolved suppresses its own playResolve chord. Cleared
   *  in _finishPointerUp regardless of whether the touch became a
   *  tap or a stroke. */
  private _pendingInvokeStrike: string | null = null;

  // ── Ink deferral — buffer points until tap-vs-stroke is decided ─────────
  /** When true, pointerdown has fired but ink layer hasn't started yet. */
  private _inkDeferred = false;
  /** Buffered stroke points during deferral (flushed to ink on stroke confirm). */
  private _deferredPoints: import('@shared/notebook_model').StrokePoint[] = [];

  // ── Drag-to-tap tracking ────────────────────────────────────────────────
  /** Timestamp of pointer-down when a drag started (used for tap detection). */
  private _dragDownTime = 0;

  // ── Tap effect layer (universal tap visuals) ────────────────────────────
  // Library of named effects ('ring' = subtle outline, 'burst' = pinch
  // golden disc). Spawned via the `_pushTapEffect` helper below, which
  // reads the user's `tapEffect` choice, applies the layout offset, and
  // queues with the right (default or themed) colour. Gated by Settings
  // → Visuals → Animations through `setTapEffectsEnabled`.
  private _tapFx = new TapEffectLayer();

  // ── Tap eligibility cooldown ─────────────────────────────────────────────
  // After a real (non-tap) ink stroke lifts, the next pen-down is treated
  // as ink rather than a tap — small marks made while writing (the dot of
  // an "i", an accent, etc.) shouldn't be classified as cadence taps and
  // shouldn't fade out.  Once enough idle time elapses since the last ink
  // pen-up, the cooldown clears and the next short touch is a real tap
  // again. A confirmed tap does NOT update _lastInkUpTime, so the second,
  // third, … taps of a rhythmic invocation all qualify in succession.
  private _lastInkUpTime = -Infinity;
  private _tapEligibleAtDown = true;

  /** Page ids whose most recent load attempt failed. The host marks
   *  these via `page.loadFailed` messages; the canvas surfaces them
   *  as a red-cross icon on the side rail and as an explicit error
   *  overlay when one is the current page. */
  private _failedPageIds = new Set<string>();

  /** The pageId the user was last parked on — recorded any time we
   *  switch the page-mode current page. Used by the "back" tab as a
   *  fallback when there's no parent folder to climb to (e.g. user is
   *  at the root landing, or returning from settings/codex). */
  private _lastPageId: string | null = null;

  /** Stylus pressure for the most recent tap, captured before
   *  `_cadence.feedTap` so the cadence's `onTapAccepted` callback can
   *  scale the visual feedback ring without needing the pressure to
   *  travel through the engine. */
  private _lastTapPressure = 0.6;
  /** Pen-down timestamp captured for an eligible candidate-tap stroke;
   *  0 if this pen-down isn't tap-eligible. Used at pen-up to compute
   *  the drum's release hold-time. */
  private _tapDownTime = 0;
  private _isWandInvoked = false;
  /** Page-local position where the wand was invoked. Used to scope
   *  cast-mode reveals (shape hints, astral text shadows) to a
   *  radius around it instead of revealing the whole page. */
  private _wandInvocationPoint: { x: number; y: number } | null = null;
  /** Auto-cancel wand mode if no stroke begins within this timeout. */
  private _wandTimeout = 0;
  /** Multi-stroke: timeout after pen-up before classifying and finalizing. */
  private _wandCastTimeout = 0;
  /** Delayed flux resolution — gives time for multi-stroke continuation. */
  private _fluxResolveTimeout = 0;

  /** Spell name toast shown briefly after a successful cast. */
  private _spellToast: { name: string; family: string; x: number; y: number; birthMs: number } | null = null;

  // ── Strand Calculus / Flux Engine ────────────────────────────────────────
  /** Live strand graph for current wand cast. */
  private _strandGraph: StrandGraph = createStrandGraph();
  /** Spatial hash for crossing detection during live drawing. */
  private _strandHash = new SegmentHash();
  /** Lagrangian flux simulation state. */
  private _fluxState: FluxState = createFluxState();
  /** Timestamp of last flux tick (ms). */
  private _lastFluxTick = 0;
  /** Precomputed rectangular-loop flags for rendering. */
  private _rectLoopCache = new Map<number, boolean>();
  /** Whether the flux system has produced a resolution this cast. */
  private _fluxResolved = false;

  /** Brief bright flicker on copy — bbox to highlight. */
  private _copyFlash: { bbox: { x: number; y: number; width: number; height: number }; birthMs: number } | null = null;

  /** Clipboard for copy/paste spells: snapshot of block(s). */
  private _spellClipboard: { blocks: Array<{ kind: string; bbox: { x: number; y: number; width: number; height: number }; content?: unknown }>; strokes: Array<{ id: string; points: unknown[]; style: unknown }> } | null = null;

  /** True when the current page has at least one NPC sigil block. */
  private _hasNpcOnPage = false;
  /** Frame counter used to skip NPC movement tick on alternate frames. */
  private _npcTickFrame = 0;
  /** Shared timestamp for the current frame — avoids repeated Date.now() calls. */
  private _frameNow = 0;

  // ── Single-tap deferred action ──────────────────────────────────────────
  private _singleTapTimer = 0;
  private _pendingSingleTap: { x: number; y: number } | null = null;

  // ── Pop dialog (inline page-scrawled menus) ─────────────────────────────
  private _popDialog = new PopDialogLayer();
  private _popDialogStrokeActive = false;

  // ── Snap subsystem ──────────────────────────────────────────────────────
  private _snap = new SnapManager();
  /** Live snap indicator — nearest target while drawing. */
  private _snapIndicator: SnapResult | null = null;
  /** Snap that locked the stroke start point. */
  private _snapStart: SnapResult | null = null;

  // ── Text input overlay ──────────────────────────────────────────────────
  private _textInput = new TextInputLayer();

  // ── Handwriting recognition (OCR) ──────────────────────────────────────
  private _hwreco = new HandwritingRecognizer();

  // ── Off-thread recognition bridges (Web Workers) ──────────────────────
  private _shapeBridge = new ShapeBridge();
  private _spellBridge = new SpellBridge();

  // ── Handwriting → text composition session ─────────────────────────────
  private _textCompose!: TextComposition;
  /** Birth stamp of the last hint for which we deployed a ring. */
  private _lastHintRingBirth = 0;

  // ── Arcane analysis animation ───────────────────────────────────────────
  private _arcaneAnim: ArcaneAnim | null = null;
  /** Persona detected by _classifyRune — read by _finaliseArcaneBlock. */
  private _lastDetectedPersona: 'ireth' | 'laura' | null = null;

  // ── Debug flash overlay (visual stage reporting) ───────────────────────
  private _debugFlashes: Array<{
    x: number; y: number;
    color: string; label: string;
    birthMs: number;
    shape: 'circle' | 'rect' | 'x';
    w?: number; h?: number;
  }> = [];

  // ── Selection widgets (centralized cross/lace/tap selection) ─────────
  private _pageHeaderRegion = new SelectionWidget();
  private _sectionHeaderRegion = new SelectionWidget();
  private _settingsRegion = new SelectionWidget();
  private _dissolves = new DissolveManager();

  // ── Settings ────────────────────────────────────────────────────────────
  private _settings: GrimoireSettings = { ...DEFAULT_SETTINGS };
  /** Hit data for settings options, rebuilt each frame. */
  private _settingsHitItems: WidgetItem[] = [];

  /** FSA vault status, pushed by the host on init and after vault state changes.
   *  - supported: true on Chromium-based browsers (desktop + Android).
   *  - active: a directory handle is currently the persistence backend.
   *  - folderName: display label of the picked folder, when active. */
  private _vaultStatus: { supported: boolean; active: boolean; folderName: string | null } = {
    supported: typeof (window as unknown as { showDirectoryPicker?: unknown }).showDirectoryPicker === 'function',
    active: false,
    folderName: null,
  };

  /** Active settings sub-page. Paginated to fit the fixed canvas
   *  height — overflow would otherwise hide later sections like the
   *  Vault folder picker. The four pages mirror logical groupings the
   *  user navigates the journal by:
   *    0 — Hints & Spell detection
   *    1 — Visuals & Debug
   *    2 — Drawing (default pen)
   *    3 — Vault & Notebook stats */
  private _settingsPage: 0 | 1 | 2 | 3 | 4 = 0;

  // ── Handle drag state ──────────────────────────────────────────────────
  // When a stroke starts near a selection handle, it enters drag mode:
  // the handle follows the pen to the stroke endpoint, updating the bbox.
  private _dragHandle: {
    handleId: import('./notebook_state').HandleId;
    lastX: number;
    lastY: number;
    lastAngle: number;
    originX: number;
    originY: number;
  } | null = null;

  // ── Vertex drag state (individual figure vertex) ───────────────────────
  private _dragVertex: {
    blockId: string;
    vertexIndex: number;  // -1 = center sentinel for circle/ellipse/point
    lastX: number;
    lastY: number;
  } | null = null;

  // ── Control point drag state (bezier CP on figure edge) ────────────────
  private _dragCP: {
    blockId: string;
    edgeIndex: number;
    cpIndex: number;
    lastX: number;
    lastY: number;
  } | null = null;

  // Pending spell queued while the charge engine runs
  private _pendingSpell: {
    spellId: string;
    params: Record<string, unknown>;
    confidence: number;
    family: string;
  } | null = null;

  // Page turn gesture
  private _pageTurnDragStart: { x: number; y: number } | null = null;
  private _pageTurnProgress = 0;
  private _pageTurnDirection: 'forward' | 'back' = 'forward';

  // View mode: which special view is active
  private _viewMode: 'page' | 'codex' | 'settings' = 'page';

  // ── Page groups ──────────────────────────────────────────────────────────
  // Each tab represents a group of ordered pages.  Journal pages are one group,
  // codex pages are auto-generated from the spell registry, etc.

  /** Intra-group page index (for codex, index, settings — multi-page groups). */
  private _groupPageIndex = 0;

  // ── Interaction context (the "where gate") ──────────────────────────────
  // Resolved once per gesture on pointerdown.  Locked for the stroke duration.

  private _activeContext: InteractionContext = JOURNAL_CONTEXT;
  private _activeBoxId: string | null = null;
  /** Current page-level interaction policy (rebuilt on page/view change). */
  private _policy: PageInteractionPolicy = { defaultContext: JOURNAL_CONTEXT, boxes: [] };

  // ── Codex feedback ──────────────────────────────────────────────────────
  private _codexFeedback: { text: string; alpha: number; family: string } | null = null;

  // Tab bar hit rects (updated each frame)
  private _tabHitRects: Array<{ index: number; x: number; y: number; w: number; h: number; kind?: string }> = [];
  private _pageSidebarHitRects: Array<{ index: number; x: number; y: number; w: number; h: number; kind?: string; pageId?: string; nodePath?: string }> = [];
  /** Sheet paginator arrows rendered in the page header. */
  private _sheetArrowHitRects: Array<{ blockId: string; dir: -1 | 1; x: number; y: number; w: number; h: number }> = [];
  private _tabBarHeight = 30;

  // Codex page-nav arrow hit rects (updated each frame)
  private _codexNavHitRects: Array<{ dir: 'back' | 'forward'; x: number; y: number; w: number; h: number }> = [];

  // Action scroll hit rects (clock-skip, option buttons)
  private _scrollHitRects: ScrollHitRect[] = [];

  // Folder-landing content-row hit rects (canvas-absolute, regenerated per
  // frame when the current page is a folder landing).
  private _landingHitRects: Array<{ pageId: string; x: number; y: number; w: number; h: number }> = [];

  // ── Unified UI hit rects (canvas-absolute, updated each frame) ──────────
  private _uiHitRects: UiHitRect[] = [];

  // ── Journal side-tab navigation ──────────────────────────────────────────
  /** Maps parent directory path → last canvas pageId visited at that level.
   *  Used so "level up" returns to where the user was on the parent level. */
  private _navHistory = new Map<string, string>();
  /** Current sibling context for the side tabs — recomputed on every page change. */
  private _journalNavContext: {
    parentPath: string;
    parentTitle: string;
    grandparentPath: string | null;
    items: Array<{
      id: string; title: string; isFolder: boolean;
      pageId?: string; isActive: boolean;
    }>;
  } | null = null;

  // Page header inline color swatches hit data (updated each frame)
  private _pageHeaderColorSwatches: Array<{ color: string; x: number; y: number; r: number }> = [];

  // Section header page interaction hit data (updated each frame)
  private _sectionHeaderHitData: {
    nameLineRect: { x: number; y: number; w: number; h: number };
    colorSpots: Array<{ color: string; x: number; y: number; r: number }>;
  } | null = null;

  // Page header title hit rect (updated each frame)
  private _pageHeaderTitleRect: { x: number; y: number; w: number; h: number } | null = null;

  // RAF handle
  private _rafHandle = 0;
  /** Last frame timestamp (seconds) for delta calculation. */
  private _lastFrameT = 0;
  /** Wall-clock ms of the last astral-discard sweep. Throttled to ~200ms. */
  private _lastAstralTick = 0;
  /** Debug dialog — surfaced by the SL tap cadence. Generic tabbed
   *  panel surfacing every page-scoped lever: styling, spells,
   *  invocations, helpers. Content rebuilt every frame from
   *  `_buildDebugTabs()` so rows reflect live state. */
  private _debugDialog = new DebugDialog(() => this._buildDebugTabs());
  private _debugDragActive = false;

  // ── New spell engine (P2) ─────────────────────────────────────────────
  // Sits alongside the legacy spell pipeline behind a runtime flag, so
  // we can A/B test gestures before flipping over. Single-stroke
  // evaluation at release — see spell/engine.ts.
  private _newSpellEngine = new SpellEngine();
  /** New spell engine is the default. Opt-out via `localStorage
   *  'ox-new-spells' === 'false'` to fall back to the legacy pipeline. */
  private _newSpellsEnabled(): boolean {
    try { return localStorage.getItem('ox-new-spells') !== 'false'; }
    catch { return true; }
  }

  /** Build the EngineContext for a one-shot stroke evaluation. The
   *  geometry closures use the current page + ink layer. */
  private _buildEngineContext(): NewSpellEngineCtx {
    const page = this._state?.currentPage();
    const blocks = page?.blocks;
    const strokes = this._ink.getStrokes();

    const bboxContains = (
      bbox: { x: number; y: number; width: number; height: number },
      p: PModelPoint,
    ): boolean => (
      p.x >= bbox.x && p.x <= bbox.x + bbox.width &&
      p.y >= bbox.y && p.y <= bbox.y + bbox.height
    );

    /** Bbox ∈ lasso — generous "user circled this content" predicate.
     *  TWO sufficient conditions, either selects:
     *    (1) bbox CENTRE inside the lasso polygon, OR
     *    (2) ≥ 30% of a 4×4 grid sampled across the bbox inside the polygon.
     *  Either gate is enough — keeps the gesture aggressive without
     *  picking up content the lasso clearly missed. */
    const MC_THRESHOLD = 0.3;
    const bboxMostlyInRegion = (
      bbox: { x: number; y: number; width: number; height: number },
      region: RegionBounds,
    ): boolean => {
      const cx = bbox.x + bbox.width / 2;
      const cy = bbox.y + bbox.height / 2;
      if (!region.points || region.points.length < 3) {
        const r = region.bbox;
        // Centre inside region.bbox is good enough for a degenerate polygon.
        if (cx >= r.x && cx <= r.x + r.width && cy >= r.y && cy <= r.y + r.height) {
          return true;
        }
        const ix = Math.max(0, Math.min(bbox.x + bbox.width, r.x + r.width) - Math.max(bbox.x, r.x));
        const iy = Math.max(0, Math.min(bbox.y + bbox.height, r.y + r.height) - Math.max(bbox.y, r.y));
        const blockArea = Math.max(1, bbox.width * bbox.height);
        return (ix * iy) / blockArea >= MC_THRESHOLD;
      }
      // (1) Centre-in-polygon — the cheap intuitive case.
      if (pointInPolygon(cx, cy, region.points)) return true;
      // (2) Monte Carlo — for blocks the centre missed but lasso clipped.
      const cols = 4, rows = 4, total = cols * rows;
      let inside = 0;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const sx = bbox.x + ((c + 0.5) / cols) * bbox.width;
          const sy = bbox.y + ((r + 0.5) / rows) * bbox.height;
          if (pointInPolygon(sx, sy, region.points)) inside++;
        }
      }
      return inside / total >= MC_THRESHOLD;
    };

    /** Squared distance from a point to a line segment. */
    const pointToSegmentDist2 = (
      p: PModelPoint, a: PModelPoint, b: PModelPoint,
    ): number => {
      const dx = b.x - a.x, dy = b.y - a.y;
      const len2 = dx * dx + dy * dy;
      if (len2 === 0) {
        const ex = p.x - a.x, ey = p.y - a.y;
        return ex * ex + ey * ey;
      }
      let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
      t = Math.max(0, Math.min(1, t));
      const cx = a.x + t * dx, cy = a.y + t * dy;
      const ex = p.x - cx, ey = p.y - cy;
      return ex * ex + ey * ey;
    };

    /** True if any sample of the stroke comes within `proximity` of the
     *  line segment a→b. Robust to gaps between consecutive stroke
     *  points — treats the line as a thick eraser brush. */
    const LINE_CROSS_PROXIMITY = 12;       // px
    const LINE_CROSS_PROXIMITY2 = LINE_CROSS_PROXIMITY * LINE_CROSS_PROXIMITY;
    const strokeCrossedByLine = (
      points: ReadonlyArray<{ x: number; y: number }>,
      from: PModelPoint, to: PModelPoint,
    ): boolean => {
      for (const p of points) {
        if (pointToSegmentDist2(p, from, to) < LINE_CROSS_PROXIMITY2) return true;
      }
      return false;
    };

    return {
      pageId: this._state?.currentPageId ?? null,
      selection: this._state.selection,
      origin: 'gesture',
      blockAtPoint: (p) => {
        if (!blocks) return null;
        for (const [id, b] of blocks) {
          if (bboxContains(b.bbox, p)) return id;
        }
        return null;
      },
      blocksInRegion: (region) => {
        const ids: string[] = [];
        if (!blocks) return ids;
        // Monte Carlo: ≥60% of the block's bbox samples must fall inside
        // the lasso polygon. Generous enough that loose loops still
        // select; tight enough that lassos drawn next to (not over)
        // content don't pull it in.
        for (const [id, b] of blocks) {
          if (bboxMostlyInRegion(b.bbox, region)) ids.push(id);
        }
        return ids;
      },
      strokesInRegion: () => {
        // Selection is blocks-only — strokes inside ink blocks are
        // covered by their owning block's containment.
        return [];
      },
      strokesCrossingLine: (from, to) => {
        const ids: string[] = [];
        for (const [id, s] of strokes) {
          if (strokeCrossedByLine(s.points, from, to)) ids.push(id);
        }
        return ids;
      },
      findInkBlocksByStrokes: (sids) => {
        // Walk page.blocks once, pick the ink blocks whose strokeIds set
        // includes any of the listed stroke ids. De-duplicated.
        if (!blocks) return [];
        const want = new Set(sids);
        const out: string[] = [];
        for (const [bid, b] of blocks) {
          if (b.kind !== 'ink') continue;
          const ink = b as import('./notebook_state').WvInkBlock;
          for (const sid of ink.strokeIds) {
            if (want.has(sid)) { out.push(bid); break; }
          }
        }
        return out;
      },
    };
  }

  /** Public wrapper used by `_tryFluxResolution` — returns true when a
   *  spell was matched (regardless of action count). The caller uses
   *  the boolean to decide success vs misfire teardown animation. */
  private _tryNewSpellEngineMatched(
    strokePts: ReadonlyArray<{ x: number; y: number; timestamp: number }>,
  ): boolean {
    const ctx = this._buildEngineContext();
    const result = this._newSpellEngine.evaluate(strokePts, ctx);
    if (!result.match || !result.spellId) {
      console.log(`[new-spell] shape=${result.shape.kind} → no match`);
      return false;
    }
    console.log(
      `[new-spell] '${result.spellId}' detected (shape=${result.shape.kind}) → ${result.actions.length} action(s)`,
    );
    for (const a of result.actions) this._dispatch(a);
    const allPts = this._spell.allPoints.length > 0
      ? this._spell.allPoints
      : this._spellAllPoints;
    const toastPt = allPts[Math.floor(allPts.length / 2)];
    if (toastPt) {
      this._spellToast = {
        name: shortSpellLabel(result.spellId),
        family: 'act',
        x: toastPt.x,
        y: toastPt.y - 12,
        birthMs: performance.now(),
      };
    }
    return true;
  }

  /** Legacy entry point — returns dispatched action count. Kept for
   *  any callers that branch on "did it do anything". The matched-only
   *  variant above is the one used in the cast-resolution path. */
  private _tryNewSpellEngine(strokePts: ReadonlyArray<{ x: number; y: number; timestamp: number }>): number {
    if (!this._newSpellsEnabled()) return 0;
    if (strokePts.length < 2) return 0;
    const ctx = this._buildEngineContext();
    const result = this._newSpellEngine.evaluate(strokePts, ctx);

    if (!result.match || !result.spellId) {
      console.log(`[new-spell] shape=${result.shape.kind} → no match`);
      return 0;
    }
    console.log(
      `[new-spell] '${result.spellId}' detected (shape=${result.shape.kind}) → ${result.actions.length} action(s)`,
    );
    for (const a of result.actions) this._dispatch(a);

    // Toast fires on detection, not on action count — same parchment
    // styling as the legacy path so the on-canvas feedback is uniform.
    const allPts = this._spell.allPoints.length > 0
      ? this._spell.allPoints
      : this._spellAllPoints;
    const toastPt = allPts[Math.floor(allPts.length / 2)];
    if (toastPt) {
      this._spellToast = {
        name: shortSpellLabel(result.spellId),
        family: 'act',
        x: toastPt.x,
        y: toastPt.y - 12,
        birthMs: performance.now(),
      };
    }
    return result.actions.length;
  }
  /** Adventure day length in real seconds (5 minutes = one in-world day). */
  private static readonly ADV_DAY_LENGTH_S = 300;

  // ── Agent subsystem ─────────────────────────────────────────────────────
  private _agent: AgentOrchestrator | null = null;
  /** Streaming text fragments awaiting animation. */
  private _agentStreamQueue: Array<{ persona: string; delta: string; done: boolean }> = [];
  /** Active streaming animation state per page. */
  private _agentStreamAnim: {
    persona: string;
    text: string;
    revealedChars: number;
    blockId: string;
    pageId: string;
    startTime: number;
    done: boolean;
  } | null = null;
  /** NPC presence indicators for current page. */
  // NPC presence folded into arcane block .activated state

  // ──────────────────────────────────────────────────────────────────────────

  constructor(container: HTMLElement) {
    this._canvas = document.createElement('canvas');
    this._canvas.style.cssText = 'position:absolute;top:0;left:0;touch-action:none;cursor:crosshair;';
    container.appendChild(this._canvas);

    const ctx = this._canvas.getContext('2d');
    if (!ctx) { throw new Error('Canvas 2D not available'); }
    this._ctx = ctx;
    this._vscode = acquireVsCodeApi();

    this._bindEvents();
    this._resize();
    window.addEventListener('resize', () => this._resize());

    // Cadence patterns — sequences of S (short gap) / L (long gap)
    // between consecutive taps. Tap count = sequence length + 1.
    // Single absolute threshold separates S from L (cadence_engine
    // owns the constant). We don't reset on resolve, so a 3-tap 'SL'
    // chain fires the stylus menu even though 'S' already fired cast
    // on the second tap.
    //
    //   WAND_CAST       = 'S'  →  2 taps (tap-tap quick)
    //   STYLUS_MENU     = 'SL' →  3 taps (quick pair, pause, tap)
    //   STYLUS_MENU_ALT = 'LS' →  3 taps (tap, pause, quick pair)
    //
    // Both 3-tap rhythms (one S + one L gap, in either order) summon
    // the menu — symmetric, fun, hard to misfire. SS and LL never
    // resolve anything by design.
    const WAND_CAST: CadencePattern = {
      id: 'thrice-knock',
      sequence: 'S',
      spreadPx: 80,
      priority: 10,
    };
    // SL: short-gap then long-gap — surfaces the debug menu (current
    // injection channel for decoration / page-style invocations until
    // the wand-cast pipeline grows them).
    // LS: long-gap then short-gap — keeps the stylus sidebar binding.
    // Cast (S, 2 taps quick) still fires intra-chain on the second
    // tap when present; the third tap's resolve cancels it.
    const DEBUG_MENU_SL: CadencePattern = {
      id: 'debug-menu',
      sequence: 'SL',
      spreadPx: 80,
      priority: 10,
    };
    const STYLUS_MENU_LS: CadencePattern = {
      id: 'stylus-summon',
      sequence: 'LS',
      spreadPx: 80,
      priority: 10,
    };
    this._cadence.register(WAND_CAST);
    this._cadence.register(DEBUG_MENU_SL);
    this._cadence.register(STYLUS_MENU_LS);

    // Wire cadence callbacks.
    //   onTapAccepted — every tap. Paint a pulse-coloured ring + note.
    //   onResolved    — pattern matched. Flash a themed resolve burst
    //                   at the centroid, audio chord, and fire the
    //                   invocation. Cast is implicit for thrice-knock;
    //                   any other pattern cancels the cast first.
    //   onSingleTap   — lone first-tap with no follow-up. Run the
    //                   single-tap fallback (caret reposition / dismiss).
    //   onChainEnd    — multi-tap chain timed out without a fresh
    //                   resolve. Cast (if still on) was put up by an
    //                   earlier resolve in the same chain, so dismiss
    //                   it now that the chain is done.
    // Stylus side-button shortcut callback — the engine fires
    // ── Tool registry → _penStyle mirror ─────────────────────────
    // The registry is the source of truth for per-tool params.
    // Every change (tool switch or param edit) refreshes the
    // synchronous _penStyle snapshot so the rest of the canvas
    // (stylus menu pre-select, stroke commit payloads, ink layer
    // begin) doesn't have to know about the registry.
    this._toolRegistry.onChange = () => this._mirrorActiveToolToPenStyle();

    // draw-while-pressed at pen-down (or via late-grace), and the
    // canvas applies the action as a HELD modal state for the rest
    // of the stroke. press-and-tap fires alongside at pen-up if the
    // stroke didn't move; press-while-drawing is a one-shot if side
    // arrives mid-draw.
    this._stylusBindings.onAction = (action, x, y, trigger) => {
      // ── Held actions on draw-while-pressed ──────────────────────────
      // These don't run synchronously through _executeStylusAction —
      // they set a "held" flag that the rest of the canvas reads, and
      // onStrokeEnd reverts. Only the visual / audio feedback is the
      // same as a one-shot trigger.
      if (trigger === 'draw-while-pressed' && action === 'tool-swap') {
        if (this._recentTools.length >= 2 && !this._stylusSwapHeld) {
          this._swapRecentTools();
          this._stylusSwapHeld = true;
        }
        this._flashStylusFeedback(x, y);
        return;
      }
      // shape-boost is "held" on either draw-while-pressed (set at
      // pen-down with side held) or press-while-drawing (opt-in to
      // boost mid-stroke). Both flip the same flag; onStrokeEnd
      // clears it once the stroke ends.
      if ((trigger === 'draw-while-pressed' || trigger === 'press-while-drawing')
          && action === 'shape-boost') {
        this._shapeBoostHeld = true;
        this._flashStylusFeedback(x, y);
        return;
      }
      // press-and-tap of tool-swap on top of an already-held swap
      // means "make the swap permanent" — cancel the revert and
      // suppress the second swap (we'd otherwise undo and re-apply).
      if (trigger === 'press-and-tap' && action === 'tool-swap' && this._stylusSwapHeld) {
        this._stylusSwapHeld = false;
        this._flashStylusFeedback(x, y);
        return;
      }
      // press-and-tap of shape-boost is meaningless as a one-shot
      // (there's no stroke to boost), and during a held boost the
      // user clearly didn't draw — feedback only.
      if (action === 'shape-boost') {
        this._flashStylusFeedback(x, y);
        return;
      }
      // For everything else: revert any held swap FIRST (so the
      // user's bound press-and-tap action runs against the original
      // tool, not the alt one), then dispatch normally.
      if (trigger === 'press-and-tap' && this._stylusSwapHeld) {
        this._stylusSwapHeld = false;
        this._swapRecentTools();
      }
      this._executeStylusAction(action, x, y);
    };

    // End-of-stroke bookkeeping. If draw-while-pressed held any
    // modal state and nothing during the stroke explicitly cancelled
    // the revert, undo it here so each held action is strictly
    // scoped to the side-held gesture.
    this._stylusBindings.onStrokeEnd = (_fired) => {
      if (this._stylusSwapHeld) {
        this._stylusSwapHeld = false;
        this._swapRecentTools();
      }
      if (this._shapeBoostHeld) {
        this._shapeBoostHeld = false;
      }
    };

    // Slim Pen bridge — optional local helper for the Slim Pen 2's
    // OS-captured features (top click / haptics / raw HID). Auto-
    // connects in the background; if the helper isn't running, this
    // stays disconnected and the journal works as before.
    this._slimPenBridge.onStatusChange = (status, info, caps) => {
      this._slimPenStatus = status;
      this._slimPenInfo = info;
      this._slimPenCaps = caps;
      if (status === 'connected' && info?.battery != null) {
        // Treat the freshly-arrived hello/info as a battery sample.
        this._slimPenBatteryReadAt = performance.now();
      }
    };
    this._slimPenBridge.onPenEvent = (event) => {
      // Maintain a ring buffer of the last 6 events for the panel.
      const preview = typeof event.value === 'number'
        ? String(event.value)
        : (typeof event.value === 'string' ? event.value : JSON.stringify(event.value ?? ''));
      this._slimPenEvents.unshift({
        ts: performance.now(),
        source: event.source ?? event.kind,
        preview,
      });
      if (this._slimPenEvents.length > 6) this._slimPenEvents.length = 6;
      if (event.source === 'battery') {
        this._slimPenBatteryReadAt = performance.now();
      }
      // Stage 4 hookup goes here: when source === 'top-click', dispatch
      // through the stylus binding engine. Waiting on the UUID
      // identification via probe_buttons.py.
    };
    this._slimPenBridge.start();

    this._cadence.onTapAccepted = (_idx, patId, pulse, x, y) => {
      const climax = patId === 'thrice-knock' ? 'cast'
        : patId === 'stylus-summon' ? 'stylus'
        : patId === 'debug-menu' ? 'stylus'
        : null;
      this._pushTapEffect(x, y, { climax, pressure: this._lastTapPressure });
      // Audio: per-pulse note for neutral taps. For climax taps the
      // pen-down strike (or playResolve in onResolved) covers the audio.
      if (!climax) {
        this._tapAudio.playTapPulse(pulse);
      }
    };
    this._cadence.onResolved = (id, taps) => {
      const mx = taps.reduce((s, t) => s + t.x, 0) / taps.length;
      const my = taps.reduce((s, t) => s + t.y, 0) / taps.length;
      // Audio: if pen-down already fired the bright invocation strike
      // for this same id, that single hit *is* the feedback — skip the
      // staggered chord (it would land 100+ ms later and feel laggy).
      if (this._pendingInvokeStrike !== id) {
        this._tapAudio.playResolve(id);
      }
      if (id === 'stylus-summon') {
        // Stylus menu fires on tap 3 of an LS chain. Cast did not fire
        // on the first L gap, but cancel defensively in case other
        // chains intersected.
        if (this._isWandInvoked) this._cancelWand();
        this._toggleStylusSidebar(mx, my);
      } else if (id === 'debug-menu') {
        // Debug menu (SL) — cast came up on tap 2's S; tap 3 swaps it
        // for the debug pop. Provisional injection channel for
        // decoration / page-style invocations.
        if (this._isWandInvoked) this._cancelWand();
        this._toggleDebugMenu(mx, my);
      } else if (id === 'thrice-knock') {
        this._invokeWand(mx, my);
      }
    };
    this._cadence.onSingleTap = (x, y) => {
      // A lone tap dismisses cast (single tap = "exit" gesture in
      // cast mode) before running the normal fallback.
      if (this._isWandInvoked) this._cancelWand();
      this._pendingSingleTap = { x, y };
      this._onSingleTapConfirmed();
    };
    this._cadence.onChainEnd = () => {
      // Multi-tap chain ended without a fresh resolve. If cast was
      // raised by an earlier resolve in the same chain (e.g. the user
      // tapped 'SS' but never followed up with the L for stylus menu),
      // it should fall away with the chain. Pen-down clears the wand
      // idle timeout, but onChainEnd fires only when the pen has been
      // up for the full continuation window, so this is safe to
      // unconditionally cancel.
      if (this._isWandInvoked) this._cancelWand();
    };

    // ── Handwriting composition session ──
    this._textCompose = new TextComposition(this._hwreco, {
      createBlock: (pageId, bbox, fontSize) => {
        const ts = Date.now();
        const blockId = `${pageId}.text.${ts.toString(36)}`;
        this._dispatch({ type: 'block.create', blockId, pageId, kind: 'text', bbox, origin: 'gesture', timestamp: ts });
        this._dispatch({ type: 'text.update', blockId, pageId, content: '', origin: 'gesture', timestamp: ts });
        this._pushUndo({
          reverse: [{ type: 'block.delete', blockId, pageId, origin: 'gesture', timestamp: ts }],
          forward: [{ type: 'block.create', blockId, pageId, kind: 'text', bbox, origin: 'gesture', timestamp: ts }],
        });
        // Set the derived fontSize on the block (makeWvBlock defaults to 14)
        const page = this._state?.currentPage();
        if (page) {
          const block = page.blocks.get(blockId);
          if (block && block.kind === 'text') {
            (block as import('./notebook_state').WvTextBlock).fontSize = fontSize;
          }
        }
        return blockId;
      },
      updateBlock: (blockId, pageId, content) => {
        this._dispatch({ type: 'text.update', blockId, pageId, content, origin: 'gesture', timestamp: Date.now() });
        // Fit block bbox to content (OCR text width) and ink words bounds
        const page = this._state?.currentPage();
        if (page) {
          const block = page.blocks.get(blockId);
          if (block && block.kind === 'text') {
            const tb = block as import('./notebook_state').WvTextBlock;
            // OCR text width
            this._ctx.font = `${tb.fontSize ?? 16}px Georgia, serif`;
            const tw = this._ctx.measureText(content || '').width + 12;
            // Ink words width: rightmost edge relative to block x
            let iw = 0;
            if (tb.inkWords) {
              for (const ink of tb.inkWords) {
                const right = ink.dstBBox.x + ink.dstBBox.w - tb.bbox.x;
                if (right > iw) iw = right;
              }
              iw += 12; // padding
            }
            tb.bbox = { ...tb.bbox, width: Math.max(tw, iw, 40) };
          }
        }
      },
      eraseStrokes: (pageId, strokeIds) => {
        for (const id of strokeIds) {
          this._eraseStrokeForSelection(id, pageId);
        }
      },
      getBlockContent: (blockId) => {
        const page = this._state?.currentPage();
        if (!page) return undefined;
        const block = page.blocks.get(blockId);
        if (!block || block.kind !== 'text') return undefined;
        return (block as import('./notebook_state').WvTextBlock).content;
      },
      measureTextWidth: (text, fontSize) => {
        this._ctx.font = `${fontSize}px Georgia, serif`;
        return this._ctx.measureText(text).width;
      },
      storeInkWord: (blockId, strokes, srcBBox, dstBBox) => {
        const page = this._state?.currentPage();
        if (!page) return;
        const block = page.blocks.get(blockId);
        if (!block || block.kind !== 'text') return;
        const tb = block as import('./notebook_state').WvTextBlock;
        if (!tb.inkWords) tb.inkWords = [];
        // Flatten all stroke points into a single polyline
        const pts: Array<{ x: number; y: number; pressure: number }> = [];
        for (const s of strokes) {
          for (const p of s.points) pts.push({ x: p.x, y: p.y, pressure: p.pressure });
        }
        tb.inkWords.push({
          points: pts,
          color: this._penStyle.color,
          size: this._penStyle.size,
          srcBBox: { x: srcBBox.x, y: srcBBox.y, w: srcBBox.width, h: srcBBox.height },
          dstBBox: { x: dstBBox.x, y: dstBBox.y, w: dstBBox.w, h: dstBBox.h },
        });
      },
    });

    this._vscode.postMessage({ type: 'ready' });
  }

  // ── Agent integration ──────────────────────────────────────────────────────

  /** Attach the LLM agent orchestrator (called from pwa_main after boot). */
  setAgent(agent: AgentOrchestrator): void {
    this._agent = agent;
  }

  /**
   * Invoke a character agent on the current page.
   * Maps arcane block types to persona IDs and streams the response.
   */
  private async _invokeAgent(persona: 'ireth' | 'laura', blockId: string): Promise<void> {
    if (!this._agent) return;

    const page = this._state?.currentPage();
    if (!page) return;

    const arcBlock = page.blocks.get(blockId);

    // Mark processing (greeting happens on sigil creation, not on ask)
    if (arcBlock && arcBlock.kind === 'arcane') {
      const arc = arcBlock as import('./notebook_state').WvArcaneBlock;
      arc.processing = true;
    }

    // Initialize streaming animation state
    this._agentStreamAnim = {
      persona,
      text: '',
      revealedChars: 0,
      blockId,
      pageId: page.id,
      startTime: 0,
      done: false,
    };

    // Build a Page-like object from webview state for the serializer
    const pageLike = this._buildPageForAgent(page);

    // Gather cross-NPC events for this page (other NPCs' actions)
    const pageEvents = this._state.getPageEvents(page.id);

    try {
      const output = await this._agent.invoke(
        persona,
        pageLike,
        blockId,
        undefined,  // No region bounds for now
        (token) => {
          // Stream tokens into the animation queue
          this._agentStreamQueue.push({
            persona,
            delta: token.delta,
            done: token.finishReason !== null,
          });
        },
        pageEvents,
      );

      // Record the NPC action as a page event for cross-NPC awareness
      if (output && page) {
        const evtKind = (output.kind === 'say' || output.kind === 'write-text'
          || output.kind === 'insert-block' || output.kind === 'displace'
          || output.kind === 'draw-glyph')
          ? output.kind as import('./notebook_state').PageEvent['kind']
          : 'say';
        this._state.pushPageEvent(page.id, {
          persona: persona as 'ireth' | 'laura',
          kind: evtKind,
          content: output.content ?? (output as any).text ?? '',
          timestamp: Date.now(),
        });
      }

      // If the output kind is NOT 'say', the content was dispatched as a
      // permanent action (insert-block, write-text, etc).  Clear the
      // ephemeral overlay so the same text isn't shown twice.
      if (output && output.kind !== 'say') {
        if (arcBlock && arcBlock.kind === 'arcane') {
          const arc = arcBlock as import('./notebook_state').WvArcaneBlock;
          arc.ephemeralSay = undefined;
          arc.ephemeralSayAt = undefined;
        }
        // Kill streaming animation so it doesn't re-set ephemeralSay
        this._agentStreamAnim = null;
        this._agentStreamQueue.length = 0;
      }
    } catch (err) {
      console.error('[agent] invocation failed:', err);
      const errMsg = err instanceof Error ? err.message : 'Unknown error';
      this._agentStreamQueue.push({
        persona,
        delta: `\n[Connection failed: ${errMsg}]`,
        done: true,
      });
    } finally {
      // Clear processing state
      if (arcBlock && arcBlock.kind === 'arcane') {
        const arc = arcBlock as import('./notebook_state').WvArcaneBlock;
        arc.processing = false;

        // After response: send NPC toward the newest agent-written block
        const curPage = this._state?.currentPage();
        if (curPage) {
          let newestId: string | undefined;
          let newestTs = 0;
          for (const [bid, b] of curPage.blocks) {
            if (bid.startsWith('agent_') && b.kind === 'text') {
              // agent block IDs end with a base-36 timestamp
              const parts = bid.split('_');
              const ts = parseInt(parts[parts.length - 1], 36);
              if (ts > newestTs) { newestTs = ts; newestId = bid; }
            }
          }
          if (newestId) {
            this._npcGoToBlock(arc, curPage, newestId);
          }
        }
      }
    }
  }

  /**
   * Dismiss a sigil with a farewell message and fade-out animation.
   * After ~1.5s the block is removed from the page.
   */
  private _dismissSigil(arc: import('./notebook_state').WvArcaneBlock, pageId: string): void {
    if (arc.dismissing) return;  // already dismissing

    const persona = arc.arcaneType === 'laura_channel' ? 'laura' : 'ireth';
    const farewells: Record<string, string[]> = {
      laura: ['See ya!', 'Bye~', 'Later!', 'Catch you around!', 'Off I go~'],
      ireth: ['Until next time.', 'I withdraw.', 'So be it.', 'Very well.', 'Farewell.'],
    };
    const farewell = (farewells[persona] ?? farewells.ireth)[Math.floor(Math.random() * 5)];

    arc.dismissing = true;
    arc.dismissAt = Date.now();
    arc.ephemeralSay = farewell;
    arc.ephemeralSayAt = Date.now();
    arc.processing = false;
    arc.moveState = 'standing';

    // Record farewell as page event
    this._state.pushPageEvent(pageId, {
      persona: persona as 'ireth' | 'laura',
      kind: 'say',
      content: farewell,
      timestamp: Date.now(),
    });

    // Remove the block after the fade animation completes
    setTimeout(() => {
      const page = this._state?.currentPage();
      if (page && page.blocks.has(arc.id)) {
        page.blocks.delete(arc.id);
        this._refreshNpcFlag();
      }
    }, 1500);
  }

  /** Convert WvPage to a minimal Page shape for the agent serializer. */
  private _buildPageForAgent(page: import('./notebook_state').WvPage): import('@shared/notebook_model').Page {
    const blocks = new Map<string, import('@shared/notebook_model').Block>();
    for (const [id, b] of page.blocks) {
      const base = { id, kind: b.kind, bbox: b.bbox, rotation: b.rotation ?? 0 };
      if (b.kind === 'text') {
        blocks.set(id, { ...base, kind: 'text', content: (b as import('./notebook_state').WvTextBlock).content ?? '' } as import('@shared/notebook_model').Block);
      } else if (b.kind === 'arcane') {
        blocks.set(id, { ...base, kind: 'arcane', arcaneType: (b as import('./notebook_state').WvArcaneBlock).arcaneType, arcaneState: (b as import('./notebook_state').WvArcaneBlock).arcaneState } as import('@shared/notebook_model').Block);
      } else {
        blocks.set(id, base as import('@shared/notebook_model').Block);
      }
    }
    return {
      id: page.id,
      title: page.title,
      index: page.index,
      blocks,
      arrows: new Map(),
      densityLevel: page.densityLevel,
      createdAt: 0,
      updatedAt: 0,
    } as import('@shared/notebook_model').Page;
  }

  /**
   * Sync NPC presence → arcane block activation.
   *
   * The sigil IS the presence indicator.  In the Fold, sigils are always
   * activated when the NPC is in the same room.  Outside the Fold, sigils
   * are dormant unless the character was explicitly invoked.
   * Only one character sigil can be activated at a time.
   */
  /** Scan the current page for NPC arcane blocks and update `_hasNpcOnPage`. */
  private _refreshNpcFlag(): void {
    this._hasNpcOnPage = false;
    const page = this._state?.currentPage();
    if (!page) return;
    for (const [, block] of page.blocks) {
      if (block.kind !== 'arcane') continue;
      const arc = block as import('./notebook_state').WvArcaneBlock;
      if (arc.arcaneType === 'laura_channel' || arc.arcaneType === 'ireth_channel') {
        this._hasNpcOnPage = true;
        return;
      }
    }
  }

  private _updateNpcPresences(): void {
    if (!this._state) return;
    const page = this._state.currentPage();
    if (!page) return;
    const pageId = this._state.currentPageId;
    const isFoldPage = this._state.isFoldPage();

    for (const [, block] of page.blocks) {
      if (block.kind !== 'arcane') continue;
      const arc = block as import('./notebook_state').WvArcaneBlock;
      // Skip non-NPC arcane types
      if (arc.arcaneType !== 'laura_channel' && arc.arcaneType !== 'ireth_channel') continue;

      const persona = arc.arcaneType === 'laura_channel' ? 'laura' : 'ireth';
      const npc = this._state.getNpcPresence(persona);

      // Keep activated while processing or recently speaking (any page)
      const recentSay = arc.ephemeralSayAt
        ? (Date.now() - arc.ephemeralSayAt) < 100_000
        : false;
      const busy = !!arc.processing || recentSay;

      if (isFoldPage && npc) {
        // In fold: activated if NPC is in THIS room OR busy speaking
        const npcPageId = 'fold_' + npc.roomId.replace(/-/g, '_');
        arc.activated = pageId === npcPageId || busy;
        arc.npcActivity = npc.activity;
      } else {
        // Outside fold: activated while processing or speaking
        arc.activated = busy;
        if (!busy) arc.npcActivity = undefined;
      }
    }
  }

  // ── NPC movement tick ──────────────────────────────────────────────────────

  private _tickNpcMovement(): void {
    const page = this._state?.currentPage();
    if (!page) return;
    const now = this._frameNow;

    for (const [, block] of page.blocks) {
      if (block.kind !== 'arcane') continue;
      const arc = block as import('./notebook_state').WvArcaneBlock;
      if (arc.arcaneType !== 'laura_channel' && arc.arcaneType !== 'ireth_channel') continue;

      // Initialize position to sigil center if not set
      if (arc.moveX === undefined) {
        arc.moveX = arc.bbox.x + arc.bbox.width / 2;
        arc.moveY = arc.bbox.y + arc.bbox.height / 2;
        arc.moveTargetX = arc.moveX;
        arc.moveTargetY = arc.moveY;
        arc.moveState = 'standing';
        arc.moveStateAt = now;
      }

      const stateAge = now - (arc.moveStateAt ?? now);

      switch (arc.moveState) {
        case 'standing': {
          // After standing for 6–12 seconds, either roam brownian or visit nearby
          if (!arc.processing && stateAge > 6000 + (arc.glyphSeed % 6) * 1000) {
            // 40% chance: visit a nearby non-arcane block
            const nearby = this._findNearbyBlock(arc, page, 200);
            if (nearby && Math.random() < 0.4) {
              this._npcGoToBlock(arc, page, nearby);
            } else {
              // Brownian micro-target: small random offset 20–50px from current pos
              this._npcPickBrownianTarget(arc);
              arc.moveState = 'roaming';
              arc.moveStateAt = now;
            }
          }
          break;
        }
        case 'going_to': {
          // Move toward target
          const dx = (arc.moveTargetX ?? arc.moveX!) - arc.moveX!;
          const dy = (arc.moveTargetY ?? arc.moveY!) - arc.moveY!;
          const dist = Math.hypot(dx, dy);
          if (dist < 2) {
            // Arrived — stand near the target
            arc.moveX = arc.moveTargetX;
            arc.moveY = arc.moveTargetY;
            arc.moveState = 'standing';
            arc.moveStateAt = now;
            arc.moveNearBlock = undefined;
          } else {
            const speed = Math.min(dist * 0.06, 1.5);
            arc.moveX! += (dx / dist) * speed;
            arc.moveY! += (dy / dist) * speed;
          }
          break;
        }
        case 'roaming': {
          // Brownian drift toward micro-target, then pause standing
          const dx = (arc.moveTargetX ?? arc.moveX!) - arc.moveX!;
          const dy = (arc.moveTargetY ?? arc.moveY!) - arc.moveY!;
          const dist = Math.hypot(dx, dy);
          if (dist < 2 || stateAge > 6000) {
            // Pause standing before next move
            arc.moveX = arc.moveTargetX ?? arc.moveX;
            arc.moveY = arc.moveTargetY ?? arc.moveY;
            arc.moveState = 'standing';
            arc.moveStateAt = now;
          } else {
            const speed = Math.min(dist * 0.02, 0.5);
            arc.moveX! += (dx / dist) * speed;
            arc.moveY! += (dy / dist) * speed;
          }
          break;
        }
        case 'sitting': {
          // Stay put
          break;
        }
      }
    }
  }

  /** Pick a small brownian micro-target near the NPC's current position. */
  private _npcPickBrownianTarget(
    arc: import('./notebook_state').WvArcaneBlock,
  ): void {
    const pageW = this._layout?.width ?? 600;
    const pageH = this._layout?.height ?? 800;
    const cx = arc.moveX ?? arc.bbox.x + arc.bbox.width / 2;
    const cy = arc.moveY ?? arc.bbox.y + arc.bbox.height / 2;
    const angle = Math.random() * Math.PI * 2;
    const r = 20 + Math.random() * 30;
    arc.moveTargetX = Math.max(30, Math.min(pageW - 30, cx + Math.cos(angle) * r));
    arc.moveTargetY = Math.max(80, Math.min(pageH - 30, cy + Math.sin(angle) * r));
  }

  /** Find the closest non-arcane block within `range` px of the NPC. */
  private _findNearbyBlock(
    arc: import('./notebook_state').WvArcaneBlock,
    page: import('./notebook_state').WvPage,
    range: number,
  ): string | undefined {
    const cx = arc.moveX ?? arc.bbox.x + arc.bbox.width / 2;
    const cy = arc.moveY ?? arc.bbox.y + arc.bbox.height / 2;
    let bestId: string | undefined;
    let bestDist = range;
    for (const [id, b] of page.blocks) {
      if (b.kind === 'arcane') continue;
      const bx = b.bbox.x + b.bbox.width / 2;
      const by = b.bbox.y + b.bbox.height / 2;
      const d = Math.hypot(cx - bx, cy - by);
      if (d < bestDist) { bestDist = d; bestId = id; }
    }
    return bestId;
  }

  /** Find a clear area near the NPC for roaming. Avoids other blocks. */
  private _npcPickRoamTarget(
    arc: import('./notebook_state').WvArcaneBlock,
    page: import('./notebook_state').WvPage,
  ): void {
    // Sigil's visual outer-circle radius — collision boundary
    const sigilR = Math.min(arc.bbox.width, arc.bbox.height) * 0.38;
    const margin = Math.max(40, sigilR + 10);
    const pageW = this._layout?.width ?? 600;
    const pageH = this._layout?.height ?? 800;
    const cx = arc.moveX ?? arc.bbox.x + arc.bbox.width / 2;
    const cy = arc.moveY ?? arc.bbox.y + arc.bbox.height / 2;

    // Try a few random candidates; pick the one farthest from all blocks
    let bestX = cx, bestY = cy, bestMinDist = 0;
    for (let attempt = 0; attempt < 8; attempt++) {
      // Random point within ~120px of current position, clamped to page
      const angle = Math.random() * Math.PI * 2;
      const radius = 40 + Math.random() * 80;
      const tx = Math.max(margin, Math.min(pageW - margin, cx + Math.cos(angle) * radius));
      const ty = Math.max(margin + 60, Math.min(pageH - margin, cy + Math.sin(angle) * radius));

      // Compute minimum distance to any block (edge-to-edge using sigil radius)
      let minDist = Infinity;
      for (const [, b] of page.blocks) {
        if (b.kind === 'arcane') continue; // don't avoid other sigils
        const bx = b.bbox.x + b.bbox.width / 2;
        const by = b.bbox.y + b.bbox.height / 2;
        const blockR = (b.bbox.width + b.bbox.height) / 4;
        const d = Math.hypot(tx - bx, ty - by) - blockR - sigilR;
        if (d < minDist) minDist = d;
      }
      if (minDist > bestMinDist) {
        bestMinDist = minDist;
        bestX = tx;
        bestY = ty;
      }
    }

    arc.moveTargetX = bestX;
    arc.moveTargetY = bestY;
  }

  /** After agent response: send NPC to stand near the output block. */
  private _npcGoToBlock(
    arc: import('./notebook_state').WvArcaneBlock,
    page: import('./notebook_state').WvPage,
    targetBlockId: string,
  ): void {
    const block = page.blocks.get(targetBlockId);
    if (!block) return;
    // Stand to the right of the target block, or below if near edge
    const margin = 20;
    const pageW = this._layout?.width ?? 600;
    let tx = block.bbox.x + block.bbox.width + margin + 30;
    let ty = block.bbox.y + block.bbox.height / 2;
    if (tx + 50 > pageW) {
      tx = block.bbox.x + block.bbox.width / 2;
      ty = block.bbox.y + block.bbox.height + margin + 30;
    }
    arc.moveTargetX = tx;
    arc.moveTargetY = ty;
    arc.moveState = 'going_to';
    arc.moveStateAt = Date.now();
    arc.moveNearBlock = targetBlockId;
  }

  // ── Agent streaming text animation ─────────────────────────────────────────

  /**
   * Process queued stream tokens and animate text appearing on the page.
   * Each delta appends to the accumulator; text reveals char by char.
   */
  private _tickAgentStreamAnim(
    ctx: CanvasRenderingContext2D,
    L: import('./page_renderer').PageLayout,
    t: number,
  ): void {
    const anim = this._agentStreamAnim!;
    const page = this._state?.currentPage();
    const arcBlock = page?.blocks.get(anim.blockId);

    // Drain stream queue into the animation accumulator
    while (this._agentStreamQueue.length > 0) {
      const token = this._agentStreamQueue[0];
      if (token.persona !== anim.persona) break;
      this._agentStreamQueue.shift();
      anim.text += token.delta;
      if (token.done) {
        // Stream complete — defer JSON.parse to a microtask so it never
        // blocks the current frame (the raw text is legible enough to
        // display for one extra frame).
        const rawSnapshot = anim.text;
        queueMicrotask(() => {
          const cleaned = this._extractAgentContent(rawSnapshot);
          if (cleaned !== rawSnapshot && anim.text === rawSnapshot) {
            anim.text = cleaned;
          }
        });
        anim.revealedChars = anim.text.length;
        anim.done = true;
      }
    }

    // Advance revealed character count (3 chars per frame ≈ handwriting speed)
    if (anim.revealedChars < anim.text.length) {
      anim.revealedChars = Math.min(
        anim.text.length,
        anim.revealedChars + 3,
      );
    }

    // Extract display text progressively during streaming
    let displayText = anim.text.slice(0, anim.revealedChars);
    const contentStart = displayText.indexOf('"content": "');
    if (contentStart >= 0) {
      const valueStart = contentStart + '"content": "'.length;
      displayText = displayText.slice(valueStart).replace(/"\s*[,}]?\s*$/, '');
    } else if (displayText.startsWith('{')) {
      displayText = '';
    }

    // Pipe the revealed text into the arcane block's ephemeralSay
    if (arcBlock && arcBlock.kind === 'arcane') {
      const arc = arcBlock as import('./notebook_state').WvArcaneBlock;
      if (displayText.length > 0) {
        arc.ephemeralSay = displayText;
        if (!arc.ephemeralSayAt) arc.ephemeralSayAt = this._frameNow;
      }
    }

    // Clean up only after stream is done AND text fully revealed
    if (anim.done
        && anim.revealedChars >= anim.text.length
        && this._agentStreamQueue.length === 0) {
      if (!anim.startTime) anim.startTime = t;
      // Keep streaming state for 1 second after completion, then clear
      if (t - anim.startTime > 1) {
        this._agentStreamAnim = null;
      }
    }
  }

  /**
   * Extract human-readable content from the LLM's JSON output.
   * Falls back to the raw text if not valid JSON.
   */
  private _extractAgentContent(raw: string): string {
    const trimmed = raw.trim();
    try {
      const parsed = JSON.parse(trimmed);
      return parsed.content ?? parsed.text ?? trimmed;
    } catch {
      // Try to extract "content" from partial/malformed JSON
      const m = trimmed.match(/"content"\s*:\s*"([^"]+)"/);
      return m ? m[1] : trimmed;
    }
  }

  // ── VS Code message handling ───────────────────────────────────────────────

  onHostMessage(msg: { type: string; payload?: unknown; notebook?: NotebookInit;
                       pageId?: string; strokes?: Array<import('@shared/notebook_model').InkStroke>;
                       blocks?: unknown[]; arrows?: unknown[]; failed?: boolean;
                       landingMeta?: { folderPath: string; description: string } }): void {
    switch (msg.type) {
      case 'init':
        this._initNotebook(msg.notebook!);
        break;
      case 'action': {
        const act = msg.payload as GrimoireAction;
        this._applyAction(act);
        break;
      }
      case 'page.load':
        // The host now sends blocks + arrows alongside strokes when a page
        // gets hydrated lazily. Inject them into NotebookState first so the
        // upcoming render frame sees the real contents instead of an empty
        // stub. Then load strokes as before.
        if (msg.blocks || msg.arrows) {
          this._state.setPageContents(
            msg.pageId!,
            msg.blocks as Parameters<typeof this._state.setPageContents>[1],
            msg.arrows as Parameters<typeof this._state.setPageContents>[2],
          );
        }
        this._loadPageStrokes(msg.pageId!, msg.strokes ?? []);
        break;
      case 'page.loadFailed':
        if (msg.pageId) {
          if (msg.failed === false) {
            this._failedPageIds.delete(msg.pageId);
          } else {
            this._failedPageIds.add(msg.pageId);
            // Dead-end guard: if the user is parked on the failed page
            // (e.g., session restored a now-broken page), escape to the
            // root landing so the canvas isn't a blank wall. The
            // red-cross icon stays on the failed tab for a retry.
            // Synthetic id mirrors `landingPageId('')` from
            // standalone_host — kept hardcoded to avoid pulling the
            // host module into the canvas's import graph.
            if (msg.pageId === this._state.currentPageId) {
              this._switchContent({ viewMode: 'page', pageId: '_folder:' });
            }
          }
        }
        break;
      case 'settings.data':
        if (msg.payload && typeof msg.payload === 'object') {
          this._settings = { ...DEFAULT_SETTINGS, ...(msg.payload as Partial<GrimoireSettings>) };
        }
        setSelectionAnimationsEnabled(this._settings.animations);
        setTapEffectsEnabled(this._settings.animations);
        // Restore persisted side-button bindings (no-op when none saved).
        this._stylusBindings.deserialize(this._settings.stylusBindings as Record<string, StylusActionId>);
        // Restore per-tool params + active tool selection. Mirror
        // active tool's params into _penStyle so the canvas's
        // synchronous reads (stylus menu pre-select, stroke commit
        // payloads, etc.) see the right values right away.
        this._toolRegistry.hydrate(this._settings.tools);
        this._mirrorActiveToolToPenStyle();
        // Restore stylus-sidebar visibility (Batch 7).
        if (typeof this._settings.stylusSidebarVisible === 'boolean') {
          this._stylusSidebarVisible = this._settings.stylusSidebarVisible;
        }
        break;
      case 'vault.status':
        if (msg.payload && typeof msg.payload === 'object') {
          const p = msg.payload as { supported?: boolean; active?: boolean; folderName?: string | null };
          this._vaultStatus = {
            supported: p.supported ?? this._vaultStatus.supported,
            active: !!p.active,
            folderName: p.folderName ?? null,
          };
        }
        break;
      case 'vault.reloadRequired':
        // Vault backend was swapped — reload to start fresh against the new
        // persistence target. No half-migrated state to worry about.
        try { window.location.reload(); } catch { /* SSR / sandboxed case */ }
        break;
    }
  }

  private _initNotebook(nb: NotebookInit): void {
    this._page.setTextureSeed(nb.meta.coverSeed);
    this._state.initialise({
      currentPageIndex: nb.currentPageIndex,
      currentPageId: nb.currentPageId,
      sections: nb.sections,
      pages: nb.pages,
    });
    // Populate side-tab context for the initial page (no _switchContent fires on boot).
    if (nb.currentPageId) {
      this._recordNavAndRefresh(nb.currentPageId);
    }
  }

  private _loadPageStrokes(
    _pageId: string,
    strokes: Array<import('@shared/notebook_model').InkStroke>,
  ): void {
    // Populate ink layer and also seed the cache for this page
    this._ink.clearPage();
    for (const s of strokes) {
      this._ink.addCommittedStroke(s);
    }
    this._pageStrokeCache.set(_pageId, this._ink.getStrokes());

    // Wire grain canvas for brush engine (textured/wet tools need this)
    const sec = this._state.currentSection();
    this._ink.setGrainCanvas(this._texture.grainCanvas(sec?.color ?? null));
  }

  // ── Content switch (the "editor slot") ─────────────────────────────────
  // Single entry-point for ALL page and view transitions.  Flushes the
  // current content, clears transient state, and loads the target content.

  private _switchContent(target: {
    viewMode: 'page' | 'codex' | 'settings';
    pageId?: string;
  }): void {
    // 0. Validate target — don't commit a partial view change with a
    //    pageId the state doesn't know about, since that leaves viewMode
    //    flipped but currentPageIndex stale (= the "unwiped index" symptom
    //    where the prior page renders on top of a fresh navigation).
    if (target.viewMode === 'page' && target.pageId) {
      const state = this._state as unknown as { _pageOrder?: string[] };
      const order = state?._pageOrder;
      if (order && !order.includes(target.pageId)) {
        console.warn('[switchContent] unknown pageId:', target.pageId,
          '— page tree out of sync; abort switch');
        return;
      }
    }

    // 1. Cache current page strokes before leaving
    if (this._viewMode === 'page' && this._state) {
      const curId = this._state.currentPageId;
      if (curId && curId !== 'unknown') {
        this._pageStrokeCache.set(curId, this._ink.getStrokes());
        // Record outgoing page id as the "last page" — the back tab
        // uses this when no parent is available (e.g. user was at root
        // and clicked into settings/codex/etc; back returns here).
        this._lastPageId = curId;
      }

      // 1b. Remove NPC sigils from the old page — sigils are ephemeral
      const oldPage = this._state.currentPage();
      if (oldPage) {
        const toRemove: string[] = [];
        for (const [blockId, block] of oldPage.blocks) {
          if (block.kind !== 'arcane') continue;
          const arc = block as import('./notebook_state').WvArcaneBlock;
          if (arc.arcaneType === 'laura_channel' || arc.arcaneType === 'ireth_channel') {
            toRemove.push(blockId);
          }
        }
        for (const id of toRemove) {
          oldPage.blocks.delete(id);
        }
      }
    }

    // 2. Cancel any in-flight charge / spell / wand
    this._chargeEngine.cancel();
    this._spell.reset();
    this._pendingSpell = null;
    if (this._isWandInvoked) { this._cancelWand(); }
    if (this._textCompose.active) { this._textCompose.end(); }

    // 2b. Wire grain canvas for the incoming page
    const incomingSec = this._state.currentSection();
    this._ink.setGrainCanvas(this._texture.grainCanvas(incomingSec?.color ?? null));

    // 3. Clear transient state
    this._ink.clearPage();
    this._state.clearSelection();
    this._state.clearFocus();
    this._undoStack.length = 0;
    this._redoStack.length = 0;
    this._groupDepth = 0;
    this._groupBuffer.length = 0;
    this._wizardScroll.close();
    this._tapRingLayer.clear();
    this._cadence.reset();
    this._tapFx.clear();
    if (this._popDialog.active) { this._popDialog.dismiss(); }
    this._settingsRegion.clear();
    this._snap.invalidate();
    this._hasNpcOnPage = false; // will be recomputed on next frame

    // 4. Switch view mode
    this._viewMode = target.viewMode;

    // 5. If navigating to a journal page, load its content
    if (target.viewMode === 'page' && target.pageId) {
      // Update notebook state page index
      this._state.apply({
        type: 'page.goto',
        pageId: target.pageId,
        origin: 'gesture',
        timestamp: Date.now(),
      } as GrimoireAction);

      // Restore strokes from cache (instant) or request from host
      const cached = this._pageStrokeCache.get(target.pageId);
      if (cached) {
        this._ink.setStrokes(cached);
      } else {
        // Cache miss — ask host for strokes
        this._vscode.postMessage({ type: 'page.requestStrokes', pageId: target.pageId });
      }

      // Notify host about the navigation (fire-and-forget)
      this._vscode.postMessage({ type: 'page.navigate', pageId: target.pageId });

      // Record where we're going so "level up" can return to the right place.
      this._recordNavAndRefresh(target.pageId);
    }

    // 6. Codex-specific setup
    if (target.viewMode === 'codex') {
      this._groupPageIndex = 0;
      this._refreshCodex();
    } else if (target.viewMode === 'settings') {
      this._groupPageIndex = 0;
      this._vscode.postMessage({ type: 'settings.load' });
    }

    // 7. Rebuild interaction policy for the new context
    this._updatePolicy();
    this._refreshNpcFlag();
  }

  private _applyAction(action: GrimoireAction): void {
    // Update notebook state for all block/page/focus/wizard actions
    this._state.apply(action);

    switch (action.type) {
      case 'ink.commit':
        this._ink.addCommittedStroke({
          id: action.strokeId,
          points: action.points,
          style: action.style,
          timestamp: action.timestamp,
          pageLayer: action.pageLayer,
        });
        break;

      case 'ink.erase':
        if (typeof action.target === 'string') {
          // Compute bbox before removal so we can dirty the region
          const erasedStroke = this._ink.getStrokes().get(action.target);

          this._ink.removeStroke(action.target);
          // Keep the passive shadow-group store in sync so the astral
          // overlay's detected text refreshes (or disappears) when the
          // user erases handwriting.
          if (this._textCompose && action.pageId) {
            this._textCompose.forgetStroke(action.target, action.pageId);
          }
          void erasedStroke;
        }
        break;

      case 'pen.style': {
        // Route through the ToolRegistry so per-tool params (color,
        // size, opacity) persist independently. A pen.style patch
        // can carry a tool switch, param overrides, or both:
        //   1. Tool switch first (loads that tool's saved params).
        //   2. Then any explicit color/size/opacity overrides write
        //      to the (now-)active tool. So the user can atomically
        //      "switch to pencil with this colour" and the colour
        //      sticks to pencil for next time.
        const patch = action.style as Partial<typeof this._penStyle> | undefined;
        if (patch) {
          if (typeof patch.style === 'string' && patch.style !== this._toolRegistry.activeId) {
            this._toolRegistry.pick(patch.style as PenStyle);
            this._noteToolChange(patch.style);
          }
          if (patch.color !== undefined || patch.size !== undefined || patch.opacity !== undefined) {
            this._toolRegistry.updateActiveParams({
              color:   patch.color,
              size:    patch.size,
              opacity: patch.opacity,
            });
          }
          // Non-tool-tracked fields the registry doesn't own
          // (thinning / smoothing / streamline overrides — rare,
          // kept for protocol compatibility with anything sending
          // them).
          if (patch.thinning   !== undefined) this._penStyle.thinning = patch.thinning;
          if (patch.smoothing  !== undefined) this._penStyle.smoothing = patch.smoothing;
          if (patch.streamline !== undefined) this._penStyle.streamline = patch.streamline;
        }
        // Persist any change so per-tool params survive reloads.
        this._settings.tools = this._toolRegistry.serialize();
        this._saveSettings();
        break;
      }

      case 'focus.enter':
        // Handled by _state.apply — no CanvasApp-level state needed
        break;

      case 'focus.exit':
        // Handled by _state.apply — no CanvasApp-level state needed
        break;

      case 'wizard.respond': {
        // Feed wizard response into the scroll dialogue
        const m = (action as { marginalia?: { pageId: string; anchorBlockId?: string; x: number; y: number; text: string; glyphStyle: string } }).marginalia;
        if (m && this._wizardScroll.visible) {
          this._wizardScroll.addWizardTurn({
            id: `mn_${Date.now()}`,
            pageId: m.pageId,
            anchorBlockId: m.anchorBlockId,
            x: m.x, y: m.y,
            text: m.text,
            glyphStyle: m.glyphStyle as 'calm' | 'warning' | 'archaic' | 'dismissive',
            createdAt: Date.now(),
          });
        }
        break;
      }

      case 'page.turn':
        // Content flushing handled by _switchContent — this path only runs
        // when the action bounces back from the host.
        break;

      case 'page.goto':
        // Content flushing handled by _switchContent.
        break;

      case 'spell.begin':
        this._spellAllPoints = [];
        this._spell.reset();
        break;

      case 'spell.cast':
        break;

      case 'spell.fail':
        this._pendingSpell = null;
        this._spell.reset();
        break;

      case 'spell.discover':
        this._refreshCodex();
        break;

      // ── Ink stroke transforms (keep strokes in sync with block bbox) ───────
      case 'block.move': {
        const blk = this._state.currentPage()?.blocks.get(action.blockId);
        if (blk?.kind === 'ink') {
          const sids = (blk as import('./notebook_state').WvInkBlock).strokeIds;
          const strokes = this._ink.getStrokes();
          for (const sid of sids) {
            const s = strokes.get(sid);
            if (s) { for (const p of s.points) { p.x += action.dx; p.y += action.dy; } }
          }
          this._ink.markDirty();
          this._postStrokeTransform(action.pageId, sids, { kind: 'move', dx: action.dx, dy: action.dy });
        }
        break;
      }

      case 'block.scale': {
        const blk = this._state.currentPage()?.blocks.get(action.blockId);
        if (blk?.kind === 'ink') {
          const sids = (blk as import('./notebook_state').WvInkBlock).strokeIds;
          const { pivot, sx, sy } = action;
          const strokes = this._ink.getStrokes();
          for (const sid of sids) {
            const s = strokes.get(sid);
            if (s) { for (const p of s.points) { p.x = pivot.x + (p.x - pivot.x) * sx; p.y = pivot.y + (p.y - pivot.y) * sy; } }
          }
          this._ink.markDirty();
          this._postStrokeTransform(action.pageId, sids, { kind: 'scale', px: pivot.x, py: pivot.y, sx, sy });
        }
        break;
      }

      case 'content.modifier.set': {
        // Block patches happen inside _state.apply. Stroke patches need
        // to reach the InkLayer's own stroke records so locked/astral
        // affect rendering and erase-gating.
        const strokes = this._ink.getStrokes();  // shallow copy — values are shared
        const patch = action.patch;
        for (const t of action.targets) {
          if (t.kind !== 'stroke') continue;
          const s = strokes.get(t.id) as (import('@shared/notebook_model').InkStroke & {
            locked?: boolean; groupId?: string;
            astral?: { birth: number; ttlMs: number };
          }) | undefined;
          if (!s) continue;
          if (patch.locked !== undefined) s.locked = patch.locked;
          if (patch.groupId !== undefined) {
            if (patch.groupId === null) delete s.groupId;
            else s.groupId = patch.groupId;
          }
          if (patch.astral !== undefined) {
            if (patch.astral === null) delete s.astral;
            else s.astral = patch.astral;
          }
        }
        this._ink.markDirty();
        break;
      }

      case 'block.rotate': {
        const blk = this._state.currentPage()?.blocks.get(action.blockId);
        if (blk?.kind === 'ink') {
          const sids = (blk as import('./notebook_state').WvInkBlock).strokeIds;
          const { pivot, dAngle } = action;
          const cos = Math.cos(dAngle);
          const sin = Math.sin(dAngle);
          const strokes = this._ink.getStrokes();
          for (const sid of sids) {
            const s = strokes.get(sid);
            if (s) {
              for (const p of s.points) {
                const rx = p.x - pivot.x;
                const ry = p.y - pivot.y;
                p.x = pivot.x + rx * cos - ry * sin;
                p.y = pivot.y + rx * sin + ry * cos;
              }
            }
          }
          this._ink.markDirty();
          // Recompute bbox from actual stroke points (axis-aligned may differ after rotation)
          let rMinX = Infinity, rMinY = Infinity, rMaxX = -Infinity, rMaxY = -Infinity;
          for (const sid of sids) {
            const s = strokes.get(sid);
            if (s) { for (const p of s.points) { if (p.x < rMinX) rMinX = p.x; if (p.y < rMinY) rMinY = p.y; if (p.x > rMaxX) rMaxX = p.x; if (p.y > rMaxY) rMaxY = p.y; } }
          }
          if (rMinX < Infinity) {
            blk.bbox = { x: rMinX - 4, y: rMinY - 4, width: (rMaxX - rMinX) + 8, height: (rMaxY - rMinY) + 8 };
          }
          this._postStrokeTransform(action.pageId, sids, { kind: 'rotate', px: pivot.x, py: pivot.y, angle: dAngle });
        }
        break;
      }
    }

    // Invalidate snap index for any action that may alter figure geometry
    if (action.type === 'figure.create' || action.type === 'figure.moveVertex' ||
        action.type === 'figure.update' || action.type === 'block.move' ||
        action.type === 'block.delete' || action.type === 'block.scale' ||
        action.type === 'block.rotate') {
      this._snap.invalidate();
    }
  }

  // ── Pointer events ─────────────────────────────────────────────────────────

  /** Pre-down sample buffer — captures pen contact points delayed by OS gesture disambiguation. */
  private _preDownBuffer: StrokePoint[] = [];
  private _preDownPointerId: number = -1;

  private _bindEvents(): void {
    const c = this._canvas;
    c.addEventListener('pointerdown',   this._onPointerDown,   { passive: false });
    c.addEventListener('pointermove',   this._onPointerMove,   { passive: false });
    c.addEventListener('pointerup',     this._onPointerUp,     { passive: false });
    c.addEventListener('pointercancel', this._onPointerCancel, { passive: false });

    // Pointer presence — drives the hovering/idle context for stylus
    // shortcut bindings. Both mouse and pen fire these (pen fires on
    // entering / leaving proximity range).
    c.addEventListener('pointerenter', (e) => {
      this._pointerPresent = true;
      this._lastPointerType = e.pointerType;
    });
    c.addEventListener('pointerleave', (_e) => {
      this._pointerPresent = false;
    });

    // Suppress Windows pen-hold context menu
    c.addEventListener('contextmenu', (e) => { e.preventDefault(); });

    // Buffer pre-down samples from the OS — fires before pointerdown during gesture disambiguation
    if ('onpointerrawupdate' in c) {
      c.addEventListener('pointerrawupdate' as any, this._onPointerRawUpdate, { passive: true });
    }

    // Safety net: re-commit if pointer capture is lost mid-stroke
    c.addEventListener('lostpointercapture', this._onLostPointerCapture);

    window.addEventListener('keydown', (e) => {
      // When text input is active, let it handle all keys
      if (this._textInput.active) return;

      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        this._undo();
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        this._redo();
      }
      // Codex page navigation via arrow keys
      if (this._viewMode === 'codex') {
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          this._codexTurnPage('forward');
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          this._codexTurnPage('back');
        }
      }
    });
  }

  // ── pointerrawupdate — captures pre-down pen samples ───────────────────
  private _onPointerRawUpdate = (e: PointerEvent): void => {
    // Only buffer while no stroke is active (i.e. before pointerdown fires)
    if (this._activeStrokeId) return;
    // Only buffer pen input — finger/mouse don't suffer from OS hold-back
    if (e.pointerType !== 'pen') return;

    const pt = this._toStrokePoint(e);
    // Track which pointer is being buffered so we don't mix devices
    if (this._preDownPointerId !== e.pointerId) {
      this._preDownBuffer = [];
      this._preDownPointerId = e.pointerId;
    }
    this._preDownBuffer.push(pt);
    // Keep only the last 10 samples (~50-100ms worth at 200 Hz)
    if (this._preDownBuffer.length > 10) {
      this._preDownBuffer.shift();
    }
  };

  // ── lostpointercapture — safety net for mid-stroke capture loss ────────
  private _onLostPointerCapture = (_e: PointerEvent): void => {
    if (!this._activeStrokeId) return;
    // Treat like a cancel — gracefully commit or discard
    this._gracefulStrokeEnd();
  };

  private _onPointerDown = (e: PointerEvent): void => {
    e.preventDefault();
    if (!this._layout || !this._ink) { return; }

    // ── Build frame (single getBoundingClientRect) ───────────────────────
    const f = this._buildFrame(e);

    // Diagnostic capture for the debug HUD: every pointerdown event,
    // even ones that don't get to the binding engine.
    this._lastPointerType = e.pointerType;
    this._lastPointerButtons = e.buttons;
    this._lastButtonEvent = `down ${e.pointerType} btn=${e.button} bm=${e.buttons}`;

    // ── Side-button mid-stroke press (bm transitioned 1 → 3) ──────────
    // pointerdown with button === 2 only fires when the user presses
    // the barrel button while the tip is already touching. The engine
    // distinguishes two sub-cases internally: late-arrival (no
    // displacement yet — fold into "side held early") vs genuine
    // press-while-drawing. Don't start a new stroke or capture the
    // pointer — the existing stroke continues normally.
    if (e.button === 2 && this._activeStrokeId) {
      // The engine itself fires the right trigger here:
      // draw-while-pressed (late-arrival grace, before stroke moves)
      // or press-while-drawing (real mid-draw side press). The
      // canvas's onAction handler applies / holds whatever state.
      this._stylusBindings.onSidePressedMidStroke(f.pageX, f.pageY);
      return;
    }

    // Capture the pointer for the rest of the stroke pipeline (so we
    // get all samples reliably even if the pointer leaves the canvas
    // mid-stroke).
    try { this._canvas.setPointerCapture(e.pointerId); } catch { /* webview may block */ }

    // ── 0a. Debug dialog hit test ──────────────────────────────────────
    // When the debug dialog is up it owns input. Tap on a tab, subtab,
    // or row routes to its handler; close X dismisses; drag handle starts
    // a drag (handled in pointer-move / pointer-up below); panel taps
    // are consumed; outside taps dismiss but fall through.
    if (this._debugDialog.isOpen()) {
      const hit = this._debugDialog.hitTest(f.absX, f.absY);
      const result = this._debugDialog.applyHit(hit, f.absX, f.absY);
      if (result === 'drag-begin') {
        this._debugDragActive = true;
        try { this._canvas.setPointerCapture(e.pointerId); } catch { /* ignore */ }
        return;
      }
      if (result === 'consumed') return;
      // 'dismiss' — outside tap; fall through so the click still acts on
      // whatever it landed on in the canvas.
      this._debugDialog.dismiss();
    }

    // ── 0. Stylus sidebar hit test (Batch 7) ──────────────────────────
    // The permanent left-margin palette intercepts taps before any
    // stroke / spell pipeline kicks in. Hit test in canvas-absolute
    // coords so the layout (which uses canvas absolutes) lines up.
    if (this._stylusSidebarVisible && this._stylusSidebarLayout) {
      const hit = hitTestSidebar(this._stylusSidebarLayout, f.absX, f.absY);
      if (hit) {
        this._applySidebarHit(hit);
        return;
      }
    }

    // ── 0b. Left-margin draw ban (only while sidebar is showing) ──
    // When the sidebar is visible the column is reserved — pointer-
    // down inside it doesn't start strokes. When the sidebar is
    // hidden the margin is free again, so the user reclaims the
    // space until the next invocation. The invocation animates a
    // wipe of any content the user had drawn there (see
    // _wipeMarginColumn), so the menu visibly takes its space back.
    if (this._stylusSidebarVisible
        && this._viewMode === 'page'
        && f.pageX < LEFT_MARGIN_RESERVED_PX) {
      return;
    }

    // ── 1. Canvas-absolute UI hit test (one flat loop) ──────────────────
    for (const r of this._uiHitRects) {
      if (f.absX >= r.x && f.absX <= r.x + r.w && f.absY >= r.y && f.absY <= r.y + r.h) {
        switch (r.target.kind) {
          case 'tab':          this._handleTabClick(r.target.data); return;
          case 'codexNav':     this._codexTurnPage(r.target.data.dir); return;
          case 'sidebar':      this._handlePageSidebarClick(r.target.data); return;
          case 'scroll':       this._handleScrollHit(r.target.data); return;
          case 'contentsRow': {
            // Ignore rows that don't resolve to a page (empty folder with no
            // child files, section header with no body page, etc.) — clicking
            // them is a no-op rather than silently switching into an orphan
            // view.
            const pid = r.target.data.pageId;
            if (pid) { this._switchContent({ viewMode: 'page', pageId: pid }); }
            return;
          }
          case 'subpageArrow': {
            const { blockId, dir } = r.target.data;
            const page = this._state.currentPage();
            const blk = page?.blocks.get(blockId) as
              (import('./notebook_state').WvTextBlock & { _subpageIndex?: number; _subpageCount?: number }) | undefined;
            if (blk) {
              const count = blk._subpageCount ?? 1;
              const curr = blk._subpageIndex ?? 0;
              const next = Math.max(0, Math.min(count - 1, curr + dir));
              (blk as Record<string, unknown>)._subpageIndex = next;
              // The next animation frame will re-render with the new slice
              // and re-emit the strip; no explicit invalidation needed.
            }
            return;
          }
        }
      }
    }

    // ── 2. Pop dialog intercept (single resolveHit call) ────────────────
    if (this._popDialog.active) {
      const dh = this._popDialog.resolveHit(f.pageX, f.pageY);
      if (dh === 'close') { this._popDialog.dismiss(); return; }
      if (dh === 'drag')  { this._popDialog.beginDrag(f.pageX, f.pageY); this._popDialogStrokeActive = true; return; }
      if (dh === 'miss')  {
        if (!this._popDialog.persistent) { this._popDialog.dismiss(); }
        // fall through to normal handling
      }
      // 'body' → strokes inside the dialog pass through; analysis at pen-up
    }

    // ── 2b. One-shot tap-ring pending mark — defers to pen-up ─────────
    // Tap-vs-stroke evaluation has priority. We just mark the ring as
    // _pendingOneShot here; pen-up's confirmOneShotTap fires the
    // resolve only if the gesture stayed a tap. If it became a real
    // stroke, the ring stays pending (no spurious automation kick-in
    // mid-stroke) and quietly expires. Drawing through a hint ring
    // therefore doesn't get hijacked by the automation.
    this._tapRingLayer.checkPointerDown(f.pageX, f.pageY, performance.now());

    // ── 3. Text input intercept ─────────────────────────────────────────
    if (this._textInput.active) {
      if (this._textInput.hitTest(f.pageX, f.pageY)) { this._textInput.refocus(); return; }
      this._textInput.close();
    }

    // ── 4. Interaction context resolution ───────────────────────────────
    const resolved = resolveContext(this._policy, f.pageX, f.pageY);
    this._activeContext = resolved.context;
    this._activeBoxId = resolved.boxId;
    if (this._activeContext.tool === 'none') { return; }

    // Eraser is active either via the hardware eraser-end (Surface
    // Pen flips bit 5 / sets buttons=32) or via the eraser tool
    // selected in the stylus menu. Both go through the same real-
    // time hit-test path so erasing always feels live and never
    // commits a black ink stroke.
    const hardwareEraser = f.button === 5 || f.buttons === 32;
    this._isEraser = hardwareEraser
                  || this._toolRegistry.activeId === 'eraser';
    if (this._isEraser) this._browserPenCaps.eraserSeen = true;

    // When the hardware back-tip becomes active, sync the tool
    // registry so the sidebar reflects the active eraser. Cache the
    // user's previous tool; pen-up restores it. No-op if the
    // registry was already on eraser (user picked it manually) — in
    // that case there's nothing to restore.
    if (hardwareEraser && this._toolRegistry.activeId !== 'eraser') {
      this._hardwareEraserPrevTool = this._toolRegistry.activeId;
      this._toolRegistry.pick('eraser');
    }

    // Gather coalesced hardware samples
    const coalescedDown = ('getCoalescedEvents' in e) ? e.getCoalescedEvents() : [];
    const rawEvents = coalescedDown.length > 0 ? coalescedDown : [e];
    let firstPt = this._toStrokePoint(rawEvents[0]);

    // ── 5. Eraser mode ──────────────────────────────────────────────────
    if (this._isEraser) {
      this._beginUndoGroup();
      // Start a fresh ghost for this drag. Any previously-active
      // ghost (rare — only if pen-up was missed) gets its fadeStart
      // set so it dies on schedule rather than lingering.
      if (this._eraserActiveGhost && this._eraserActiveGhost.fadeStart === null) {
        this._eraserActiveGhost.fadeStart = performance.now();
      }
      // Snapshot the eraser's params at drag start so the ghost's
      // size + smoothness stay consistent for the whole drag even
      // if the active-tool params shift.
      const erParams = this._toolRegistry.active().params;
      const ghost = {
        points: [] as Array<{ x: number; y: number }>,
        size: Math.max(2, erParams.size),
        opacity: Math.max(0, Math.min(1, erParams.opacity)),
        fadeStart: null as number | null,
      };
      this._eraserGhosts.push(ghost);
      this._eraserActiveGhost = ghost;
      for (const ce of rawEvents) {
        const cpt = this._toStrokePoint(ce);
        ghost.points.push({ x: cpt.x, y: cpt.y });
        // Real-time partial erase: radius = ghost.size so the visual
        // ghost width matches the actual erase footprint.
        this._eraseAtCircle(cpt.x, cpt.y, ghost.size);
      }
      return;
    }

    // ── 6. Focus zone exit ──────────────────────────────────────────────
    const focusState = this._state?.focus;
    if (focusState?.active && f.pointerType === 'pen') {
      let inFocus = false;
      if (typeof focusState.region === 'object' && focusState.region !== null && 'bbox' in focusState.region) {
        const r = (focusState.region as { bbox: { x: number; y: number; width: number; height: number } }).bbox;
        inFocus = firstPt.x >= r.x && firstPt.x <= r.x + r.width && firstPt.y >= r.y && firstPt.y <= r.y + r.height;
      }
      if (!inFocus) {
        this._dispatch({ type: 'focus.exit', origin: 'gesture', timestamp: Date.now() });
      }
    }

    const isWandEarly = this._activeContext.tool === 'wand' || this._isWandInvoked;

    // ── 7. Figure vertex / control-point drag (single merged loop) ──────
    if (!isWandEarly && this._viewMode === 'page' && this._state.selection.blockIds.size > 0) {
      const page = this._state.currentPage();
      if (page) {
        const VERT_HIT = 20;
        const CP_HIT = 18;
        let bestVertDist = VERT_HIT;
        let bestVertBlockId: string | null = null;
        let bestVertIdx = -1;
        let bestCpDist = CP_HIT;
        let bestCpBlockId: string | null = null;
        let bestCpEdgeIdx = -1;
        let bestCpIdx = -1;

        for (const [bid, blk] of page.blocks) {
          if (blk.kind !== 'figure') continue;
          if (!this._state.selection.blockIds.has(bid)) continue;
          const fig = blk as import('./notebook_state').WvFigureBlock;

          // Vertices
          for (let vi = 0; vi < fig.vertices.length; vi++) {
            const v = fig.vertices[vi];
            const dx = firstPt.x - v.x, dy = firstPt.y - v.y;
            const d = Math.sqrt(dx * dx + dy * dy);
            if (d < bestVertDist) { bestVertDist = d; bestVertBlockId = bid; bestVertIdx = vi; }
          }
          // Center (circles/ellipses/points)
          if (fig.center && (fig.shapeKind === 'circle' || fig.shapeKind === 'ellipse' || fig.shapeKind === 'point')) {
            const dx = firstPt.x - fig.center.x, dy = firstPt.y - fig.center.y;
            const d = Math.sqrt(dx * dx + dy * dy);
            if (d < bestVertDist) { bestVertDist = d; bestVertBlockId = bid; bestVertIdx = -1; }
          }
          // Control points
          for (let ei = 0; ei < fig.edges.length; ei++) {
            const cps = fig.edges[ei].controlPoints;
            if (!cps) continue;
            for (let ci = 0; ci < cps.length; ci++) {
              const dx = firstPt.x - cps[ci].x, dy = firstPt.y - cps[ci].y;
              const d = Math.sqrt(dx * dx + dy * dy);
              if (d < bestCpDist) { bestCpDist = d; bestCpBlockId = bid; bestCpEdgeIdx = ei; bestCpIdx = ci; }
            }
          }
        }

        // Vertex wins over CP (tighter radius, checked first)
        if (bestVertBlockId !== null) {
          this._dragVertex = { blockId: bestVertBlockId, vertexIndex: bestVertIdx, lastX: firstPt.x, lastY: firstPt.y, originX: firstPt.x, originY: firstPt.y };
          this._dragDownTime = performance.now();
          this._beginUndoGroup();
          this._activeStrokeId = makeStrokeId();
          this._spellAllPoints = [];
          return;
        }
        if (bestCpBlockId !== null) {
          this._dragCP = { blockId: bestCpBlockId, edgeIndex: bestCpEdgeIdx, cpIndex: bestCpIdx, lastX: firstPt.x, lastY: firstPt.y, originX: firstPt.x, originY: firstPt.y };
          this._dragDownTime = performance.now();
          this._beginUndoGroup();
          this._activeStrokeId = makeStrokeId();
          this._spellAllPoints = [];
          return;
        }
      }
    }

    // ── 8. Selection handle drag ────────────────────────────────────────
    if (this._state.selection.blockIds.size > 0 && !isWandEarly && this._viewMode === 'page') {
      const page = this._state.currentPage();
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      if (page) {
        for (const bid of this._state.selection.blockIds) {
          const blk = page.blocks.get(bid);
          if (!blk) continue;
          minX = Math.min(minX, blk.bbox.x);
          minY = Math.min(minY, blk.bbox.y);
          maxX = Math.max(maxX, blk.bbox.x + blk.bbox.width);
          maxY = Math.max(maxY, blk.bbox.y + blk.bbox.height);
        }
      }

      if (minX < Infinity) {
        const selBbox = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
        const handles = computeHandles(selBbox, 10);  // pad=10 matches block_renderer
        const HANDLE_RADIUS = 16;
        const DEL_RADIUS = 22;  // larger hit zone for the small X icon

        // ── Modifier strip hit-test ──────────────────────────────────────
        // Pills sit below the selection bbox; check before handles so a
        // tap on a pill doesn't fall into a drag handle.
        const stripHit = this._hitTestModifierStrip(firstPt.x, firstPt.y);
        if (stripHit) {
          this._toggleSelectionModifier(stripHit.kind, stripHit.active);
          this._currentStroke = null;
          this._strokeId = null;
          this._spellAllPoints = [];
          return;
        }

        // Priority: check del handle first with a generous radius
        const delH = handles.find(h => h.id === 'del')!;
        const delDx = firstPt.x - delH.x, delDy = firstPt.y - delH.y;
        const delDist = Math.sqrt(delDx * delDx + delDy * delDy);

        let nearestHandle: typeof handles[0] | null = null;
        let nearestDist = HANDLE_RADIUS;

        if (delDist < DEL_RADIUS) {
          nearestHandle = delH;
          nearestDist = delDist;
        } else {
          for (const h of handles) {
            if (h.id === 'del') continue;  // already checked
            const dx = firstPt.x - h.x, dy = firstPt.y - h.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < nearestDist) {
              nearestDist = dist;
              nearestHandle = h;
            }
          }
        }

        if (nearestHandle) {
          // Delete handle — immediately delete selected blocks, no drag
          if (nearestHandle.id === 'del') {
            const now = Date.now();
            const pageId = this._state.currentPageId;
            const page = this._state.currentPage();
            const reverse: GrimoireAction[] = [];
            const forward: GrimoireAction[] = [];
            for (const id of this._state.selection.blockIds) {
              // NPC sigils: dismiss with farewell instead of hard-delete
              if (page) {
                const blk = page.blocks.get(id);
                if (blk && blk.kind === 'arcane') {
                  const arc = blk as import('./notebook_state').WvArcaneBlock;
                  if (arc.arcaneType === 'laura_channel' || arc.arcaneType === 'ireth_channel') {
                    this._dismissSigil(arc, pageId);
                    continue;
                  }
                }
              }
              // Save block data for undo before deleting
              if (page) {
                const blk = page.blocks.get(id);
                if (blk) {
                  const fig = blk as import('./notebook_state').WvFigureBlock;
                  if (blk.kind === 'figure') {
                    reverse.push({
                      type: 'figure.create', blockId: id, pageId, shapeKind: fig.shapeKind,
                      vertices: fig.vertices.map((v, vi) => ({ id: `v${vi}`, x: v.x, y: v.y })),
                      edges: fig.edges.map((e, ei) => ({ id: `e${ei}`, from: `v${e.from}`, to: `v${e.to}`, type: e.type, controlPoints: e.controlPoints })),
                      bbox: blk.bbox, center: fig.center, rx: fig.rx, ry: fig.ry,
                      penSize: fig.penSize, lineStyle: fig.lineStyle,
                      replacedStrokeIds: fig.replacedStrokeIds ?? [],
                      origin: 'gesture', timestamp: now,
                    } as GrimoireAction);
                  } else if (blk.kind === 'text') {
                    const txt = blk as import('./notebook_state').WvTextBlock;
                    reverse.push({ type: 'block.create', blockId: id, pageId, kind: 'text', bbox: blk.bbox, origin: 'gesture', timestamp: now });
                    if (txt.content) reverse.push({ type: 'text.update', blockId: id, pageId, content: txt.content, origin: 'gesture', timestamp: now });
                  } else {
                    reverse.push({ type: 'block.create', blockId: id, pageId, kind: blk.kind as any, bbox: blk.bbox, origin: 'gesture', timestamp: now });
                  }
                }
              }
              const delAction: GrimoireAction = { type: 'block.delete', blockId: id, pageId, origin: 'gesture', timestamp: now };
              this._dispatch(delAction);
              forward.push(delAction);
            }
            if (forward.length > 0) this._pushUndo({ reverse, forward });
            this._state.selection.blockIds.clear();
            return;
          }
          // Enter handle drag mode — transforms apply live on every move
          const cx = minX + (maxX - minX) / 2;
          const cy = minY + (maxY - minY) / 2;
          this._dragHandle = {
            handleId: nearestHandle.id,
            lastX: firstPt.x,
            lastY: firstPt.y,
            lastAngle: nearestHandle.id === 'rot' ? Math.atan2(firstPt.y - cy, firstPt.x - cx) : 0,
            originX: firstPt.x,
            originY: firstPt.y,
          };
          this._dragDownTime = performance.now();
          this._beginUndoGroup();
          this._activeStrokeId = makeStrokeId(); // sentinel so pointer-move/up route here
          this._spellAllPoints = [];
          return;
        }

        // ── 8b. Body-drag — pointer inside selection bbox → translate ────
        // If no handle was hit but the pointer is inside (or near) the
        // merged selection bbox, start a translation drag automatically.
        // This gives a much larger & more intuitive grab target than the
        // tiny center handle alone.
        const bodyPad = 8;
        if (firstPt.x >= minX - bodyPad && firstPt.x <= maxX + bodyPad &&
            firstPt.y >= minY - bodyPad && firstPt.y <= maxY + bodyPad) {
          this._dragHandle = {
            handleId: 'center',
            lastX: firstPt.x,
            lastY: firstPt.y,
            lastAngle: 0,
            originX: firstPt.x,
            originY: firstPt.y,
          };
          this._dragDownTime = performance.now();
          this._beginUndoGroup();
          this._activeStrokeId = makeStrokeId();
          this._spellAllPoints = [];
          return;
        }
      }
    }

    // ── 9. Cancel stale tap state from previous touch ──────────────────
    clearTimeout(this._singleTapTimer);
    this._pendingSingleTap = null;

    // Begin new stroke
    const id = makeStrokeId();
    this._activeStrokeId = id;
    // Notify the stylus binding engine of the pen-down. If side is
    // held at this instant the engine fires draw-while-pressed
    // synchronously (action applied + held until pen-up).
    this._stylusBindings.onPenDown(e.buttons, f.pageX, f.pageY);
    // Multi-stroke: don't reset _spellAllPoints if continuing an active wand spell
    const isWandContinuation = (this._activeContext.tool === 'wand' || this._isWandInvoked)
      && this._spell.state === 'active' && !this._spell.penDown;
    if (!isWandContinuation) {
      this._spellAllPoints = [];
    }

    // Snap check — does the stroke start near a hookable figure point?
    const page = this._state.currentPage();
    this._snapStart = null;
    this._snapIndicator = null;
    if (page) {
      const sr = this._snap.findNearest(firstPt.x, firstPt.y, page.blocks as any);
      if (sr) {
        this._snapStart = sr;
        // Override the first point to the exact snap target
        firstPt = { ...firstPt, x: sr.target.x, y: sr.target.y };
      }
    }

    // Dismiss selection when starting any non-wand stroke
    // (wand strokes are spell gestures, not regular drawing)
    // NOTE: dismissal deferred to pen-up / commit so taps don't clear it.
    const isWand = this._activeContext.tool === 'wand' || this._isWandInvoked;

    // Tap-vs-writing cooldown. A new pen-down qualifies for tap
    // classification only if we've been idle (no inked stroke pen-up)
    // for at least one short-pulse worth (SL_THRESHOLD_MS). Otherwise
    // the small mark we're about to draw is most likely a character
    // feature (the dot of an "i", the cross of a "t") and shouldn't
    // be treated as a cadence tap. Tap-shaped strokes do NOT extend
    // _lastInkUpTime (see pen-up branch) so a missed tap that lands
    // as ink doesn't poison the next genuine tap.
    this._tapEligibleAtDown =
      (e.timeStamp - this._lastInkUpTime) >= SL_THRESHOLD_MS;

    // Predictive invocation strike. If a chain is alive and the gap
    // up to this pen-down would land a resolving pulse, fire the
    // bright triple-strike *now* — at contact — instead of waiting
    // for pen-up's feedTap → playResolve. Replaces the regular drum
    // kick for this tap; the pen-up onResolved suppresses its own
    // playResolve since the strike already conveyed it.
    let invokeStrikeId: string | null = null;
    if (this._cadence.active && this._activeContext.spellsAllowed) {
      invokeStrikeId = this._cadence.peekResolve(firstPt.x, firstPt.y, e.timeStamp);
      if (invokeStrikeId) {
        this._tapAudio.playInvokeStrike(invokeStrikeId, firstPt.pressure ?? 0.6);
        this._pendingInvokeStrike = invokeStrikeId;
      }
    }

    // Pause the cadence chain timer while the pen is down. If the
    // stroke turns out to be a tap, feedTap re-arms the timer; if it
    // turns out to be ink, the pen-up branch calls cadence.resume().
    // Without this, a chain timer fired mid-stroke could fire
    // onSingleTap or onChainEnd while the user is still drawing.
    this._cadence.pause();

    // Drum: fire the kick at pen-down — the note is audible from the
    // moment of contact, and pen-up later cuts/dampens what's left.
    // Skipped for non-tap-eligible pen-downs, wand strokes, and taps
    // that already played the brighter invocation strike.
    if (!isWand && this._tapEligibleAtDown && !invokeStrikeId) {
      this._tapDownTime = e.timeStamp;
      audioEngine.drum()?.tapDown(id, { pressure: firstPt.pressure });
    } else {
      this._tapDownTime = 0;
    }

    // ── Ink deferral: buffer points instead of starting ink immediately.
    // This prevents "little stroke" artifacts from taps. Ink layer only
    // begins when we're sure it's a real stroke (displacement or duration
    // exceeds tap thresholds).
    if (!isWand) {
      this._inkDeferred = true;
      this._deferredPoints = [];
    } else {
      this._inkDeferred = false;
      this._deferredPoints = [];
    }

    // Start tension drone + spell layer tracing when wand stroke begins
    if (isWand) {
      clearTimeout(this._wandTimeout); // casting started — cancel auto-revert
      clearTimeout(this._wandCastTimeout); // multi-stroke: cancel pending classify
      clearTimeout(this._fluxResolveTimeout); // multi-stroke: cancel pending resolution
      this._tapAudio.startWandTension();
      if (this._spell.state === 'active' && !this._spell.penDown) {
        // Multi-stroke continuation — add another stroke
        this._spell.beginNextStroke();
      } else {
        this._spell.startTracing();
        // Reset strand graph + flux for new cast
        clearGraph(this._strandGraph);
        this._strandHash.clear();
        beginWandCast(this._fluxState);
        this._rectLoopCache.clear();
        this._fluxResolved = false;
        this._lastFluxTick = performance.now();
      }
      // Begin live construction for this stroke (uses first point fed below)
      // Deferred to first point push — see _feedLiveWandPoint.
    }

    // Feed pre-down buffered samples (captured by pointerrawupdate before OS released pointerdown)
    if (this._preDownBuffer.length > 0 && e.pointerType === 'pen') {
      const firstCoalesced = rawEvents.length > 0 ? this._toStrokePoint(rawEvents[0]).timestamp : Infinity;
      for (const bp of this._preDownBuffer) {
        // Only prepend samples that are genuinely earlier than the coalesced batch
        if (bp.timestamp < firstCoalesced) {
          // Start-snap: override first buffered point to exact target
          if (this._snapStart && this._spellAllPoints.length === 0) {
            bp.x = this._snapStart.target.x;
            bp.y = this._snapStart.target.y;
          }
          this._spellAllPoints.push(bp);
          if (isWand) { this._spell.feedPoint(bp); this._feedLiveWandPoint(bp); }
          else        { this._deferredPoints.push(bp); }
        }
      }
      this._preDownBuffer = [];
    }

    // Feed ALL coalesced samples from the down event — captures first-ms points
    for (const ce of rawEvents) {
      const pt = this._toStrokePoint(ce);
      // Start-snap: lock the very first sample to the exact target
      if (this._snapStart && this._spellAllPoints.length === 0) {
        pt.x = this._snapStart.target.x;
        pt.y = this._snapStart.target.y;
      }
      this._spellAllPoints.push(pt);
      if (isWand) {
        this._spell.feedPoint(pt);
        this._feedLiveWandPoint(pt);
      } else {
        this._deferredPoints.push(pt);
      }
    }
  };

  private _onPointerMove = (e: PointerEvent): void => {
    e.preventDefault();

    // Track pointer presence + position regardless of source. This
    // makes the 'hovering' shortcut context fire for both pen-in-
    // proximity and mouse-over-canvas. _pointerPresent is also set
    // by pointerenter; this fallback covers cases where enter wasn't
    // observed (e.g. capture started elsewhere).
    this._pointerPresent = true;
    this._lastPointerType = e.pointerType;
    this._lastPointerButtons = e.buttons;
    const _frame = this._buildFrame(e);
    this._lastPointerX = _frame.pageX;
    this._lastPointerY = _frame.pageY;

    // Browser-side pen capability detection — once we see a real
    // tilt or pressure value, the Slim Pen settings panel surfaces
    // it as 'available'. Cheap latch: never clears, since hardware
    // capabilities don't disappear mid-session.
    if (e.pointerType === 'pen') {
      if (!this._browserPenCaps.tilt && (e.tiltX !== 0 || e.tiltY !== 0)) {
        this._browserPenCaps.tilt = true;
      }
      if (!this._browserPenCaps.pressure && e.pressure > 0 && e.pressure < 1) {
        this._browserPenCaps.pressure = true;
      }
      // Latch live values for the Slim Pen panel readouts. Updated
      // every pen-typed move, so opening Settings → Slim Pen gives
      // a live "tilt: +12°, -3°  pressure: 0.42" the user can watch
      // change as they tilt / press the pen.
      this._liveTilt.tiltX = e.tiltX || 0;
      this._liveTilt.tiltY = e.tiltY || 0;
      this._liveTilt.pressure = e.pressure || 0;
      this._liveTilt.ts = performance.now();
    }

    // ── Pop dialog stroke / drag routing ────────────────────────────────
    if (this._popDialogStrokeActive) {
      const pt = this._toStrokePoint(e);
      if (this._popDialog.dragging) {
        this._popDialog.moveDrag(pt.x, pt.y);
      }
      return;
    }

    // ── Debug dialog drag ───────────────────────────────────────────────
    if (this._debugDragActive) {
      const pt = this._toStrokePoint(e);
      this._debugDialog.drag(pt.x, pt.y);
      return;
    }

    // Gather ALL hardware samples delivered since last frame
    const coalesced = ('getCoalescedEvents' in e) ? e.getCoalescedEvents() : [];
    const events = coalesced.length > 0 ? coalesced : [e];

    // ── Eraser drag routing (no _activeStrokeId — eraser doesn't
    //     start a stroke, just sweeps a circle erasing as it goes).
    //     Has to run BEFORE the no-stroke early-return below or only
    //     the pen-down sample lands and the trail looks like a single
    //     dot instead of a continuous swatch.
    if (this._isEraser) {
      const ghost = this._eraserActiveGhost;
      const r = ghost ? ghost.size : Math.max(2, this._toolRegistry.active().params.size);
      for (const ce of events) {
        const cpt = this._toStrokePoint(ce);
        if (ghost) ghost.points.push({ x: cpt.x, y: cpt.y });
        this._eraseAtCircle(cpt.x, cpt.y, r);
      }
      return;
    }

    if (!this._activeStrokeId) { return; }

    // ── Handle drag routing — apply transforms live ─────────────────────
    if (this._dragHandle) {
      const lastEvt = events[events.length - 1];
      const pt = this._toStrokePoint(lastEvt);
      this._applyHandleDragIncrement(pt.x, pt.y);
      return;
    }

    // ── Vertex drag routing — move individual figure vertex ──────────────
    if (this._dragVertex) {
      const lastEvt = events[events.length - 1];
      const pt = this._toStrokePoint(lastEvt);
      this._applyVertexDragIncrement(pt.x, pt.y);
      return;
    }

    // ── Control-point drag routing — move bezier CP ──────────────────────
    if (this._dragCP) {
      const lastEvt = events[events.length - 1];
      const pt = this._toStrokePoint(lastEvt);
      this._applyCPDragIncrement(pt.x, pt.y);
      return;
    }

    // Process every coalesced sample — no lost points between frames
    const isWandMove = this._activeContext.tool === 'wand' || this._isWandInvoked;
    for (const ce of events) {
      const pt = this._toStrokePoint(ce);
      this._spellAllPoints.push(pt);
      // Stylus binding engine: feed every move so the engine can
      // track total stroke displacement (drives the late-grace vs
      // press-while-drawing decision regardless of when side was
      // pressed).
      this._stylusBindings.notifyMove(pt.x, pt.y);
      if (isWandMove) {
        this._spell.feedPoint(pt);
        this._feedLiveWandPoint(pt);
      } else if (this._inkDeferred) {
        // Still in deferral — buffer the point
        this._deferredPoints.push(pt);
        // Check if displacement or duration crossed stroke threshold
        const first = this._spellAllPoints[0];
        const dx = pt.x - first.x, dy = pt.y - first.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const dur = pt.timestamp - first.timestamp;
        if (dist > 18 || dur > 200) {
          // Confirmed stroke — flush deferred buffer to ink layer
          this._flushDeferredInk();
        }
      } else {
        this._ink.addPoint(pt);
      }
    }

    // Pen-on-paper scratch texture — driven by three raw measures the
    // texture instrument knows how to map: linear speed, direction
    // angle, and stylus pressure. Fires for BOTH normal drawing and
    // cast strokes; the texture instrument biases the scratch tone
    // when cast mode is active (set by tap_audio.startWandTension /
    // stopWandTension), so cast feels muffled-resonant against the
    // brighter pen-on-paper baseline. One sound path, no extra hum.
    if (this._spellAllPoints.length >= 2) {
      const a = this._spellAllPoints[this._spellAllPoints.length - 2];
      const b = this._spellAllPoints[this._spellAllPoints.length - 1];
      const dt = Math.max(1, b.timestamp - a.timestamp);
      const dx = b.x - a.x, dy = b.y - a.y;
      const speed = Math.hypot(dx, dy) / dt;          // px/ms
      const angle = Math.atan2(dy, dx);                // rad
      const pressure = b.pressure ?? 0.6;
      audioEngine.texture()?.setScratchMotion(speed, angle, pressure);
    }

    // Live snap indicator — check if current stroke tip is near a target
    if (!isWandMove && this._spellAllPoints.length > 3) {
      const tip = this._spellAllPoints[this._spellAllPoints.length - 1];
      const pg = this._state.currentPage();
      this._snapIndicator = pg ? this._snap.findNearest(tip.x, tip.y, pg.blocks as any) : null;
    }

    // Page-turn drag (edge of page, horizontal)
    if (this._pageTurnDragStart) {
      const lastEvt = events[events.length - 1];
      const lastMPt = this._toStrokePoint(lastEvt);
      const dx = lastMPt.x - this._pageTurnDragStart.x;
      const dy = lastMPt.y - this._pageTurnDragStart.y;
      if (Math.abs(dy) < 30 && Math.abs(dx) > 10) {
        this._pageTurnProgress = Math.min(Math.abs(dx) / 120, 1);
        this._pageTurnDirection = dx < 0 ? 'forward' : 'back';
      }
    }
  };

  private _onPointerUp = (e: PointerEvent): void => {
    e.preventDefault();
    this._preDownBuffer = []; // clear buffer on lift

    // Diagnostic capture for the debug HUD.
    this._lastPointerType = e.pointerType;
    this._lastPointerButtons = e.buttons;
    if (e.button !== -1) {
      this._lastButtonEvent = `up   ${e.pointerType} btn=${e.button} bm=${e.buttons}`;
    }

    // ── Side-button release on pointerup ──────────────────────────────
    // pointerup with button === 2 fires when the side button changes
    // state. Two cases:
    //   (a) tip is STILL down (e.buttons has any other bit set) — the
    //       user lifted the side mid-stroke. Don't end the stroke.
    //   (b) tip is also released (e.buttons === 0) — this is the
    //       *whole* pen-up, just delivered via the side-button event
    //       (Windows' "right click" style mapping for side+tip on
    //       Slim Pen 2). Fall through so _finishPointerUp runs and
    //       the engine sees onPenUp; otherwise press-and-tap never
    //       fires for quick taps.
    if (e.button === 2 && e.buttons !== 0) {
      return;
    }

    // Hint ring resolved at pen-down — pen-up is a no-op for the rest
    // of the pipeline. Reset the flag so the next gesture goes
    // through the normal path.
    if (this._consumedAtPenDown) {
      this._consumedAtPenDown = false;
      return;
    }

    // ── Pop dialog stroke / drag end ─────────────────────────────────────
    if (this._popDialogStrokeActive) {
      if (this._popDialog.dragging) {
        this._popDialog.endDrag();
      }
      this._popDialogStrokeActive = false;
      return;
    }

    // ── Debug dialog drag end ────────────────────────────────────────────
    if (this._debugDragActive) {
      this._debugDialog.endDrag();
      this._debugDragActive = false;
      return;
    }

    if (!this._activeStrokeId) {
      // Close any open eraser undo group (eraser returns early and never sets _activeStrokeId)
      if (this._isEraser && this._groupDepth > 0) { this._endUndoGroup(); }
      this._isEraser = false;
      // Set fadeStart on the active ghost so it begins its fade-out
      // animation. Render loop will drop it once fully transparent.
      if (this._eraserActiveGhost) {
        this._eraserActiveGhost.fadeStart = performance.now();
        this._eraserActiveGhost = null;
      }
      // Restore the user's previously-picked tool when the hardware
      // back-tip lifts. (No-op if the user manually picked eraser.)
      if (this._hardwareEraserPrevTool !== null) {
        this._toolRegistry.pick(this._hardwareEraserPrevTool);
        this._hardwareEraserPrevTool = null;
      }
      return;
    }

    const now = performance.now();

    // ── Handle drag completion (transforms already applied live) ─────────
    if (this._dragHandle) {
      const dh = this._dragHandle;
      const totalDist = Math.hypot(dh.lastX - dh.originX, dh.lastY - dh.originY);
      const dragDur = now - this._dragDownTime;
      this._endUndoGroup();
      this._dragHandle = null;
      this._activeStrokeId = null;
      // Barely-moved drag → treat as tap for cadence engine
      if (totalDist < 28 && dragDur < 280 && this._activeContext.spellsAllowed) {
        this._feedTapToCadence(dh.originX, dh.originY, now, 0.6);
      } else if (totalDist < 6) {
        this._state.clearSelection();
      }
      this._finishPointerUp();
      return;
    }

    // ── Vertex drag completion ───────────────────────────────────────────
    if (this._dragVertex) {
      const dv = this._dragVertex;
      const totalDist = Math.hypot(dv.lastX - dv.originX, dv.lastY - dv.originY);
      const dragDur = now - this._dragDownTime;
      this._endUndoGroup();
      this._dragVertex = null;
      this._activeStrokeId = null;
      if (totalDist < 28 && dragDur < 280 && this._activeContext.spellsAllowed) {
        this._feedTapToCadence(dv.originX, dv.originY, now, 0.6);
      }
      this._finishPointerUp();
      return;
    }

    // ── Control-point drag completion ────────────────────────────────────
    if (this._dragCP) {
      const dc = this._dragCP;
      const totalDist = Math.hypot(dc.lastX - dc.originX, dc.lastY - dc.originY);
      const dragDur = now - this._dragDownTime;
      this._endUndoGroup();
      this._dragCP = null;
      this._activeStrokeId = null;
      if (totalDist < 28 && dragDur < 280 && this._activeContext.spellsAllowed) {
        this._feedTapToCadence(dc.originX, dc.originY, now, 0.6);
      }
      this._finishPointerUp();
      return;
    }

    // Capture final coalesced samples (pen may have moved since last pointermove)
    const coalesced = ('getCoalescedEvents' in e) ? e.getCoalescedEvents() : [];
    const events = coalesced.length > 0 ? coalesced : [e];
    for (const ce of events) {
      const cpt = this._toStrokePoint(ce);
      this._spellAllPoints.push(cpt);
      if (this._inkDeferred) {
        this._deferredPoints.push(cpt);
      } else if (!((this._activeContext.tool === 'wand') || this._isWandInvoked)) {
        this._ink.addPoint(cpt);
      }
    }

    const isWand = this._activeContext.tool === 'wand' || this._isWandInvoked;

    if (isWand) {
      // ── Wand mode (policy-driven or invocation-driven) ─────────────────
      this._tapAudio.stopWandTension();

      const isTapLikeCast = isTap(this._spellAllPoints);

      if (isTapLikeCast) {
        // Tap in cast mode → tear down the cast graph and feed the
        // tap to cadence. The cadence chain is intact, so this tap
        // continues the sequence — letters become 'SS' after the
        // cast's first 'S', and a follow-up L-gap tap will resolve
        // 'SSL' (stylus menu) cleanly.
        this._spell.reset();
        this._cancelWand();
        if (this._activeContext.spellsAllowed) {
          const tap = tapFromStroke(this._spellAllPoints, this._activeStrokeId!);
          const p0 = this._spellAllPoints[0]?.pressure ?? 0.6;
          this._feedTapToCadence(tap.x, tap.y, tap.time, p0);
        }
      } else if (this._spellAllPoints.length >= 2) {
        // Multi-stroke: lift pen → accelerate consumption, start timeout
        this._spell.liftPen();

        // ── New spell engine path ────────────────────────────────────────
        // The flux / strand-graph machinery is legacy plumbing — when the
        // new engine is on, skip it entirely. Just hand the just-finished
        // stroke to the engine and let _tryFluxResolution's gate do the
        // teardown. No strand operations, no flux state walks, no timeout
        // backstop needed.
        if (this._newSpellsEnabled()) {
          this._tryFluxResolution();   // gate runs new engine + tears down
          this._finishPointerUp();
          return;
        }

        // ── Legacy strand graph (gated below `_newSpellsEnabled`) ────────
        if (!this._fluxResolved) {
          // Remove the live pair (its node IDs will become stale after clearLiveStrand)
          const fs = this._fluxState;
          const livePairIdx = fs.pairs.findIndex(p => p.live);
          if (livePairIdx >= 0) {
            const lp = fs.pairs[livePairIdx];
            fs.clampedNodes.delete(lp.sourceNodeId);
            fs.clampedNodes.delete(lp.coSourceNodeId);
            fs.pairs.splice(livePairIdx, 1);
          }

          // Clear live construction state (preview strand/loops) before
          // rebuilding the stroke properly via addStroke.
          clearLiveStrand(this._strandGraph, this._strandHash);

          // Use only the CURRENT stroke (last in spell_layer), not the
          // accumulated _spellAllPoints which spans all multi-stroke points.
          const strokes = this._spell.allStrokes;
          const currentStroke = strokes[strokes.length - 1];
          if (currentStroke && currentStroke.length >= 2) {
            const result = addStroke(this._strandGraph, currentStroke, this._strandHash);
            // Source = stroke start (oscillating), Ground = stroke end
            addWandSource(fs, result.startNodeId as number);
            addWandSink(fs, result.endNodeId as number);
            // Activate any newly detected loops
            for (const loop of result.newLoops) {
              activateLoop(this._strandGraph, fs, loop);
              this._rectLoopCache.set(loop.id as number, isRectangularLoop(this._strandGraph, loop));
            }
          }
          // Attempt resolution immediately — multi-stroke is protected by
          // the pen-down handler clearing the cast timeout and continuing.
          this._tryFluxResolution();
        }

        clearTimeout(this._wandCastTimeout);
        const capturedId = this._activeStrokeId!;
        // Hard backstop — energy-based lifecycle handles resolution/fizzle first
        const timeout = 5000;
        this._wandCastTimeout = window.setTimeout(() => {
          // Timeout fired — no more strokes expected. Classify + finalize.
          this._classifyAndConsume(capturedId);
          if (this._isWandInvoked) {
            this._cancelWand();
          }
        }, timeout);
      } else {
        this._spell.reset();
      }
    } else {
      // ── Normal pen mode ────────────────────────────────────────────────

      // A short stroke is treated as a tap only if the cooldown clears.
      // Recently-lifted ink (writing) keeps short strokes as ink so the
      // dot of an "i" or the cross of a "t" doesn't get classified as a
      // cadence tap.
      const wantTap = this._tapEligibleAtDown && isTap(this._spellAllPoints);

      if (wantTap) {
        // Tap path — fade the dot via the unified dissolve, no persistent
        // ink left behind. The cadence engine receives the tap; if it
        // resolves an invocation, the resolver fires the burst + audio
        // synchronously on this very tap.
        const tap = this._activeContext.spellsAllowed
          ? tapFromStroke(this._spellAllPoints, this._activeStrokeId!)
          : null;

        // One-shot ring intercept (hint rings, analyse-knock, etc.).
        let oneShotId: string | null = null;
        if (tap) {
          oneShotId = this._tapRingLayer.confirmOneShotTap(tap.x, tap.y);
        }

        // Drum: the pen-down opened a closed note; resolve it now with
        // the actual hold time so the envelope matches the user's tap
        // duration. (One-shot ring tap-down also opened a note — we
        // still want the kick on those.)
        if (this._tapDownTime > 0 && this._activeStrokeId) {
          const holdMs = e.timeStamp - this._tapDownTime;
          audioEngine.drum()?.tapRelease(this._activeStrokeId, holdMs);
          this._tapDownTime = 0;
        }

        if (oneShotId) {
          // Hint ring consumed the tap. Discard the buffered ink and
          // dispatch the one-shot's action.
          this._inkDeferred = false;
          this._deferredPoints = [];
          this._ink.cancelStroke();
          this._dispatchOneShotResolve(oneShotId, tap!.x, tap!.y);
        } else {
          // Genuine tap. Briefly commit so we have a stroke object the
          // selection analyser can consume on regions; if no region
          // claims it, we fade and erase ourselves so nothing persists.
          if (this._inkDeferred) { this._flushDeferredInk(); }
          const stroke = this._ink.commitStroke();

          if (tap) {
            const p0 = this._spellAllPoints[0]?.pressure ?? 0.6;
            this._feedTapToCadence(tap.x, tap.y, tap.time, p0);
          }

          if (stroke) {
            const pageId = this._state.currentPageId;
            // Hand the dot to the selection analyser first. It returns
            // true when a region has consumed (erased + faded) the
            // stroke; otherwise we own the cleanup.
            const consumed = this._analyzeStrokeForSelection(
              stroke, this._spellAllPoints, pageId,
            );
            if (!consumed) {
              // Not on any interactor — fade the dot ourselves.
              const L = this._layout;
              const absPts = stroke.points.map(p => ({ x: p.x + L.x, y: p.y + L.y }));
              if (absPts.length >= 2 && this._settings.animations) {
                this._dissolves.add('fade', absPts);
              }
              this._eraseStrokeForSelection(stroke.id, pageId);
            }
          }
        }
      } else {
        // Drum: pen-down opened a closed note speculatively, but this
        // ended up being a real stroke (or a cooldown-blocked short
        // mark). Discard the closed note silently — it never opened.
        if (this._tapDownTime > 0 && this._activeStrokeId) {
          audioEngine.drum()?.tapDiscard(this._activeStrokeId);
          this._tapDownTime = 0;
        }

        // Not a tap — confirmed stroke. Flush any remaining deferred ink.
        if (this._inkDeferred) {
          this._flushDeferredInk();
        }
        // User started writing — they're committing to ink, not
        // accepting any pending hint. Clear all hints (cast-mode or
        // freshly-drawn).
        this._shapeHintPending.clear();

        // Endpoint snap — if the stroke tip is near a snap target, lock it.
        // Captured here for the post-commit split_edge dispatch below
        // (so a stroke ending at an edge midpoint inserts a topological
        // vertex on the source figure, not just a visual coincidence).
        let endSnap: SnapResult | null = null;
        if (this._spellAllPoints.length > 0) {
          const tip = this._spellAllPoints[this._spellAllPoints.length - 1];
          const pg = this._state.currentPage();
          endSnap = pg ? this._snap.findNearest(tip.x, tip.y, pg.blocks as any) : null;
          if (endSnap) {
            tip.x = endSnap.target.x;
            tip.y = endSnap.target.y;
            this._ink.snapEndpoint(endSnap.target.x, endSnap.target.y);
          }
        }

        const stroke = this._ink.commitStroke();
        if (stroke) {
          // If there's an active selection, check if this stroke touches
          // the selection bbox — if so dissolve it instead of committing.
          let strokeRejected = false;
          if (this._state.selection.blockIds.size > 0 && stroke.points.length >= 3) {
            const selBbox = this._state.selectionBbox();
            if (selBbox) {
              const PAD = 6;
              const sx = selBbox.x - PAD, sy = selBbox.y - PAD;
              const sr = selBbox.x + selBbox.width + PAD;
              const sb = selBbox.y + selBbox.height + PAD;
              let touches = false;
              for (const p of stroke.points) {
                if (p.x >= sx && p.x <= sr && p.y >= sy && p.y <= sb) {
                  touches = true; break;
                }
              }
              if (touches) {
                // Dissolve: soft-fade the stroke and erase from state
                this._ink.addSoftFade(stroke.id);
                this._dispatch({
                  type: 'ink.erase', pageId: this._state.currentPageId,
                  target: stroke.id, origin: 'gesture', timestamp: Date.now(),
                });
                // Do NOT clear selection — the stroke was rejected
                strokeRejected = true;
              }
            }
            if (!strokeRejected) {
              // Real drawn stroke outside selection → dismiss selection
              this._state.clearSelection();
            }
          }
          if (!strokeRejected) {
          const pageId = this._state.currentPageId;
          this._dispatch({
            type: 'ink.commit',
            strokeId: stroke.id,
            pageId,
            points: stroke.points,
            style: stroke.style,
            origin: 'gesture',
            timestamp: Date.now(),
          });
          this._pushUndo({
            reverse: [{ type: 'ink.erase', pageId, target: stroke.id, origin: 'gesture', timestamp: Date.now() } as GrimoireAction],
            forward:  [{ type: 'ink.commit', strokeId: stroke.id, pageId, points: stroke.points, style: stroke.style, pageLayer, origin: 'gesture', timestamp: Date.now() } as GrimoireAction],
          });

          // ── Topological mid-line connection (Batch 3b) ──────────
          // If the stroke started or ended at an existing edge's
          // midpoint, insert a real vertex on the source figure so
          // the connection isn't just visual. Each midpoint is split
          // independently — a stroke that bridges two midpoints
          // splits BOTH source figures.
          this._splitEdgeIfMidpoint(this._snapStart, pageId);
          this._splitEdgeIfMidpoint(endSnap, pageId);

          // ── Handwriting composition feed ───────────────────────
          // Both text and shape recognition are gated on cast mode: a stroke
          // drawn during a cast is magic, not content, and analysing it as
          // either a word or a primitive shape pollutes the meta layer with
          // garbage. _inCastMode is computed once below and reused.
          const inCastMode = this._isWandInvoked
            || this._spell?.state !== 'idle';
          if (!inCastMode
              && this._viewMode === 'page'
              && this._activeContext === JOURNAL_CONTEXT) {
            this._textCompose.feedStrokeWithPage(
              { id: stroke.id, points: stroke.points },
              pageId,
            );
          }

          // ── Shape recognition — gated by shapeMode setting ─────────
          // Skip tiny strokes (dots, commas, accent marks) — they're never
          // meaningful shapes and suggesting for them creates noise.
          // Cast-mode gate already applied via the shared inCastMode above.
          //
          // Skip for any "interactor surface": settings view and active pop
          // dialogs both want every stroke to flow into the selection
          // analyser (so taps/draws/lassos all select and fade the same
          // way). Page-header / section-header swatches still defer to
          // shape recognition, since those swatches sit on the live page.
          const inInteractorSurface = this._viewMode === 'settings'
            || this._popDialog.active;

          const minShapeLen = this._strokeLength(stroke.points);
          const sb = this._strokeBbox(stroke.points);
          const minShapeDim = Math.max(sb.w, sb.h);
          const MIN_SHAPE_ARCLEN = 28;  // ~a decent digit or tick
          const MIN_SHAPE_DIM    = 14;  // ~bigger than a comma or dot

          // Time-aware dwell pre-check (Batch 3a). A stroke that
          // holds in place ≥280ms with ≤12px drift is a "point"
          // gesture — independent of what recogniseShape would say,
          // and bypassing the MIN_SHAPE_DIM gate (a dwell has tiny
          // bbox by definition). Either splits an existing edge
          // (when the dwell landed on a midpoint snap) or creates
          // a fresh point figure.
          const dwell = !inCastMode && !inInteractorSurface
            ? classifyDwell(stroke.points)
            : null;

          // Boost overrides the shapeMode='off' gate so the user can
          // hold the side button and force shape detection on a
          // stroke even if they normally keep autoshape disabled.
          const boost = this._shapeBoostHeld;
          if (dwell) {
            this._handleDwellPoint(dwell, stroke.id, pageId);
          } else if (!inCastMode
              && !inInteractorSurface
              && (boost || this._settings.shapeMode !== 'off')
              && minShapeLen >= MIN_SHAPE_ARCLEN
              && minShapeDim >= MIN_SHAPE_DIM) {
            const capturedStroke = stroke;
            const capturedSpellPts = this._spellAllPoints.slice();
            const capturedPageId = pageId;
            // While boosted: force 'full' replacement at the most
            // permissive confidence the recogniser supports, so the
            // gesture's whole point — "definitely turn this into a
            // shape" — actually lands.
            const capturedMode = boost ? 'full' : this._settings.shapeMode;
            const minConf = boost ? 0.40 : (capturedMode === 'full' ? 0.60 : 0.50);

            this._shapeBridge.analyseShape(stroke.points, minConf).then(result => {
              if (!result.simplifiable || !result.shape) {
                this._analyzeStrokeForSelection(capturedStroke, capturedSpellPts, capturedPageId);
                return;
              }
              // Reject near-degenerate spline / line suggestions: if a
              // spline's control points are essentially colinear (or a
              // line's residual off-axis is tiny), the suggestion is
              // pointless — the stroke already looks like what the shape
              // would replace it with.
              if (this._shapeSuggestionLooksPointless(result.shape, capturedStroke.points)) {
                this._analyzeStrokeForSelection(capturedStroke, capturedSpellPts, capturedPageId);
                return;
              }
              if (capturedMode === 'full') {
                this._replaceStrokeWithShape(result.shape, capturedStroke.id, capturedPageId);
              } else {
                this._spawnShapeHintRing(result.shape, capturedStroke.id, capturedPageId);
              }
            });
          } else {
            // Off mode / tiny stroke: skip shape analysis, feed to selection
            // analyzer instead (dots / commas would never trigger selection
            // either, but the cheap pre-filter avoids wasted work).
            this._analyzeStrokeForSelection(stroke, this._spellAllPoints, pageId);
          }
          } // end if (!strokeRejected)
        }

        // Record this pen-up so the next pen-down can decide whether
        // the cooldown still suppresses tap-classification (writing →
        // no tap; idle past SL_THRESHOLD_MS → tap allowed). We only
        // refresh the cooldown for genuinely-writing strokes — a
        // tap-shaped commit (the dot of an "i", or a missed cadence
        // tap that fell through eligibility) does NOT extend it, so
        // one stray short stroke can't cascade into blocking the
        // next deliberate tap.
        if (!isTap(this._spellAllPoints)) {
          this._lastInkUpTime = e.timeStamp;
        }
      }
    }

    this._finishPointerUp();

    if (this._pageTurnProgress > 0.6) {
      if (this._viewMode === 'codex') {
        this._codexTurnPage(this._pageTurnDirection);
      } else {
        const visPages = this._state.visiblePages();
        const dir = this._pageTurnDirection === 'forward' ? 1 : -1;
        const curVisIdx = this._state.visiblePageIndex;
        const nextVisIdx = curVisIdx + dir;
        if (nextVisIdx >= 0 && nextVisIdx < visPages.length) {
          this._switchContent({ viewMode: 'page', pageId: visPages[nextVisIdx].id });
        }
      }
    }

    // Silence the pen-on-paper scratch — texture instrument falls
    // back to its default (silent scratch) baseline on pen-up.
    audioEngine.texture()?.silenceScratch();

    this._pageTurnDragStart = null;
    this._pageTurnProgress = 0;
    this._activeStrokeId = null;
    this._snapIndicator = null;
    this._snapStart = null;
  };

  private _onPointerCancel = (_e: PointerEvent): void => {
    audioEngine.texture()?.silenceScratch();
    if (this._tapDownTime > 0 && this._activeStrokeId) {
      audioEngine.drum()?.tapDiscard(this._activeStrokeId);
      this._tapDownTime = 0;
    }
    this._gracefulStrokeEnd();
  };

  /**
  /**
   * Flush deferred ink points — start the ink layer stroke and replay
   * all buffered points.  Called when we've confirmed this is a real draw
   * stroke, not a tap.
   */
  private _flushDeferredInk(): void {
    if (!this._inkDeferred || !this._activeStrokeId) return;
    this._inkDeferred = false;
    this._ink.beginStroke(this._activeStrokeId, this._penStyle);
    for (const pt of this._deferredPoints) {
      this._ink.addPoint(pt);
    }
    this._deferredPoints = [];
  }

  /**
   * Erase every cooldown-tap stroke that's still inside the cooldown
   * window (i.e. could plausibly have contributed to the just-resolved
   * cadence pattern).  Strokes outside the window are pruned without
   * deletion — they're old enough that they're definitely intentional
   * marks, not cast taps.
   */
  /**
   * Gracefully end a stroke that was interrupted (cancel / lost capture).
   * Commits the ink if the stroke is substantial enough to be intentional,
   * otherwise discards it silently.
   */
  private _gracefulStrokeEnd(): void {
    if (!this._activeStrokeId) return;

    // Wand strokes are always discarded on cancel — spell classification
    // requires a clean, complete gesture.
    const isWand = this._activeContext.tool === 'wand' || this._isWandInvoked;
    if (isWand) {
      this._ink.cancelStroke();
      this._pendingSpell = null;
      this._spell.reset();
      this._chargeEngine.cancel();
      if (this._isWandInvoked) { this._cancelWand(); }
      this._activeStrokeId = null;
      return;
    }

    // For normal ink: commit if the stroke has enough substance to be
    // intentional (≥5 points AND ≥15 px arc length), otherwise discard.
    const MIN_CANCEL_POINTS = 5;
    const MIN_CANCEL_ARC    = 15;
    const pts = this._spellAllPoints;
    if (pts.length >= MIN_CANCEL_POINTS && computeStrokeLength(pts) >= MIN_CANCEL_ARC) {
      // If ink was deferred, flush it now before committing
      if (this._inkDeferred) { this._flushDeferredInk(); }
      const stroke = this._ink.commitStroke();
      if (stroke) {
        const pageId = this._state.currentPageId;
        this._dispatch({
          type: 'ink.commit',
          strokeId: stroke.id,
          pageId,
          points: stroke.points,
          style: stroke.style,
          origin: 'gesture',
          timestamp: Date.now(),
        });
        this._pushUndo({
          reverse: [{ type: 'ink.erase', pageId, target: stroke.id, origin: 'gesture', timestamp: Date.now() } as GrimoireAction],
          forward:  [{ type: 'ink.commit', strokeId: stroke.id, pageId, points: stroke.points, style: stroke.style, origin: 'gesture', timestamp: Date.now() } as GrimoireAction],
        });

        // ── Handwriting composition feed (graceful end) ─────────
        if (this._viewMode === 'page' && this._activeContext === JOURNAL_CONTEXT) {
          this._textCompose.feedStrokeWithPage(
            { id: stroke.id, points: stroke.points },
            pageId,
          );
        }
      }
    } else {
      if (this._inkDeferred) {
        this._inkDeferred = false;
        this._deferredPoints = [];
      } else {
        this._ink.cancelStroke();
      }
    }

    this._pendingSpell = null;
    this._spell.reset();
    this._chargeEngine.cancel();
    this._stylusBindings.reset();
    // Revert any held modal state so a cancelled side-held stroke
    // doesn't leak that state past the gesture.
    if (this._stylusSwapHeld) {
      this._stylusSwapHeld = false;
      this._swapRecentTools();
    }
    if (this._shapeBoostHeld) {
      this._shapeBoostHeld = false;
    }
    this._activeStrokeId = null;
    this._dragHandle = null;
    this._dragVertex = null;
    this._dragCP = null;
    this._pageTurnDragStart = null;
    this._pageTurnProgress = 0;
  }

  // ── Handle drag → block scale / move ───────────────────────────────────────

  /**
   * Apply one incremental handle-drag frame.  Called on every pointer-move
   * so the selection deforms continuously under the pen.
   */
  private _applyHandleDragIncrement(curX: number, curY: number): void {
    const dh = this._dragHandle;
    if (!dh) { return; }

    const page = this._state.currentPage();
    if (!page) { return; }

    const sel = this._state.selection;
    if (sel.blockIds.size === 0) { return; }

    // Compute the merged bbox of all selected blocks (updated each frame)
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const id of sel.blockIds) {
      const blk = page.blocks.get(id);
      if (!blk) { continue; }
      minX = Math.min(minX, blk.bbox.x);
      minY = Math.min(minY, blk.bbox.y);
      maxX = Math.max(maxX, blk.bbox.x + blk.bbox.width);
      maxY = Math.max(maxY, blk.bbox.y + blk.bbox.height);
    }
    if (minX === Infinity) { return; }

    const bw = maxX - minX;
    const bh = maxY - minY;
    const dx = curX - dh.lastX;
    const dy = curY - dh.lastY;
    const now = Date.now();
    const pageId = this._state.currentPageId;

    // Group expansion — selecting any group member pulls in its groupmates
    // for move/scale/rotate so the bundle stays cohesive.
    const targetIds = this._expandByGroup(sel.blockIds);

    // Translation — center handle
    if (dh.handleId === 'center') {
      for (const id of targetIds) {
        this._dispatch({ type: 'block.move', blockId: id, pageId, dx, dy, origin: 'gesture', timestamp: now });
      }
      this._pushUndo({
        reverse: targetIds.map(id => ({ type: 'block.move' as const, blockId: id, pageId, dx: -dx, dy: -dy, origin: 'gesture' as const, timestamp: now })),
        forward: targetIds.map(id => ({ type: 'block.move' as const, blockId: id, pageId, dx, dy, origin: 'gesture' as const, timestamp: now })),
      });
      dh.lastX = curX;
      dh.lastY = curY;
      return;
    }

    // Rotation — continuous angular delta from last frame
    if (dh.handleId === 'rot') {
      const cx = minX + bw / 2;
      const cy = minY + bh / 2;
      const curAngle = Math.atan2(curY - cy, curX - cx);
      const dAngle = curAngle - dh.lastAngle;
      const pivot = { x: cx, y: cy };
      for (const id of targetIds) {
        this._dispatch({ type: 'block.rotate', blockId: id, pageId, dAngle, pivot, origin: 'gesture', timestamp: now });
      }
      this._pushUndo({
        reverse: targetIds.map(id => ({ type: 'block.rotate' as const, blockId: id, pageId, dAngle: -dAngle, pivot, origin: 'gesture' as const, timestamp: now })),
        forward: targetIds.map(id => ({ type: 'block.rotate' as const, blockId: id, pageId, dAngle, pivot, origin: 'gesture' as const, timestamp: now })),
      });
      dh.lastX = curX;
      dh.lastY = curY;
      dh.lastAngle = curAngle;
      return;
    }

    // Scale — incremental ratio from last frame
    const affectsL = dh.handleId === 'tl' || dh.handleId === 'l' || dh.handleId === 'bl';
    const affectsR = dh.handleId === 'tr' || dh.handleId === 'r' || dh.handleId === 'br';
    const affectsT = dh.handleId === 'tl' || dh.handleId === 't' || dh.handleId === 'tr';
    const affectsB = dh.handleId === 'bl' || dh.handleId === 'b' || dh.handleId === 'br';

    let newMinX = minX, newMinY = minY, newMaxX = maxX, newMaxY = maxY;
    if (affectsL) { newMinX += dx; }
    if (affectsR) { newMaxX += dx; }
    if (affectsT) { newMinY += dy; }
    if (affectsB) { newMaxY += dy; }

    const newW = Math.max(newMaxX - newMinX, 8);
    const newH = Math.max(newMaxY - newMinY, 8);

    const sx = bw > 0 ? newW / bw : 1;
    const sy = bh > 0 ? newH / bh : 1;

    const pivotX = affectsL ? maxX : minX;
    const pivotY = affectsT ? maxY : minY;

    for (const id of targetIds) {
      this._dispatch({
        type: 'block.scale', blockId: id, pageId,
        sx, sy, pivot: { x: pivotX, y: pivotY },
        origin: 'gesture', timestamp: now,
      });
    }
    this._pushUndo({
      reverse: targetIds.map(id => ({ type: 'block.scale' as const, blockId: id, pageId, sx: 1 / sx, sy: 1 / sy, pivot: { x: pivotX, y: pivotY }, origin: 'gesture' as const, timestamp: now })),
      forward: targetIds.map(id => ({ type: 'block.scale' as const, blockId: id, pageId, sx, sy, pivot: { x: pivotX, y: pivotY }, origin: 'gesture' as const, timestamp: now })),
    });

    dh.lastX = curX;
    dh.lastY = curY;
  }

  /** Apply an incremental vertex move during vertex drag. */
  private _applyVertexDragIncrement(curX: number, curY: number): void {
    const dv = this._dragVertex;
    if (!dv) return;
    const dx = curX - dv.lastX;
    const dy = curY - dv.lastY;
    if (dx === 0 && dy === 0) return;
    const pageId = this._state.currentPageId;

    if (dv.vertexIndex === -1) {
      // Dragging center (circle/ellipse/point) — move entire block
      this._dispatch({
        type: 'block.move', blockId: dv.blockId, pageId,
        dx, dy, origin: 'gesture', timestamp: Date.now(),
      });
      this._pushUndo({
        reverse: [{ type: 'block.move', blockId: dv.blockId, pageId, dx: -dx, dy: -dy, origin: 'gesture', timestamp: Date.now() }],
        forward: [{ type: 'block.move', blockId: dv.blockId, pageId, dx, dy, origin: 'gesture', timestamp: Date.now() }],
      });
    } else {
      // Dragging individual vertex
      const fwd = {
        type: 'figure.moveVertex' as const, blockId: dv.blockId, pageId,
        vertexIndex: dv.vertexIndex, dx, dy,
        origin: 'gesture' as const, timestamp: Date.now(),
      } satisfies GrimoireAction;
      this._dispatch(fwd);
      this._pushUndo({
        reverse: [{ type: 'figure.moveVertex' as const, blockId: dv.blockId, pageId, vertexIndex: dv.vertexIndex, dx: -dx, dy: -dy, origin: 'gesture' as const, timestamp: Date.now() } satisfies GrimoireAction],
        forward: [fwd],
      });
    }

    dv.lastX = curX;
    dv.lastY = curY;
  }

  /** Apply an incremental control-point move during CP drag. */
  private _applyCPDragIncrement(curX: number, curY: number): void {
    const dc = this._dragCP;
    if (!dc) return;
    const dx = curX - dc.lastX;
    const dy = curY - dc.lastY;
    if (dx === 0 && dy === 0) return;
    const fwd = {
      type: 'figure.moveControlPoint' as const,
      blockId: dc.blockId,
      pageId: this._state.currentPageId,
      edgeIndex: dc.edgeIndex,
      cpIndex: dc.cpIndex,
      dx, dy,
      origin: 'gesture' as const,
      timestamp: Date.now(),
    } satisfies GrimoireAction;
    this._dispatch(fwd);
    this._pushUndo({
      reverse: [{ type: 'figure.moveControlPoint' as const, blockId: dc.blockId, pageId: this._state.currentPageId, edgeIndex: dc.edgeIndex, cpIndex: dc.cpIndex, dx: -dx, dy: -dy, origin: 'gesture' as const, timestamp: Date.now() } satisfies GrimoireAction],
      forward: [fwd],
    });
    dc.lastX = curX;
    dc.lastY = curY;
  }

  // ── Single-tap deferred action ──────────────────────────────────────────
  //
  // Called by cadence engine when a lone tap is confirmed (no pattern matched).
  // - Text block hit → open text input
  // - Other block / nearby stroke → select with edit handles

  private _onSingleTapConfirmed(): void {
    const pt = this._pendingSingleTap;
    this._pendingSingleTap = null;
    if (!pt) return;

    const page = this._state.currentPage();
    if (!page) return;
    const pageId = this._state.currentPageId;

    // 0) Handwriting composition — tap repositions cursor or ends session
    if (this._textCompose.active) {
      const hit = this._state.hitTest(pt.x, pt.y);
      if (hit && hit.kind === 'text') {
        const block = page.blocks.get(hit.id);
        if (block && block.kind === 'text') {
          const tb = block as import('./notebook_state').WvTextBlock;
          // Approximate character index from tap x within the text block
          const ctx = this._ctx;
          ctx.font = `${tb.fontSize ?? 16}px Georgia, serif`;
          const relX = pt.x - tb.bbox.x - 4;
          let charIdx = 0;
          const content = tb.content ?? '';
          for (let i = 0; i <= content.length; i++) {
            const w = ctx.measureText(content.slice(0, i)).width;
            if (w > relX) break;
            charIdx = i;
          }
          if (this._textCompose.tapOnBlock(hit.id, charIdx)) return;
          // Different text block — end current session, start on this one
          this._textCompose.startOnExistingBlock(
            hit.id, pageId, charIdx, tb.fontSize ?? 16, tb.bbox,
          );
          return;
        }
      }
      // Tap on empty space or non-text block — end composition
      this._textCompose.end();
    }

    // 1) Arcane proximity test — check moveX/moveY (visual position) directly,
    //    since NPCs roam away from their bbox origin.
    {
      const TAP_R = 36;  // generous tap radius around visual sigil center
      let closestArc: import('./notebook_state').WvArcaneBlock | null = null;
      let closestDist = Infinity;
      for (const [, b] of page.blocks) {
        if (b.kind !== 'arcane') continue;
        const arc = b as import('./notebook_state').WvArcaneBlock;
        const sx = arc.moveX ?? (arc.bbox.x + arc.bbox.width / 2);
        const sy = arc.moveY ?? (arc.bbox.y + arc.bbox.height / 2);
        const d = Math.hypot(pt.x - sx, pt.y - sy);
        if (d <= TAP_R && d < closestDist) {
          closestDist = d;
          closestArc = arc;
        }
      }
      if (closestArc) {
        const arcType = closestArc.arcaneType;
        if (arcType !== 'user_sigil' && !closestArc.processing && this._agent) {
          const persona = arcType === 'laura_channel' ? 'laura' : 'ireth';
          this._invokeAgent(persona, closestArc.id);
        }
        return;
      }
    }

    // 2) Block hit-test (non-arcane) — single-tap NEVER selects.  The only
    //    way to select is the magic lasso gesture.  A tap on an unselected
    //    block routes to that block's natural action (open text input,
    //    toggle handwritten/OCR display, etc.).  A tap on the empty area
    //    or on an existing selection just clears the selection (handled
    //    by the fall-through clear at the end of this function).
    const hit = this._state.hitTest(pt.x, pt.y);
    if (hit && hit.kind === 'text') {
      const block = page.blocks.get(hit.id);
      if (block && block.kind === 'text') {
        const tb = block as import('./notebook_state').WvTextBlock;
        // System-placed text blocks (fold descriptions) — no action
        if (this._state.isFoldPage()
            && (block.id.startsWith('fold_') || block.id.startsWith('user_arcane'))) {
          return;
        }
        if (tb.inkWords && tb.inkWords.length > 0) {
          // Show display-mode toggle popup
          const isOcr = tb.displayMode === 'ocr';
          const options: PopOption[] = [
            { label: '✏️ Handwritten', value: 'handwritten', group: 0 },
            { label: 'Aa OCR Text',   value: 'ocr',         group: 0 },
          ];
          const preSelected = new Map<number, number>();
          preSelected.set(0, isOcr ? 1 : 0);
          this._popDialog.open({
            anchorX: pt.x + this._layout.x,
            anchorY: pt.y + this._layout.y,
            anchor: 'top',
            options,
            maxStrokes: 0,
            preSelected,
            pageWidth: this._layout.width,
            pageHeight: this._layout.height,
            onSelect: (_group, option) => {
              tb.displayMode = option.value as 'handwritten' | 'ocr';
            },
          });
          return;
        }
        this._openTextInput(tb, pageId);
        return;
      }
    }
    // Other block kinds (figure, latex, code, ink): single-tap is a no-op
    // for selection.  Dragging the lasso wand around them selects.

    // 3) Layer spots — handled by `_analyzeStrokeForSelection` via the
    //    `_layerRegion` SelectionWidget. The tap branch in pen-up routes
    //    every committed dot through that path, so a layer tap selects the
    //    layer with the same icon-glow + fade pipeline as a draw-over.

    // 4) Page header title — single-tap to rename
    const currentSec = this._state.currentSection();
    const L = this._layout;

    if (this._pageHeaderTitleRect && !this._state.isFoldPage()) {
      const r = this._pageHeaderTitleRect;
      // Rects are canvas-absolute; tap pt is page-local → subtract layout offset
      const rx = r.x - L.x, ry = r.y - L.y;
      if (pt.x >= rx && pt.x <= rx + r.w && pt.y >= ry && pt.y <= ry + r.h) {
        this._openHeaderRename('page');
        return;
      }
    }

    // 5) Section header title — single-tap to rename
    if (this._sectionHeaderHitData) {
      const nr = this._sectionHeaderHitData.nameLineRect;
      const nrx = nr.x - L.x, nry = nr.y - L.y;
      if (pt.x >= nrx && pt.x <= nrx + nr.w && pt.y >= nry && pt.y <= nry + nr.h) {
        this._openHeaderRename('section-title');
        return;
      }
    }

    // 6) Fall-through: a tap that didn't land on any UI affordance and
    //    has an active selection clears the selection.  Selection-handle
    //    taps are absorbed earlier in pen-down (drag handlers), so reaching
    //    here means the tap missed every handle — deselect immediately.
    if (this._state.selection.blockIds.size > 0) {
      this._state.clearSelection();
    }
  }

  // ── Cross / lace selection analysis ─────────────────────────────────────
  //
  // After a stroke is committed as normal ink, check whether it crossed any
  // selection region: active pop dialog, page header color swatches, or
  // section header color spots.
  //
  // All strokes that enter a selection region are ALWAYS erased — the visual
  // feedback (wonky morph / cross-appear) is handled by SelectionWidget.

  private _analyzeStrokeForSelection(
    stroke: import('@shared/notebook_model').InkStroke,
    pts: StrokePoint[],
    pageId: string,
  ): boolean {
    if (pts.length === 0) return false;

    // No length threshold. Every stroke — even a single dot tap — gets
    // routed through the region check below and, if it landed inside,
    // fades out (whether or not it triggered a selection).

    // Helper: erase a region stroke and play the unified fade dissolve.
    // Used by every selection surface below — whether the stroke triggered
    // a selection or not, the visible ink fades out so the page stays clean.
    // Dissolve manager expects canvas-absolute coords; `pts` are page-local,
    // so apply the layout offset before queueing.
    const L = this._layout;
    const absPts = pts.map(p => ({ x: p.x + L.x, y: p.y + L.y }));
    const eraseAndFade = () => {
      if (this._settings.animations) {
        this._dissolves.add('fade', absPts);
      }
      this._eraseStrokeForSelection(stroke.id, pageId);
    };

    // ── 1. Pop dialog ──────────────────────────────────────────────────
    if (this._popDialog.active) {
      const b = this._popDialog.dialogBbox;
      let crossesDialog = false;
      for (const p of pts) {
        if (p.x >= b.x - 8 && p.x <= b.x + b.w + 8 &&
            p.y >= b.y - 8 && p.y <= b.y + b.h + 8) {
          crossesDialog = true;
          break;
        }
      }
      if (crossesDialog) {
        // analyzeExternalStroke runs the per-item animation when it hits;
        // we always fade the inked stroke afterward.
        this._popDialog.analyzeExternalStroke(pts);
        eraseAndFade();
        return true;
      }
    }

    // ── 3. Page header color swatches ──────────────────────────────────
    if (this._pageHeaderColorSwatches.length > 0) {
      this._syncPageHeaderRegion();
      if (this._pageHeaderRegion.strokeCrossesRegion(pts)) {
        const sel = this._pageHeaderRegion.analyzeStroke(pts);
        eraseAndFade();
        if (sel) {
          this._pageHeaderRegion.applySelection(sel.item, sel.gesture, pts);
          const curPage = this._state.currentPage();
          if (curPage) {
            this._dispatch({
              type: 'page.update',
              pageId: curPage.id,
              color: sel.item.value as string,
              origin: 'gesture',
              timestamp: Date.now(),
            });
          }
        }
        return true;
      }
    }

    // ── 4. Section header color spots ──────────────────────────────────
    if (this._sectionHeaderHitData && this._sectionHeaderHitData.colorSpots.length > 0) {
      this._syncSectionHeaderRegion();
      if (this._sectionHeaderRegion.strokeCrossesRegion(pts)) {
        const sel = this._sectionHeaderRegion.analyzeStroke(pts);
        eraseAndFade();
        if (sel) {
          this._sectionHeaderRegion.applySelection(sel.item, sel.gesture, pts);
          const currentSec = this._state.currentSection();
          if (currentSec) {
            this._dispatch({
              type: 'section.update',
              sectionId: currentSec.id,
              color: sel.item.value as string,
              origin: 'gesture',
              timestamp: Date.now(),
            });
          }
        }
        return true;
      }
    }

    // ── 5. (Removed) ────────────────────────────────────────────────
    // Block crossing selection was here but caused normal drawing strokes
    // near blocks to be consumed. Selection is handled by wand lasso instead.

    // ── 6. Settings option selection ───────────────────────────────────
    if (this._viewMode === 'settings' && this._settingsHitItems.length > 0) {
      if (this._settingsRegion.strokeCrossesRegion(pts)) {
        const sel = this._settingsRegion.analyzeStroke(pts);
        eraseAndFade();
        if (sel) {
          this._settingsRegion.applySelection(sel.item, sel.gesture, pts);
          this._onSettingsSelect(sel.item.group, sel.item);
        }
        return true;
      }
      // Any stroke in settings mode outside selection regions → fade silently
      eraseAndFade();
      return true;
    }

    // No region matched. The stroke is still on the ink layer; the caller
    // (tap path) may decide to fade it anyway, or non-tap committed strokes
    // simply persist as ink.
    return false;
  }

  /** Sync page header swatches into the SelectionWidget and preSelect current color. */
  private _syncPageHeaderRegion(currentColor?: string): void {
    const L = this._layout;
    const items: WidgetItem[] = this._pageHeaderColorSwatches.map(s => ({
      cx: s.x - L.x,
      cy: s.y - L.y,
      r: s.r,
      group: 0,
      value: s.color,
      kind: 'swatch',
    }));
    this._pageHeaderRegion.updateItems(items);
    if (currentColor && !this._pageHeaderRegion.animating) {
      const idx = items.findIndex(it => it.value === currentColor);
      if (idx >= 0) this._pageHeaderRegion.preSelect(0, idx);
    }
  }

  /** Sync section header spots into the SelectionWidget and preSelect current color. */
  private _syncSectionHeaderRegion(currentColor?: string): void {
    if (!this._sectionHeaderHitData) return;
    const L = this._layout;
    const items: WidgetItem[] = this._sectionHeaderHitData.colorSpots.map(s => ({
      cx: s.x - L.x,
      cy: s.y - L.y,
      r: s.r,
      group: 0,
      value: s.color,
      kind: 'swatch',
    }));
    this._sectionHeaderRegion.updateItems(items);
    if (currentColor && !this._sectionHeaderRegion.animating) {
      const idx = items.findIndex(it => it.value === currentColor);
      if (idx >= 0) this._sectionHeaderRegion.preSelect(0, idx);
    }
  }

  /** Single entry point for handing a confirmed tap to the cadence
   *  engine. Captures the pressure (used by the onTapAccepted callback
   *  to scale the visual ring) and feeds the tap. All five call sites
   *  in pointerup go through this — the previous duplication hid the
   *  invariant that pressure-then-feedTap must happen in lockstep. */
  private _feedTapToCadence(x: number, y: number, time: number, pressure: number): void {
    this._lastTapPressure = pressure;
    this._cadence.feedTap(x, y, time);
  }

  /** Cadence + wand state reconciliation that must run on every
   *  pointerup, including the drag-completion early returns. Unpauses
   *  the chain timer (no-op if feedTap already re-armed it), extends
   *  the wand idle window if cast is still up, and clears the
   *  pen-down invocation-strike flag (consumed in onResolved). */
  private _finishPointerUp(): void {
    this._cadence.resume();
    if (this._isWandInvoked) {
      this._armWandIdleTimeout();
    }
    this._pendingInvokeStrike = null;
    // Stylus binding engine: settle the stroke. press-and-tap fires
    // here if the stroke started with side-held and never crossed
    // the engine's own displacement threshold (no need to consult
    // tap_cadence — the engine has its own commit logic).
    if (this._stylusBindings.sideHeldDuringStroke) {
      const pos = this._spellAllPoints[this._spellAllPoints.length - 1];
      const x = pos?.x ?? this._lastPointerX;
      const y = pos?.y ?? this._lastPointerY;
      this._stylusBindings.onPenUp(x, y);
    }
  }

  /** Stylus state diagnostic HUD — shown only when Settings → Debug
   *  overlay is on. Helps figure out whether a particular pen actually
   *  delivers side-button events to the browser (some pens, notably
   *  the Slim Pen 2, route their tail-click through Windows OS
   *  shortcuts and the page never sees it). */
  private _drawStylusHud(ctx: CanvasRenderingContext2D): void {
    if (!this._settings.debugOverlay) return;
    const L = this._layout;
    const x = L.x + 8;
    let y = L.y + L.height - 96;
    const lines: string[] = [];
    // Resolve current context the same way the engine sees it.
    const context = this._activeStrokeId !== null
      ? 'drawing'
      : this._pointerPresent ? 'hovering' : 'idle';
    const tip = this._isEraser ? 'eraser' : 'pen';
    lines.push(`pointer  ${this._lastPointerType || '—'}  buttons=${this._lastPointerButtons}`);
    lines.push(`pos      ${Math.round(this._lastPointerX)}, ${Math.round(this._lastPointerY)}`);
    lines.push(`state    ${context} · ${tip}`);
    lines.push(`last     ${this._lastButtonEvent || '— (no button event yet)'}`);

    ctx.save();
    ctx.font = '10px "JetBrains Mono", "Consolas", monospace';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    // Soft parchment-tinted backdrop so the text stays readable on
    // any page colour without dominating the canvas.
    ctx.fillStyle = 'rgba(232, 212, 174, 0.85)';
    const w = 240, h = lines.length * 13 + 8;
    ctx.fillRect(x - 4, y - 4, w, h);
    ctx.fillStyle = '#3a2010';
    ctx.globalAlpha = 0.9;
    for (const line of lines) {
      ctx.fillText(line, x, y);
      y += 13;
    }
    ctx.restore();
  }

  /** Live S/L letter readout for the cadence chain. Sits a hair above
   *  the most recent tap so the user can see whether the engine read
   *  their last gap as short or long. Gated on the Debug overlay
   *  setting — kept out of the regular UI by default. */
  private _drawCadenceHud(ctx: CanvasRenderingContext2D): void {
    if (!this._settings.debugOverlay) return;
    const letters = this._cadence.letters;
    if (!letters) return;
    const anchor = this._cadence.lastTap;
    if (!anchor) return;
    const L = this._layout;
    ctx.save();
    ctx.font = '600 12px "JetBrains Mono", "Consolas", monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    // Per-letter tinting so the user can read the chain at a glance:
    // S in muted purple, L in muted blue (matches the per-tap rings).
    const totalWidth = ctx.measureText(letters).width;
    let cx = anchor.x + L.x - totalWidth / 2;
    const cy = anchor.y + L.y - 22;
    ctx.globalAlpha = 0.85;
    for (const ch of letters) {
      ctx.fillStyle = ch === 'S' ? TAP_RING_SHORT_COLOR : TAP_RING_LONG_COLOR;
      const w = ctx.measureText(ch).width;
      ctx.fillText(ch, cx + w / 2, cy);
      cx += w + 1;
    }
    ctx.restore();
  }

  /** Queue the user's chosen per-tap visual at the given page-local
   *  point. Animation matches what's selected in Settings → Tap effect
   *  (glow / ring / burst); the colour and intensity highlight the
   *  invocation-resolving "climax" taps:
   *
   *    climax 'cast'   → purple, larger, longer-lived (invoke-glow
   *                      variant for the glow kind).
   *    climax 'stylus' → blue, same emphasis as cast.
   *    no climax       → faint warm white at normal scale.
   *
   *  `pressure` (0..1) gently scales neutral taps. */
  private _pushTapEffect(
    pageX: number, pageY: number,
    opts: { pressure?: number; climax?: 'cast' | 'stylus' | null } = {},
  ): void {
    const kind = this._settings.tapEffect;
    if (kind === 'off') return;

    let color: string;
    let scale: number;
    let fxKind: 'ring' | 'burst' | 'glow' | 'invoke-glow' = kind;

    if (opts.climax === 'cast') {
      color = INVOKE_GLOW_CAST_COLOR;
      scale = 1.4;
      // Glow kind upgrades to the richer invoke-glow gradient + longer
      // lifetime for climax taps; ring/burst just use a bigger scale.
      if (kind === 'glow') fxKind = 'invoke-glow';
    } else if (opts.climax === 'stylus') {
      color = INVOKE_GLOW_STYLUS_COLOR;
      scale = 1.4;
      if (kind === 'glow') fxKind = 'invoke-glow';
    } else {
      color = TAP_NEUTRAL_COLOR;
      const p = opts.pressure;
      scale = p === undefined ? 1 : 0.8 + Math.max(0, Math.min(1, p)) * 0.5;
    }

    const L = this._layout;
    this._tapFx.push(fxKind, pageX + L.x, pageY + L.y, color, scale);
  }

  /** Erase a committed stroke used as a selection gesture. */
  private _eraseStrokeForSelection(strokeId: string, pageId: string): void {
    this._ink.removeStroke(strokeId);
    this._dispatch({
      type: 'ink.erase',
      pageId,
      target: strokeId,
      origin: 'gesture',
      timestamp: Date.now(),
    });
  }


  // ── Spell cast pipeline (consumption model) ─────────────────────────────────

  /**
   * Classify the wand stroke and hand control to spell_layer's consumption.
   * The consumption visual always plays.  The result (valid / invalid)
   * determines the resolution animation when consumption finishes.
   */
  private async _classifyAndConsume(strokeId: string): Promise<void> {
    // New spell engine owns cast resolution when its flag is on —
    // _tryFluxResolution has already either dispatched its actions or
    // logged a no-match. The legacy classifier below must not run.
    if (this._newSpellsEnabled()) {
      this._spell.finalize(true);
      if (this._isWandInvoked) this._cancelWand();
      return;
    }

    // Snapshot block layout for the worker-side hitTest
    const blocks = this._serializePageBlocks();
    const strokes = this._spell.allStrokes;
    const points = strokes.flat();
    const pageId = this._state.currentPageId;
    const selBlockIds = this._state.selection.blockIds.size > 0
      ? [...this._state.selection.blockIds] : null;

    // ── Flux-based classification (primary) ───────────────────────────────
    // Run a final flux tick to settle motes, then read signature
    const now = performance.now();
    tickFlux(this._strandGraph, this._fluxState, Math.min(now - this._lastFluxTick, 100));
    this._lastFluxTick = now;
    const sig = readFluxSignature(this._strandGraph, this._fluxState);

    const hitTest: FluxBlockHitTest = (x, y) => {
      const reversed = [...blocks].reverse();
      for (const b of reversed) {
        const pad = b.kind === 'figure' ? 8 : 0;
        if (x >= b.bbox.x - pad && x <= b.bbox.x + b.bbox.width + pad &&
            y >= b.bbox.y - pad && y <= b.bbox.y + b.bbox.height + pad) {
          return { id: b.id as any, kind: b.kind };
        }
      }
      return null;
    };

    // ── Layered classification: glyph → topology → fallback ──────────────
    //
    // Order matters.  Specific intents first; the generic "line erase"
    // catches only ambiguous remnants.
    //
    //   1. Gesture matcher — orientation-sensitive shape templates (wind,
    //      triangles, ∞, …).  AUTHORITATIVE: a match here wins outright,
    //      no flux comparison.
    //   2. Flux topology — select.region (closed-or-near-closed lasso) and
    //      erase.region (closed lasso, CW direction).  Run only if the
    //      gesture matcher missed.
    //   3. Generic erase.line — accepted only as a last-resort discard
    //      when nothing more specific matched.
    //
    // The previous flow ran (1) and (2) in parallel and picked the higher
    // confidence — but flux's hardcoded confidences (0.75 for erase.line)
    // drowned out the matcher's own dist-based confidence (often 0.1–0.3
    // for borderline glyph matches), so erase.line kept winning over
    // wind sigils.  Authoritative-matcher fixes that.
    let result = null as ReturnType<typeof classifyFromFlux>;
    let lastGesture: ReturnType<typeof matchGesture> = null;

    // Single-stroke only.  Multi-stroke wand casts go straight to the flux
    // classifier — joining them confused users who naturally draw multi-
    // stroke glyphs and didn't get a match.  Templates are intentionally
    // single-stroke shapes (one continuous pen-down for wind, ▲, ▼, ∞, …).
    if (strokes.length === 1 && strokes[0].length >= 4) {
      lastGesture = matchGesture(strokes[0], GESTURE_TEMPLATES);
      if (lastGesture) {
        result = {
          spellId: lastGesture.id,
          confidence: 0.95,
          parameters: { _gestureDistance: lastGesture.distance },
        };
      }
    }
    // Diagnostics: stash the attempt + the actual stroke so we can inspect
    // it from the console.  Useful for tuning thresholds OR for tracing
    // a template against a real hand-drawn glyph that didn't match.
    (this as unknown as Record<string, unknown>)._lastGestureAttempt = lastGesture
      ? {
          id: lastGesture.id,
          distance: lastGesture.distance,
          matched: true,
          strokeCount: strokes.length,
          pointCount: strokes[0]?.length ?? 0,
        }
      : {
          matched: false,
          reason: strokes.length !== 1
            ? `multi-stroke (${strokes.length})`
            : (strokes[0]?.length ?? 0) < 4
              ? 'too-few-points'
              : 'no-template-within-threshold',
          strokeCount: strokes.length,
          pointCount: strokes[0]?.length ?? 0,
        };
    // Also stash the actual point list so we can copy it as a candidate
    // template when a hand-drawn glyph fails to match the canonical one.
    (this as unknown as Record<string, unknown>)._lastSpellStroke = strokes.length === 1
      ? strokes[0].map(p => ({ x: Math.round(p.x), y: Math.round(p.y) }))
      : null;

    if (!result) {
      const fluxResult = classifyFromFlux(this._strandGraph, sig, hitTest);
      if (fluxResult) result = fluxResult;
    }

    // Legacy fallback (rare path — only when flux + matcher both miss).
    if (!result || result.confidence < 0.25) {
      const legacyResult = await this._spellBridge.classify(strokes, blocks, pageId);
      if (legacyResult && legacyResult.confidence >= 0.25) {
        result = { spellId: legacyResult.spellId, confidence: legacyResult.confidence, parameters: legacyResult.parameters };
      }
    }

    const valid = !!(result && result.confidence >= 0.25);

    if (valid) {
      // ── Enrich classification with stroke start context ─────────────────
      const startPt = points[0];
      if (startPt) {
        result!.parameters._startX = startPt.x;
        result!.parameters._startY = startPt.y;
      }
      if (selBlockIds) {
        result!.parameters._selectionActive = true;
        result!.parameters._selectedBlockIds = selBlockIds;
      }

      this._pendingSpell = {
        spellId:    result!.spellId,
        params:     result!.parameters,
        confidence: result!.confidence,
        family:     result!.spellId.split('.')[0],
      };
    } else {
      this._pendingSpell = null;
      // Misfire feedback deferred — consumption plays through first
    }

    // Tell spell_layer classification is done — consumption continues and resolves
    this._spell.finalize(valid);
  }

  /**
   * Feed a point to the live strand graph during wand drawing.
   * On first call per stroke, begins the live strand.
   * On subsequent calls, extends it and checks for self-crossings.
   */
  private _feedLiveWandPoint(pt: StrokePoint): void {
    if (this._fluxResolved) return;
    // Strand-graph / flux is legacy plumbing. The new spell engine does
    // single-stroke evaluation at release — it never reads the strand
    // graph. Skip the mid-stroke graph maintenance entirely when the
    // new engine is on so we don't burn cycles for nothing.
    if (this._newSpellsEnabled()) return;

    const g = this._strandGraph;
    if (!g._live) {
      // First point of this stroke — begin live construction
      beginLiveStroke(g, pt, this._strandHash);
      // Source = pen tip (ground, follows pen). Oscillating source left at
      // stroke start so the circuit persists after pen-up.
      const live = g._live!;
      const strand = g.strands.get(live.strandId)!;
      // Stroke start = oscillating source (stays put)
      // Pen tip = ground (co-source, follows pen during drawing)
      addWandSource(this._fluxState, strand.fromNode as number);
      updateLiveCoSource(this._fluxState, strand.toNode as number);
      return;
    }

    const result = feedLivePoint(g, pt, this._spellAllPoints, this._strandHash);

    // Update co-source to follow pen tip (the live strand's toNode moves)
    const live = g._live;
    if (live) {
      const strand = g.strands.get(live.strandId);
      if (strand) {
        updateLiveCoSource(this._fluxState, strand.toNode as number);
      }
    }

    // If a loop was detected mid-draw, activate it for field effects
    for (const loop of result.newLoops) {
      activateLoop(this._strandGraph, this._fluxState, loop);
      this._rectLoopCache.set(loop.id as number, isRectangularLoop(this._strandGraph, loop));
    }
  }

  /** Build a hitTest closure from current page blocks. */
  private _buildFluxHitTest(): FluxBlockHitTest {
    const blocks = this._serializePageBlocks();
    return (x, y) => {
      const reversed = [...blocks].reverse();
      for (const b of reversed) {
        const pad = b.kind === 'figure' ? 8 : 0;
        if (x >= b.bbox.x - pad && x <= b.bbox.x + b.bbox.width + pad &&
            y >= b.bbox.y - pad && y <= b.bbox.y + b.bbox.height + pad) {
          return { id: b.id as any, kind: b.kind };
        }
      }
      return null;
    };
  }

  /** Enrich a flux result with selection context and commit it. */
  private _commitFluxSpell(result: { spellId: string; confidence: number; parameters: Record<string, unknown> }): void {
    this._fluxResolved = true;
    resolveWand(this._fluxState);
    clearTimeout(this._wandCastTimeout);

    const selBlockIds = this._state.selection.blockIds.size > 0
      ? [...this._state.selection.blockIds] : null;
    if (selBlockIds) {
      result.parameters._selectionActive = true;
      result.parameters._selectedBlockIds = selBlockIds;
    }

    // Enrich with stroke start + a snapshot of the full spell stroke.
    // The snapshot is needed because `_resolveSpell` runs later (after the
    // consumption animation), and by then the user may have put the pen
    // down again, resetting `_spellAllPoints`.  Cast-time-only handlers
    // like wind.fly need access to the original glyph stroke.
    if (this._spellAllPoints.length > 0) {
      const startPt = this._spellAllPoints[0];
      result.parameters._startX = startPt.x;
      result.parameters._startY = startPt.y;
      result.parameters._spellStroke = this._spellAllPoints.map(p => ({ x: p.x, y: p.y }));
    }

    this._pendingSpell = {
      spellId:    result.spellId,
      params:     result.parameters,
      confidence: result.confidence,
      family:     result.spellId.split('.')[0],
    };

    this._spell.finalize(true);
    if (this._isWandInvoked) {
      this._cancelWand();
    }
  }

  /**
   * Live flux resolution — called from the draw loop every ~33ms while
   * there are active loops.  Fires spells mid-draw when flux accumulates.
   */
  private _tryLiveFluxResolution(): void {
    if (this._fluxResolved) return;
    // New spell engine owns cast resolution end-to-end when the flag is
    // on. The render-loop live-flux path used to fire legacy spells
    // mid-cast (notably `select.region` via classifyFromFlux), winning
    // the race before _tryFluxResolution could run. Bail out so the
    // pen-up _tryFluxResolution path is the only resolver.
    if (this._newSpellsEnabled()) return;
    if (this._strandGraph.loops.length === 0) return;

    const sig = readFluxSignature(this._strandGraph, this._fluxState);
    if (sig.fieldStrength < 0.01 && sig.loops.length === 0) return;

    const result = classifyFromFlux(this._strandGraph, sig, this._buildFluxHitTest());

    // Fire immediately for high-confidence matches
    if (result && result.confidence >= 0.7) {
      this._commitFluxSpell(result);
    }
  }

  /**
   * Attempt flux resolution on pen-up — called after adding a stroke.
   *
   * Layered classification, same precedence as _classifyAndConsume:
   *   1. Gesture matcher (wind, future glyphs) — authoritative.
   *   2. Flux topology (select.region, erase.region) — only if the
   *      matcher missed.
   * The legacy bridge fallback runs only at the timeout-driven
   * _classifyAndConsume; here we want to fire promptly when intent
   * is unambiguous.
   */
  private _tryFluxResolution(): void {
    if (this._fluxResolved) return;

    // ── 0. New spell engine (P2) ─────────────────────────────────────────
    // When `ox-new-spells` is on, the new engine owns cast resolution.
    // Single-stroke evaluation: detect the shape, run any matching
    // recipe (which dispatches its actions immediately), then ALWAYS
    // tear cast mode down — successful or not, the stroke is consumed
    // and we exit cast. Legacy gesture/flux/classifier is fully bypassed.
    if (this._newSpellsEnabled()) {
      const strokes = this._spell.allStrokes;
      const lastStroke = strokes[strokes.length - 1];
      let matched = false;
      if (lastStroke && lastStroke.length >= 2) {
        // Detection-aware: any matched spell → "success" animation,
        // even when the recipe emitted zero actions (e.g. lasso over
        // empty space, line that didn't cross anything). Still consumes
        // the stroke cleanly.
        matched = this._tryNewSpellEngineMatched(lastStroke);
      }
      this._fluxResolved = true;
      resolveWand(this._fluxState);
      clearTimeout(this._wandCastTimeout);
      this._spell.finalize(matched);
      if (this._isWandInvoked) this._cancelWand();
      return;
    }

    // ── 1. Glyph match first ────────────────────────────────────────────
    const allStrokes = this._spell.allStrokes;
    if (allStrokes.length === 1 && allStrokes[0].length >= 4) {
      const m = matchGesture(allStrokes[0], GESTURE_TEMPLATES);
      // Stash the spell-stroke snapshot so __oxApp._lastSpellStroke reflects
      // even matches that fired through this eager path (the timeout-driven
      // _classifyAndConsume path also stashes, so the two stay consistent).
      (this as unknown as Record<string, unknown>)._lastSpellStroke =
        allStrokes[0].map(p => ({ x: Math.round(p.x), y: Math.round(p.y) }));
      (this as unknown as Record<string, unknown>)._lastGestureAttempt = m
        ? { id: m.id, distance: m.distance, matched: true, via: 'tryFluxResolution', strokeCount: 1, pointCount: allStrokes[0].length }
        : { matched: false, reason: 'no-template-within-threshold', via: 'tryFluxResolution', strokeCount: 1, pointCount: allStrokes[0].length };
      if (m) {
        this._commitFluxSpell({
          spellId: m.id,
          confidence: 0.95,
          parameters: { _gestureDistance: m.distance },
        });
        return;
      }
    }

    // ── 2. Flux topology ────────────────────────────────────────────────
    // Quick flux tick to propagate motes from any new loop
    const now = performance.now();
    const dt = Math.min(now - this._lastFluxTick, 100);
    if (dt > 0) {
      tickFlux(this._strandGraph, this._fluxState, dt);
      this._lastFluxTick = now;
    }

    const sig = readFluxSignature(this._strandGraph, this._fluxState);
    const result = classifyFromFlux(this._strandGraph, sig, this._buildFluxHitTest());

    // Any spell with decent confidence fires on pen-up
    if (result && result.confidence >= 0.5) {
      this._commitFluxSpell(result);
    }
  }

  /** Snapshot current page blocks for off-thread hitTest. */
  private _serializePageBlocks(): SerializedBlock[] {
    const page = this._state.currentPage();
    if (!page) return [];
    const out: SerializedBlock[] = [];
    for (const block of page.blocks.values()) {
      out.push({ id: block.id, kind: block.kind, bbox: { ...block.bbox } });
    }
    return out;
  }

  /**
   * Called by spell_layer.onConsumed when the consumption wavefront
   * reaches the end of the stroke.
   */
  private _onConsumptionComplete(valid: boolean): void {
    const spell = this._pendingSpell;
    this._pendingSpell = null;

    if (!valid || !spell) {
      // Misfire — consumption finished, now show feedback
      if (!valid) {
        this._tapAudio.playMisfire();
        this._dispatch({ type: 'spell.fail', strokeId: makeStrokeId(), reason: 'misfired', origin: 'gesture', timestamp: Date.now() });
      }
      return;
    }

    // ── Diverted dispatch: codex practice / tutorial sandbox ──────────────
    if (this._activeContext.dispatchMode === 'diverted') {
      this._activeContext.onDivertedCast?.(
        spell.spellId, spell.params, spell.family, spell.confidence,
      );
      return;
    }

    this._tapAudio.playCast(spell.family);

    // Show spell name toast near the cast location
    const template = this._spellRegistry.get(spell.spellId);
    const allPts = this._spell.allPoints.length > 0 ? this._spell.allPoints : this._spellAllPoints;
    const toastPt = allPts[Math.floor(allPts.length / 2)];
    if (template && toastPt) {
      this._spellToast = {
        name: template.name,
        family: spell.family,
        x: toastPt.x,
        y: toastPt.y - 12,
        birthMs: performance.now(),
      };
    }

    const pageId = this._state.currentPageId;
    const now = Date.now();

    this._dispatch({
      type: 'spell.cast',
      spellId: spell.spellId,
      family: spell.family as 'act' | 'bind' | 'transmute' | 'invoke' | 'unknown',
      parameters: spell.params,
      strokeId: makeStrokeId(),
      chargeDurationMs: 300,
      origin: 'gesture',
      timestamp: now,
    });

    this._resolveSpell(spell.spellId, spell.params, spell.family, pageId, now);
  }

  private _resolveSpell(
    spellId: string,
    params: Record<string, unknown>,
    family: string,
    pageId: string,
    now: number,
  ): void {
    // Active spell vocabulary — keep this list aligned with the templates
    // active in flux_classifier.  Anything outside this set is silently
    // ignored, even if the legacy classifier produced a low-confidence
    // fallback for it.  Re-enable individual spells by adding them here
    // and (if disabled) re-enabling their flux template.
    const ACTIVE_SPELLS = new Set([
      'select.region',
      'erase.region',
      'erase.line',
      'wind.fly',
    ]);
    if (!ACTIVE_SPELLS.has(spellId)) {
      return;
    }
    switch (spellId) {
      case 'select.point': {
        const blockId = params.target as string | null;
        if (blockId) {
          this._state.selectBlock(blockId, family);
          // Open text input if a text block is tapped
          const page = this._state.currentPage();
          const block = page?.blocks.get(blockId);
          if (block?.kind === 'text') {
            this._openTextInput(block as import('./notebook_state').WvTextBlock, pageId);
          }
        } else {
          this._state.clearSelection();
          if (this._textInput.active) this._textInput.close();
        }
        break;
      }
      case 'select.region': {
        const region = params.region as import('@shared/notebook_model').RegionBounds | null;
        if (region) {
          this._state.selectRegion(region, family);
        }
        break;
      }
      case 'erase.region': {
        const region = params.region as import('@shared/notebook_model').RegionBounds | null;
        if (region) {
          const reverse: GrimoireAction[] = [];
          const forward: GrimoireAction[] = [];
          // Erase all strokes whose bounding box overlaps the lasso region
          const hits = this._ink.strokesInRegion(region.bbox);
          for (const id of hits) {
            const saved = this._ink.getStrokes().get(id);
            if (saved) reverse.push({ type: 'ink.commit', strokeId: saved.id, pageId, points: saved.points, style: saved.style, origin: 'gesture', timestamp: now });
            const ea: GrimoireAction = { type: 'ink.erase', pageId, target: id, origin: 'gesture', timestamp: now };
            this._dispatch(ea);
            forward.push(ea);
          }
          // Also delete blocks whose center is inside the lasso region
          const page = this._state.currentPage();
          if (page) {
            const r = region.bbox;
            for (const [blockId, block] of page.blocks) {
              const b = block.bbox;
              const cx = b.x + b.width / 2;
              const cy = b.y + b.height / 2;
              const centerInside = cx >= r.x && cx <= r.x + r.width &&
                                   cy >= r.y && cy <= r.y + r.height;
              if (centerInside) {
                // NPC sigils: dismiss with farewell instead of hard-delete
                if (block.kind === 'arcane') {
                  const arc = block as import('./notebook_state').WvArcaneBlock;
                  if (arc.arcaneType === 'laura_channel' || arc.arcaneType === 'ireth_channel') {
                    this._dismissSigil(arc, pageId);
                    continue;
                  }
                }
                reverse.push({ type: 'block.create', blockId, pageId, kind: block.kind as any, bbox: block.bbox, origin: 'gesture', timestamp: now });
                const da: GrimoireAction = { type: 'block.delete', blockId, pageId, origin: 'gesture', timestamp: now };
                this._dispatch(da);
                forward.push(da);
              }
            }
          }
          if (forward.length > 0) this._pushUndo({ reverse, forward });
        }
        break;
      }
      case 'erase.line': {
        // Straight line-cast: erase everything the line crosses
        const ls = params.lineStart as { x: number; y: number } | undefined;
        const le = params.lineEnd as { x: number; y: number } | undefined;
        const lb = params.lineBbox as { x: number; y: number; width: number; height: number } | undefined;
        if (ls && le && lb) {
          const reverse: GrimoireAction[] = [];
          const forward: GrimoireAction[] = [];
          // Erase strokes that overlap the line's bounding box
          const hits = this._ink.strokesInRegion(lb);
          for (const id of hits) {
            const saved = this._ink.getStrokes().get(id);
            if (saved) reverse.push({ type: 'ink.commit', strokeId: saved.id, pageId, points: saved.points, style: saved.style, origin: 'gesture', timestamp: now });
            const ea: GrimoireAction = { type: 'ink.erase', pageId, target: id, origin: 'gesture', timestamp: now };
            this._dispatch(ea);
            forward.push(ea);
          }
          // Delete blocks whose bbox the line crosses
          const page = this._state.currentPage();
          if (page) {
            for (const [blockId, block] of page.blocks) {
              const b = block.bbox;
              if (lineIntersectsRect(ls.x, ls.y, le.x, le.y, b.x, b.y, b.width, b.height)) {
                // NPC sigils: dismiss with farewell instead of hard-delete
                if (block.kind === 'arcane') {
                  const arc = block as import('./notebook_state').WvArcaneBlock;
                  if (arc.arcaneType === 'laura_channel' || arc.arcaneType === 'ireth_channel') {
                    this._dismissSigil(arc, pageId);
                    continue;
                  }
                }
                reverse.push({ type: 'block.create', blockId, pageId, kind: block.kind as any, bbox: block.bbox, origin: 'gesture', timestamp: now });
                const da: GrimoireAction = { type: 'block.delete', blockId, pageId, origin: 'gesture', timestamp: now };
                this._dispatch(da);
                forward.push(da);
              }
            }
          }
          if (forward.length > 0) this._pushUndo({ reverse, forward });
        }
        break;
      }
      case 'wind.fly': {
        // Find isolated tiny strokes inside the wind glyph's swept region,
        // animate them flying off in the wind direction, then erase.
        this._castWindFly(pageId, now, params);
        break;
      }
      case 'cell.run': {
        const blockId = params.blockId as string | undefined;
        if (blockId) {
          this._dispatch({ type: 'cell.run', blockId, pageId, origin: 'gesture', timestamp: now });
        }
        break;
      }
      case 'pen.style': {
        // Spell-on-arrow: if the transmute gesture overlaps an existing arrow,
        // cycle its relation type instead of changing pen style
        const arrowHit = this._state.hitTestArrow(this._spellAllPoints);
        if (arrowHit) {
          const types = ['derives', 'implies', 'contradicts', 'references', 'annotates', 'bound', 'contains', 'precedes'];
          const curIdx = types.indexOf(arrowHit.type);
          const nextType = types[(curIdx + 1) % types.length];

          // Determine direction: stroke start→end vs arrow from→to
          // If the stroke runs opposite to the arrow, reverse direction
          const sStart = this._spellAllPoints[0];
          const sEnd = this._spellAllPoints[this._spellAllPoints.length - 1];
          const page = this._state.currentPage();
          const fromBlock = page?.blocks.get(arrowHit.fromId);
          const toBlock = page?.blocks.get(arrowHit.toId);

          let fromId = arrowHit.fromId;
          let toId = arrowHit.toId;
          if (fromBlock && toBlock && sStart && sEnd) {
            const fromCx = fromBlock.bbox.x + fromBlock.bbox.width / 2;
            const fromCy = fromBlock.bbox.y + fromBlock.bbox.height / 2;
            const toCx = toBlock.bbox.x + toBlock.bbox.width / 2;
            const toCy = toBlock.bbox.y + toBlock.bbox.height / 2;
            // Dot product of stroke direction vs arrow direction
            const arrowDx = toCx - fromCx, arrowDy = toCy - fromCy;
            const strokeDx = sEnd.x - sStart.x, strokeDy = sEnd.y - sStart.y;
            if (arrowDx * strokeDx + arrowDy * strokeDy < 0) {
              // Reverse direction
              fromId = arrowHit.toId;
              toId = arrowHit.fromId;
            }
          }

          // Dispatch updated link (replaces the old arrow via same ID)
          this._dispatch({
            type: 'block.link',
            arrowId: arrowHit.id,
            pageId,
            fromId,
            toId,
            relationType: nextType,
            confirmed: true, // user-cast spell confirms it
            origin: 'gesture',
            timestamp: now,
          });
        } else {
          const style = params.style as Record<string, unknown> | undefined;
          if (style) { this._dispatch({ type: 'pen.style', style, origin: 'gesture', timestamp: now }); }
        }
        break;
      }
      // ── Tier 2 compound spells — resolved via registry ──────────────────
      case 'derive.step':
      case 'annotate.link':
      case 'block.duplicate':
      // arcane.open — RETIRED in P3 (arcane / computation system gone).
      // NPC-sigil / code-block placement still dispatches `arcane.open`
      // directly elsewhere; the spell-cast gesture path no-ops here.
      case 'arcane.open':
        break;

      // ── Layer up / down (retired) ───────────────────────────────────────
      // The page-layer system was retired in favour of per-content
      // modifiers (lock / group / astral) — these spells now no-op.
      // Kept as case stubs so spell registrations don't error.
      case 'layer.up':
      case 'layer.down':
        break;

      // ── Copy block ──────────────────────────────────────────────────────
      case 'copy.block': {
        const blockId = params.blockId as string | null;
        if (blockId) {
          const page = this._state.currentPage();
          const block = page?.blocks.get(blockId);
          if (block) {
            // Snapshot the block + any strokes overlapping it
            const blkCopy = { kind: block.kind, bbox: { ...block.bbox }, content: (block as any).content };
            const overlapping: Array<{ id: string; points: unknown[]; style: unknown }> = [];
            const strokes = this._ink.getStrokes();
            for (const [sid, s] of strokes) {
              // Check if stroke centroid falls inside block bbox
              if (s.points.length > 0) {
                const sp = s.points[0] as { x: number; y: number };
                const b = block.bbox;
                if (sp.x >= b.x && sp.x <= b.x + b.width && sp.y >= b.y && sp.y <= b.y + b.height) {
                  overlapping.push({ id: sid, points: [...s.points], style: s.style });
                }
              }
            }
            this._spellClipboard = { blocks: [blkCopy], strokes: overlapping };
            this._copyFlash = { bbox: { ...block.bbox }, birthMs: performance.now() };
          }
        }
        break;
      }

      // ── Paste here ──────────────────────────────────────────────────────
      case 'paste.here': {
        if (this._spellClipboard && this._spellClipboard.blocks.length > 0) {
          const tx = params.targetX as number;
          const ty = params.targetY as number;
          const src = this._spellClipboard.blocks[0];
          const dx = tx - (src.bbox.x + src.bbox.width / 2);
          const dy = ty - (src.bbox.y + src.bbox.height / 2);
          const newBlockId = `${pageId}.${src.kind}.${now.toString(36)}`;
          const newBbox = { x: src.bbox.x + dx, y: src.bbox.y + dy, width: src.bbox.width, height: src.bbox.height };
          this._dispatch({
            type: 'block.create',
            blockId: newBlockId,
            pageId,
            kind: src.kind as any,
            bbox: newBbox,
            origin: 'gesture',
            timestamp: now,
          } as import('@shared/grimoire_action').GrimoireAction);
          // Duplicate strokes offset to new position
          for (const s of this._spellClipboard.strokes) {
            const newPts = (s.points as Array<{ x: number; y: number }>).map(p => ({ ...p, x: p.x + dx, y: p.y + dy }));
            const nsid = `${pageId}.s.${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;
            this._dispatch({
              type: 'ink.commit',
              strokeId: nsid,
              pageId,
              points: newPts,
              style: s.style,
              origin: 'gesture',
              timestamp: now,
            } as import('@shared/grimoire_action').GrimoireAction);
          }
        }
        break;
      }

      // ── Lasso copy (region) ─────────────────────────────────────────────
      case 'copy.region': {
        const region = params.region as import('@shared/notebook_model').RegionBounds | null;
        if (region) {
          const page = this._state.currentPage();
          if (page) {
            const r = region.bbox;
            const copiedBlocks: Array<{ kind: string; bbox: { x: number; y: number; width: number; height: number }; content?: unknown }> = [];
            for (const [, block] of page.blocks) {
              if (!this._state.isBlockVisible(block)) continue;
              const b = block.bbox;
              // Block center must be inside the lasso region
              const cx = b.x + b.width / 2;
              const cy = b.y + b.height / 2;
              const centerInside = cx >= r.x && cx <= r.x + r.width &&
                                   cy >= r.y && cy <= r.y + r.height;
              if (centerInside) {
                copiedBlocks.push({ kind: block.kind, bbox: { ...b }, content: (block as any).content });
              }
            }
            const copiedStrokes: Array<{ id: string; points: unknown[]; style: unknown }> = [];
            const strokes = this._ink.getStrokes();
            for (const [sid, s] of strokes) {
              if (s.points.length > 0) {
                const sp = s.points[0] as { x: number; y: number };
                if (sp.x >= r.x && sp.x <= r.x + r.width && sp.y >= r.y && sp.y <= r.y + r.height) {
                  copiedStrokes.push({ id: sid, points: [...s.points], style: s.style });
                }
              }
            }
            this._spellClipboard = { blocks: copiedBlocks, strokes: copiedStrokes };
            this._copyFlash = { bbox: { ...r }, birthMs: performance.now() };
          }
        }
        break;
      }

      // ── Lasso snapshot (capture to system clipboard as image) ────────────
      case 'snapshot.region': {
        const region = params.region as import('@shared/notebook_model').RegionBounds | null;
        if (region) {
          const r = region.bbox;
          // Capture the region from the canvas directly
          try {
            const dpr = window.devicePixelRatio || 1;
            const tmpCanvas = document.createElement('canvas');
            tmpCanvas.width = Math.ceil(r.width * dpr);
            tmpCanvas.height = Math.ceil(r.height * dpr);
            const tmpCtx = tmpCanvas.getContext('2d');
            if (tmpCtx && this._canvas) {
              tmpCtx.drawImage(
                this._canvas,
                r.x * dpr, r.y * dpr, r.width * dpr, r.height * dpr,
                0, 0, tmpCanvas.width, tmpCanvas.height,
              );
              tmpCanvas.toBlob(async (blob) => {
                if (blob) {
                  try {
                    await navigator.clipboard.write([
                      new ClipboardItem({ 'image/png': blob }),
                    ]);
                  } catch (_) { /* clipboard permission denied — silent fail */ }
                }
              }, 'image/png');
            }
          } catch (_) { /* canvas tainted or other error */ }
        }
        break;
      }

      // arcane.eval — RETIRED in P3.
      case 'arcane.eval':
        break;
    }
  }

  // ── Analyse Knock — region insert (3-tap invocation) ───────────────────

  /**
   * Analyse ink strokes near the tap location to detect a drawn box with a
   * rune inside.  Context-aware: only creates a block when a closed box-like
  // ── Wand mode lifecycle ──────────────────────────────────────────────────

  /** Enter wand mode. When called from the cadence resolver the cast
   *  centroid is passed in so we can scan nearby page content for
   *  shape suggestions and spawn hint rings on each — a tap on any
   *  ring accepts that hint, otherwise the rings expire with the cast.
   *
   *  Idle timeout is re-armed at every pen-up via _armWandIdleTimeout,
   *  so the cast persists while the user is actively interacting and
   *  falls away when they stop. */
  private _invokeWand(cx?: number, cy?: number): void {
    this._isWandInvoked = true;
    this._canvas.style.cursor = 'pointer';
    if (this._textCompose.active) { this._textCompose.end(); }
    // The astral layer (5) surfaces handwriting-recognition shadows
    // (predicted text under each scribble). It's hidden by default
    // and shows only while casting.
    this._setAstralVisible(true);
    this._armWandIdleTimeout();
    // Stash the invocation point so the astral overlay + shape-hint
    // scan can both scope reveals to a radius around it instead of
    // lighting up the whole page.
    if (cx !== undefined && cy !== undefined) {
      this._wandInvocationPoint = { x: cx, y: cy };
      this._scanCastNearbyContent(cx, cy);
    }
  }

  /** Scan nearby ink strokes around (cx, cy) and spawn shape-hint
   *  rings on each one whose recognition produces a usable suggestion.
   *  Async-ish: each stroke's recognition runs through ShapeBridge
   *  which returns a Promise; rings are spawned as results land.
   *
   *  Bounded: only strokes whose bbox center is within CAST_HINT_RADIUS
   *  px of (cx, cy) are considered, and tiny strokes (under the same
   *  thresholds the writing path uses) are skipped. */
  private _scanCastNearbyContent(cx: number, cy: number): void {
    const page = this._state.currentPage();
    if (!page) return;
    const pageId = this._state.currentPageId;
    // Scoped to a tight radius around the invocation point so the
    // page doesn't desaturate with hint rings page-wide. Was 220;
    // 130 keeps the reveal local — the user can re-cast somewhere
    // else to peek at that area.
    const CAST_HINT_RADIUS = 130;
    const MIN_ARCLEN = 28;
    const MIN_DIM    = 14;
    const MIN_CONF   = 0.50;
    const HINT_WINDOW_MS = 4000;   // hints live longer than the default 1.5 s

    const strokes = this._ink.getStrokes();
    for (const [strokeId, stroke] of strokes) {
      // Already has a pending hint? Skip.
      if (this._shapeHintPending.has(`__shape_hint__:${strokeId}`)) continue;

      const bbox = this._strokeBbox(stroke.points);
      const sx = bbox.x + bbox.w / 2;
      const sy = bbox.y + bbox.h / 2;
      if (Math.hypot(sx - cx, sy - cy) > CAST_HINT_RADIUS) continue;

      const arclen = this._strokeLength(stroke.points);
      if (arclen < MIN_ARCLEN) continue;
      if (Math.max(bbox.w, bbox.h) < MIN_DIM) continue;

      this._shapeBridge.analyseShape(stroke.points, MIN_CONF).then(result => {
        if (!result.simplifiable || !result.shape) return;
        if (this._shapeSuggestionLooksPointless(result.shape, stroke.points)) return;
        // Bail if cast already ended while recognition was pending.
        if (!this._isWandInvoked) return;
        this._spawnShapeHintRing(result.shape, strokeId, pageId, HINT_WINDOW_MS);
      });
    }
  }

  /** (Re-)arm the wand-idle auto-cancel. Called from _invokeWand and
   *  from pointerup whenever the cast is still standing — that way a
   *  dawdle without further pen activity dismisses the cast, but a
   *  steady stream of taps (e.g. building 'SSL') keeps it alive. */
  private _armWandIdleTimeout(): void {
    clearTimeout(this._wandTimeout);
    this._wandTimeout = window.setTimeout(() => this._cancelWand(), WAND_IDLE_MS);
  }

  /** Revert from wand mode back to normal tool mode. */
  private _cancelWand(): void {
    clearTimeout(this._wandTimeout);
    clearTimeout(this._wandCastTimeout);
    clearTimeout(this._fluxResolveTimeout);
    this._isWandInvoked = false;
    this._wandInvocationPoint = null;
    this._setAstralVisible(false);
    this._canvas.style.cursor = 'crosshair';
    // Drop the texture instrument's cast-mode bias so the next stroke
    // sounds like normal pen-on-paper. Without this, _cancelWand
    // paths that don't go through pen-up (idle timeout, chain end,
    // resolve handlers, page switch) leave the scratch in its
    // muffled cast tone.
    this._tapAudio.stopWandTension();
    // Cast-spawned hint rings are LEFT to expire on their own. Yanking
    // them off the moment cast cancels would flash a burst of ring
    // dismissals at every cast end (chain timeout, follow-up resolve,
    // idle, …) — the hints already carry their own ~4 s window, which
    // outlasts every typical end path.
    // Hard-reset flux + strand state so next cast starts fresh
    resetFlux(this._fluxState);
    clearGraph(this._strandGraph);
    this._strandHash.clear();
    this._rectLoopCache.clear();
    this._fluxResolved = false;
  }

  /**
   * Analyse a tap location for block insertion (the "page insert" gesture).
   *
   * container stroke is found (with the tap inside it).  Kicks off the arcane
   * glow → resolve/fail → blend animation; block creation is deferred to the
   * blend phase so the user sees the full sequence.
   */
  private async _analyseRegionForInsert(cx: number, cy: number): Promise<void> {
    const pageId = this._state.currentPageId;
    const now = performance.now();
    this._flashDebug(cx, cy, '#00e5ff', 'ANALYSE enter');

    // ── Check if tap lands inside an existing block first ────────────────
    // Only intercept for structured block kinds (text opens format popup,
    // code/arcane/latex block analysis).  Ink blocks are just stroke groups
    // and must NOT block the box-detection scan.
    const currentPage = this._state.currentPage();
    if (currentPage) {
      // ── Arcane proximity check first (NPC visual position, not bbox) ──
      const ARC_TAP_R = 36;
      for (const block of currentPage.blocks.values()) {
        if (block.kind !== 'arcane') continue;
        const arc = block as import('./notebook_state').WvArcaneBlock;
        const sx = arc.moveX ?? (arc.bbox.x + arc.bbox.width / 2);
        const sy = arc.moveY ?? (arc.bbox.y + arc.bbox.height / 2);
        if (Math.hypot(cx - sx, cy - sy) <= ARC_TAP_R) {
          const arcType = arc.arcaneType;
          // Triple-tap on an NPC sigil → dismiss it with farewell
          if (arcType === 'laura_channel' || arcType === 'ireth_channel') {
            this._flashDebug(cx, cy, '#ff6666', `DISMISS sigil ${arcType}`);
            this._dismissSigil(arc, pageId);
            return;
          }
          this._flashDebug(cx, cy, '#b680ff', `ACTIVATE sigil ${arcType}`);
          if (this._agent && !arc.processing && arcType !== 'user_sigil') {
            const persona = arcType === 'laura_channel' ? 'laura' : 'ireth';
            this._invokeAgent(persona, block.id);
          }
          return;
        }
      }

      // ── Non-arcane block hit test (bbox based) ──
      for (const block of currentPage.blocks.values()) {
        if (block.kind === 'ink' || block.kind === 'arcane') continue;
        const b = block.bbox;
        if (cx >= b.x && cx <= b.x + b.width && cy >= b.y && cy <= b.y + b.height) {
          if (block.kind === 'text') {
            this._flashDebug(cx, cy, '#ffbb00', 'HIT existing text block');
            this._openTextFormatPopup(block as import('./notebook_state').WvTextBlock, pageId, cx, cy);
            return;
          }
          this._flashDebug(cx, cy, '#ff4444', `HIT existing ${block.kind} block`, 'x');
          return; // inside another block kind — do nothing
        }
      }
    }

    // ── Scan for all strokes near the tap (ink + figure edges) ─────────
    const SCAN_RADIUS = 200;
    const scanBox = {
      x: cx - SCAN_RADIUS, y: cy - SCAN_RADIUS,
      width: SCAN_RADIUS * 2, height: SCAN_RADIUS * 2,
    };
    const hitIds = this._ink.strokesInRegion(scanBox);

    // Unified strokes: includes raw ink AND figure block edges
    const allStrokes = this._unifiedPageStrokes();
    const hitSet = new Set(hitIds);
    for (const [id, stroke] of allStrokes) {
      if (hitSet.has(id)) continue;
      let mnX2 = Infinity, mnY2 = Infinity, mxX2 = -Infinity, mxY2 = -Infinity;
      for (const p of stroke.points) {
        if (p.x < mnX2) mnX2 = p.x; if (p.y < mnY2) mnY2 = p.y;
        if (p.x > mxX2) mxX2 = p.x; if (p.y > mxY2) mxY2 = p.y;
      }
      if (cx >= mnX2 && cx <= mxX2 && cy >= mnY2 && cy <= mxY2) {
        hitSet.add(id);
      }
    }
    console.log(`[arcane-debug] stroke scan: region=${hitIds.length} containment=${hitSet.size - hitIds.length} total=${hitSet.size} allStrokes=${allStrokes.size}`);
    if (hitSet.size === 0) {
      this._flashDebug(cx, cy, '#ff4444', `NO STROKES (total=${allStrokes.size})`, 'x');
      return;
    }
    this._flashDebug(cx, cy, '#ffdd44', `${hitSet.size} strokes found`, 'rect', SCAN_RADIUS * 0.3, SCAN_RADIUS * 0.3);

    type StrokeInfo = {
      id: string;
      points: import('@shared/notebook_model').StrokePoint[];
      source: 'ink' | 'figure';
      bbox: { x: number; y: number; width: number; height: number };
    };
    const candidates: StrokeInfo[] = [];
    for (const id of hitSet) {
      const stroke = allStrokes.get(id);
      if (!stroke) continue;
      let smnX = Infinity, smnY = Infinity, smxX = -Infinity, smxY = -Infinity;
      for (const p of stroke.points) {
        if (p.x < smnX) smnX = p.x; if (p.y < smnY) smnY = p.y;
        if (p.x > smxX) smxX = p.x; if (p.y > smxY) smxY = p.y;
      }
      candidates.push({ id, points: stroke.points, source: stroke.source,
        bbox: { x: smnX, y: smnY, width: smxX - smnX, height: smxY - smnY } });
    }
    if (candidates.length === 0) {
      this._flashDebug(cx, cy, '#ff4444', 'NO CANDIDATES', 'x');
      return;
    }

    // ── Find box-like container ───────────────────────────────────────────
    let boxIdx = -1;
    let bestBoxArea = 0;
    for (let i = 0; i < candidates.length; i++) {
      const s = candidates[i];
      const pts = s.points;
      const b = s.bbox;
      const area = b.width * b.height;
      const first = pts[0], last = pts[pts.length - 1];
      const closeDist = (pts.length >= 2) ? Math.hypot(last.x - first.x, last.y - first.y) : Infinity;
      const perim = this._strokeLength(pts);
      const closeRatio = perim > 0 ? closeDist / perim : Infinity;
      const aspect = Math.min(b.width, b.height) > 0
        ? Math.max(b.width, b.height) / Math.min(b.width, b.height) : Infinity;
      const tapInside = cx >= b.x && cx <= b.x + b.width && cy >= b.y && cy <= b.y + b.height;

      // Log every candidate for diagnostics
      console.log(
        `[arcane-debug] candidate[${i}]: pts=${pts.length} bbox=${Math.round(b.width)}x${Math.round(b.height)}` +
        ` area=${Math.round(area)} perim=${Math.round(perim)} close=${Math.round(closeDist)}` +
        ` closeRatio=${closeRatio.toFixed(2)} aspect=${aspect.toFixed(1)} tapInside=${tapInside}`,
      );

      if (pts.length < 8) continue;
      if (!tapInside) continue;
      // Figure-sourced edges can't be box containers
      if (s.source === 'figure') continue;
      if (b.width < 30 || b.height < 22) continue;
      if (area < 800) continue;
      if (aspect > 6) continue;
      if (perim < 60) continue;
      if (closeDist > perim * 0.35) continue;

      if (area > bestBoxArea) { bestBoxArea = area; boxIdx = i; }
    }

    if (boxIdx < 0) {
      this._flashDebug(cx, cy, '#ff6600', `NO BOX (${candidates.length} candidates)`, 'x');
      return;
    }

    // ── Box found — classify rune strokes spatially inside the box ───────
    const boxStroke = candidates[boxIdx];
    const bx = boxStroke.bbox;
    this._flashDebug(bx.x, bx.y - 16, '#44ff44', `BOX ${Math.round(bx.width)}x${Math.round(bx.height)}`, 'rect', bx.width, bx.height);

    // Rune containment: a stroke counts as "inside the box" if its center
    // is within the box and the majority of its bbox overlaps the box.
    // We allow generous spillover (20 px) because hand-drawn T bars etc.
    // often overshoot the drawn box edge.
    const RUNE_MARGIN = 20;
    const runeStrokes = candidates.filter((s, i) => {
      if (i === boxIdx) return false;
      const r = s.bbox;
      // Center of the rune stroke must be inside the box
      const rcx = r.x + r.width / 2;
      const rcy = r.y + r.height / 2;
      const centerInside = rcx >= bx.x - RUNE_MARGIN && rcx <= bx.x + bx.width + RUNE_MARGIN &&
                           rcy >= bx.y - RUNE_MARGIN && rcy <= bx.y + bx.height + RUNE_MARGIN;
      if (!centerInside) {
        console.log(`[arcane-debug] rune reject[${i}]: center (${Math.round(rcx)},${Math.round(rcy)}) outside box`);
      }
      return centerInside;
    });

    let blockKind: import('@shared/notebook_model').BlockKind | null = null;
    if (runeStrokes.length > 0) {
      this._lastDetectedPersona = null;
      blockKind = this._classifyRune(runeStrokes);
      this._flashDebug(bx.x + bx.width / 2, bx.y + bx.height / 2 - 14, '#ff88ff',
        `RUNE:${runeStrokes.length} → ${blockKind} persona=${this._lastDetectedPersona}`);
    } else {
      // No runes — try shape recognition on ALL strokes in the box and offer popup
      const allBoxStrokeIds: string[] = [boxStroke.id, ...candidates.filter((_, i) => i !== boxIdx).map(s => s.id)];
      const allBoxPoints = allBoxStrokeIds.flatMap(id => {
        const st = allStrokes.get(id);
        return st ? st.points : [];
      });
      const shapeCandidates = await this._shapeBridge.recogniseCandidates(boxStroke.points, 5);
      if (shapeCandidates.length > 0) {
        this._flashDebug(bx.x + bx.width / 2, bx.y + bx.height / 2 - 14, '#44ddff',
          `SHAPES: ${shapeCandidates.map(s => s.kind).join(', ')}`);
        this._openShapeFitPopup(
          bx.x + bx.width / 2,
          bx.y + bx.height + 10,
          shapeCandidates,
          allBoxStrokeIds,
          pageId,
        );
        return;
      }
      this._flashDebug(bx.x + bx.width / 2, bx.y + bx.height / 2 - 14, '#ff8800',
        `NO RUNES, NO SHAPES`);
    }

    // Aggregate bounding box (box stroke only for a tight fit)
    const bbox = { x: bx.x, y: bx.y, width: bx.width, height: bx.height };

    // ── Kick off the arcane animation state machine ─────────────────────
    this._flashDebug(cx, cy, '#cc44ff', `ARCANE START kind=${blockKind ?? 'null'}`);
    this._arcaneAnim = {
      phase: 'box-pulse', startMs: now,
      boxStrokeId: boxStroke.id,
      runeStrokeIds: runeStrokes.map(s => s.id),
      bbox,
      blockKind,
      blockId: null,
      pageId,
      detectedPersona: this._lastDetectedPersona ?? undefined,
    };
  }

  /** Push a debug flash overlay visible for ~3 s.  Coordinates are page-local. */
  private _flashDebug(
    x: number, y: number,
    color: string, label: string,
    shape: 'circle' | 'rect' | 'x' = 'circle',
    w = 0, h = 0,
  ): void {
    if (this._settings.debugOverlay) {
      this._debugFlashes.push({ x, y, color, label, birthMs: performance.now(), shape, w, h });
    }
    console.log(`[arcane-debug] ${label}  (${Math.round(x)}, ${Math.round(y)})`);
  }

  /** Sum of segment distances for a stroke. */
  private _strokeLength(pts: import('@shared/notebook_model').StrokePoint[]): number {
    let len = 0;
    for (let i = 1; i < pts.length; i++) {
      len += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
    }
    return len;
  }

  /**
   * Wind-fly cast: identify isolated tiny strokes inside the wind glyph's
   * swept region (its bbox + halo) and animate them flying off in the
   * wind direction, then erase them.  "Isolated" means the stroke's bbox
   * (slightly inflated) doesn't overlap any other stroke or block — the
   * sweep only catches genuine stray pen-down marks, never content that
   * has neighbours.
   */
  private _castWindFly(pageId: string, now: number, params?: Record<string, unknown>): void {
    // Prefer the stroke snapshot stashed in spell params at cast-commit
    // time — `_spellAllPoints` may have been reset by a subsequent pen
    // contact between commit and resolution.
    const stashed = params?._spellStroke as Array<{ x: number; y: number }> | undefined;
    const windPts = (stashed && stashed.length >= 4)
      ? stashed
      : this._spellAllPoints;
    if (windPts.length < 4) return;
    const wb = this._strokeBbox(windPts);

    // ── Wind direction: principal bbox axis, signed by head→tail ─────────
    // Using the head→tail VECTOR directly is unreliable — the glyph's
    // hook adds vertical excursion and a horizontal-ish wind gets read
    // as steeply diagonal.  Aligning with the bbox's longer side (and
    // signing by which way along that axis the user actually drew)
    // matches the visual orientation of the sigil instead.
    const head = windPts[0];
    const tail = windPts[windPts.length - 1];
    let dx: number, dy: number;
    if (wb.w >= wb.h) {
      // Bbox wider than tall → horizontal wind
      dx = tail.x >= head.x ? 1 : -1;
      dy = 0;
    } else {
      // Bbox taller than wide → vertical wind
      dx = 0;
      dy = tail.y >= head.y ? 1 : -1;
    }

    // ── Conic AOE ────────────────────────────────────────────────────────
    // The wind sweeps a CONE projecting forward from the glyph along the
    // principal axis.  Apex sits just behind the head along −dir, half-
    // angle 45°, length scales with the glyph's bbox.  A point P is in
    // the cone iff:
    //   (P − apex) · dir ≥ 0        (in front of apex)
    //   angle(P − apex, dir) ≤ ½α   (within the wedge)
    //   |P − apex| ≤ length         (within range)
    const bboxDim = Math.max(wb.w, wb.h);
    // Intensity = bbox-based scalar.  ~1 for a "standard" 200 px wind;
    // smaller doodles cast smaller / weaker, big sweeping ones cast bigger.
    const intensity = Math.max(0.5, Math.min(2.5, bboxDim / 200));
    const coneLength = bboxDim * 4.0;
    const coneHalfAngle = Math.PI / 4;            // 45°
    const coneCos = Math.cos(coneHalfAngle);
    // Apex sits one bbox-half behind the head along −dir, so the entire
    // glyph plus a bit of slack lives inside the cone.  Generous setback
    // keeps strokes that are at the SIDES of the glyph (perpendicular to
    // the axis) inside the wedge.
    const apexX = head.x - dx * (bboxDim * 0.5);
    const apexY = head.y - dy * (bboxDim * 0.5);

    const inCone = (px: number, py: number): boolean => {
      const vx = px - apexX, vy = py - apexY;
      const d = Math.hypot(vx, vy);
      if (d > coneLength) return false;
      if (d < 1e-6) return true;
      const dot = (vx * dx + vy * dy) / d;
      return dot >= coneCos;
    };

    // ── Stroke selection thresholds, scaled by intensity ─────────────────
    const TINY_ARCLEN_MAX = 60 * intensity;
    const TINY_DIM_MAX    = 40 * intensity;
    // "Borderline" = up to 1.6× the tiny limit — these resist (vibrate)
    // rather than fly.
    const RESIST_ARCLEN_MAX = TINY_ARCLEN_MAX * 1.6;
    const RESIST_DIM_MAX    = TINY_DIM_MAX    * 1.6;
    const ISOLATION_HALO    = 16;

    const allStrokes = this._ink.getStrokes();
    const page = this._state.currentPage();
    const blocks = page ? [...page.blocks.values()] : [];

    const tinyInCone:   string[] = [];   // ≤ TINY threshold AND in cone
    const borderInCone: string[] = [];   // tiny < x ≤ resist AND in cone
    const strokeBboxes = new Map<string, { x: number; y: number; w: number; h: number }>();
    // Track per-stroke metrics so we can re-test "is this stroke itself a
    // candidate?" cheaply in the isolation pass.
    const strokeMetrics = new Map<string, { arclen: number; maxDim: number }>();

    for (const [sid, stroke] of allStrokes) {
      if (stroke.points.length === 0) continue;
      const sb = this._strokeBbox(stroke.points as Array<{ x: number; y: number }>);
      strokeBboxes.set(sid, sb);
      const arclen = this._strokeLength(stroke.points);
      const maxDim = Math.max(sb.w, sb.h);
      strokeMetrics.set(sid, { arclen, maxDim });

      const cx = sb.x + sb.w / 2, cy = sb.y + sb.h / 2;
      if (!inCone(cx, cy)) continue;

      if (arclen <= TINY_ARCLEN_MAX && maxDim <= TINY_DIM_MAX) {
        tinyInCone.push(sid);
      } else if (arclen <= RESIST_ARCLEN_MAX && maxDim <= RESIST_DIM_MAX) {
        borderInCone.push(sid);
      }
    }

    // ── Isolation filter — anchored by content, not by other strays ─────
    // The point of "isolation" is to not blow away marks that are part of
    // something — the dot of an "i", a tick on a chart, a smudge inside a
    // text block.  But a CLUSTER of stray dots near each other is still all
    // stray; they should fly together.
    //
    // So: only NON-CANDIDATE strokes (and any block) count as anchors.
    // Tiny + borderline candidates are themselves potential leaves; they
    // don't anchor each other.
    const stray: string[] = [];
    for (const sid of tinyInCone) {
      const sb = strokeBboxes.get(sid)!;
      const ix0 = sb.x - ISOLATION_HALO, iy0 = sb.y - ISOLATION_HALO;
      const ix1 = sb.x + sb.w + ISOLATION_HALO, iy1 = sb.y + sb.h + ISOLATION_HALO;
      let lonely = true;
      for (const [otherSid, otherBbox] of strokeBboxes) {
        if (otherSid === sid) continue;
        const om = strokeMetrics.get(otherSid)!;
        // Skip other tiny / borderline strokes — they're potential leaves
        // themselves, not "content" anchoring this one.
        if (om.arclen <= RESIST_ARCLEN_MAX && om.maxDim <= RESIST_DIM_MAX) continue;
        if (this._bboxesOverlap(ix0, iy0, ix1, iy1, otherBbox)) { lonely = false; break; }
      }
      if (!lonely) continue;
      for (const blk of blocks) {
        const b = blk.bbox;
        if (this._bboxesOverlap(
          ix0, iy0, ix1, iy1,
          { x: b.x, y: b.y, w: b.width, h: b.height },
        )) { lonely = false; break; }
      }
      if (lonely) stray.push(sid);
    }

    // Diagnostic
    (this as unknown as Record<string, unknown>)._lastWindFly = {
      sigilBbox: { w: wb.w, h: wb.h, principalAxis: wb.w >= wb.h ? 'horizontal' : 'vertical' },
      head: { x: head.x, y: head.y },
      tail: { x: tail.x, y: tail.y },
      cone: { apexX, apexY, dirX: dx, dirY: dy, length: coneLength, halfAngleRad: coneHalfAngle },
      intensity,
      bboxDim,
      thresholds: { tinyArclen: TINY_ARCLEN_MAX, tinyDim: TINY_DIM_MAX, resistArclen: RESIST_ARCLEN_MAX, resistDim: RESIST_DIM_MAX },
      tinyInConeCount: tinyInCone.length,
      borderInConeCount: borderInCone.length,
      isolatedStrayCount: stray.length,
      totalStrokesOnPage: allStrokes.size,
    };

    // ── Layout-relative absolute coords for the dissolve layer ───────────
    const L = this._layout;
    const toAbs = (p: { x: number; y: number }) => ({ x: p.x + L.x, y: p.y + L.y });

    // ── 1. AOE flash so the user can see the conic region ────────────────
    this._dissolves.add('wind-aoe', [], {
      cone: {
        apexX: apexX + L.x,
        apexY: apexY + L.y,
        dirX: dx,
        dirY: dy,
        length: coneLength,
        halfAngle: coneHalfAngle,
      },
    });

    // ── 2. Wind-sigil afterglow on the cast stroke ───────────────────────
    {
      const sigilPts = windPts.map(toAbs);
      this._dissolves.add('wind-sigil', sigilPts, { windDx: dx, windDy: dy });
    }

    // ── 3. Borderline strokes vibrate in place (no erasure) ──────────────
    for (const sid of borderInCone) {
      const stroke = allStrokes.get(sid);
      if (!stroke) continue;
      const absPts = (stroke.points as Array<{ x: number; y: number }>).map(toAbs);
      this._dissolves.add('wind-resist', absPts, { windDx: dx, windDy: dy });
    }

    if (stray.length === 0) return;

    // ── 4. Tiny isolated strokes fly off; originals erased synchronously
    const undoReverse: GrimoireAction[] = [];
    const undoForward: GrimoireAction[] = [];

    for (const sid of stray) {
      const stroke = allStrokes.get(sid);
      if (!stroke) continue;

      const absPts = (stroke.points as Array<{ x: number; y: number }>).map(toAbs);

      // Per-leaf jitter — direction spread + speed variance.  Speed scaled
      // by intensity so a stronger wind actually pushes harder.
      const jitter = (Math.random() - 0.5) * 0.45;
      const speedJ = (0.75 + Math.random() * 0.5) * intensity;
      const c = Math.cos(jitter), s = Math.sin(jitter);
      const lx = (dx * c - dy * s) * speedJ;
      const ly = (dx * s + dy * c) * speedJ;
      const rotSpeed = (Math.random() - 0.5) * 4.5;
      this._dissolves.add('wind-leaf', absPts, { windDx: lx, windDy: ly, rotSpeed });

      undoReverse.push({
        type: 'ink.commit', strokeId: sid, pageId,
        points: stroke.points, style: stroke.style,
        origin: 'gesture', timestamp: now,
      } as GrimoireAction);
      undoForward.push({
        type: 'ink.erase', pageId, target: sid,
        origin: 'gesture', timestamp: Date.now(),
      } as GrimoireAction);
      this._ink.removeStroke(sid);
      this._dispatch({
        type: 'ink.erase', pageId, target: sid,
        origin: 'gesture', timestamp: Date.now(),
      });
    }
    if (undoForward.length > 0) {
      this._pushUndo({ reverse: undoReverse, forward: undoForward });
    }
  }

  private _bboxesOverlap(
    ax0: number, ay0: number, ax1: number, ay1: number,
    b: { x: number; y: number; w: number; h: number },
  ): boolean {
    return !(b.x > ax1 || b.x + b.w < ax0 || b.y > ay1 || b.y + b.h < ay0);
  }

  /** Axis-aligned bounding box of a point list. */
  private _strokeBbox(
    pts: import('@shared/notebook_model').StrokePoint[] | Array<{ x: number; y: number }>,
  ): { x: number; y: number; w: number; h: number } {
    if (pts.length === 0) return { x: 0, y: 0, w: 0, h: 0 };
    let minX = pts[0].x, maxX = pts[0].x;
    let minY = pts[0].y, maxY = pts[0].y;
    for (let i = 1; i < pts.length; i++) {
      const p = pts[i];
      if (p.x < minX) minX = p.x;
      if (p.x > maxX) maxX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.y > maxY) maxY = p.y;
    }
    return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
  }

  /**
   * Reject near-degenerate shape suggestions.  Triggers for:
   *   - `spline` / `curve` whose vertices are essentially colinear — the
   *     user drew an almost-straight wiggle and the recogniser offered to
   *     replace it with an identical-looking curve.  Pointless.
   *   - `line` whose peak off-axis residual is under ~1.5 px — the stroke
   *     already IS a straight line and the suggestion adds nothing.
   *   - `ellipse` / `circle` that effectively collapses to a line
   *     (very flat aspect).
   *
   * The perpendicular-distance residual uses the shape's own endpoints so
   * we compare against the suggested geometry rather than the raw ink.
   */
  private _shapeSuggestionLooksPointless(
    shape: RecognisedShape,
    strokePts: import('@shared/notebook_model').StrokePoint[],
  ): boolean {
    const bb = this._strokeBbox(strokePts);
    const span = Math.max(bb.w, bb.h);

    // Degenerate ellipse / circle (very flat)
    if ((shape.kind === 'ellipse' || shape.kind === 'circle')
        && shape.rx != null && shape.ry != null) {
      const maxR = Math.max(shape.rx, shape.ry);
      const minR = Math.min(shape.rx, shape.ry);
      if (maxR > 0 && minR / maxR < 0.12) return true; // too flat — it's a line
    }

    // Line/spline/curve: measure peak perpendicular distance of the stroke
    // from the line connecting the shape's first↔last vertex.  If the peak
    // residual is a tiny fraction of the span, the "curve" or "line" is
    // indistinguishable from the ink.
    if (shape.kind === 'line' || shape.kind === 'spline' || shape.kind === 'curve') {
      const v = shape.vertices;
      if (!v || v.length < 2 || strokePts.length < 3) return false;
      const a = v[0], b = v[v.length - 1];
      const dx = b.x - a.x, dy = b.y - a.y;
      const chord = Math.hypot(dx, dy);
      if (chord < 8) return false;
      let peak = 0;
      for (const p of strokePts) {
        const num = Math.abs(dy * p.x - dx * p.y + b.x * a.y - b.y * a.x);
        const d = num / chord;
        if (d > peak) peak = d;
      }
      if (shape.kind === 'line') {
        // A "line" suggestion on ink that's already ~colinear is noise.
        if (peak < 2.0) return true;
      } else {
        // Spline/curve: the curvature has to be at least ~6% of the span,
        // otherwise the curve is visually identical to a line (the stroke
        // already looks like that).
        if (peak / Math.max(span, 1) < 0.06) return true;
      }
    }

    return false;
  }

  // ── Unified page content query ─────────────────────────────────────────
  //
  // Returns all visible strokes on the current page, including synthetic
  // strokes generated from figure block edges.  This lets systems like
  // _analyseRegionForInsert see ALL content regardless of how it was created.

  private _unifiedPageStrokes(): Map<string, {
    points: import('@shared/notebook_model').StrokePoint[];
    source: 'ink' | 'figure';
  }> {
    const result = new Map<string, {
      points: import('@shared/notebook_model').StrokePoint[];
      source: 'ink' | 'figure';
    }>();

    // Raw ink strokes
    for (const [id, stroke] of this._ink.getStrokes()) {
      result.set(id, { points: stroke.points, source: 'ink' });
    }

    // Convert figure block edges to synthetic point arrays
    const page = this._state.currentPage();
    if (page) {
      for (const [blockId, block] of page.blocks) {
        if (block.kind !== 'figure') continue;
        const fig = block as import('./notebook_state').WvFigureBlock;
        const now = performance.now();
        for (let ei = 0; ei < fig.edges.length; ei++) {
          const edge = fig.edges[ei];
          const from = fig.vertices[edge.from];
          const to = fig.vertices[edge.to];
          if (!from || !to) continue;
          // Synthesise a straight-line stroke between edge endpoints
          const synId = `${blockId}.__edge${ei}`;
          const pts: import('@shared/notebook_model').StrokePoint[] = [];
          const steps = Math.max(Math.ceil(Math.hypot(to.x - from.x, to.y - from.y) / 4), 4);
          for (let s = 0; s <= steps; s++) {
            const t = s / steps;
            pts.push({
              x: from.x + (to.x - from.x) * t,
              y: from.y + (to.y - from.y) * t,
              pressure: 0.5, tiltX: 0, tiltY: 0, twist: 0, timestamp: now,
            });
          }
          result.set(synId, { points: pts, source: 'figure' });
        }
      }
    }
    return result;
  }

  // ── Arcane animation state machine (per-frame) ──────────────────────────

  /**
   * Ticked every frame from the render loop.
   * Drives the glow → resolve/fail → blend sequence, drawing overlays and
   * creating the block at the appropriate moment.
   */
  private _tickArcaneAnim(ctx: CanvasRenderingContext2D, ox: number, oy: number): void {
    const anim = this._arcaneAnim;
    if (!anim) return;

    const now = performance.now();
    const elapsed = now - anim.startMs;

    switch (anim.phase) {
      // ── Box pulse: box stroke brightness oscillates ("detected!") ──
      case 'box-pulse': {
        const t = Math.min(elapsed / ARCANE_BOX_PULSE_MS, 1);
        // 2.5 sine pulses over the phase duration
        const pulse = Math.sin(t * Math.PI * 5) * 0.5 + 0.5; // 0→1 oscillation
        const alpha = 0.08 + pulse * 0.28;
        this._drawStrokeGlow(ctx, ox, oy, [anim.boxStrokeId], '#e8dcc0', alpha, 1, anim.bbox);
        if (t >= 1) {
          if (anim.runeStrokeIds.length > 0) {
            // Runes present → continue to rune pulse
            anim.startMs = now;
            anim.phase = 'rune-pulse';
          } else {
            // Empty box or no runes — flicker was the feedback, stop
            this._arcaneAnim = null;
          }
        }
        break;
      }

      // ── Rune pulse: rune strokes brightness oscillates ─────────────
      case 'rune-pulse': {
        const t = Math.min(elapsed / ARCANE_RUNE_PULSE_MS, 1);
        const pulse = Math.sin(t * Math.PI * 5) * 0.5 + 0.5;
        const alpha = 0.08 + pulse * 0.32;
        // Keep box visible at low steady glow while runes pulse
        this._drawStrokeGlow(ctx, ox, oy, [anim.boxStrokeId], '#e8dcc0', 0.12, 1, anim.bbox);
        if (anim.runeStrokeIds.length > 0) {
          this._drawStrokeGlow(ctx, ox, oy, anim.runeStrokeIds, '#c8a84a', alpha, 1, anim.bbox);
        }
        if (t >= 1) {
          if (anim.blockKind) {
            // Recognised rune → proceed to resolve (inflate + fade + block creation)
            anim.startMs = now;
            anim.phase = 'resolve';
            this._tapAudio.playArcaneResolve();
          } else {
            // Unrecognised shape — flicker was the feedback, just stop
            this._arcaneAnim = null;
          }
        }
        break;
      }

      // ── Resolve: rune strokes inflate + fade; box fades; frame appears
      case 'resolve': {
        const t = Math.min(elapsed / ARCANE_RESOLVE_MS, 1);
        // Ease-out for smooth deceleration
        const ease = 1 - (1 - t) * (1 - t);

        // Rune strokes: inflate outward and fade simultaneously
        const runeScale = 1 + ease * 0.25;
        const runeAlpha = 0.35 * (1 - ease);
        if (anim.runeStrokeIds.length > 0) {
          this._drawStrokeGlow(ctx, ox, oy, anim.runeStrokeIds, '#c8a84a', runeAlpha, runeScale, anim.bbox);
        }

        // Box: steady fade
        this._drawStrokeGlow(ctx, ox, oy, [anim.boxStrokeId], '#e8dcc0', 0.25 * (1 - ease), 1, anim.bbox);

        // Create block partway through
        if (t > 0.3 && !anim.blockId) {
          this._finaliseArcaneBlock(anim);
        }

        // Block frame materialises
        if (anim.blockId && t > 0.3) {
          const a = (t - 0.3) / 0.7;
          const b = anim.bbox;
          const P = 8;
          ctx.save();
          ctx.globalAlpha = a * 0.35;
          ctx.strokeStyle = '#5a3820';
          ctx.lineWidth = 1;
          ctx.setLineDash([4, 3]);
          ctx.strokeRect(ox + b.x + P, oy + b.y + P, b.width - P * 2, b.height - P * 2);
          ctx.restore();
        }

        if (t >= 1) {
          // Lava-fade consumed strokes
          const ts = Date.now();
          const reverse: GrimoireAction[] = [];
          const forward: GrimoireAction[] = [];
          for (const id of [anim.boxStrokeId, ...anim.runeStrokeIds]) {
            const saved = this._ink.getStrokes().get(id);
            if (saved) reverse.push({ type: 'ink.commit', strokeId: saved.id, pageId: anim.pageId, points: saved.points, style: saved.style, origin: 'gesture', timestamp: ts });
            this._ink.addLavaFade(id);
            const ea: GrimoireAction = { type: 'ink.erase', pageId: anim.pageId, target: id, origin: 'gesture', timestamp: ts };
            this._dispatch(ea);
            forward.push(ea);
          }
          if (forward.length > 0) this._pushUndo({ reverse, forward });
          this._arcaneAnim = null;
        }
        break;
      }
    }
  }

  /** Draw glow lines over specific strokes, optionally scaled from the container centre. */
  private _drawStrokeGlow(
    ctx: CanvasRenderingContext2D, ox: number, oy: number,
    strokeIds: string[], color: string, alpha: number,
    scale: number, container: { x: number; y: number; width: number; height: number },
  ): void {
    if (alpha < 0.005) return;
    const allStrokes = this._ink.getStrokes();
    const cxBox = container.x + container.width / 2;
    const cyBox = container.y + container.height / 2;

    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = color;
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    for (const id of strokeIds) {
      const stroke = allStrokes.get(id);
      if (!stroke || stroke.points.length < 2) continue;
      ctx.beginPath();
      for (let i = 0; i < stroke.points.length; i++) {
        const p = stroke.points[i];
        const px = ox + cxBox + (p.x - cxBox) * scale;
        const py = oy + cyBox + (p.y - cyBox) * scale;
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      }
      ctx.stroke();
    }
    ctx.restore();
  }

  /** Create the actual block from the validated arcane animation. */
  private _finaliseArcaneBlock(anim: ArcaneAnim): void {
    const pageId = anim.pageId;
    const ts = Date.now();
    const kind = anim.blockKind ?? 'text';
    const P = 8;
    let bbox = {
      x: anim.bbox.x + P, y: anim.bbox.y + P,
      width: Math.max(anim.bbox.width - P * 2, 40),
      height: Math.max(anim.bbox.height - P * 2, 30),
    };

    // Arcane sigils use a fixed canonical size centered on the drawn area
    const SIGIL_SIZE = 72;
    if (kind === 'arcane') {
      const cx = anim.bbox.x + anim.bbox.width / 2;
      const cy = anim.bbox.y + anim.bbox.height / 2;
      bbox = { x: cx - SIGIL_SIZE / 2, y: cy - SIGIL_SIZE / 2, width: SIGIL_SIZE, height: SIGIL_SIZE };
    }

    // ── NPC arcane: enforce single instance per persona per page ──────
    if (kind === 'arcane') {
      const persona = anim.detectedPersona ?? 'ireth';
      const npcType = persona === 'laura' ? 'laura_channel' : 'ireth_channel';
      const page = this._state.currentPage();
      let existing: import('./notebook_state').WvArcaneBlock | null = null;
      if (page) {
        for (const [, b] of page.blocks) {
          if (b.kind === 'arcane' && (b as import('./notebook_state').WvArcaneBlock).arcaneType === npcType) {
            existing = b as import('./notebook_state').WvArcaneBlock;
            break;
          }
        }
      }

      // Greeting per persona
      const greetings: Record<string, string[]> = {
        ireth: ['You called?', 'I am here.', 'Speak, and I shall listen.', 'At your service.', 'Yes?'],
        laura: ['Hey!', 'What\'s up?', 'I\'m here~', 'Hiya!', 'Let\'s see...'],
      };
      const greeting = (greetings[persona] ?? greetings.ireth)[Math.floor(Math.random() * 5)];

      if (existing) {
        // Teleport existing sigil to new draw position
        anim.blockId = existing.id;
        existing.moveX = bbox.x + SIGIL_SIZE / 2;
        existing.moveY = bbox.y + SIGIL_SIZE / 2;
        existing.moveState = 'standing';
        existing.moveStateAt = ts;
        existing.ephemeralSay = greeting;
        existing.ephemeralSayAt = ts;
        // Record greeting as a page event for cross-NPC awareness
        this._state.pushPageEvent(pageId, {
          persona: persona as 'ireth' | 'laura',
          kind: 'say',
          content: greeting,
          timestamp: ts,
        });
        return;  // no new block needed
      }

      // Create new NPC sigil
      const blockId = `${pageId}.${kind}.${ts.toString(36)}`;
      anim.blockId = blockId;
      this._dispatch({ type: 'block.create', blockId, pageId, kind, bbox, origin: 'gesture', timestamp: ts });
      this._pushUndo({
        reverse: [{ type: 'block.delete', blockId, pageId, origin: 'gesture', timestamp: ts }],
        forward: [{ type: 'block.create', blockId, pageId, kind, bbox, origin: 'gesture', timestamp: ts }],
      });
      this._dispatch({ type: 'arcane.open', blockId, pageId, arcaneType: npcType, origin: 'gesture', timestamp: ts });

      // Show greeting on the newly-created block
      const newArc = this._state.currentPage()?.blocks.get(blockId);
      if (newArc && newArc.kind === 'arcane') {
        const arc = newArc as import('./notebook_state').WvArcaneBlock;
        arc.ephemeralSay = greeting;
        arc.ephemeralSayAt = ts;
      }
      // Record greeting as a page event for cross-NPC awareness
      this._state.pushPageEvent(pageId, {
        persona: persona as 'ireth' | 'laura',
        kind: 'say',
        content: greeting,
        timestamp: ts,
      });
      this._hasNpcOnPage = true;
      return;
    }

    const blockId = `${pageId}.${kind}.${ts.toString(36)}`;
    anim.blockId = blockId;

    this._dispatch({ type: 'block.create', blockId, pageId, kind, bbox, origin: 'gesture', timestamp: ts });
    this._pushUndo({
      reverse: [{ type: 'block.delete', blockId, pageId, origin: 'gesture', timestamp: ts }],
      forward: [{ type: 'block.create', blockId, pageId, kind, bbox, origin: 'gesture', timestamp: ts }],
    });

    if (kind === 'text') {
      this._openTextInput({ id: blockId, kind: 'text' as const, bbox, content: '' } as any, pageId);
    }
    if (kind === 'code') {
      // Code blocks open as arcane computation channels
      this._dispatch({ type: 'arcane.open', blockId, pageId, arcaneType: 'code', origin: 'gesture', timestamp: ts });
    }
    // latex blocks are created but don't need immediate follow-up —
    // the block renderer shows an empty latex placeholder for input.
  }

  /**
   * Rune shape classifier for box+rune arcane insertion.
   *
   * Rune vocabulary:
   *   T      (2 strokes: vertical + horizontal bar)     → text
   *   < >    (2 strokes: left angle + right angle)      → code
   *   ∫ / S  (1 sinuous stroke, mostly vertical)        → latex
   *   ✳ 3-line star (3 strokes through center)          → arcane (laura)
   *   ✳ 4+-line star (4+ strokes through center)        → arcane (ireth)
   */
  private _classifyRune(
    strokes: Array<{ id: string; points: import('@shared/notebook_model').StrokePoint[] }>,
  ): import('@shared/notebook_model').BlockKind | null {
    const allPts = strokes.flatMap(s => s.points);
    if (allPts.length < 3) return null;

    // ── Per-stroke feature extraction ──────────────────────────────────
    type StrokeFeatures = {
      points: import('@shared/notebook_model').StrokePoint[];
      dx: number; dy: number; adx: number; ady: number;
      len: number; winding: number; closed: boolean;
      aspect: number; // adx/ady (>1 = horizontal, <1 = vertical)
      vShape: number; // 0..1  — how V-shaped (angle bracket-like)
      vDir: 'left' | 'right' | 'none'; // opening direction of V
    };

    const features: StrokeFeatures[] = strokes.map(s => {
      const pts = s.points;
      const first = pts[0], last = pts[pts.length - 1];
      const dx = last.x - first.x;
      const dy = last.y - first.y;
      const adx = Math.abs(dx);
      const ady = Math.abs(dy);

      // Arc length
      let len = 0;
      for (let i = 1; i < pts.length; i++) {
        len += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
      }

      // Winding (cumulative turning angle / 2π)
      let turn = 0;
      for (let i = 2; i < pts.length; i++) {
        const a1 = Math.atan2(pts[i - 1].y - pts[i - 2].y, pts[i - 1].x - pts[i - 2].x);
        const a2 = Math.atan2(pts[i].y - pts[i - 1].y, pts[i].x - pts[i - 1].x);
        let d = a2 - a1;
        while (d > Math.PI)  d -= 2 * Math.PI;
        while (d < -Math.PI) d += 2 * Math.PI;
        turn += d;
      }
      const winding = Math.abs(turn) / (2 * Math.PI);

      // Closure: start–end distance < 25% of arc length
      const closeDist = Math.hypot(dx, dy);
      const closed = len > 10 && closeDist < len * 0.25;

      const aspect = ady > 1 ? adx / ady : (adx > 1 ? 10 : 1);

      // V-shape detection: find the point farthest from the start–end line
      // in the X direction. A V-bracket has a sharp apex and two arms.
      let vShape = 0;
      let vDir: 'left' | 'right' | 'none' = 'none';
      if (pts.length >= 4 && ady > 8) {
        // Sort by Y to find middle of stroke vertically
        const midIdx = Math.round(pts.length / 2);
        const mid = pts[midIdx];
        const baseline = (first.x + last.x) / 2;
        const apex = mid.x;
        const deflection = apex - baseline;
        const span = Math.max(adx, ady);
        const relDeflection = Math.abs(deflection) / span;
        // A V-shape has the apex deflected sideways from the endpoints
        if (relDeflection > 0.15) {
          // Check arms are roughly equal length
          const arm1 = Math.hypot(mid.x - first.x, mid.y - first.y);
          const arm2 = Math.hypot(last.x - mid.x, last.y - mid.y);
          const armRatio = Math.min(arm1, arm2) / Math.max(arm1, arm2);
          if (armRatio > 0.3) {
            vShape = Math.min(relDeflection * 2, 1);
            vDir = deflection < 0 ? 'left' : 'right';
          }
        }
      }

      return { points: pts, dx, dy, adx, ady, len, winding, closed, aspect, vShape, vDir };
    });

    // ── Rule 1a: Ireth's sigil — crossed star (4+ lines through center)
    // 4 or more straight strokes all passing through a common center.
    // ── Rule 1b: Laura's sigil — crossed star (3 lines through center)
    // Exactly 3 straight strokes through center — simpler star.
    if (strokes.length >= 3) {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const f of features) {
        for (const p of f.points) {
          if (p.x < minX) minX = p.x;
          if (p.y < minY) minY = p.y;
          if (p.x > maxX) maxX = p.x;
          if (p.y > maxY) maxY = p.y;
        }
      }
      const bboxW = maxX - minX;
      const bboxH = maxY - minY;
      const bboxAspect = bboxW > 1 && bboxH > 1 ? Math.max(bboxW, bboxH) / Math.min(bboxW, bboxH) : 10;
      const centerX = (minX + maxX) / 2;
      const centerY = (minY + maxY) / 2;
      const centerR = Math.max(bboxW, bboxH) * 0.25;

      // Count straight strokes passing through center region
      let throughCenter = 0;
      for (const f of features) {
        const endDist = Math.hypot(f.dx, f.dy);
        const isStraight = f.len > 8 && endDist > f.len * 0.55;
        if (!isStraight) continue;
        for (const p of f.points) {
          if (Math.hypot(p.x - centerX, p.y - centerY) < centerR) {
            throughCenter++;
            break;
          }
        }
      }

      // Crossed star: most strokes pass through center, compact bbox
      if (throughCenter >= 3 && bboxAspect < 2.5 && Math.max(bboxW, bboxH) > 25) {
        if (throughCenter >= 4) {
          // Ireth: 4+ crossing lines (complex starburst)
          this._lastDetectedPersona = 'ireth';
        } else {
          // Laura: exactly 3 crossing lines (simpler star)
          this._lastDetectedPersona = 'laura';
        }
        return 'arcane';
      }
    }

    // Single closed stroke — NOT arcane (circle/O no longer triggers sigils)
    // (was Rule 1c: circle detection — removed)

    // ── Rule 2: Angle brackets < > → code ─────────────────────────────
    // Two strokes, both V-shaped, one opening left and one opening right
    if (strokes.length === 2) {
      const [a, b] = features;
      if (a.vShape > 0.2 && b.vShape > 0.2) {
        const hasLeft  = a.vDir === 'left'  || b.vDir === 'left';
        const hasRight = a.vDir === 'right' || b.vDir === 'right';
        if (hasLeft && hasRight) return 'code';
        // Even if both face same direction, two clear V strokes → code
        if (a.vShape > 0.35 && b.vShape > 0.35) return 'code';
      }
    }

    // ── Rule 3: T shape → text ────────────────────────────────────────
    // Two strokes: one roughly vertical, one roughly horizontal
    if (strokes.length >= 2) {
      let hasVert = false, hasHoriz = false;
      for (const f of features) {
        if (f.points.length < 2) continue;
        if (f.aspect < 0.5 && f.ady > 10) hasVert = true;
        if (f.aspect > 2 && f.adx > 10)   hasHoriz = true;
      }
      if (hasVert && hasHoriz) return 'text';
    }

    // ── Rule 4: Integral / S-curve → latex ────────────────────────────
    // Single stroke, predominantly vertical, sinuous (winding 0.3 – 1.5),
    // not closed (start and end well separated vertically)
    if (strokes.length === 1) {
      const f = features[0];
      if (!f.closed && f.ady > 15 && f.aspect < 1.2 &&
          f.winding > 0.25 && f.winding < 1.5 && f.len > 25) {
        return 'latex';
      }
    }

    // ── Rule 5 removed (spiral/swirl no longer triggers arcane) ──────

    return null;
  }

  // ── Tab bar interaction ──────────────────────────────────────────────────

  private _handleTabClick(tab: { index: number; kind?: string; locked?: boolean }): void {
    if (tab.kind === 'codex') {
      this._switchContent({ viewMode: 'codex' });
      return;
    }
    if (tab.kind === 'settings') {
      this._switchContent({ viewMode: 'settings' });
      return;
    }
    if (tab.kind === 'newPage') {
      this._createSiblingPage();
      return;
    }
  }

  /**
   * Quick-action: create a new page next to the current one.  In journal
   * mode the new page lands in the same folder as the current page's file;
   * the standalone host reads the current page's journalPath to pick the
   * destination so there's no explicit folder picker.
   *
   * Note: this creates a sibling page, not a sibling subpage within the
   * current sheet — sheets are tied to a single on-disk file.
   */
  private _createSiblingPage(): void {
    if (!this._state) return;
    const now = Date.now();
    const pageId = `page_${now.toString(36)}`;
    const insertAfter = this._state.pageCount - 1;
    const currentSec = this._state.currentSection();

    // Derive a reasonable title from "Untitled" + a number.  The title is
    // used for both the codex row label and, when the host slugifies it,
    // the on-disk filename.
    const title = `Untitled ${this._nextPageNumber()}`;

    this._dispatch({
      type: 'page.create',
      pageId,
      insertAfterIndex: insertAfter,
      title,
      sectionId: currentSec?.id,
      origin: 'gesture',
      timestamp: now,
    } as GrimoireAction);
    this._switchContent({ viewMode: 'page', pageId });
  }

  // ── Journal side-tab navigation helpers ─────────────────────────────────

  /**
   * Called after every page navigation: records the destination in the
   * history map so "level up" can return to the right sibling, then
   * refreshes the side-tab context.
   */
  private _recordNavAndRefresh(pageId: string): void {
    const host = (window as unknown as Record<string, unknown>).__oxHost as
      { _journalMode?: boolean; getJournalSiblings?: (id: string) => unknown } | undefined;
    if (!host?._journalMode || typeof host.getJournalSiblings !== 'function') {
      this._journalNavContext = null;
      return;
    }
    const ctx = host.getJournalSiblings(pageId) as typeof this._journalNavContext;
    this._journalNavContext = ctx ?? null;
    if (ctx) {
      // Store the current page as the "last visited" at this level.
      this._navHistory.set(ctx.parentPath, pageId);
    }
  }

  /**
   * Navigate to the parent directory level.  Arrives at the last page
   * visited at that level, or the first child of the grandparent if
   * history has no record yet.
   */
  private _navigateLevelUp(): void {
    const host = (window as unknown as Record<string, unknown>).__oxHost as
      { _journalMode?: boolean; getJournalSiblings?: (id: string) => unknown; getJournalSiblingsAtFolder?: (path: string) => unknown } | undefined;

    // ── Fallback path #1 — non-page view modes ────────────────────────
    // From settings / codex / any side view, "back" is the escape
    // hatch: return to the last page the user was reading. This is
    // the only legitimate "go to last visited" semantic now —
    // sibling-level history is no longer used for sideways jumps.
    if (this._viewMode !== 'page') {
      if (this._lastPageId) {
        this._switchContent({ viewMode: 'page', pageId: this._lastPageId });
      }
      return;
    }

    // ── Fallback path #2 — non-journal or missing nav context ─────────
    // No file-tree to climb. Best we can do is the lastPageId
    // escape so the user isn't dead-ended.
    const nav = this._journalNavContext;
    if (!host?._journalMode || !nav) {
      if (this._lastPageId && this._lastPageId !== this._state.currentPageId) {
        this._switchContent({ viewMode: 'page', pageId: this._lastPageId });
      }
      return;
    }

    // ── Primary path — go UP one folder level ─────────────────────────
    // On a file: target = parent folder's landing (one level up).
    // On a folder landing: target = grandparent folder's landing
    //   (so "up from folder X" lands on X's parent, not X itself).
    // Sibling-level history is intentionally NOT consulted here —
    // the previous behaviour kept yanking the user sideways to a
    // recently-visited file in the destination folder, which felt
    // unpredictable. Direct landing navigation gives a stable
    // "always goes up" rule.
    const current = this._state.currentPage();
    const isOnFolderLanding = current?.kind === 'folder-landing';
    const targetFolder = isOnFolderLanding ? nav.grandparentPath : nav.parentPath;
    if (targetFolder == null) {
      // At root — no further up to go. Fallback to lastPageId so
      // tapping back from a root file still gets the user
      // somewhere useful instead of being a no-op.
      if (this._lastPageId && this._lastPageId !== this._state.currentPageId) {
        this._switchContent({ viewMode: 'page', pageId: this._lastPageId });
      }
      return;
    }
    const landingId = '_folder:' + targetFolder;
    this._switchContent({ viewMode: 'page', pageId: landingId });
  }

  // ── Page sidebar interaction ────────────────────────────────────────────

  private _handlePageSidebarClick(tab: { index: number; kind?: string; pageId?: string; nodePath?: string }): void {
    const now = Date.now();

    // Codex view — tab click navigates codex pages
    if (this._viewMode === 'codex') {
      if (tab.index >= 0 && tab.index < this._codex.totalPageCount) {
        this._groupPageIndex = tab.index;
      }
      return;
    }

    if (tab.kind === 'add') {
      const currentSec = this._state.currentSection();
      const pageId = `page_${now.toString(36)}`;
      const insertAfter = this._state.pageCount - 1;
      this._dispatch({
        type: 'page.create',
        pageId,
        insertAfterIndex: insertAfter,
        title: `Page ${this._nextPageNumber()}`,
        sectionId: currentSec?.id,
        origin: 'gesture',
        timestamp: now,
      });
      this._switchContent({ viewMode: 'page', pageId });
      return;
    }

    // ── Journal side-tab kinds ────────────────────────────────────────────

    if (tab.kind === 'back') {
      this._navigateLevelUp();
      return;
    }

    if (tab.kind === 'newpage') {
      this._createSiblingPage();
      return;
    }

    if (tab.kind === 'delete' && tab.pageId) {
      // Refuse to delete folders or the last remaining page —
      // defence-in-depth; the tab is gated upstream too.
      const target = this._state.currentPage();
      if (!target || target.kind === 'folder-landing') return;
      if (this._state.pageCount <= 1) return;
      this._dispatch({
        type: 'page.delete',
        pageId: tab.pageId,
        origin: 'gesture',
        timestamp: Date.now(),
      });
      return;
    }

    if (tab.kind === 'folder' || tab.kind === 'adventure-sub') {
      // Navigate to the last visited page in this folder, or its landing.
      const lastVisited = tab.nodePath ? this._navHistory.get(tab.nodePath) : undefined;
      const target = lastVisited ?? tab.pageId;
      if (!target) return;

      // The folder's landing/leaves only enter the page order after
      // _ensureFolderLoaded fires (lazy navigation). If the user taps
      // a sibling folder we haven't entered yet, the synthetic landing
      // pageId won't be in _pageOrder and _switchContent will reject
      // it. Load the folder first, then navigate.
      const host = (window as unknown as Record<string, unknown>).__oxHost as
        { loadFolder?: (path: string) => Promise<void> } | undefined;
      if (tab.nodePath !== undefined && host?.loadFolder) {
        void host.loadFolder(tab.nodePath).then(() => {
          this._switchContent({ viewMode: 'page', pageId: target });
        });
      } else {
        this._switchContent({ viewMode: 'page', pageId: target });
      }
      return;
    }

    // Regular page tab — navigate by pageId (preferred) or visible index
    if (tab.pageId) {
      this._switchContent({ viewMode: 'page', pageId: tab.pageId });
      return;
    }
    if (tab.index >= 0) {
      const visPages = this._state.visiblePages();
      const targetPage = visPages[tab.index];
      if (targetPage) {
        this._switchContent({ viewMode: 'page', pageId: targetPage.id });
      }
    }
  }

  // ── Section creation ─────────────────────────────────────────────────────

  private _createSection(): void {
    const now = Date.now();
    const sectionId = `sec_${now.toString(36)}`;
    const headerPageId = `page_${(now + 1).toString(36)}`;
    const sectionIdx = this._state.sections().length;
    const color = SECTION_PALETTE[sectionIdx % SECTION_PALETTE.length];

    this._dispatch({
      type: 'section.create',
      sectionId,
      headerPageId,
      title: `Section ${sectionIdx + 1}`,
      color,
      insertAfterIndex: this._state.pageCount - 1,
      origin: 'gesture',
      timestamp: now,
    });

    this._switchContent({ viewMode: 'page', pageId: headerPageId });
  }

  // ── Section header page interaction ─────────────────────────────────────

  // _checkSectionHeaderTap removed — swatch selection now uses cross/lace analysis.

  // ── Interaction policy management ─────────────────────────────────────────

  /** Rebuild _policy for the current view mode.  Called on every view/page change. */
  private _updatePolicy(): void {
    if (this._viewMode === 'codex') {
      // Entire codex page: wand ink (fades), spells detected & visualised,
      // but actions diverted — effects intercepted, not executed.
      this._policy = {
        defaultContext: {
          tool: 'wand',
          spellsAllowed: true,
          dispatchMode: 'diverted',
          onDivertedCast: (spellId, _params, family, _conf) => {
            this._onCodexSpellRecognised(spellId, family);
          },
        },
        boxes: [],
      };
    } else if (this._viewMode === 'settings') {
      // Settings: allow ink so cross/lace selection gestures work
      this._policy = {
        defaultContext: { tool: 'pen', spellsAllowed: false, dispatchMode: 'live' },
        boxes: [],
      };
    } else {
      // Page view
      const sec = this._state.currentSection();
      if (sec && sec.kind === 'adventure') {
        // Adventure section: policy depends on which sub-tab
        const pageId = this._state.currentPageId;
        if (pageId === 'adv_action') {
          this._policy = { defaultContext: ADVENTURE_ACTION_CONTEXT, boxes: [] };
        } else if (pageId === 'adv_bookmarks') {
          this._policy = {
            defaultContext: {
              ...ADVENTURE_BOOKMARK_CONTEXT,
              onDivertedCast: (spellId, _params, family, _conf) => {
                this._onAdventureBookmarkCast(spellId, family);
              },
            },
            boxes: [],
          };
        } else if (pageId.startsWith('adv_inventory')) {
          this._policy = { defaultContext: ADVENTURE_INVENTORY_CONTEXT, boxes: [] };
        } else {
          this._policy = { defaultContext: ADVENTURE_READONLY_CONTEXT, boxes: [] };
        }
      } else {
        this._policy = { defaultContext: JOURNAL_CONTEXT, boxes: [] };
      }
    }
  }

  /** Codex diverted-cast handler — shows feedback when a spell is recognised. */
  private _onCodexSpellRecognised(spellId: string, family: string): void {
    const template = this._spellRegistry.get(spellId);
    const name = template?.name ?? spellId;
    this._codexFeedback = { text: name, alpha: 1, family };
  }

  /** Adventure bookmark diverted-cast handler — placeholder for glyph matching. */
  private _onAdventureBookmarkCast(_spellId: string, _family: string): void {
    // TODO: match drawn glyph against bookmark glyph references
    // For now, just flash feedback like the codex.
  }

  /** Handle a click on an action scroll interactive element. */
  private _handleScrollHit(hit: ScrollHitRect): void {
    if (!this._state) return;
    const ts = performance.now();
    if (hit.kind === 'clock-skip') {
      this._dispatch({
        type: 'adventure.block.skip',
        blockId: hit.blockId,
        origin: 'gesture',
        timestamp: ts,
      } as import('@shared/grimoire_action').AdventureBlockSkipAction);
    } else if (hit.kind === 'option' && hit.optionId) {
      this._dispatch({
        type: 'adventure.block.select',
        blockId: hit.blockId,
        optionId: hit.optionId,
        origin: 'gesture',
        timestamp: ts,
      } as import('@shared/grimoire_action').AdventureBlockSelectAction);
    }
  }

  // ── Pop dialog menus ──────────────────────────────────────────────────────

  /** Toggle the permanent stylus sidebar (Batch 7). The previous
   *  cadence / side-button menu invocation routes call this; they
   *  pass anchor coords (legacy from the pop-up days), which we
   *  ignore — the sidebar lives at a fixed page-margin position.
   *  Showing the sidebar triggers `_wipeMarginColumn` so any ink
   *  the user had drawn in the now-reserved column gets evicted
   *  with a procedural animation. */
  private _toggleStylusSidebar(_cx: number, _cy: number): void {
    const wasVisible = this._stylusSidebarVisible;
    this._stylusSidebarVisible = !wasVisible;
    this._settings.stylusSidebarVisible = this._stylusSidebarVisible;
    this._saveSettings();
    if (!wasVisible) {
      // Going hidden → visible: the menu claims its space.
      this._wipeMarginColumn();
    }
  }

  /** Toggle the SL-summoned debug dialog. */
  private _toggleDebugMenu(cx: number, cy: number): void {
    this._debugDialog.toggle(cx, cy);
  }

  /** Build the tab tree for the debug dialog, evaluated every frame so
   *  selected-states / live details reflect current page + canvas state. */
  private _buildDebugTabs(): DebugTab[] {
    return [
      this._buildStylingTab(),
      this._buildSpellsTab(),
      this._buildInvocationsTab(),
      this._buildMiscTab(),
    ];
  }

  // ── STYLING tab ───────────────────────────────────────────────────────────
  private _buildStylingTab(): DebugTab {
    const page = this._state?.currentPage();
    const cur = {
      bodyJust:  page?.decorations?.body?.justification,
      bodyGfx:   page?.decorations?.body?.graphics,
      margUtil:  page?.decorations?.margins?.utility,
      margGfx:   page?.decorations?.margins?.graphics,
      frame:     page?.decorations?.frame,
    };
    const slotRows = (
      slot: 'body.justification' | 'body.graphics' | 'margins.utility' | 'margins.graphics' | 'frame',
      items: Array<{ id: string; label: string }>,
      activeId: string | undefined,
    ): DebugAction[] => items.map(it => ({
      id: it.id,
      label: it.label,
      selected: (activeId === it.id) || (!activeId && it.id === 'none'),
      onClick: () => this._dispatchDecorationSet(slot, it.id),
    }));

    const subtabs: DebugSubtab[] = [
      { id: 'body-just', label: 'Body Just.',
        actions: slotRows('body.justification', _listBodyJustifications(), cur.bodyJust) },
      { id: 'body-gfx', label: 'Body Gfx',
        actions: slotRows('body.graphics', listBodyGraphics(), cur.bodyGfx) },
      { id: 'marg-util', label: 'Marg. Util.',
        actions: slotRows('margins.utility', listMarginUtilities(), cur.margUtil) },
      { id: 'marg-gfx', label: 'Marg. Gfx',
        actions: slotRows('margins.graphics', listMarginGraphics(), cur.margGfx) },
      { id: 'frame', label: 'Frame',
        actions: slotRows('frame', listFrameDecorations(), cur.frame) },
    ];
    return { id: 'styling', label: 'Styling', subtabs };
  }

  private _dispatchDecorationSet(
    slot: 'body.justification' | 'body.graphics' | 'margins.utility' | 'margins.graphics' | 'frame',
    styleId: string,
  ): void {
    const pageId = this._state.currentPageId;
    if (!pageId) return;
    const styleArg: string | null = styleId === 'none' ? null : styleId;
    this._dispatch({
      type: 'page.decoration.set', pageId, slot, style: styleArg,
      origin: 'gesture', timestamp: Date.now(),
    });
  }

  // ── SPELLS tab ────────────────────────────────────────────────────────────
  private _buildSpellsTab(): DebugTab {
    // Two sub-listings: the legacy SpellRegistry (gesture-pipeline) and
    // the new SpellEngine (single-stroke pipeline, P2). Each row dispatches
    // its own invocation path.
    const legacy = this._spellRegistry?.all?.() ?? [];
    const discovered = new Set((this._spellRegistry?.codex?.() ?? []).map(s => s.id));
    const legacyActions: DebugAction[] = legacy.map(sp => ({
      id: `legacy:${sp.id}`,
      label: sp.id,
      detail: discovered.has(sp.id) ? 'discovered · legacy' : 'hidden · legacy',
      onClick: () => this._castSpellFromDebug(sp.id),
    }));
    legacyActions.sort((a, b) => a.label.localeCompare(b.label));

    const newSpells = this._newSpellEngine.list();
    const newActions: DebugAction[] = newSpells.map(sp => ({
      id: `new:${sp.id}`,
      label: sp.id,
      detail: 'new engine',
      onClick: () => this._invokeNewSpellFromDebug(sp.id),
    }));
    newActions.sort((a, b) => a.label.localeCompare(b.label));

    return {
      id: 'spells',
      label: 'Spells',
      subtabs: [
        { id: 'new', label: 'New', actions: newActions },
        { id: 'legacy', label: 'Legacy', actions: legacyActions },
      ],
    };
  }

  /** Invoke a new-engine recipe against the current selection. Used by
   *  the debug menu — bypasses geometry detection entirely. */
  private _invokeNewSpellFromDebug(spellId: string): void {
    const ctx = this._buildEngineContext();
    // Most recipes use no params; selection-driven ones read state.
    const actions = this._newSpellEngine.invokeManually(spellId, {}, ctx);
    console.log(`[new-spell] manual '${spellId}' → ${actions.length} action(s)`);
    for (const a of actions) this._dispatch(a);
  }

  /** Cast a spell from the debug menu. Most spells need parameters — we
   *  pass an empty bag and rely on each spell's own param-guard. Errors
   *  log to the console; the menu stays open so the user can try again. */
  private _castSpellFromDebug(spellId: string): void {
    const now = Date.now();
    const dummyStrokeId = `dbg_${now.toString(36)}`;
    this._dispatch({
      type: 'spell.cast', spellId, family: 'invoke',
      parameters: {}, strokeId: dummyStrokeId, chargeDurationMs: 0,
      origin: 'keyboard', timestamp: now,
    });
    console.log(`[debug] cast spell '${spellId}'`);
  }

  // ── INVOCATIONS tab ───────────────────────────────────────────────────────
  private _buildInvocationsTab(): DebugTab {
    const L = this._layout;
    const cx = L.x + L.width / 2;
    const cy = L.y + L.height / 2;
    const actions: DebugAction[] = [
      { id: 'wand.invoke',  label: 'Invoke wand (cast mode)',
        selected: this._isWandInvoked,
        onClick: () => this._invokeWand(cx, cy) },
      { id: 'wand.cancel',  label: 'Cancel wand', detail: 'safe',
        onClick: () => this._cancelWand() },
      { id: 'stylus.toggle', label: 'Toggle stylus sidebar',
        selected: this._stylusSidebarVisible,
        onClick: () => this._toggleStylusSidebar(cx, cy) },
      { id: 'lockfocus.toggle', label: 'Toggle lock focus',
        selected: this._state?.lockFocus ?? false,
        onClick: () => this._state.setLockFocus(!this._state.lockFocus) },
      { id: 'astral.toggle', label: 'Toggle astral overlay',
        selected: this._castAstralVisible,
        onClick: () => this._setAstralVisible(!this._castAstralVisible) },
      { id: 'wipe.column', label: 'Wipe stylus margin column',
        detail: 'destructive',
        onClick: () => this._wipeMarginColumn() },
    ];
    return { id: 'invoke', label: 'Invoke', actions };
  }

  // ── MISC tab ──────────────────────────────────────────────────────────────
  private _buildMiscTab(): DebugTab {
    const pageId = this._state?.currentPageId ?? '';
    const newOn = this._newSpellsEnabled();
    const actions: DebugAction[] = [
      { id: 'newspells.toggle', label: 'New spell engine',
        detail: newOn ? 'ON' : 'off (legacy)',
        selected: newOn,
        onClick: () => {
          try {
            // New engine is on by default; toggle writes the opt-out
            // marker. Clearing it returns to default-on.
            if (newOn) localStorage.setItem('ox-new-spells', 'false');
            else localStorage.removeItem('ox-new-spells');
          } catch { /* ignore */ }
        } },
      { id: 'page.copyId', label: 'Copy page id', detail: pageId.slice(0, 12) + (pageId.length > 12 ? '…' : ''),
        onClick: () => {
          if (!pageId) return;
          navigator.clipboard?.writeText(pageId).catch(() => {});
          console.log(`[debug] page id → clipboard: ${pageId}`);
        } },
      { id: 'page.clearStrokes', label: 'Clear all strokes (this page)',
        detail: 'destructive',
        onClick: () => this._debugClearStrokesOnCurrentPage() },
      { id: 'page.clearBlocks', label: 'Clear all blocks (this page)',
        detail: 'destructive',
        onClick: () => this._debugClearBlocksOnCurrentPage() },
      { id: 'page.clearDecorations', label: 'Clear page decorations',
        onClick: () => this._debugClearDecorations() },
      { id: 'state.dump', label: 'Dump state to console',
        onClick: () => this._debugDumpState() },
      { id: 'page.forceSave', label: 'Force save',
        onClick: () => {
          const host = (window as unknown as { __oxHost?: { saveIndex?: () => Promise<void> } }).__oxHost;
          host?.saveIndex?.().catch(console.error);
          console.log('[debug] force save fired');
        } },
      { id: 'app.reload', label: 'Reload PWA',
        detail: 'page',
        onClick: () => window.location.reload() },
    ];
    return { id: 'misc', label: 'Misc', actions };
  }

  // ── Misc tab helpers ──────────────────────────────────────────────────────
  private _debugClearStrokesOnCurrentPage(): void {
    const pageId = this._state.currentPageId;
    if (!pageId) return;
    const strokes = Array.from(this._ink.getStrokes().keys());
    const now = Date.now();
    for (const sid of strokes) {
      this._dispatch({
        type: 'ink.erase', pageId, target: sid,
        origin: 'system', timestamp: now,
      });
    }
    console.log(`[debug] cleared ${strokes.length} strokes`);
  }

  private _debugClearBlocksOnCurrentPage(): void {
    const pageId = this._state.currentPageId;
    const page = this._state.currentPage();
    if (!pageId || !page) return;
    const blockIds = Array.from(page.blocks.keys());
    const now = Date.now();
    for (const bid of blockIds) {
      this._dispatch({
        type: 'block.delete', blockId: bid, pageId,
        origin: 'system', timestamp: now,
      });
    }
    console.log(`[debug] cleared ${blockIds.length} blocks`);
  }

  private _debugClearDecorations(): void {
    const pageId = this._state.currentPageId;
    if (!pageId) return;
    const now = Date.now();
    const slots: Array<'body.justification' | 'body.graphics' | 'margins.utility' | 'margins.graphics' | 'frame'> = [
      'body.justification', 'body.graphics', 'margins.utility', 'margins.graphics', 'frame',
    ];
    for (const slot of slots) {
      this._dispatch({
        type: 'page.decoration.set', pageId, slot, style: null,
        origin: 'system', timestamp: now,
      });
    }
    console.log('[debug] cleared all page decorations');
  }

  private _debugDumpState(): void {
    const page = this._state?.currentPage();
    console.group('[debug] state dump');
    console.log('page id:', this._state?.currentPageId);
    console.log('page:', page);
    console.log('selection:', this._state?.selection);
    console.log('lockFocus:', this._state?.lockFocus);
    console.log('wand:', this._isWandInvoked, 'astral:', this._castAstralVisible);
    console.log('sidebar visible:', this._stylusSidebarVisible);
    console.log('strokes (page):', this._ink.getStrokes());
    console.groupEnd();
  }

  /** Walk every committed stroke on the current page; for any whose
   *  geometry falls inside the sidebar's reserved column, split at
   *  the boundary, push the inside runs as animated wipe fragments,
   *  and rebuild the surviving stroke from the outside runs. The
   *  data on the reserved area is genuinely lost — that's the
   *  gameplay: utilities take their space rather than ban drawing. */
  private _wipeMarginColumn(): void {
    if (this._viewMode !== 'page') return;
    const pageId = this._state.currentPageId;
    if (!pageId) return;
    const strokes = Array.from(this._ink.getStrokes().values());
    if (strokes.length === 0) return;

    const thresholdX = LEFT_MARGIN_RESERVED_PX;
    const now = performance.now();

    for (const stroke of strokes) {
      // Quick reject: if every point sits past the threshold, skip.
      let anyInside = false;
      for (const p of stroke.points) {
        if (p.x < thresholdX) { anyInside = true; break; }
      }
      if (!anyInside) continue;

      const { inside, outside } = clipPolylineAtVerticalLine(stroke.points, thresholdX);

      // Push each inside run as a fragment with a per-fragment anim
      // pick (so a long ribbon crossing the column can mix fade and
      // slice-fall).
      for (const seg of inside) {
        if (seg.length < 2) continue;
        this._marginWipe.push(makeFragment(seg, stroke.style, pickWipeAnim(), now));
      }

      // Rebuild the surviving stroke. Erase the original always;
      // re-commit each outside run as a new stroke (new id) so the
      // ink layer + persistence stay consistent. Stroke linkages
      // (snap, etc.) refresh naturally.
      this._dispatch({
        type: 'ink.erase', pageId, target: stroke.id,
        origin: 'gesture', timestamp: Date.now(),
      });
      for (let i = 0; i < outside.length; i++) {
        const seg = outside[i];
        if (seg.length < 2) continue;
        const newId = `${stroke.id}.w${i.toString(36)}`;
        this._dispatch({
          type: 'ink.commit',
          strokeId: newId,
          pageId,
          points: seg,
          style: stroke.style,
          origin: 'gesture',
          timestamp: Date.now(),
        });
      }
    }
  }

  /** Apply a sidebar item tap. Dispatches the same actions the
   *  legacy pop-up dialog used, so downstream behaviour (per-tool
   *  param routing, persistence, shape mode toggle, etc.) is
   *  unchanged. Called from the pointer-down hit test. */
  private _applySidebarHit(it: SidebarHitItem): void {
    switch (it.group) {
      case 'tool':
        this._dispatch({
          type: 'pen.style', style: { style: it.value as string },
          origin: 'gesture', timestamp: Date.now(),
        });
        break;
      case 'color':
        this._dispatch({
          type: 'pen.style', style: { color: it.value as string },
          origin: 'gesture', timestamp: Date.now(),
        });
        break;
      case 'thickness':
        this._dispatch({
          type: 'pen.style', style: { size: it.value as number },
          origin: 'gesture', timestamp: Date.now(),
        });
        break;
      case 'opacity':
        this._dispatch({
          type: 'pen.style', style: { opacity: it.value as number },
          origin: 'gesture', timestamp: Date.now(),
        });
        break;
      case 'shape':
        this._settings.shapeMode = it.value as ShapeMode;
        this._saveSettings();
        break;
      case 'autoLatex':
        this._settings.autoLatex = !this._settings.autoLatex;
        this._saveSettings();
        break;
      case 'visualFx':
        this._settings.visualFx = !this._settings.visualFx;
        this._saveSettings();
        break;
      case 'lockFocus':
        // Swap interactivity: locked content becomes selectable / editable,
        // unlocked content fades and goes inert. notebook_state.setLockFocus
        // also clears the current selection so the modes stay clean.
        this._state.setLockFocus(!this._state.lockFocus);
        break;
    }
  }

  /** Legacy floating stylus pop-up. Kept around for any caller that
   *  still asks for a pop-up at a specific anchor. The default
   *  invocation now toggles the sidebar instead. */
  private _openStylusMenu(cx: number, cy: number): void {
    const anchor = cx > (this._layout.width / 2) ? 'bottom-right' : 'bottom-left' as const;
    const options: PopOption[] = [];

    // Group 0: Thickness (swatch circles) — Row 0, left portion
    for (const th of THICKNESS_OPTIONS) {
      options.push({
        label: th.label,
        group: 0,
        value: th.size,
        display: 'swatch',
        swatchColor: '#5a3820',
        swatchRadius: th.radius,
        row: 0,
      });
    }

    // Group 1: Tool type (icon-only) — Row 0, inline after sizes
    for (const tool of TOOL_DEFS) {
      options.push({
        label: tool.label,
        group: 1,
        value: tool.id,
        display: 'icon-only',
        icon: tool.icon,
        row: 0,
      });
    }

    // Group 2: Ink colors (swatch circles) — Row 1
    for (const color of INK_COLORS) {
      options.push({
        label: '',
        group: 2,
        value: color,
        display: 'swatch',
        swatchColor: color,
        swatchRadius: 5,
        row: 1,
      });
    }

    // Group 3: Text auto-recognition toggle — Row 2, icon-only
    options.push({
      label: 'T',
      group: 3,
      value: this._settings.autoLatex ? 'on' : 'off',
      display: 'icon-only',
      icon: 'T',
      row: 2,
    });

    // Group 4: Shape mode (Off / Hint / Full) — Row 2, inline selector
    const shapeModes: Array<{ label: string; value: string }> = [
      { label: '⊘', value: 'off' },
      { label: '◎', value: 'hint' },
      { label: '●', value: 'full' },
    ];
    for (const sm of shapeModes) {
      options.push({
        label: sm.label,
        group: 4,
        value: sm.value,
        display: 'icon-only',
        icon: sm.label,
        row: 2,
      });
    }

    // Group 5: Visual FX toggle — Row 2, icon-only
    options.push({
      label: '✦',
      group: 5,
      value: this._settings.visualFx ? 'on' : 'off',
      display: 'icon-only',
      icon: '✦',
      row: 2,
    });

    // Group 6: Opacity (icon-only presets) — Row 3
    const opacityPresets = [
      { label: '25%', value: 0.25, icon: '◔' },
      { label: '50%', value: 0.5,  icon: '◑' },
      { label: '75%', value: 0.75, icon: '◕' },
      { label: '100%', value: 1.0, icon: '●' },
    ];
    for (const op of opacityPresets) {
      options.push({
        label: op.label,
        group: 6,
        value: op.value,
        display: 'icon-only',
        icon: op.icon,
        row: 3,
      });
    }

    // Pre-select current pen state
    const preSelected = new Map<number, number>();
    // Find current thickness
    const thIdx = THICKNESS_OPTIONS.findIndex(t => Math.abs(t.size - this._penStyle.size) < 0.1);
    if (thIdx >= 0) preSelected.set(0, thIdx);
    // Find current tool
    const toolBase = options.findIndex(o => o.group === 1);
    const tIdx = TOOL_DEFS.findIndex(t => t.id === this._penStyle.style);
    if (tIdx >= 0 && toolBase >= 0) preSelected.set(1, toolBase + tIdx);
    // Find current color
    const colorBase = options.findIndex(o => o.group === 2);
    const cIdx = INK_COLORS.indexOf(this._penStyle.color);
    if (cIdx >= 0 && colorBase >= 0) preSelected.set(2, colorBase + cIdx);
    // Pre-select shape mode
    const smBase = options.findIndex(o => o.group === 4);
    const smIdx = ['off', 'hint', 'full'].indexOf(this._settings.shapeMode);
    if (smIdx >= 0 && smBase >= 0) preSelected.set(4, smBase + smIdx);
    // Pre-select text recognition (icon-only: 'on' or 'off')
    const txtBase = options.findIndex(o => o.group === 3);
    if (txtBase >= 0) preSelected.set(3, txtBase);  // single item — always select it when on
    // Pre-select VFX (icon-only: 'on' or 'off')
    const vfxBase = options.findIndex(o => o.group === 5);
    if (vfxBase >= 0) preSelected.set(5, vfxBase);  // single item
    // Pre-select opacity
    const opBase = options.findIndex(o => o.group === 6);
    const opIdx = opacityPresets.findIndex(o => Math.abs(o.value - this._penStyle.opacity) < 0.05);
    if (opIdx >= 0 && opBase >= 0) preSelected.set(6, opBase + opIdx);

    this._popDialog.open({
      anchorX: cx,
      anchorY: cy,
      anchor,
      options,
      maxStrokes: 0, // static — stays open until dismiss
      preSelected,
      pageWidth: this._layout.width,
      pageHeight: this._layout.height,
      onSelect: (group, option) => {
        if (group <= 2 || group === 6) {
          const style: Record<string, unknown> = {};
          if (group === 0) style.size = option.value as number;
          if (group === 1) style.style = option.value as string;
          if (group === 2) style.color = option.value as string;
          if (group === 6) style.opacity = option.value as number;
          if (Object.keys(style).length > 0) {
            this._dispatch({
              type: 'pen.style',
              style,
              origin: 'gesture',
              timestamp: Date.now(),
            });
          }
        } else if (group === 3) {
          // Text auto-recognition toggle (icon-only — cycles on/off)
          this._settings.autoLatex = !this._settings.autoLatex;
          option.value = this._settings.autoLatex ? 'on' : 'off';
          this._saveSettings();
        } else if (group === 4) {
          // Shape mode selector (Off / Hint / Full)
          this._settings.shapeMode = option.value as ShapeMode;
          this._saveSettings();
        } else if (group === 5) {
          // Visual FX toggle (icon-only — cycles on/off)
          this._settings.visualFx = !this._settings.visualFx;
          option.value = this._settings.visualFx ? 'on' : 'off';
          this._saveSettings();
        }
      },
      onResolve: () => { /* instant-apply via onSelect covers this */ },
      onDismiss: () => { /* nothing to do */ },
    });
  }

  /** Open a page color picker popup at the given position. */
  private _openPageColorPicker(cx: number, cy: number): void {
    const options: PopOption[] = [];
    for (const color of PAGE_PALETTE) {
      options.push({
        label: '',
        group: 0,
        value: color,
        display: 'swatch',
        swatchColor: color,
        swatchRadius: 8,
      });
    }
    const anchor = cx > (this._layout.width / 2) ? 'bottom-right' : 'bottom-left' as const;
    this._popDialog.open({
      anchorX: cx - this._layout.x,
      anchorY: cy - this._layout.y,
      anchor,
      options,
      maxStrokes: 1,
      onResolve: (selections) => {
        const pick = selections.get(0);
        if (pick) {
          const curPage = this._state.currentPage();
          if (curPage) {
            this._dispatch({
              type: 'page.update',
              pageId: curPage.id,
              color: pick.value as string,
              origin: 'gesture',
              timestamp: Date.now(),
            });
          }
        }
      },
      onDismiss: () => { /* nothing to do */ },
    });
  }

  // ── Text block formatting popup ─────────────────────────────────────────

  // ── Shape fit popup (evaluate invocation fallback) ────────────────────

  private static readonly _SHAPE_ICONS: Record<string, string> = {
    point: '●', line: '╱', arrow: '→', 'double-arrow': '↔', curve: '⌒',
    circle: '◯', ellipse: '⬭', rectangle: '▭', polygon: '⬡',
  };

  private _openShapeFitPopup(
    cx: number, cy: number,
    candidates: RecognisedShape[],
    strokeIds: string[],
    pageId: string,
  ): void {
    // Group 0: shape candidates
    const options: PopOption[] = candidates.map((shape, i) => ({
      label: `${shape.kind}  ${Math.round(shape.confidence * 100)}%`,
      group: 0,
      value: i,
      icon: CanvasApp._SHAPE_ICONS[shape.kind] ?? '?',
    }));

    // Group 1: line style options (icon-only row)
    options.push(
      { label: '—', group: 1, value: 'solid', display: 'icon-only' as const, icon: '—' },
      { label: '╌', group: 1, value: 'dashed', display: 'icon-only' as const, icon: '╌' },
      { label: '···', group: 1, value: 'dotted', display: 'icon-only' as const, icon: '···' },
    );

    // Group 2: width presets (icon-only row)
    options.push(
      { label: 'thin', group: 2, value: 0.8, display: 'icon-only' as const, icon: '╎' },
      { label: 'normal', group: 2, value: 1.5, display: 'icon-only' as const, icon: '┃' },
      { label: 'thick', group: 2, value: 3.0, display: 'icon-only' as const, icon: '█' },
    );

    // Group 3: edge type switching (only for line-like or curve shapes)
    const firstKind = candidates[0]?.kind;
    const isLineLike = firstKind === 'line' || firstKind === 'arrow' || firstKind === 'double-arrow' || firstKind === 'curve';
    if (isLineLike) {
      options.push(
        { label: 'line', group: 3, value: 'line', display: 'icon-only' as const, icon: '╱' },
        { label: 'arrow', group: 3, value: 'arrow', display: 'icon-only' as const, icon: '→' },
        { label: 'double', group: 3, value: 'double-arrow', display: 'icon-only' as const, icon: '↔' },
        { label: 'curve', group: 3, value: 'curve', display: 'icon-only' as const, icon: '⌒' },
      );
    }

    const anchor = cx > (this._layout.width / 2) ? 'top-right' : 'top-left' as const;
    this._popDialog.open({
      anchorX: cx,
      anchorY: cy,
      anchor,
      options,
      maxStrokes: 1,
      preSelected: new Map([
        [1, options.findIndex(o => o.group === 1 && o.value === 'solid')],
        [2, options.findIndex(o => o.group === 2 && o.value === 1.5)],
        ...(isLineLike ? [[3, options.findIndex(o => o.group === 3 && o.value === (firstKind ?? 'line'))] as [number, number]] : []),
      ]),
      onResolve: (selections) => {
        const pick = selections.get(0);
        if (!pick) return;
        const shape = candidates[pick.value as number];
        if (!shape) return;

        // Read line style from group 1 selection (default solid)
        const stylePick = selections.get(1);
        const lineStyle = (stylePick?.value as 'solid' | 'dashed' | 'dotted') ?? 'solid';

        // Read width from group 2 selection (default current pen size)
        const widthPick = selections.get(2);
        const penSize = (widthPick?.value as number) ?? this._penStyle.size;

        // Read edge type from group 3 selection (if line-like)
        const edgePick = selections.get(3);
        const edgeType = edgePick?.value as 'line' | 'arrow' | 'double-arrow' | 'curve' | undefined;

        const figId = `${pageId}.figure.${Date.now().toString(36)}`;
        const verts = shape.vertices.map((v, i) => ({ id: `v${i}`, x: v.x, y: v.y }));
        let edges = shape.edges.map((e, i) => ({
          id: `e${i}`,
          from: `v${e.from}`,
          to: `v${e.to}`,
          type: e.type,
          controlPoints: e.controlPoints,
        }));

        // Apply edge type override from popup
        let finalKind = shape.kind;
        if (edgeType) {
          if (edgeType === 'double-arrow') {
            const fwd = edges.map(e => ({ ...e, type: 'arrow' }));
            const rev = edges.map((e, i) => ({
              id: `e${i}r`, from: e.to, to: e.from, type: 'arrow' as const,
              controlPoints: e.controlPoints,
            }));
            edges = [...fwd, ...rev];
            finalKind = 'double-arrow';
          } else {
            edges = edges.map(e => ({ ...e, type: edgeType }));
            finalKind = edgeType === 'arrow' ? 'arrow' : edgeType === 'curve' ? 'curve' : 'line';
          }
        }

        // Save stroke data before erasing (for undo)
        const savedStrokes = strokeIds.map(sid => this._ink.getStrokes().get(sid)).filter(Boolean) as import('./ink_layer').InkStroke[];

        // Batch 6: kinematic envelope from the longest source stroke
        // (best fidelity — most samples to map onto the new geometry).
        const longestSource = savedStrokes.length > 0
          ? savedStrokes.reduce((best, s) => s.points.length > best.points.length ? s : best, savedStrokes[0])
          : undefined;
        const envelope = this._buildShapeEnvelope(shape, longestSource);

        const figAction = {
          type: 'figure.create' as const,
          blockId: figId,
          pageId,
          shapeKind: finalKind,
          vertices: verts,
          edges,
          bbox: shape.bbox,
          center: shape.center,
          rx: shape.rx,
          ry: shape.ry,
          penSize,
          lineStyle: lineStyle === 'solid' ? undefined : lineStyle,
          replacedStrokeIds: strokeIds,
          renderPoints: envelope?.renderPoints,
          inkStyle: envelope?.inkStyle,
          origin: 'gesture' as const,
          timestamp: Date.now(),
        } satisfies GrimoireAction;
        this._dispatch(figAction);

        // Erase all replaced strokes
        const eraseActions: GrimoireAction[] = [];
        for (const sid of strokeIds) {
          const ea: GrimoireAction = { type: 'ink.erase', pageId, target: sid, origin: 'gesture', timestamp: Date.now() };
          this._dispatch(ea);
          eraseActions.push(ea);
          this._ink.removeStroke(sid);
        }

        // Composite undo: delete figure + re-commit all saved strokes
        const reverse: GrimoireAction[] = [
          { type: 'block.delete', blockId: figId, pageId, origin: 'gesture', timestamp: Date.now() },
          ...savedStrokes.map(s => ({ type: 'ink.commit' as const, strokeId: s.id, pageId, points: s.points, style: s.style, origin: 'gesture' as const, timestamp: Date.now() })),
        ];
        this._pushUndo({ reverse, forward: [figAction, ...eraseActions] });
      },
      onDismiss: () => { /* cancelled — keep ink as-is */ },
    });
  }

  // ── Shape replacement helper (used by full-mode and hint-ring accept) ──

  /** Build the kinematic envelope + style snapshot for a freshly
   *  recognised shape. Re-maps the source stroke's pressure / tilt /
   *  timestamp samples onto the clean parametric geometry so the
   *  figure body renders through the ink layer with the same brush
   *  feel as the source stroke. Returns null when the shape kind
   *  isn't supported yet (curves with controlPoints are deferred —
   *  legacy block_renderer path handles them). */
  private _buildShapeEnvelope(
    shape: RecognisedShape,
    sourceStroke: import('./ink_layer').InkStroke | undefined,
  ): { renderPoints: import('@shared/notebook_model').StrokePoint[]; inkStyle: import('@shared/notebook_model').InkStyle } | null {
    if (!sourceStroke || sourceStroke.points.length < 2) return null;
    let sampler: ReturnType<typeof pathSamplerForEdges> = null;
    if ((shape.kind === 'circle' || shape.kind === 'ellipse') && shape.center && shape.rx !== undefined) {
      sampler = pathSamplerForEllipse(shape.center.x, shape.center.y, shape.rx, shape.ry ?? shape.rx);
    } else if (shape.edges && shape.edges.length > 0) {
      // Only line-bodied edges resample cleanly; curves with control
      // points need bezier sampling (deferred). Arrow / double-arrow
      // bodies are line-shaped — the head is rendered separately.
      const lineEdges = shape.edges.filter(e => e.type === 'line' || e.type === 'arrow');
      if (lineEdges.length > 0) {
        sampler = pathSamplerForEdges(shape.vertices, lineEdges);
      }
    }
    if (!sampler) return null;
    const renderPoints = resampleAlong(sourceStroke.points, sampler);
    if (renderPoints.length < 2) return null;
    return { renderPoints, inkStyle: { ...sourceStroke.style } };
  }

  private _replaceStrokeWithShape(
    shape: RecognisedShape,
    strokeId: string,
    pageId: string,
  ): void {
    // Save stroke data before erasing (for undo)
    const savedStroke = this._ink.getStrokes().get(strokeId);

    const figId = `${pageId}.figure.${Date.now().toString(36)}`;
    const verts = shape.vertices.map((v, i) => ({ id: `v${i}`, x: v.x, y: v.y }));
    const edges = shape.edges.map((e, i) => ({
      id: `e${i}`,
      from: `v${e.from}`,
      to: `v${e.to}`,
      type: e.type,
      controlPoints: e.controlPoints,
    }));
    // Batch 6: kinematic envelope + ink-style snapshot so the figure
    // body renders through the ink layer with the source stroke's
    // brush feel.
    const envelope = this._buildShapeEnvelope(shape, savedStroke);
    const figAction = {
      type: 'figure.create' as const,
      blockId: figId,
      pageId,
      shapeKind: shape.kind,
      vertices: verts,
      edges,
      bbox: shape.bbox,
      center: shape.center,
      rx: shape.rx,
      ry: shape.ry,
      penSize: this._penStyle.size,
      replacedStrokeIds: [strokeId],
      renderPoints: envelope?.renderPoints,
      inkStyle: envelope?.inkStyle,
      origin: 'gesture' as const,
      timestamp: Date.now(),
    } satisfies GrimoireAction;
    this._dispatch(figAction);
    const eraseAction: GrimoireAction = { type: 'ink.erase', pageId, target: strokeId, origin: 'gesture', timestamp: Date.now() };
    this._dispatch(eraseAction);
    this._ink.removeStroke(strokeId);

    // Composite undo: delete figure + re-commit stroke
    const reverse: GrimoireAction[] = [
      { type: 'block.delete', blockId: figId, pageId, origin: 'gesture', timestamp: Date.now() },
    ];
    if (savedStroke) {
      reverse.push({ type: 'ink.commit', strokeId: savedStroke.id, pageId, points: savedStroke.points, style: savedStroke.style, origin: 'gesture', timestamp: Date.now() });
    }
    this._pushUndo({ reverse, forward: [figAction, eraseAction] });
  }

  // ── Mid-line topology helper (Batch 3b) ────────────────────────────────
  //
  // Dispatch figure.split_edge if the snap result is a midpoint on a
  // line edge of an existing figure. No-op for vertex / centre snaps
  // (those are already first-class anchors) and for non-line edges
  // (curve/arrow splits need controlPoint-aware logic — deferred).
  private _splitEdgeIfMidpoint(sr: SnapResult | null, pageId: string): void {
    if (!sr || sr.target.kind !== 'midpoint') return;
    const t = sr.target;
    const page = this._state.currentPage();
    const block = page?.blocks.get(t.blockId);
    if (!block || block.kind !== 'figure') return;
    const fig = block as unknown as { edges: Array<{ type: string }> };
    const edge = fig.edges?.[t.sourceIndex];
    if (!edge || edge.type !== 'line') return;
    this._dispatch({
      type: 'figure.split_edge',
      blockId: t.blockId,
      pageId,
      edgeIndex: t.sourceIndex,
      x: t.x, y: t.y,
      origin: 'gesture',
      timestamp: Date.now(),
    });
    this._snap.invalidate();
  }

  // ── Dwell-point handler (Batch 3a) ─────────────────────────────────────
  //
  // The pen-up shape-recognition block calls this when classifyDwell()
  // returned a hit. Two outcomes depending on context:
  //
  //   - Dwell landed on an existing edge midpoint (via _snapStart):
  //     dispatch figure.split_edge to insert a real vertex on the
  //     source figure; the dwell ink is erased. The connection is now
  //     topological — moveVertex etc. can target it like any other.
  //
  //   - Dwell landed in empty space: create a fresh shapeKind='point'
  //     figure at the dwell centre and erase the stroke ink.
  //
  // In both cases the snap index is invalidated so the new vertex /
  // point becomes a target for subsequent strokes.
  private _handleDwellPoint(
    dwell: { x: number; y: number },
    strokeId: string,
    pageId: string,
  ): void {
    const savedStroke = this._ink.getStrokes().get(strokeId);
    const ts = Date.now();

    // 1. Mid-edge dwell — split the existing line.
    const startSnap = this._snapStart;
    if (startSnap && startSnap.target.kind === 'midpoint') {
      const t = startSnap.target;
      const page = this._state.currentPage();
      const block = page?.blocks.get(t.blockId);
      const fig = block && block.kind === 'figure' ? block as unknown as { edges: Array<{ type: string }> } : null;
      const edge = fig?.edges?.[t.sourceIndex];
      if (edge && edge.type === 'line') {
        const splitAction: GrimoireAction = {
          type: 'figure.split_edge', blockId: t.blockId, pageId,
          edgeIndex: t.sourceIndex, x: t.x, y: t.y,
          origin: 'gesture', timestamp: ts,
        };
        const eraseAction: GrimoireAction = {
          type: 'ink.erase', pageId, target: strokeId,
          origin: 'gesture', timestamp: ts,
        };
        this._dispatch(splitAction);
        this._dispatch(eraseAction);
        this._ink.removeStroke(strokeId);
        this._snap.invalidate();
        // Undo: just restore the stroke. Reversing a split_edge
        // cleanly is part of batch 3 deferred work — for now a
        // user-visible undo restores the dwell ink and leaves the
        // (topologically harmless) extra vertex behind.
        if (savedStroke) {
          this._pushUndo({
            reverse: [{
              type: 'ink.commit', strokeId: savedStroke.id, pageId,
              points: savedStroke.points, style: savedStroke.style,
              origin: 'gesture', timestamp: ts,
            }],
            forward: [splitAction, eraseAction],
          });
        }
        return;
      }
    }

    // 2. Free-space dwell — create a point figure at the centre.
    const figId = `${pageId}.figure.${ts.toString(36)}`;
    const r = 2.5;
    const figAction: GrimoireAction = {
      type: 'figure.create',
      blockId: figId,
      pageId,
      shapeKind: 'point',
      vertices: [{ id: 'v0', x: dwell.x, y: dwell.y }],
      edges: [],
      bbox: { x: dwell.x - r, y: dwell.y - r, width: r * 2, height: r * 2 },
      center: { x: dwell.x, y: dwell.y },
      penSize: this._penStyle.size,
      replacedStrokeIds: [strokeId],
      origin: 'gesture',
      timestamp: ts,
    };
    const eraseAction: GrimoireAction = {
      type: 'ink.erase', pageId, target: strokeId,
      origin: 'gesture', timestamp: ts,
    };
    this._dispatch(figAction);
    this._dispatch(eraseAction);
    this._ink.removeStroke(strokeId);
    this._snap.invalidate();

    const reverse: GrimoireAction[] = [
      { type: 'block.delete', blockId: figId, pageId, origin: 'gesture', timestamp: ts },
    ];
    if (savedStroke) {
      reverse.push({
        type: 'ink.commit', strokeId: savedStroke.id, pageId,
        points: savedStroke.points, style: savedStroke.style,
        origin: 'gesture', timestamp: ts,
      });
    }
    this._pushUndo({ reverse, forward: [figAction, eraseAction] });
  }

  // ── Shape hint tap rings ───────────────────────────────────────────────
  //
  // Spawns a one-shot tap ring at the shape's natural anchor point. Tap
  // to accept → replace ink; timeout → keep ink as-is. The map allows
  // multiple concurrent hints (cast-mode scan can spawn one per nearby
  // analyzable stroke); each ring carries a unique patternId of the
  // form `__shape_hint__:<strokeId>` so confirmOneShotTap returns the
  // right one to look up.

  private _shapeHintPending = new Map<string, {
    shape: RecognisedShape;
    strokeId: string;
    pageId: string;
    birth: number;
  }>();

  // ── Stylus side-button shortcut wiring ──────────────────────────────
  //
  // The binding engine (stylus_bindings.ts) only sees the three
  // triggers Windows actually delivers — draw-while-pressed, press-
  // while-drawing, press-and-tap — and resolves them through a single
  // bm-bitmask state machine driven from the canvas pointer handlers.
  // The canvas dispatches resolved actions through _executeStylusAction.
  // Audio + visual feedback are uniform across actions: a single drum
  // hit + the user's chosen tap-effect ring at the action point.

  /** Visual + audio feedback for an activated shortcut. Reuses the
   *  existing drum kick and tap-effect ring so we don't add audio
   *  engines. The ring tint is the cast climax purple so shortcuts
   *  read as "magic moment" — distinct from neutral taps. */
  private _flashStylusFeedback(x: number, y: number): void {
    // Single id for the down/release pair — two performance.now()
    // calls would yield different sub-millisecond strings and
    // tapRelease would early-return, leaving the body oscillator
    // ringing at sustain for the full 5-second safety stop (a long
    // muffled hum after every shortcut).
    const id = `stylus-shortcut-${performance.now()}`;
    audioEngine.drum()?.tapDown(id, { pressure: 0.8 });
    audioEngine.drum()?.tapRelease(id, 60);
    // Stylus shortcuts always use a ring flash (not the user's
    // configured tap-effect kind) — visually distinct from a normal
    // tap so the user can tell at a glance which one fired. Bypasses
    // _pushTapEffect for that reason; _tapFx.push is already a no-op
    // when animations are globally disabled.
    const L = this._layout;
    this._tapFx.push('ring', x + L.x, y + L.y, INVOKE_GLOW_CAST_COLOR, 1.4);
  }

  /** Action dispatcher. Some actions reuse existing pipelines (menu,
   *  cast, tool swap); others are stubbed with a debug toast so the
   *  binding catalogue is exercised end-to-end. Stubs land as the
   *  feature catalogue grows. */
  private _executeStylusAction(action: StylusActionId, x: number, y: number): void {
    this._flashStylusFeedback(x, y);
    switch (action) {
      case 'open-stylus-menu':
        this._toggleStylusSidebar(x, y);
        break;
      case 'invoke-cast':
        if (!this._isWandInvoked) this._invokeWand(x, y);
        break;
      case 'tool-swap':
        this._swapRecentTools();
        break;
      case 'select':
        this._flashDebug(x, y, '#80a0d0', 'Select (coming soon)');
        break;
      case 'draw-line':
        this._flashDebug(x, y, '#80a0d0', 'Draw line (coming soon)');
        break;
      case 'draw-circle':
        this._flashDebug(x, y, '#80a0d0', 'Draw circle (coming soon)');
        break;
      case 'draw-airbrush':
        this._flashDebug(x, y, '#80a0d0', 'Airbrush (coming soon)');
        break;
      case 'noop':
        break;
    }
  }

  /** Toggle between the two most recently used ink tools. The list is
   *  maintained whenever a tool is set elsewhere (stylus menu / state
   *  restore); 'tool-swap' is a fast way to bounce between e.g. ink
   *  and eraser. Routes through the ToolRegistry so the previous
   *  tool's saved params (its own colour / size / opacity) come
   *  back, not the active tool's colour stuck onto a different
   *  tool id. */
  private _swapRecentTools(): void {
    if (this._recentTools.length < 2) return;
    const [current, prev] = this._recentTools;
    this._recentTools = [prev, current];
    this._toolRegistry.pick(prev as PenStyle);
    // onChange already mirrors into _penStyle; persist the active-id
    // change so reload comes back on `prev`.
    this._settings.tools = this._toolRegistry.serialize();
    this._saveSettings();
  }

  /** Note a tool change (called from settings / stylus menu / state
   *  load). Keeps a 2-deep stack so tool-swap has something to bounce
   *  between. */
  private _noteToolChange(tool: string): void {
    if (this._recentTools[0] === tool) return;
    this._recentTools.unshift(tool);
    if (this._recentTools.length > 2) this._recentTools.length = 2;
  }

  /** Mirror the active tool's params into _penStyle. Called by the
   *  registry's onChange hook AND directly from settings hydration.
   *  Keeps the rest of the canvas reading from _penStyle exactly as
   *  it does today; the registry stays the source of truth. */
  private _mirrorActiveToolToPenStyle(): void {
    const t = this._toolRegistry.active();
    this._penStyle.style   = t.id;
    this._penStyle.color   = t.params.color;
    this._penStyle.size    = t.params.size;
    this._penStyle.opacity = t.params.opacity;
  }

  /** Dispatch the action behind a one-shot ring resolve. Called from
   *  both pen-down (touch-to-trigger) and pen-up (tap-to-trigger). */
  private _dispatchOneShotResolve(patternId: string, x: number, y: number): void {
    if (patternId === 'stylus-summon') {
      this._toggleStylusSidebar(x, y);
    } else if (patternId === 'analyse-knock') {
      this._flashDebug(x, y, '#00e5ff', 'ANALYSE-KNOCK fired');
      this._cancelWand();
      this._analyseRegionForInsert(x, y);
    } else if (patternId.startsWith('__shape_hint__:') && this._shapeHintPending.has(patternId)) {
      const h = this._shapeHintPending.get(patternId)!;
      this._shapeHintPending.delete(patternId);
      this._replaceStrokeWithShape(h.shape, h.strokeId, h.pageId);
    } else if (patternId === '__text_hint__') {
      this._textCompose.confirmHint();
    }
  }

  private _spawnShapeHintRing(
    shape: RecognisedShape,
    strokeId: string,
    pageId: string,
    windowMs: number = 1500,
  ): void {
    // Anchor: center for closed shapes, endpoint for open, center for point
    let hx: number, hy: number;
    if (shape.center) {
      hx = shape.center.x;
      hy = shape.center.y;
    } else if (shape.kind === 'line' || shape.kind === 'arrow' || shape.kind === 'double-arrow') {
      const first = shape.vertices[0];
      hx = first.x;
      hy = first.y;
    } else {
      hx = shape.bbox.x + shape.bbox.width / 2;
      hy = shape.bbox.y + shape.bbox.height / 2;
    }

    // Per-stroke unique pattern id so multiple hints can coexist.
    const patternId = `__shape_hint__:${strokeId}`;
    this._shapeHintPending.set(patternId, { shape, strokeId, pageId, birth: performance.now() });

    // Hint rings now use the active pen colour so they stand out
    // against parchment and visibly relate to the user's drawing
    // tool, instead of the previous near-parchment cream that was
    // hard to tap.
    this._tapRingLayer.deployOneShot(
      hx, hy,
      this._penStyle.color,
      windowMs,
      patternId,
      HINT_RING_RADIUS,
    );
  }

  /** Render the snap crosshair at the target location. The "live"
   *  indicator (the one tracking the current stroke tip) gets a
   *  brighter highlight so the link intent is visible at a glance;
   *  the snap-start anchor stays subtle so it doesn't distract. */
  private _drawSnapIndicator(
    ctx: CanvasRenderingContext2D,
    L: import('./page_renderer').PageLayout,
    snapOverride?: SnapResult,
  ): void {
    const sr = snapOverride ?? this._snapIndicator;
    if (!sr) return;
    const isLive = !snapOverride;     // override = start-snap (subtle)
    const t = sr.target;
    const px = L.x + t.x;
    const py = L.y + t.y;
    const r = isLive ? 8 : 6;

    ctx.save();
    if (isLive) {
      // Live link — bright cast-blue tint, full opacity. Easy to see
      // at the moment of hooking, fades out the second the stroke
      // moves away (the indicator is cleared by the snap manager).
      ctx.globalAlpha = 0.92;
      ctx.strokeStyle = '#3a8cd0';
      ctx.lineWidth = 1.6;
    } else {
      const alpha = 0.45 * (1 - sr.distance / 16);
      ctx.globalAlpha = Math.max(0.15, alpha);
      ctx.strokeStyle = '#5a3820';
      ctx.lineWidth = 0.8;
    }

    // Crosshair
    ctx.beginPath();
    ctx.moveTo(px - r, py);
    ctx.lineTo(px + r, py);
    ctx.moveTo(px, py - r);
    ctx.lineTo(px, py + r);
    ctx.stroke();

    // Small circle
    ctx.beginPath();
    ctx.arc(px, py, isLive ? 4 : 3, 0, Math.PI * 2);
    ctx.stroke();

    if (isLive) {
      // Soft halo ring outside the crosshair — telegraphs "this is
      // where the line is going to attach" without obscuring the
      // target itself.
      ctx.globalAlpha = 0.25;
      ctx.beginPath();
      ctx.arc(px, py, 12, 0, Math.PI * 2);
      ctx.stroke();
    }

    ctx.restore();
  }

  /** Real-time eraser stamp at (cx, cy) with the given outer radius.
   *  Opacity acts as the edge-smoothing knob — full opacity hard-
   *  cuts at r; lower opacity shrinks the hard-cut radius and ramps
   *  the surviving points' pressure from 0 (at the hard edge) to 1
   *  (at the outer edge), so the cut fades smoothly rather than
   *  snapping to a circle.
   *
   *  For every stroke whose any point is inside the disc, clip at
   *  the hard radius, dispatch ink.erase for the original, soften
   *  the outside runs, and re-commit each as a fresh stroke (new
   *  id so subsequent eraser stamps see the shorter geometry).
   *  Undo restores the original. */
  private _eraseAtCircle(cx: number, cy: number, r: number): void {
    const pageId = this._state.currentPageId;
    if (!pageId) return;
    const allCandidates = this._ink.strokesInRadius(cx, cy, r);
    if (allCandidates.length === 0) return;

    // Lock-respecting filter — locked strokes ignore the eraser entirely.
    const strokeMap = this._ink.getStrokes();
    const candidateIds = allCandidates.filter(id => {
      const s = strokeMap.get(id) as (import('@shared/notebook_model').InkStroke & { locked?: boolean }) | undefined;
      return !s?.locked;
    });
    if (candidateIds.length === 0) return;

    // Eraser opacity → smoothing. opacity=1 ⇒ hard cut at r (no
    // smooth zone); opacity=0.5 ⇒ hard cut at r/2 with smooth
    // gradient out to r; opacity=0 ⇒ no hard cut, all surviving
    // points within r get their pressure scaled toward zero.
    const opacity = Math.max(0, Math.min(1, this._toolRegistry.active().params.opacity));
    const rHard  = r * opacity;
    const rOuter = r;

    const ts = Date.now();
    const r2 = r * r;
    for (const id of candidateIds) {
      const saved = this._ink.getStrokes().get(id);
      if (!saved) continue;
      // Confirm at least one point is actually inside the outer
      // disc — bbox hit doesn't guarantee an interior sample.
      let anyInside = false;
      for (const p of saved.points) {
        const dx = p.x - cx, dy = p.y - cy;
        if (dx * dx + dy * dy <= r2) { anyInside = true; break; }
      }
      if (!anyInside) continue;

      const { outside } = clipPolylineAtCircle(saved.points, cx, cy, rHard);
      const softened = softenOutsideRuns(outside, cx, cy, rHard, rOuter);

      const eraseAction: GrimoireAction = {
        type: 'ink.erase', pageId, target: id,
        origin: 'gesture', timestamp: ts,
      };
      this._dispatch(eraseAction);

      const commitActions: GrimoireAction[] = [];
      for (let i = 0; i < softened.length; i++) {
        const seg = softened[i];
        if (seg.length < 2) continue;
        const newId = `${id}.e${ts.toString(36)}.${i.toString(36)}`;
        const commitAction: GrimoireAction = {
          type: 'ink.commit', strokeId: newId, pageId,
          points: seg, style: saved.style,
          origin: 'gesture', timestamp: ts,
        };
        this._dispatch(commitAction);
        commitActions.push(commitAction);
      }

      this._pushUndo({
        reverse: [
          { type: 'ink.commit', strokeId: saved.id, pageId,
            points: saved.points, style: saved.style,
            origin: 'gesture', timestamp: ts } as GrimoireAction,
          ...commitActions.map(c => ({
            type: 'ink.erase', pageId, target: (c as { strokeId: string }).strokeId,
            origin: 'gesture', timestamp: ts,
          } as GrimoireAction)),
        ],
        forward: [eraseAction, ...commitActions],
      });
    }
  }

  /** Eraser ghost render — one stroked path per drag, sized + edged
   *  to match the actual erase footprint:
   *    line width = 2 × (size × opacity)   ← the hard-cut diameter
   *    shadow blur = size × (1 − opacity)  ← the soft-edge halo
   *  so the visual extent is always 2 × size (= the outer eraser
   *  radius) and the hard / soft balance tracks the opacity knob.
   *  Active drags render at peak alpha; pen-up flips fadeStart and
   *  the ghost rolls off with an ease-out, then drops. Single
   *  ctx.stroke() per ghost — no transparency pile-up. */
  private _drawEraserTrail(ctx: CanvasRenderingContext2D): void {
    if (this._eraserGhosts.length === 0) return;
    const now = performance.now();
    this._eraserGhosts = this._eraserGhosts.filter(g => {
      if (g.fadeStart === null) return true;
      return now - g.fadeStart < ERASER_TRAIL_FADE_MS;
    });
    if (this._eraserGhosts.length === 0) return;

    ctx.save();
    ctx.strokeStyle = '#3a2010';
    ctx.fillStyle = '#3a2010';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.shadowColor = '#3a2010';

    for (const g of this._eraserGhosts) {
      let alpha = ERASER_TRAIL_PEAK_ALPHA;
      if (g.fadeStart !== null) {
        const t = (now - g.fadeStart) / ERASER_TRAIL_FADE_MS;
        alpha *= Math.max(0, Math.pow(1 - t, 1.6));
      }
      if (alpha <= 0.01) continue;
      const rHard = g.size * g.opacity;
      // Min line width keeps the hard core visible at very-low
      // opacities so the user still sees the trail, even when the
      // erase itself is mostly the soft fade band.
      const lineWidth = Math.max(2, rHard * 2);
      const blur = g.size - rHard;       // = size × (1 − opacity)
      ctx.globalAlpha = alpha;
      ctx.lineWidth = lineWidth;
      ctx.shadowBlur = blur;

      const pts = g.points;
      if (pts.length === 1) {
        // Single sample — disc with the same hard-radius + halo as
        // the swept stroke would have. Matches the eraser footprint
        // immediately on pen-down.
        ctx.beginPath();
        ctx.arc(pts[0].x, pts[0].y, Math.max(1, rHard), 0, Math.PI * 2);
        ctx.fill();
        continue;
      }
      // Smooth path through midpoints — quadratic chain reads
      // smoother than a sharp polyline at any width.
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length - 1; i++) {
        const a = pts[i];
        const b = pts[i + 1];
        const mx = (a.x + b.x) / 2;
        const my = (a.y + b.y) / 2;
        ctx.quadraticCurveTo(a.x, a.y, mx, my);
      }
      const last = pts[pts.length - 1];
      ctx.lineTo(last.x, last.y);
      ctx.stroke();
    }
    ctx.restore();
  }

  /** Hover-link hint: when the pen hovers near a snap target, draw
   *  a thin square around THAT target only — telegraphs the link
   *  that would form if the user starts drawing here. No anchor
   *  fading, no ambient meta-layer reveal: anchors stay invisible
   *  unless they're about to be hooked. One findNearest() per frame
   *  during hover (same single-pass scan the active-stroke snap
   *  uses), no extra cost.
   *
   *  Skipped during active strokes — the live snap crosshair takes
   *  over there. */
  private _drawHoverSnapHint(
    ctx: CanvasRenderingContext2D,
    L: import('./page_renderer').PageLayout,
  ): void {
    if (this._activeStrokeId) return;
    if (!this._pointerPresent) return;
    if (this._lastPointerType !== 'pen') return;
    const page = this._state.currentPage();
    if (!page) return;

    const sr = this._snap.findNearest(
      this._lastPointerX, this._lastPointerY,
      page.blocks as unknown as Map<string, { kind: string } & Record<string, unknown>>,
    );
    if (!sr) return;

    const px = L.x + sr.target.x;
    const py = L.y + sr.target.y;
    const HALF = 6;

    ctx.save();
    ctx.strokeStyle = '#5a3820';
    ctx.lineWidth = 0.9;
    ctx.globalAlpha = 0.55;
    ctx.strokeRect(px - HALF, py - HALF, HALF * 2, HALF * 2);
    ctx.restore();
  }

  private _drawShapeGhost(
    ctx: CanvasRenderingContext2D,
    L: import('./page_renderer').PageLayout,
    _t: number,
  ): void {
    if (this._shapeHintPending.size === 0) return;
    const now = performance.now();
    const WINDOW = 1500;
    const expired: string[] = [];

    ctx.save();
    ctx.translate(L.x, L.y);

    for (const [patternId, h] of this._shapeHintPending) {
      const elapsed = now - h.birth;
      if (elapsed > WINDOW) { expired.push(patternId); continue; }
      const alpha = 0.55 * (1 - elapsed / WINDOW);
      const shape = h.shape;
      ctx.save();
      // Same hint colour family as the rings + astral text — the
      // active pen colour, semi-transparent — so the suggested shape
      // visibly relates to the user's drawing tool and stands out
      // against parchment instead of dissolving into it.
      const hintColor = this._penStyle.color;
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = hintColor;
      ctx.fillStyle = hintColor;
      ctx.lineWidth = 1.6;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.shadowColor = hintColor;
      ctx.shadowBlur = 6;

      if (shape.kind === 'point' && shape.center) {
        ctx.beginPath();
        ctx.arc(shape.center.x, shape.center.y, 2, 0, Math.PI * 2);
        ctx.fill();
      } else if ((shape.kind === 'circle' || shape.kind === 'ellipse') && shape.center) {
        ctx.beginPath();
        ctx.ellipse(shape.center.x, shape.center.y, shape.rx ?? 1, shape.ry ?? shape.rx ?? 1, 0, 0, Math.PI * 2);
        ctx.stroke();
      } else {
        for (const edge of shape.edges) {
          const a = shape.vertices[edge.from];
          const b = shape.vertices[edge.to];
          if (!a || !b) continue;
          ctx.beginPath();
          if (edge.controlPoints && edge.controlPoints.length >= 1) {
            ctx.moveTo(a.x, a.y);
            const cp = edge.controlPoints;
            if (cp.length === 1) ctx.quadraticCurveTo(cp[0].x, cp[0].y, b.x, b.y);
            else ctx.bezierCurveTo(cp[0].x, cp[0].y, cp[1].x, cp[1].y, b.x, b.y);
          } else {
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
          }
          ctx.stroke();

          if (edge.type === 'arrow') {
            let angle: number;
            if (edge.controlPoints && edge.controlPoints.length >= 1) {
              const lastCp = edge.controlPoints[edge.controlPoints.length - 1];
              angle = Math.atan2(b.y - lastCp.y, b.x - lastCp.x);
            } else {
              angle = Math.atan2(b.y - a.y, b.x - a.x);
            }
            ctx.beginPath();
            ctx.moveTo(b.x, b.y);
            ctx.lineTo(b.x - 6 * Math.cos(angle - 0.4), b.y - 6 * Math.sin(angle - 0.4));
            ctx.lineTo(b.x - 6 * Math.cos(angle + 0.4), b.y - 6 * Math.sin(angle + 0.4));
            ctx.closePath();
            ctx.fill();
          }
        }
        for (const v of shape.vertices) {
          ctx.beginPath();
          ctx.arc(v.x, v.y, 1.5, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.restore();
    }

    ctx.restore();
    for (const id of expired) this._shapeHintPending.delete(id);
  }

  private _openTextFormatPopup(
    block: import('./notebook_state').WvTextBlock,
    pageId: string,
    cx: number, cy: number,
  ): void {
    const anchor = cx > (this._layout.width / 2) ? 'bottom-right' : 'bottom-left' as const;
    const options: PopOption[] = [];
    const style = block.textStyle ?? {};

    // ── Row 0: Font sizes + Bold / Italic / Underline ─────────────────
    const sizes = [10, 12, 14, 18, 24];
    for (const s of sizes) {
      options.push({ label: `${s}`, group: 0, value: s, display: 'swatch', swatchColor: '#5a3820', swatchRadius: 0, row: 0 });
    }

    options.push({
      label: '', group: 2, value: 'styleToggles',
      display: 'inline-toggles', row: 0,
      subToggles: [
        { label: 'B', key: 'bold', toggled: !!style.bold },
        { label: 'I', key: 'italic', toggled: !!style.italic },
        { label: 'U', key: 'underline', toggled: !!style.underline },
      ],
    });

    // ── Row 1: Font family ─────────────────────────────────────────────
    const fonts = [
      { label: 'Serif', value: 'Georgia, serif' },
      { label: 'Sans', value: 'Helvetica, Arial, sans-serif' },
      { label: 'Mono', value: '"Courier New", monospace' },
      { label: 'Cursive', value: '"Segoe Script", cursive' },
    ];
    for (const f of fonts) {
      options.push({ label: f.label, group: 1, value: f.value, display: 'swatch', swatchColor: '#5a3820', swatchRadius: 0, row: 1 });
    }

    // ── Row 2: Text color + Alignment ──────────────────────────────────
    const textColors = ['#3a2010', '#222222', '#1a3d5c', '#5c1a1a', '#1a4c1a', '#6b3fa0'];
    for (const c of textColors) {
      options.push({ label: '', group: 3, value: c, display: 'swatch', swatchColor: c, swatchRadius: 5, row: 2 });
    }

    const aligns: Array<{ label: string; icon: string; value: string }> = [
      { label: 'Left', icon: '⫷', value: 'left' },
      { label: 'Center', icon: '⫸', value: 'center' },
      { label: 'Right', icon: '⫹', value: 'right' },
    ];
    for (const a of aligns) {
      options.push({ label: a.label, group: 4, value: a.value, display: 'icon-only', icon: a.icon, row: 2 });
    }

    // Pre-select current state
    const preSelected = new Map<number, number>();
    const sizeBase = 0;
    const sizeIdx = sizes.indexOf(block.fontSize);
    if (sizeIdx >= 0) preSelected.set(0, sizeBase + sizeIdx);
    const fontBase = options.findIndex(o => o.group === 1);
    const curFont = style.fontFamily ?? 'Georgia, serif';
    const fontIdx = fonts.findIndex(f => f.value === curFont);
    if (fontIdx >= 0 && fontBase >= 0) preSelected.set(1, fontBase + fontIdx);
    const colorBase = options.findIndex(o => o.group === 3);
    const colorIdx = textColors.indexOf(style.color ?? '#3a2010');
    if (colorIdx >= 0 && colorBase >= 0) preSelected.set(3, colorBase + colorIdx);

    this._popDialog.open({
      anchorX: cx, anchorY: cy, anchor, options,
      maxStrokes: 0, preSelected,
      pageWidth: this._layout.width, pageHeight: this._layout.height,
      onSelect: (group, option) => {
        const upd: Record<string, unknown> = {};
        if (group === 0) upd.fontSize = option.value;
        if (group === 1) upd.fontFamily = option.value;
        if (group === 2 && option.subToggles) {
          for (const sub of option.subToggles) upd[sub.key] = sub.toggled;
        }
        if (group === 3) upd.color = option.value;
        if (group === 4) upd.align = option.value;
        if (Object.keys(upd).length > 0) {
          this._dispatch({
            type: 'text.style', blockId: block.id, pageId,
            style: upd, origin: 'gesture', timestamp: Date.now(),
          });
        }
      },
      onResolve: () => { /* instant-apply */ },
      onDismiss: () => { /* nothing */ },
    });
  }

  // ── Codex page turning helpers ──────────────────────────────────────────

  private _codexTurnPage(direction: 'forward' | 'back'): void {
    const total = this._codex.totalPageCount;
    if (total === 0) { return; }
    if (direction === 'forward' && this._groupPageIndex < total - 1) {
      this._groupPageIndex++;
    } else if (direction === 'back' && this._groupPageIndex > 0) {
      this._groupPageIndex--;
    }
    this._updatePolicy();
  }

  // ── Special view renderers ──────────────────────────────────────────────

  private _drawSettingsView(ctx: CanvasRenderingContext2D, L: import('./page_renderer').PageLayout, _t: number): void {
    const leftX = L.x + L.marginLeft + 20;
    const contentW = L.width - L.marginLeft - L.marginRight - 40;
    let y = L.y + L.marginTop + 20;
    const items: WidgetItem[] = [];

    // ── Title ──────────────────────────────────────────────────────────
    ctx.fillStyle = '#3a2010';
    ctx.font = 'italic bold 18px serif';
    ctx.textAlign = 'center';
    ctx.globalAlpha = 0.7;
    ctx.fillText('Grimoire Settings', L.x + L.width / 2, y);
    y += 44;

    // Helper: draw a section heading
    const drawHeading = (text: string) => {
      ctx.font = 'bold 13px Georgia, serif';
      ctx.fillStyle = '#5a3820';
      ctx.textAlign = 'left';
      ctx.globalAlpha = 0.65;
      ctx.fillText(text, leftX, y);
      // Ruling line
      ctx.globalAlpha = 0.1;
      ctx.strokeStyle = '#5a3820';
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(leftX, y + 6);
      ctx.lineTo(leftX + contentW, y + 6);
      ctx.stroke();
      y += 24;
    };

    // Helper: draw a toggle row (checkbox + label) — page-local coords
    const drawToggle = (label: string, value: boolean, group: number, itemValue: string) => {
      const bx = leftX + 12;
      const by = y - 6;
      const cx = bx + 6;
      const cy = y;
      const r = 8;

      // Checkbox box
      ctx.strokeStyle = '#5a3820';
      ctx.lineWidth = 0.8;
      ctx.globalAlpha = 0.6;
      ctx.strokeRect(bx, by, 12, 12);
      if (value) {
        ctx.strokeStyle = '#4a1a0a';
        ctx.lineWidth = 1.5;
        ctx.globalAlpha = 0.85;
        ctx.beginPath();
        ctx.moveTo(bx + 2, by + 6);
        ctx.lineTo(bx + 5, by + 10);
        ctx.lineTo(bx + 10, by + 2);
        ctx.stroke();
      }
      // Label
      ctx.font = 'italic 12px Georgia, serif';
      ctx.fillStyle = '#3a2010';
      ctx.textAlign = 'left';
      ctx.globalAlpha = 0.8;
      ctx.fillText(label, bx + 18, y + 1);

      // Selection item (page-local)
      items.push({ cx: cx - L.x, cy: cy - L.y, r, group, value: itemValue, kind: 'checkbox' });
      y += 26;
    };

    // Helper: draw a choice row (horizontal options with swatch circles)
    const drawChoiceRow = (
      label: string,
      options: Array<{ label: string; value: string; color?: string; radius?: number }>,
      currentValue: string | number,
      group: number,
    ) => {
      ctx.font = 'italic 12px Georgia, serif';
      ctx.fillStyle = '#5a3820';
      ctx.textAlign = 'left';
      ctx.globalAlpha = 0.65;
      ctx.fillText(label, leftX + 8, y + 1);
      y += 22;

      const startX = leftX + 20;
      const spacing = Math.min(50, (contentW - 40) / options.length);
      for (let i = 0; i < options.length; i++) {
        const opt = options[i];
        const ox = startX + i * spacing;
        const oy = y;
        const isActive = String(opt.value) === String(currentValue);
        const r = opt.radius ?? 8;

        if (opt.color) {
          // Color swatch
          ctx.globalAlpha = isActive ? 0.9 : 0.5;
          ctx.fillStyle = opt.color;
          ctx.beginPath();
          ctx.arc(ox, oy, isActive ? r + 1 : r, 0, Math.PI * 2);
          ctx.fill();
        } else {
          // Text option: draw a subtle circle + label
          ctx.globalAlpha = isActive ? 0.25 : 0.08;
          ctx.fillStyle = '#5a3820';
          ctx.beginPath();
          ctx.arc(ox, oy, r + 2, 0, Math.PI * 2);
          ctx.fill();

          ctx.globalAlpha = isActive ? 0.9 : 0.55;
          ctx.fillStyle = '#3a2010';
          ctx.font = '10px Georgia, serif';
          ctx.textAlign = 'center';
          ctx.fillText(opt.label, ox, oy + 4);
        }

        items.push({
          cx: ox - L.x, cy: oy - L.y, r: r + 4, group, value: opt.value,
          kind: opt.color ? 'swatch' : 'button',
        });
      }
      y += 28;
    };

    // ── Page tabs (4 logical groupings — Vault gets its own so it never
    // overflows the canvas, and other groups stay readable). The strip
    // mimics the journal's side-tab aesthetic at a smaller scale until a
    // proper synthetic Settings section replaces this layer. ────────────
    {
      const pageTabs = [
        { label: 'Hints',     value: 0 },
        { label: 'Visuals',   value: 1 },
        { label: 'Shortcuts', value: 2 },
        { label: 'Slim Pen',  value: 3 },
        { label: 'Vault',     value: 4 },
      ];
      ctx.font = 'italic bold 12px Georgia, serif';
      ctx.textAlign = 'center';
      const tabWidth = 64;
      const tabSpacing = 8;
      const totalTabsW = pageTabs.length * tabWidth + (pageTabs.length - 1) * tabSpacing;
      const tabsStartX = L.x + (L.width - totalTabsW) / 2;
      for (let i = 0; i < pageTabs.length; i++) {
        const tab = pageTabs[i];
        const tx = tabsStartX + i * (tabWidth + tabSpacing) + tabWidth / 2;
        const isActive = this._settingsPage === tab.value;
        ctx.globalAlpha = isActive ? 0.9 : 0.45;
        ctx.fillStyle = '#3a2010';
        ctx.fillText(tab.label, tx, y);
        if (isActive) {
          ctx.globalAlpha = 0.8;
          ctx.strokeStyle = '#3a2010';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(tx - tabWidth / 2 + 10, y + 5);
          ctx.lineTo(tx + tabWidth / 2 - 10, y + 5);
          ctx.stroke();
        }
        items.push({ cx: tx - L.x, cy: y - L.y, r: 16, group: 14, value: String(tab.value), kind: 'button' });
      }
      y += 24;
    }

    if (this._settingsPage === 0) {
      // ─────────────────────────── Hints & Spells ──────────────────────

      drawHeading('Spell Detection');
      drawChoiceRow('Sensitivity', [
        { label: 'Low', value: 'low' },
        { label: 'Med', value: 'medium' },
        { label: 'High', value: 'high' },
      ], this._settings.spellSensitivity, 3);
      y += 8;

      drawHeading('Hints');
      drawChoiceRow('Shape detect', [
        { label: 'Off',  value: 'off' },
        { label: 'Hint', value: 'hint' },
        { label: 'Full', value: 'full' },
      ], this._settings.shapeMode, 9);
      y += 8;

      drawHeading('Automation');
      drawToggle('Auto-recognize LaTeX', this._settings.autoLatex, 0, 'autoLatex');
      drawToggle('Auto-detect arrows', this._settings.autoArrows, 1, 'autoArrows');

    } else if (this._settingsPage === 1) {
      // ─────────────────────────── Visuals & Debug ─────────────────────

      drawHeading('Visuals');
      drawChoiceRow('Page Grain', [
        { label: 'Off',    value: '0' },
        { label: 'Subtle', value: '0.5' },
        { label: 'Medium', value: '1' },
        { label: 'Heavy',  value: '2' },
      ], String(this._settings.grainIntensity), 8);
      drawToggle('Visual FX', this._settings.visualFx, 10, 'visualFx');
      drawToggle('Animations', this._settings.animations, 15, 'animations');
      drawChoiceRow('Tap effect', [
        { label: 'Glow',  value: 'glow' },
        { label: 'Ring',  value: 'ring' },
        { label: 'Burst', value: 'burst' },
        { label: 'Off',   value: 'off' },
      ], this._settings.tapEffect, 16);
      y += 8;

      drawHeading('Feedback');
      drawToggle('Tap sound', this._settings.tapSound, 2, 'tapSound');
      y += 8;

      drawHeading('Debug');
      drawToggle('Debug overlay', this._settings.debugOverlay, 7, 'debugOverlay');

    } else if (this._settingsPage === 2) {
      // ─────────────────────────── Shortcuts ───────────────────────────

      drawHeading('Stylus side button');
      ctx.font = 'italic 11px Georgia, serif';
      ctx.fillStyle = '#5a3820'; ctx.globalAlpha = 0.6; ctx.textAlign = 'left';
      ctx.fillText('Three triggers — only the combinations Windows', leftX + 12, y);
      y += 12;
      ctx.fillText('actually delivers (side button alone is not exposed).', leftX + 12, y);
      y += 16;
      ctx.globalAlpha = 1;

      // Group ids: 30 / 31 / 32 — one per trigger.
      const TRIGGER_GROUPS: Array<{ trigger: StylusTrigger; group: number }> = [
        { trigger: 'draw-while-pressed',  group: 30 },
        { trigger: 'press-while-drawing', group: 31 },
        { trigger: 'press-and-tap',       group: 32 },
      ];
      const choices = STYLUS_ACTIONS.map(a => ({ label: a.short, value: a.id }));
      for (const { trigger, group } of TRIGGER_GROUPS) {
        const def = STYLUS_TRIGGERS.find(t => t.id === trigger)!;
        const cur = this._stylusBindings.getBinding(trigger);
        drawChoiceRow(def.label, choices, cur, group);
        // Italic hint under the row
        ctx.font = 'italic 10px Georgia, serif';
        ctx.fillStyle = '#5a3820'; ctx.globalAlpha = 0.5;
        ctx.fillText(def.hint, leftX + 24, y);
        ctx.globalAlpha = 1;
        y += 14;
      }

    } else if (this._settingsPage === 3) {
      // ─────────────────────────── Slim Pen ────────────────────────────

      drawHeading('From the browser');
      ctx.font = 'italic 10px Georgia, serif';
      ctx.fillStyle = '#5a3820';
      ctx.globalAlpha = 0.6;
      ctx.textAlign = 'left';
      ctx.fillText('Live values from PointerEvents on this canvas.', leftX + 12, y);
      y += 14;
      ctx.globalAlpha = 1;

      // Live read-outs — prove the data is actually flowing right now,
      // not just that a capability was once seen. Dim when no event in
      // the last 2 s ("rest" state — pen lifted / out of proximity).
      const ageMs = performance.now() - this._liveTilt.ts;
      const fresh = ageMs < 2000 && this._liveTilt.ts > 0;
      const dimAlpha = fresh ? 0.9 : 0.4;

      const tiltX = Math.round(this._liveTilt.tiltX);
      const tiltY = Math.round(this._liveTilt.tiltY);
      const tiltMag = Math.hypot(tiltX, tiltY);
      const tiltStr = fresh
        ? `${tiltX >= 0 ? '+' : ''}${tiltX}°, ${tiltY >= 0 ? '+' : ''}${tiltY}°` +
          (tiltMag < 2 ? '   (vertical)' : '')
        : (this._browserPenCaps.tilt ? '—  (rest)' : '—  (no tilt seen yet)');

      const pressStr = fresh
        ? this._liveTilt.pressure.toFixed(2) +
          (this._liveTilt.pressure === 0 ? '   (lifted)' : '')
        : (this._browserPenCaps.pressure ? '—  (rest)' : '—  (no pressure seen yet)');

      const tipStr = fresh
        ? (this._isEraser ? 'eraser' : 'pen')
        : (this._browserPenCaps.eraserSeen
            ? '— (last seen pen ' + (this._isEraser ? '+ eraser)' : 'only)')
            : '— (pen only)');

      ctx.font = '11px Georgia, serif';
      ctx.fillStyle = '#3a2010';
      ctx.globalAlpha = dimAlpha;
      const labelX = leftX + 14;
      const valueX = leftX + 96;
      const rows: Array<[string, string]> = [
        ['tilt',     tiltStr],
        ['pressure', pressStr],
        ['tip',      tipStr],
      ];
      for (const [label, value] of rows) {
        ctx.fillStyle = '#5a3820';
        ctx.fillText(label, labelX, y + 7);
        ctx.fillStyle = '#3a2010';
        ctx.font = '11px "JetBrains Mono", "Consolas", monospace';
        ctx.fillText(value, valueX, y + 7);
        ctx.font = '11px Georgia, serif';
        y += 16;
      }

      // Live button-state row — dots flip green while a button is
      // currently held. Driven directly from the most recent
      // PointerEvent's `buttons` bitmask, so this lights immediately
      // when an event arrives. If you press the side button and the
      // 'side' dot stays grey, the OS isn't delivering button=2 to
      // the page (which is the actual diagnostic question).
      ctx.font = '11px Georgia, serif';
      ctx.globalAlpha = 1;
      const bm = this._lastPointerButtons;
      const buttonStates: Array<[string, boolean]> = [
        ['tip',    (bm & 1)  !== 0],
        ['side',   (bm & 2)  !== 0],
        ['eraser', (bm & 32) !== 0],
      ];
      ctx.fillStyle = '#5a3820';
      ctx.fillText('buttons', labelX, y + 7);
      let bbx = valueX;
      for (const [name, held] of buttonStates) {
        const dotColor = held ? '#3a7a3a' : '#7a6055';
        ctx.fillStyle = dotColor;
        ctx.beginPath();
        ctx.arc(bbx, y + 4, 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = held ? '#3a7a3a' : '#5a3820';
        ctx.globalAlpha = held ? 1 : 0.55;
        ctx.fillText(name, bbx + 9, y + 7);
        ctx.globalAlpha = 1;
        bbx += ctx.measureText(name).width + 22;
      }
      y += 18;
      // Update age line — tells you whether the reading is current.
      ctx.font = 'italic 10px Georgia, serif';
      ctx.fillStyle = '#5a3820';
      ctx.globalAlpha = 0.55;
      const ageStr = this._liveTilt.ts === 0
        ? 'no pen events yet — tilt the pen on the page to start'
        : fresh
          ? `updated ${(ageMs / 1000).toFixed(1)} s ago`
          : `last update ${(ageMs / 1000).toFixed(0)} s ago`;
      ctx.fillText(ageStr, labelX, y + 7);
      ctx.globalAlpha = 1;
      y += 18;

      // ── Bridge status + live bridge values. ──
      drawHeading('From the local bridge');
      const dotX = leftX + 14;
      const dotY = y + 4;
      let dotColor = '#7a6055';
      let summary = 'Not connected — bridge service isn\'t running.';
      if (this._slimPenStatus === 'connecting') {
        dotColor = '#a47a2e';
        summary = 'Connecting to local bridge…';
      } else if (this._slimPenStatus === 'connected') {
        dotColor = '#3a7a3a';
        const model = this._slimPenInfo?.model ?? 'pen';
        summary = `Connected · ${model}`;
      }
      ctx.font = '11px Georgia, serif';
      ctx.fillStyle = dotColor;
      ctx.beginPath();
      ctx.arc(dotX, dotY, 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#3a2010';
      ctx.globalAlpha = 0.85;
      ctx.fillText(summary, dotX + 12, dotY + 4);
      ctx.globalAlpha = 1;
      y += 18;

      if (this._slimPenStatus === 'connected') {
        // Live battery row with age — same value-first treatment as
        // the browser tilt/pressure rows.
        const battery = this._slimPenInfo?.battery;
        const batAgeMs = this._slimPenBatteryReadAt
          ? performance.now() - this._slimPenBatteryReadAt : Infinity;
        const batAgeStr = isFinite(batAgeMs)
          ? `read ${batAgeMs < 60000 ? `${(batAgeMs / 1000).toFixed(0)} s` : `${(batAgeMs / 60000).toFixed(1)} min`} ago`
          : 'never read';
        const batValue = typeof battery === 'number'
          ? `${battery}%   (${batAgeStr})`
          : `—   (${batAgeStr})`;
        ctx.font = '11px Georgia, serif';
        ctx.globalAlpha = 0.9;
        ctx.fillStyle = '#5a3820';
        ctx.fillText('battery', leftX + 14, y + 7);
        ctx.fillStyle = '#3a2010';
        ctx.font = '11px "JetBrains Mono", "Consolas", monospace';
        ctx.fillText(batValue, leftX + 96, y + 7);
        ctx.font = '11px Georgia, serif';
        y += 16;

        // Recent pen events from the bridge — the channel that will
        // carry top-click events once the proprietary characteristic
        // is identified (run probe_buttons.py to identify it).
        const events = this._slimPenEvents;
        if (events.length === 0) {
          ctx.font = 'italic 10px Georgia, serif';
          ctx.fillStyle = '#5a3820';
          ctx.globalAlpha = 0.55;
          ctx.fillText('no pen events yet (top click / haptics land in a future bridge stage)',
            leftX + 14, y + 7);
          ctx.globalAlpha = 1;
          y += 14;
        } else {
          ctx.font = 'italic 10px Georgia, serif';
          ctx.fillStyle = '#5a3820';
          ctx.globalAlpha = 0.6;
          ctx.fillText('recent events', leftX + 14, y + 7);
          ctx.globalAlpha = 1;
          y += 12;
          ctx.font = '10px "JetBrains Mono", "Consolas", monospace';
          ctx.fillStyle = '#3a2010';
          for (const ev of events.slice(0, 5)) {
            const age = (performance.now() - ev.ts) / 1000;
            ctx.globalAlpha = age < 5 ? 0.85 : 0.5;
            const line = `${ev.source.padEnd(12, ' ')}${ev.preview.padEnd(8)}${age.toFixed(1)} s`;
            ctx.fillText(line, leftX + 18, y + 7);
            y += 12;
          }
          ctx.globalAlpha = 1;
        }
      } else {
        ctx.font = 'italic 10px Georgia, serif';
        ctx.fillStyle = '#5a3820';
        ctx.globalAlpha = 0.7;
        ctx.fillText('Run  slim-pen-bridge\\bridge.bat  to enable extras —', leftX + 12, y);
        y += 12;
        ctx.fillText('battery (now), top click + haptics (probe_buttons.py / probe_haptic.py).', leftX + 12, y);
        ctx.globalAlpha = 1;
      }

    } else {
      // ─────────────────────────── Vault & Stats ───────────────────────

      drawHeading('Vault');
      ctx.font = 'italic 11px Georgia, serif';
      ctx.fillStyle = '#5a3820';
      ctx.textAlign = 'left';
      ctx.globalAlpha = 0.65;
      if (!this._vaultStatus.supported) {
        ctx.fillText('Folder sync unsupported in this browser.', leftX + 12, y);
        y += 22;
      } else if (this._vaultStatus.active && this._vaultStatus.folderName) {
        ctx.fillText(`Folder: ${this._vaultStatus.folderName}`, leftX + 12, y);
        y += 22;
        drawChoiceRow('', [
          { label: 'Open folder', value: 'open' },
          { label: 'Detach', value: 'detach' },
        ], '', 13);
      } else {
        ctx.fillText('Local cache (no folder picked).', leftX + 12, y);
        y += 22;
        drawChoiceRow('', [
          { label: 'Open folder', value: 'open' },
        ], '', 13);
      }
      y += 12;

      drawHeading('Notebook');
      ctx.font = '11px serif';
      ctx.textAlign = 'left';
      ctx.globalAlpha = 0.65;
      ctx.fillStyle = '#5a3820';
      ctx.fillText(`Pages: ${this._state.pageCount}`, leftX + 12, y);
      y += 18;
      const discovered = this._spellRegistry.codex().length;
      const total = this._spellRegistry.all().length;
      ctx.fillText(`Spells discovered: ${discovered} / ${total}`, leftX + 12, y);
    }

    // ── Sync selection region items ───────────────────────────────────
    this._settingsHitItems = items;
    this._settingsRegion.updateItems(items);
    // Pre-select current values
    this._preselectSettings();
  }

  /** Pre-select wonky rings for current settings values (no animation). */
  private _preselectSettings(): void {
    if (this._settingsRegion.animating) return;
    const items = this._settingsHitItems;

    // Toggles (groups 0–2): active = toggled on
    const toggleMap: Array<{ group: number; key: keyof GrimoireSettings }> = [
      { group: 0, key: 'autoLatex' },
      { group: 1, key: 'autoArrows' },
      { group: 2, key: 'tapSound' },
      { group: 7, key: 'debugOverlay' },
      { group: 10, key: 'visualFx' },
      { group: 15, key: 'animations' },
    ];
    for (const tm of toggleMap) {
      if (this._settings[tm.key]) {
        const idx = items.findIndex(it => it.group === tm.group);
        if (idx >= 0) this._settingsRegion.preSelect(tm.group, idx);
      }
    }
    // Choices (groups 3–6): active = matches setting value
    const choiceMap: Array<{ group: number; value: string }> = [
      { group: 3, value: this._settings.spellSensitivity },
      { group: 8, value: String(this._settings.grainIntensity) },
      { group: 9, value: this._settings.shapeMode },
      { group: 16, value: this._settings.tapEffect },
    ];
    // Stylus binding rows (groups 30 / 31 / 32) — pre-select the
    // currently bound action so the cell highlights match the engine.
    const SHORTCUT_ROWS: Array<{ group: number; trigger: StylusTrigger }> = [
      { group: 30, trigger: 'draw-while-pressed' },
      { group: 31, trigger: 'press-while-drawing' },
      { group: 32, trigger: 'press-and-tap' },
    ];
    for (const r of SHORTCUT_ROWS) {
      choiceMap.push({
        group: r.group,
        value: this._stylusBindings.getBinding(r.trigger),
      });
    }
    for (const cm of choiceMap) {
      const idx = items.findIndex(it => it.group === cm.group && String(it.value) === cm.value);
      if (idx >= 0) this._settingsRegion.preSelect(cm.group, idx);
    }
  }

  /** Persist settings to host. */
  private _saveSettings(): void {
    this._vscode.postMessage({ type: 'settings.save', payload: this._settings });
  }

  /** Handle a settings selection change. */
  private _onSettingsSelect(group: number, item: WidgetItem): void {
    const val = item.value as string;

    // Toggles
    if (group === 0) { this._settings.autoLatex = !this._settings.autoLatex; }
    else if (group === 1) { this._settings.autoArrows = !this._settings.autoArrows; }
    else if (group === 2) { this._settings.tapSound = !this._settings.tapSound; }
    // Choice selectors
    else if (group === 3) { this._settings.spellSensitivity = val as 'low' | 'medium' | 'high'; }
    else if (group === 7) { this._settings.debugOverlay = !this._settings.debugOverlay; }
    else if (group === 8) { this._settings.grainIntensity = parseFloat(val); }
    else if (group === 9) { this._settings.shapeMode = val as ShapeMode; }
    else if (group === 10) { this._settings.visualFx = !this._settings.visualFx; }
    // Master animations toggle (suppresses fade-outs, selection effects,
    // tap rings — selection still happens silently).
    else if (group === 15) {
      this._settings.animations = !this._settings.animations;
      setSelectionAnimationsEnabled(this._settings.animations);
      setTapEffectsEnabled(this._settings.animations);
    }
    // Tap visual selector
    else if (group === 16) {
      this._settings.tapEffect = val as GrimoireSettings['tapEffect'];
    }
    // Vault folder actions (no settings mutation, no save)
    else if (group === 13) {
      if (val === 'open') void this._openVaultFolderPicker();
      else if (val === 'detach') this._vscode.postMessage({ type: 'vault.detach' });
      return;
    }
    // Settings sub-page tab (UI-only, no save)
    else if (group === 14) {
      const v = parseInt(val, 10);
      if (v === 0 || v === 1 || v === 2 || v === 3 || v === 4) this._settingsPage = v;
      return;
    }
    // Stylus shortcut bindings (groups 30 / 31 / 32 — one per trigger)
    else if (group >= 30 && group <= 32) {
      const triggers: StylusTrigger[] = [
        'draw-while-pressed', 'press-while-drawing', 'press-and-tap',
      ];
      const trigger = triggers[group - 30];
      this._stylusBindings.setBinding(trigger, val as StylusActionId);
      this._settings.stylusBindings = this._stylusBindings.serialize();
    }

    this._saveSettings();
  }

  /** Open the native directory picker and hand the resulting handle to the host. */
  private async _openVaultFolderPicker(): Promise<void> {
    if (!this._vaultStatus.supported) return;
    try {
      const handle = await (window as unknown as {
        showDirectoryPicker: (opts?: { mode?: string }) => Promise<FileSystemDirectoryHandle>;
      }).showDirectoryPicker({ mode: 'readwrite' });
      this._vscode.postMessage({ type: 'vault.activate', payload: { handle } });
    } catch (e) {
      if ((e as DOMException)?.name !== 'AbortError') {
        console.error('[vault] picker failed:', e);
      }
    }
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  /** Rebuild the codex pages from the spell registry's discovered spells. */
  private _refreshCodex(): void {
    const discovered = this._spellRegistry.codex();
    const entries: CodexEntry[] = discovered.map(s => ({
      id: s.id,
      name: s.name,
      tier: s.tier,
      family: s.family,
      lore: s.lore,
      params: s.params.map(p => ({ name: p.name, description: p.description })),
    }));
    this._codex.update(entries);

    // Built-in invocations
    const invocations: InvocationEntry[] = [
      {
        id: 'thrice-knock',
        name: 'Invoke Wand',
        sequence: ['O', 'O'],
        color: '#e0dcd6',
        description: 'Quick double-tap to enter wand mode',
      },
      {
        id: 'stylus-summon',
        name: 'Stylus Menu',
        sequence: ['O', '_', 'O', 'O'],
        color: '#e8a020',
        description: 'Tap, pause, then quick double-tap',
      },
      {
        id: 'analyse-knock',
        name: 'Activate',
        sequence: ['O', 'O', 'O'],
        color: '#b680ff',
        description: 'Three quick taps to analyse and create',
      },
    ];
    this._codex.updateInvocations(invocations);

    // Built-in arcanes (rune-in-a-box symbols)
    const arcanes: ArcaneEntry[] = [
      {
        id: 'rune-text',
        name: 'Text Block',
        symbol: 'T',
        description: 'Draw a T inside a box for a text block',
      },
      {
        id: 'rune-code',
        name: 'Code Block',
        symbol: 'C',
        description: 'Draw a C inside a box for a code block',
      },
      {
        id: 'rune-arcane',
        name: 'Computation Block',
        symbol: '⟳',
        description: 'Spiral inside a box for a computation block',
      },
    ];
    this._codex.updateArcanes(arcanes);

    // Refresh the table-of-contents for the Contents codex page
    this._refreshContentsIndex();

    this._updatePolicy();
  }

  /**
   * Build the Contents page's flat list of rows.
   *
   * Single source of truth: the journal's folder tree.  Folders become
   * collection/chapter/section headings, files become indented page rows.
   * Every row is a Page in the unified sense — clicking any row (folder or
   * file) routes through the same `_switchContent({ viewMode: 'page' })`
   * dispatch, so there are no special cases between "codex entry" and
   * "notebook page".
   *
   * Offline fallback: when there's no journal server (IDB-only PWA launch),
   * the notebook's section/page state stands in.  Same row shape, same
   * click handling — the codex doesn't care which source supplied the rows.
   */
  /**
   * Astral-layer overlay: faint detected-text shadows beneath each shadow
   * group on the page, drawn in page-local coordinates. Visible only while
   * the astral layer is on (set by cast mode). Pure visual hint — no clicks.
   *
   * The detected text is positioned just below each group's bounding box,
   * with a left-aligned start so multi-line groups read naturally. Faint
   * italic to keep the user's handwriting visually dominant.
   */
  private _drawAstralOverlay(ctx: CanvasRenderingContext2D, pageId: string): void {
    const groups = this._textCompose.getShadowGroups(pageId);
    if (groups.length === 0) return;
    // Scope reveals to a radius around the invocation point so the
    // page doesn't desaturate with detected text page-wide. If we
    // don't have an invocation point (cast came from a non-spatial
    // route), fall back to revealing everything.
    const invoke = this._wandInvocationPoint;
    const ASTRAL_RADIUS = 150;
    ctx.save();
    // Match the hint-ring colour family — active pen colour with
    // moderate alpha so it stands against parchment without
    // overpowering the user's actual ink.
    ctx.fillStyle = this._penStyle.color;
    ctx.shadowColor = this._penStyle.color;
    ctx.shadowBlur = 3;
    ctx.globalAlpha = 0.7;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    for (const g of groups) {
      if (!g.text) continue;
      if (invoke) {
        const cx = g.bbox.x + g.bbox.width / 2;
        const cy = g.bbox.y + g.bbox.height / 2;
        if (Math.hypot(cx - invoke.x, cy - invoke.y) > ASTRAL_RADIUS) continue;
      }
      const fs = Math.round(Math.max(9, Math.min(18, g.bbox.height * 0.55)));
      ctx.font = `italic ${fs}px Georgia, serif`;
      const tx = g.bbox.x;
      const ty = g.bbox.y + g.bbox.height + 2;
      ctx.fillText(g.text, tx, ty);
    }
    ctx.restore();
  }

  /**
   * Draw a folder landing page: title at top, 2-line description beneath,
   * and a content index of the folder's immediate children. Each row is a
   * click target that dispatches page.goto.
   *
   * Folder children come from the host's `getJournalContents(folderPath)` —
   * a per-folder, non-recursive listing. Subfolders point at their landing
   * page (so clicking "Research" lands on Research's landing, not its first
   * leaf); files point at themselves.
   */
  private _drawFolderLanding(
    ctx: CanvasRenderingContext2D,
    L: import('./page_renderer').PageLayout,
    page: import('./notebook_state').WvPage,
  ): void {
    const cX = L.x + L.marginLeft;
    const cY = L.y + L.marginTop;
    const cW = L.width - L.marginLeft - L.marginRight;
    const cH = L.height - L.marginTop - L.marginBottom;

    // Two-column split: main body on the left, smaller index column on
    // the right separated by a narrow gutter.
    const leftW = Math.round(cW * 0.62);
    const gutter = Math.round(cW * 0.04);
    const rightW = cW - leftW - gutter;
    const leftCx = cX + leftW / 2;
    const rightX = cX + leftW + gutter;

    // ── LEFT: title + description ─────────────────────────────────────
    const headerY = cY + 12;
    ctx.strokeStyle = 'rgba(139, 99, 64, 0.18)';
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.moveTo(leftCx - 100, headerY - 6);
    ctx.lineTo(leftCx + 100, headerY - 6);
    ctx.stroke();

    ctx.font = 'bold 24px Georgia, serif';
    ctx.fillStyle = '#3a2510';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.globalAlpha = 0.92;
    ctx.fillText(page.title || 'Folder', leftCx, headerY + 4);
    ctx.globalAlpha = 1;

    // Description: left-aligned (rag right). The page metadata can
    // override `descAlign` when the alignment-options machinery lands;
    // for now we default to left, which reads the most book-like.
    const desc = (page.description ?? '').trim();
    if (desc) {
      ctx.font = 'italic 13px Georgia, serif';
      ctx.fillStyle = '#5a4020';
      ctx.globalAlpha = 0.7;
      ctx.textAlign = 'left';
      const descX = cX + 8;
      const descMaxW = leftW - 16;
      const lines = wrapDescriptionLines(ctx, desc, descMaxW, 4);
      let y = headerY + 36;
      for (const line of lines) {
        ctx.fillText(line, descX, y);
        y += 16;
      }
      ctx.globalAlpha = 1;
    }

    // Ornamental divider centred under the title — purely decorative.
    const dividerY = headerY + 4 + 30;
    ctx.strokeStyle = 'rgba(139, 99, 64, 0.2)';
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.moveTo(leftCx - 70, dividerY);
    ctx.lineTo(leftCx - 6, dividerY);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(leftCx + 6, dividerY);
    ctx.lineTo(leftCx + 70, dividerY);
    ctx.stroke();
    ctx.fillStyle = 'rgba(139, 99, 64, 0.3)';
    ctx.save();
    ctx.translate(leftCx, dividerY);
    ctx.rotate(Math.PI / 4);
    ctx.fillRect(-3, -3, 6, 6);
    ctx.restore();

    // ── RIGHT: index column ──────────────────────────────────────────
    const folderPath = page.folderPath ?? '';
    const rows = this._getLandingRows(folderPath);

    // Subtle vertical separator between body and index.
    ctx.strokeStyle = 'rgba(139, 99, 64, 0.10)';
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.moveTo(rightX - gutter / 2, cY + 8);
    ctx.lineTo(rightX - gutter / 2, cY + cH - 8);
    ctx.stroke();

    if (rows.length === 0) {
      ctx.font = 'italic 11px Georgia, serif';
      ctx.fillStyle = 'rgba(100, 70, 30, 0.5)';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.fillText('Empty folder.', rightX, cY + 12);
      ctx.textBaseline = 'top';
      return;
    }

    // Align index rows with the side-rail sibling tab y-positions so
    // each entry visually belongs to its tab. The first sibling tab on
    // the rail sits at L.y + 48 (= startY 12 + backH 26 + utilToSib
    // 10) when only the back utility is at the top; sibH=26, overlap=4
    // → adjacent siblings step by 22 px. Mirrors page_renderer's
    // drawPageSidebar geometry — keep these in sync if those constants
    // change.
    const SIB_FIRST_Y = L.y + 48;
    const SIB_STEP_Y = 22;
    const indexX = rightX;
    const indexW = rightW;
    const indexBottom = cY + cH - 8;
    const hits = this._drawContentIndex(ctx, rows, indexX, SIB_FIRST_Y, indexW, indexBottom - SIB_FIRST_Y, SIB_STEP_Y);
    for (const hit of hits) this._landingHitRects.push(hit);
    ctx.textBaseline = 'top';
  }

  /** Fetch the per-folder content rows from the host. Returns an empty
   *  array when not in journal mode. Reusable from any caller that
   *  wants to draw an index overlay (cast-mode side margin, scene
   *  preview, etc.). */
  private _getLandingRows(folderPath: string): Array<{
    id: string; title: string; isFolder: boolean; pageId?: string;
  }> {
    const host = (window as unknown as Record<string, unknown>).__oxHost as
      { _journalMode?: boolean; getJournalContents?: (path?: string) => Array<{
          id: string; title: string; level: number;
          isFolder: boolean; pageId?: string;
        }> | null } | undefined;
    if (!host?._journalMode || typeof host.getJournalContents !== 'function') return [];
    return (host.getJournalContents(folderPath) ?? []).map(r => ({
      id: r.id, title: r.title, isFolder: r.isFolder, pageId: r.pageId,
    }));
  }

  /** Render a vertical list of folder/page rows in a side-tab style.
   *  Each row carries a compact chip glyph (folder vs page) and a
   *  truncated title. Returns hit rects so the caller can wire taps to
   *  navigation. Generic enough to drop in any rectangular region —
   *  used by the folder-landing right column today, ready for the
   *  cast-mode side margin overlay tomorrow.
   *
   *  `rowStep` overrides the row-to-row vertical spacing — useful when
   *  the rows must align with another column (e.g. the side rail). The
   *  chip dimensions stay the same; only the y between rows changes. */
  private _drawContentIndex(
    ctx: CanvasRenderingContext2D,
    rows: Array<{ id: string; title: string; isFolder: boolean; pageId?: string }>,
    x: number, y: number, w: number, h: number,
    rowStep?: number,
  ): Array<{ pageId: string; x: number; y: number; w: number; h: number }> {
    const hits: Array<{ pageId: string; x: number; y: number; w: number; h: number }> = [];
    if (rows.length === 0) return hits;

    const rowH = 22;
    const step = rowStep ?? rowH;
    const chipW = 16;
    const chipH = 12;
    const labelGap = 8;
    const maxRows = Math.max(0, Math.floor(h / step));

    let curY = y;
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const remaining = rows.length - i;
      // Reserve the last row for an overflow indicator if needed.
      if (i >= maxRows - 1 && remaining > 1) {
        ctx.font = 'italic 10px Georgia, serif';
        ctx.fillStyle = 'rgba(100, 70, 30, 0.45)';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(`+ ${remaining} more`, x + chipW + labelGap, curY + rowH / 2);
        break;
      }

      const rowMidY = curY + rowH / 2;

      // Chip — mirrors the side-tab visual: a small filled rectangle
      // with the appropriate glyph in the middle. Folder = bluegray;
      // page = parchment-tan, matching the side rail's palette.
      const chipX = x;
      const chipY = rowMidY - chipH / 2;
      ctx.save();
      ctx.fillStyle = row.isFolder ? '#7a8fa0' : '#dcc890';
      ctx.globalAlpha = 0.75;
      ctx.beginPath();
      // Rounded rect (manual; roundRect not in all targets).
      const r = 2;
      ctx.moveTo(chipX + r, chipY);
      ctx.lineTo(chipX + chipW - r, chipY);
      ctx.arcTo(chipX + chipW, chipY, chipX + chipW, chipY + r, r);
      ctx.lineTo(chipX + chipW, chipY + chipH - r);
      ctx.arcTo(chipX + chipW, chipY + chipH, chipX + chipW - r, chipY + chipH, r);
      ctx.lineTo(chipX + r, chipY + chipH);
      ctx.arcTo(chipX, chipY + chipH, chipX, chipY + chipH - r, r);
      ctx.lineTo(chipX, chipY + r);
      ctx.arcTo(chipX, chipY, chipX + r, chipY, r);
      ctx.closePath();
      ctx.fill();
      ctx.restore();

      // Glyph on chip.
      ctx.save();
      ctx.fillStyle = row.isFolder ? '#1a3040' : '#3a2510';
      ctx.font = 'bold 9px Georgia, serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(row.isFolder ? '◆' : '·', chipX + chipW / 2, rowMidY + 0.5);
      ctx.restore();

      // Title.
      ctx.font = row.isFolder ? 'small-caps bold 12px Georgia, serif' : '12px Georgia, serif';
      ctx.fillStyle = '#3a2510';
      ctx.globalAlpha = row.isFolder ? 0.92 : 0.82;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      const labelX = x + chipW + labelGap;
      const labelMaxW = w - chipW - labelGap;
      const label = truncateForWidth(ctx, row.title || '(untitled)', labelMaxW);
      ctx.fillText(label, labelX, rowMidY);
      ctx.globalAlpha = 1;

      if (row.pageId) {
        hits.push({ pageId: row.pageId, x, y: curY, w, h: rowH });
      }
      curY += step;
    }

    return hits;
  }

  private _refreshContentsIndex(): void {
    const entries: ContentsEntry[] = [];
    if (!this._state) {
      this._codex.updateContents(entries);
      return;
    }

    // ── Canonical path: the journal tree (folder-scoped) ────────────────
    // Folder landing pages now own the primary content-index UX. The codex
    // Contents page still exists for the codex-view tab; we feed it the
    // CURRENT folder's children only — never the whole vault — so it stays
    // consistent with the new lazy-navigation model and never triggers a
    // recursive scan.
    const host = (window as unknown as Record<string, unknown>).__oxHost as
      { _journalMode?: boolean; getJournalContents?: (path?: string) => Array<{
          id: string; title: string; level: number;
          isFolder: boolean; pageId?: string;
        }> | null } | undefined;

    if (host?._journalMode && typeof host.getJournalContents === 'function') {
      // Resolve current folder from the active page: landing pages know it
      // directly, real pages get it from their journalPath via the host's
      // sibling lookup (parentPath). Default to root.
      const cur = this._state.currentPage();
      let folderPath = '';
      if (cur?.kind === 'folder-landing') {
        folderPath = cur.folderPath ?? '';
      } else if (this._journalNavContext) {
        folderPath = this._journalNavContext.parentPath ?? '';
      }
      const journalRows = host.getJournalContents(folderPath);
      if (journalRows) {
        for (const row of journalRows) {
          entries.push({
            id: row.id,
            title: row.title || '(untitled)',
            level: row.level,
            isSection: row.isFolder,
            pageId: row.pageId,
          });
        }
        this._codex.updateContents(entries);
        return;
      }
    }

    // ── Offline-only fallback: derive from the in-memory notebook ───────
    // Same entry shape as the journal path so the codex renderer and the
    // click dispatcher treat the two sources identically.
    const sections = this._state.visibleSections();
    const sectionIds = new Set(sections.map(s => s.id));
    const pages = this._state.visiblePages();

    // Orphan pages (no section) — rare but possible; render first.
    const orphans = pages.filter(p => !p.sectionId || !sectionIds.has(p.sectionId));
    for (const p of orphans) {
      entries.push({
        id: p.id,
        title: p.title || '(untitled)',
        level: 0,
        isSection: false,
        pageId: p.id,
      });
    }

    // Sections, each followed by its ordered pages.
    for (const sec of sections) {
      entries.push({
        id: sec.id,
        title: sec.title || '(untitled section)',
        level: 0,
        isSection: true,
        pageId: sec.headerPageId,
        color: sec.color,
      });

      const secPages = this._state.sectionPages(sec.id);
      for (const p of secPages) {
        // Skip the section header page — it's already represented by the
        // section row itself.
        if (p.id === sec.headerPageId) continue;
        entries.push({
          id: p.id,
          title: p.title || '(untitled)',
          level: 1,
          isSection: false,
          pageId: p.id,
        });
      }
    }

    this._codex.updateContents(entries);
  }

  private _toStrokePoint(e: PointerEvent): StrokePoint {
    const rect = this._canvas.getBoundingClientRect();
    // CSS-transform aware pointer mapping. The root applies
    // `scale(0.9)`; getBoundingClientRect returns the visual size
    // (scaled) while canvas.offsetWidth returns the unscaled CSS
    // size. Their ratio is the inverse of the active transform —
    // works correctly with any future scale changes without code
    // edits, and degrades to 1:1 when no transform is applied.
    const sx = this._canvas.offsetWidth  > 0 ? this._canvas.offsetWidth  / rect.width  : 1;
    const sy = this._canvas.offsetHeight > 0 ? this._canvas.offsetHeight / rect.height : 1;
    const L = this._layout;
    return {
      x:         (e.clientX - rect.left) * sx - L.x,
      y:         (e.clientY - rect.top)  * sy - L.y,
      pressure:  e.pressure > 0 ? e.pressure : 0.5,
      tiltX:     e.tiltX,
      tiltY:     e.tiltY,
      twist:     e.twist,
      timestamp: e.timeStamp,
    };
  }

  /** Build a PointerFrame from a PointerEvent with a single getBoundingClientRect() call. */
  private _buildFrame(e: PointerEvent): PointerFrame {
    const rect = this._canvas.getBoundingClientRect();
    const sx = this._canvas.offsetWidth  > 0 ? this._canvas.offsetWidth  / rect.width  : 1;
    const sy = this._canvas.offsetHeight > 0 ? this._canvas.offsetHeight / rect.height : 1;
    const L = this._layout;
    const absX = (e.clientX - rect.left) * sx;
    const absY = (e.clientY - rect.top)  * sy;
    return {
      absX,
      absY,
      pageX:       absX - L.x,
      pageY:       absY - L.y,
      pressure:    e.pressure > 0 ? e.pressure : 0.5,
      tiltX:       e.tiltX,
      tiltY:       e.tiltY,
      twist:       e.twist,
      timestamp:   e.timeStamp,
      pointerId:   e.pointerId,
      pointerType: e.pointerType,
      button:      e.button,
      buttons:     e.buttons,
    };
  }

  /** Convert a PointerFrame to a StrokePoint (for ink layer). */
  private _frameToStroke(f: PointerFrame): StrokePoint {
    return {
      x: f.pageX, y: f.pageY,
      pressure: f.pressure,
      tiltX: f.tiltX, tiltY: f.tiltY,
      twist: f.twist,
      timestamp: f.timestamp,
    };
  }

  // ── Text input ──────────────────────────────────────────────────────────────

  private _openTextInput(block: import('./notebook_state').WvTextBlock, pageId: string): void {
    if (this._textInput.active && this._textInput.blockId === block.id) return; // already open
    const prevContent = block.content;
    this._textInput.open({
      blockId: block.id,
      pageId,
      x: block.bbox.x,
      y: block.bbox.y,
      width: Math.max(block.bbox.width, 120),
      height: Math.max(block.bbox.height, 40),
      content: block.content,
      fontSize: block.fontSize,
      onCommit: (blockId, pid, content) => {
        const fwd: GrimoireAction = { type: 'text.update', blockId, pageId: pid, content, origin: 'gesture', timestamp: Date.now() };
        this._dispatch(fwd);
        this._pushUndo({
          reverse: [{ type: 'text.update', blockId, pageId: pid, content: prevContent, origin: 'gesture', timestamp: Date.now() }],
          forward: [fwd],
        });
      },
    });
  }

  /** Open a text input overlay over a page/section header for renaming. */
  private _openHeaderRename(target: 'page' | 'section-title' | 'section-desc'): void {
    const page = this._state.currentPage();
    const sec = this._state.currentSection();
    if (!page) return;

    let rect: { x: number; y: number; w: number; h: number } | null = null;
    let content = '';
    let fontSize = 11;
    const sentinelId = `__${target}__`;

    if (target === 'page') {
      rect = this._pageHeaderTitleRect;
      content = page.title || '';
    } else if (target === 'section-title' && sec && this._sectionHeaderHitData) {
      rect = this._sectionHeaderHitData.nameLineRect;
      content = sec.title || '';
      fontSize = 28;
    }

    if (!rect) return;
    if (this._textInput.active && this._textInput.blockId === sentinelId) return;

    // Rects are canvas-absolute; TextInput expects page-local coords
    const L = this._layout;
    const localX = rect.x - L.x;
    const localY = rect.y - L.y;

    this._textInput.open({
      blockId: sentinelId,
      pageId: page.id,
      x: localX,
      y: localY,
      width: Math.max(rect.w, 120),
      height: Math.max(rect.h, 32),
      content,
      fontSize,
      onCommit: (_id, _pid, text) => {
        const now = Date.now();
        if (target === 'page') {
          this._dispatch({
            type: 'page.update',
            pageId: page.id,
            title: text,
            origin: 'gesture',
            timestamp: now,
          });
        } else if (target === 'section-title' && sec) {
          this._dispatch({
            type: 'section.update',
            sectionId: sec.id,
            title: text,
            origin: 'gesture',
            timestamp: now,
          });
        } else if (target === 'section-desc' && sec) {
          this._dispatch({
            type: 'section.update',
            sectionId: sec.id,
            description: text,
            origin: 'gesture',
            timestamp: now,
          });
        }
      },
    });
  }

  /** Compute the next auto page number by scanning existing "Page N" titles. */
  private _nextPageNumber(): number {
    // Scope to current section so each section starts numbering at 1
    const sec = this._state.currentSection();
    const pages = sec ? this._state.sectionPages(sec.id) : this._state.pages();
    let max = 0;
    for (const p of pages) {
      const m = /^Page\s+(\d+)$/i.exec(p.title);
      if (m) { max = Math.max(max, parseInt(m[1], 10)); }
    }
    return Math.max(max + 1, pages.length + 1);
  }

  private _dispatch(action: GrimoireAction): void {
    // ── Lock guard ────────────────────────────────────────────────
    // Refuse to mutate locked content unless the action is system-origin
    // (astral discard, recovery, etc.). The action is dropped silently —
    // the selection toolbar shows lock state so the user knows why.
    if (!this._isMutationAllowed(action)) return;
    this._vscode.postMessage({ type: 'action', payload: action, locallyApplied: true });
    this._applyAction(action);
  }

  /** Hit-test the modifier strip pills laid out by BlockRenderer.
   *  Returns the pill kind + its current active state when the point
   *  lies inside one of the pill rects, otherwise null. */
  private _hitTestModifierStrip(
    x: number, y: number,
  ): { kind: 'lock' | 'group'; active: boolean } | null {
    const hits = this._blockRenderer.modifierStripHits;
    for (const h of hits) {
      if (x >= h.x && x <= h.x + h.w && y >= h.y && y <= h.y + h.h) {
        return { kind: h.kind, active: h.active };
      }
    }
    return null;
  }

  /** Toggle the given modifier across every block in the current
   *  selection. Dispatches one content.modifier.set carrying every
   *  target so the action log stays tidy. Astral is intentionally
   *  not exposed here — it stays an internal mechanism. */
  private _toggleSelectionModifier(
    kind: 'lock' | 'group',
    currentlyActive: boolean,
  ): void {
    const pageId = this._state.currentPageId;
    const page = this._state.currentPage();
    if (!page) return;
    const sel = this._state.selection;
    if (sel.blockIds.size === 0) return;

    const targets: Array<{ kind: 'block' | 'stroke'; id: string }> = [];
    for (const id of sel.blockIds) targets.push({ kind: 'block', id });

    // Include strokes belonging to selected ink blocks so locked / astral
    // affect their rendering and erase-gating too.
    for (const id of sel.blockIds) {
      const b = page.blocks.get(id) as
        (import('./notebook_state').WvInkBlock) | undefined;
      if (b?.kind === 'ink') {
        for (const sid of b.strokeIds) targets.push({ kind: 'stroke', id: sid });
      }
    }

    const now = Date.now();
    const patch: { locked?: boolean; groupId?: string | null;
                   astral?: { birth: number; ttlMs: number } | null } = {};
    if (kind === 'lock') {
      patch.locked = !currentlyActive;
    } else {
      // group
      if (currentlyActive) {
        patch.groupId = null;
      } else {
        patch.groupId = `grp_${now.toString(36)}_${Math.floor(Math.random() * 0xffff).toString(36)}`;
      }
    }

    this._dispatch({
      type: 'content.modifier.set', pageId, targets, patch,
      origin: 'gesture', timestamp: now,
    });
  }

  /** Expand a set of selected block ids to include every block that
   *  shares a groupId with one of them — so a single group member's
   *  drag carries the whole bundle. Returns the seed set unchanged
   *  if no member carries a groupId. */
  private _expandByGroup(ids: Iterable<string>): string[] {
    const page = this._state.currentPage();
    if (!page) return [...ids];
    const expanded = new Set<string>(ids);
    const tags = new Set<string>();
    for (const id of expanded) {
      const b = page.blocks.get(id) as import('./notebook_state').WvBaseBlock | undefined;
      if (b?.groupId) tags.add(b.groupId);
    }
    if (tags.size === 0) return [...expanded];
    for (const [id, b] of page.blocks) {
      const blk = b as import('./notebook_state').WvBaseBlock;
      if (blk.groupId && tags.has(blk.groupId)) expanded.add(id);
    }
    return [...expanded];
  }

  /** Scan current page strokes + blocks for expired astral entries
   *  and emit system-origin discard actions. Called by the render
   *  loop at ~5Hz. */
  private _tickAstralDiscard(): void {
    const pageId = this._state?.currentPageId;
    if (!pageId || pageId === 'unknown') return;
    const now = this._frameNow;

    // Expired strokes — emit ink.erase
    const strokes = this._ink.getStrokes();
    for (const s of strokes.values()) {
      const ast = (s as { astral?: { birth: number; ttlMs: number } }).astral;
      if (!ast) continue;
      if (now - ast.birth >= ast.ttlMs) {
        this._dispatch({
          type: 'ink.erase', pageId, target: s.id,
          origin: 'system', timestamp: now,
        });
      }
    }

    // Expired blocks — emit block.delete
    const page = this._state.currentPage();
    if (page) {
      const expired: string[] = [];
      for (const [id, b] of page.blocks) {
        const ast = (b as import('./notebook_state').WvBaseBlock).astral;
        if (!ast) continue;
        if (now - ast.birth >= ast.ttlMs) expired.push(id);
      }
      for (const id of expired) {
        this._dispatch({
          type: 'block.delete', blockId: id, pageId,
          origin: 'system', timestamp: now,
        });
      }
    }
  }

  /** Returns false for mutations against a locked block or stroke
   *  when the action does not come from `system`. */
  private _isMutationAllowed(action: GrimoireAction): boolean {
    if (action.origin === 'system') return true;
    const t = action.type;
    if (t === 'ink.erase' && typeof action.target === 'string') {
      const s = this._ink.getStrokes().get(action.target) as
        (import('@shared/notebook_model').InkStroke & { locked?: boolean }) | undefined;
      if (s?.locked) return false;
    } else if (t === 'block.move' || t === 'block.scale' || t === 'block.rotate' || t === 'block.delete') {
      const page = this._state.currentPage();
      const blk = page?.blocks.get((action as { blockId: string }).blockId) as
        (import('./notebook_state').WvBaseBlock) | undefined;
      if (blk?.locked) return false;
    }
    return true;
  }

  /**
   * Toggle the astral overlay reveal. The astral overlay surfaces
   * passive meta info — text-detection shadows, debug overlays —
   * and is gated on cast mode so the wand also acts as the canon
   * "show meta" command. The old layer-visibility flag is gone;
   * `_castAstralVisible` is a plain boolean now.
   *
   * On first reveal for a given page we feed the page's existing
   * strokes through the passive text-detection pipeline so shadows
   * appear for pre-existing handwriting, not just strokes drawn
   * this session.
   */
  private _setAstralVisible(visible: boolean): void {
    if (this._castAstralVisible === visible) return;
    this._castAstralVisible = visible;
    if (visible) {
      this._seedShadowsForCurrentPage();
    }
  }

  /** Whether the astral (meta) overlay is currently surfaced. */
  private _castAstralVisible = false;
  private get _astralVisible(): boolean {
    return this._castAstralVisible;
  }

  /** Page ids whose existing strokes have already been replayed through
   *  the passive text-detection pipeline. One-shot per page per session. */
  private _astralSeeded = new Set<string>();

  private _seedShadowsForCurrentPage(): void {
    const pageId = this._state?.currentPageId;
    if (!pageId || pageId === 'unknown') return;
    if (this._astralSeeded.has(pageId)) return;
    this._astralSeeded.add(pageId);
    const strokes = this._ink.getStrokes();
    if (!strokes || strokes.size === 0) return;
    for (const stroke of strokes.values()) {
      // Astral strokes (layer 5) aren't user content — skip them. Layer 0
      // (Design) and 1–4 (User) all feed detection.
      if ((stroke.pageLayer ?? 1) === 5) continue;
      this._textCompose.feedStrokeWithPage(
        { id: stroke.id, points: stroke.points },
        pageId,
      );
    }
  }

  /** Tell the host to apply the same point transform to its stroke data (for persistence). */
  private _postStrokeTransform(
    pageId: string,
    strokeIds: string[],
    transform: { kind: string; [k: string]: unknown },
  ): void {
    this._vscode.postMessage({ type: 'strokes.transform', pageId, strokeIds, transform });
  }

  // ── Undo / Redo engine ──────────────────────────────────────────────────────

  /** Push an undoable entry (or buffer it if inside a group). */
  private _pushUndo(entry: UndoEntry): void {
    if (this._groupDepth > 0) {
      this._groupBuffer.push(entry);
      return;
    }
    this._undoStack.push(entry);
    if (this._undoStack.length > MAX_UNDO) {
      this._undoStack.splice(0, this._undoStack.length - MAX_UNDO);
    }
    this._redoStack.length = 0;
  }

  /** Begin a composite undo group — all pushUndo calls are batched. */
  private _beginUndoGroup(): void { this._groupDepth++; }

  /** End a composite undo group — flush buffered entries as one UndoEntry. */
  private _endUndoGroup(): void {
    this._groupDepth--;
    if (this._groupDepth > 0 || this._groupBuffer.length === 0) return;
    const reverse: GrimoireAction[] = [];
    const forward: GrimoireAction[] = [];
    // Reverse order for undo, forward order for redo
    for (let i = this._groupBuffer.length - 1; i >= 0; i--) {
      reverse.push(...this._groupBuffer[i].reverse);
    }
    for (const e of this._groupBuffer) {
      forward.push(...e.forward);
    }
    this._groupBuffer.length = 0;
    this._pushUndo({ reverse, forward });
  }

  private _undo(): void {
    const entry = this._undoStack.pop();
    if (!entry) return;
    for (const a of entry.reverse) { this._dispatch(a); }
    this._redoStack.push(entry);
  }

  private _redo(): void {
    const entry = this._redoStack.pop();
    if (!entry) return;
    for (const a of entry.forward) { this._dispatch(a); }
    this._undoStack.push(entry);
  }

  // ── Resize ─────────────────────────────────────────────────────────────────

  private _resize(): void {
    const dpr = window.devicePixelRatio || 1;
    // Global UI scale: render at 1/SCALE logical pixels per visible
    // pixel, then apply a `scale(SCALE)` CSS transform on the root
    // (in pwa_main.ts) so everything visually compresses to SCALE
    // while filling the window — gives 1:1 90% rendering of the
    // whole notebook chrome without producing black bars. Pointer
    // events get correctness-corrected in _buildFrame /
    // _toStrokePoint via rect-vs-offsetWidth.
    const SCALE = 0.9;
    const w = Math.round(window.innerWidth  / SCALE);
    const h = Math.round(window.innerHeight / SCALE);

    this._canvas.width  = w * dpr;
    this._canvas.height = h * dpr;
    this._canvas.style.width  = `${w}px`;
    this._canvas.style.height = `${h}px`;
    this._canvas.style.left = '0';
    this._canvas.style.top  = '0';

    const tabBarH = this._tabBarHeight;
    // Notebook dimensions. The page absorbs the 18-px spine width
    // into its drawable body (so the "extra size" gained by the 0.9
    // shrink goes back into ink-able space), and the decorative
    // spine remains rendered to the left of the page edge.
    const pageW = 670 + 18;
    const pageH = 840;
    // Bias page-x so the spine clips off-screen-left before the page
    // does. Wide windows still centre the page (spine fully visible);
    // half-screen / narrow windows bottom pageX at 0 so the spine
    // slides into the negative-x off-screen zone.
    const pageX = Math.max(0, Math.round((w - pageW) / 2));
    const pageY = Math.round((h - pageH + tabBarH) / 2);

    this._layout = {
      x: pageX, y: pageY,
      width: pageW, height: pageH,
      marginLeft: 60, marginRight: 40,
      marginTop: 56, marginBottom: 56,
    };

    if (this._ink) {
      this._ink.resize(pageW, pageH, dpr);
    } else {
      const ctx2d = this._canvas.getContext('2d')!;
      this._ink           = new InkLayer(ctx2d, pageW, pageH, dpr);
      this._spell         = new SpellLayer(ctx2d);
      this._page          = new PageRenderer(ctx2d);
      this._texture       = new TextureLayer(ctx2d);
      this._blockRenderer = new BlockRenderer(this._canvas);
      // Wire the ink layer into block_renderer so figure bodies with
      // a kinematic envelope (Batch 6) render through the same brush
      // dispatch as live ink.
      this._blockRenderer.setInkLayer(this._ink);
      this._wizardScroll  = new WizardScroll();
      this._codex          = new CodexRenderer();
      this._state         = new WebviewNotebookState();

      // Spell consumption callback — replaces ChargeEngine for visual timing
      this._spell.onConsumed = (valid) => { this._onConsumptionComplete(valid); };

      this._chargeEngine = new ChargeEngine();
      this._chargeEngine.onResolved(    () => { /* visual now handled by spell_layer consumption */ });
      this._chargeEngine.onTick(        () => { /* visual now handled by spell_layer consumption */ });
      this._chargeEngine.onBranchFired( (branch, _wavefrontT) => {
        // Branch spark visual — future: integrate with consumption wavefront
        // Resolve sub-spell actions from the branch
        const subSpell = this._spellRegistry.get(branch.subSpellId);
        if (subSpell && this._pendingSpell) {
          // Inherit parameters from parent, allow branch overrides
          const mergedParams = { ...this._pendingSpell.params };
          for (const [k, v] of Object.entries(branch.paramMap)) {
            if (typeof v === 'string' && v in mergedParams) {
              mergedParams[k] = mergedParams[v];
            } else {
              mergedParams[k] = v;
            }
          }
          const actions = subSpell.resolve(mergedParams, 'gesture');
          for (const a of actions) { this._dispatch(a); }
        }
      });

      this._spellRegistry = createSpellRegistry((a) => this._dispatch(a));
      this._refreshCodex(); // populate codex with initially-discovered spells

      // P2: new spell engine — single-stroke detect → match → recipe.
      // Sits alongside the legacy registry above behind the
      // `_newSpellsEnabled()` flag.
      registerBuiltinSpells(this._newSpellEngine);
    }

    if (!this._rafHandle) {
      this._rafHandle = requestAnimationFrame(this._renderLoop);
    }
  }

  // ── Render loop ────────────────────────────────────────────────────────────

  private _renderLoop = (): void => {
    const dpr = window.devicePixelRatio || 1;
    const ctx = this._ctx;
    const w   = this._canvas.width  / dpr;
    const h   = this._canvas.height / dpr;
    const L   = this._layout;
    const t   = performance.now() / 1000;

    // Adventure clock — ticks whenever the adventure section is unlocked
    if (this._lastFrameT > 0 && this._state) {
      const dt = t - this._lastFrameT;
      const adv = this._state.adventure;
      if (adv.unlocked && dt > 0 && dt < 1) {
        adv.clockMs += dt * 1000;
        // Advance time-of-day: one full cycle per ADV_DAY_LENGTH_S
        adv.env.timeOfDay = (adv.clockMs / (CanvasApp.ADV_DAY_LENGTH_S * 1000)) % 1;
      }
    }
    this._lastFrameT = t;

    // Shared wall-clock timestamp for the entire frame — avoids repeated
    // Date.now() syscalls in tick functions, block renderer, etc.
    this._frameNow = Date.now();

    // Astral discard tick — drop expired astral content. Throttled to
    // ~200ms so we don't scan every frame.
    if (this._frameNow - this._lastAstralTick > 200) {
      this._lastAstralTick = this._frameNow;
      this._tickAstralDiscard();
    }

    // Update NPC presence + movement only when the page actually has sigils.
    // Movement runs at half rate (every other frame) — 0.5-1.5 px/frame speed
    // is visually identical at 30 vs 60 Hz.
    if (this._hasNpcOnPage) {
      this._updateNpcPresences();
      if (++this._npcTickFrame & 1) {
        this._tickNpcMovement();
      }
    }

    // Reset DPR scale at top of every frame
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // Begin LaTeX overlay frame — blocks not drawn this frame will hide.
    const _latexOverlay = getLatexOverlay();
    if (_latexOverlay) _latexOverlay.beginFrame();

    // 1. Dark background
    ctx.fillStyle = '#1a1410';
    ctx.fillRect(0, 0, w, h);

    // 2. Parchment page
    const currentPage = this._state?.currentPage();
    const density = currentPage?.densityLevel ?? 0;
    this._page.setPageColor(currentPage?.color ?? null);
    this._page.drawPage(L, density);

    // 2b. Body decorations — graphics first (faint, decorative), then
    // justification (writing guides). Both sit under ink. Per-page
    // registered style ids; absent sub-slots resolve to no-op.
    const decoBody = currentPage?.decorations?.body;
    if (decoBody?.graphics) {
      ctx.save();
      getBodyGraphic(decoBody.graphics).draw(ctx, L);
      ctx.restore();
    }
    if (decoBody?.justification) {
      ctx.save();
      getBodyJustification(decoBody.justification).draw(ctx, L);
      ctx.restore();
    }

    const sec = this._state?.currentSection();

    // 3. View-specific content (drawn beneath ink so strokes appear on top)

    // 3a. Codex — rendered as paged spell reference when Codex tab is active
    this._codexNavHitRects = [];
    if (this._viewMode === 'codex') {
      ctx.save();
      this._codex.drawPage(ctx, L, this._groupPageIndex, t);

      // ── Page indicator + clickable nav arrows (top, beneath title) ────
      const totalCodexPages = this._codex.totalPageCount;
      if (totalCodexPages > 0) {
        const cx = L.x + L.width / 2;
        const navY = L.y + L.marginTop - 4;
        const arrowPad = 70;
        const arrowW = 22;
        const arrowH = 18;

        // Page number
        ctx.globalAlpha = 0.55;
        ctx.fillStyle = '#5a3820';
        ctx.font = 'italic 13px Georgia, serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(
          `— ${this._groupPageIndex + 1} / ${totalCodexPages} —`,
          cx, navY,
        );

        // Back arrow
        if (this._groupPageIndex > 0) {
          ctx.globalAlpha = 0.5;
          ctx.font = '16px Georgia, serif';
          ctx.fillText('◂', cx - arrowPad, navY);
          this._codexNavHitRects.push({
            dir: 'back',
            x: cx - arrowPad - arrowW / 2,
            y: navY - arrowH / 2,
            w: arrowW,
            h: arrowH,
          });
        }

        // Forward arrow
        if (this._groupPageIndex < totalCodexPages - 1) {
          ctx.globalAlpha = 0.5;
          ctx.font = '16px Georgia, serif';
          ctx.fillText('▸', cx + arrowPad, navY);
          this._codexNavHitRects.push({
            dir: 'forward',
            x: cx + arrowPad - arrowW / 2,
            y: navY - arrowH / 2,
            w: arrowW,
            h: arrowH,
          });
        }
      }

      // Diverted cast feedback flash
      if (this._codexFeedback) {
        const fb = this._codexFeedback;
        fb.alpha = Math.max(0, fb.alpha - 0.012);
        if (fb.alpha > 0) {
          const fbY = L.y + L.height * 0.55;
          ctx.globalAlpha = fb.alpha * 0.9;
          ctx.font = 'bold 16px Georgia, serif';
          ctx.textAlign = 'center';
          const familyColors: Record<string, string> = {
            act: '#e8a020', bind: '#6a9fc0', transmute: '#b05fd0', invoke: '#50c87a',
          };
          ctx.fillStyle = familyColors[fb.family] ?? '#c0b080';
          ctx.fillText(`✦ ${fb.text} ✦`, L.x + L.width / 2, fbY);
        } else {
          this._codexFeedback = null;
        }
      }

      ctx.restore();
    }

    // 3b. Settings view
    if (this._viewMode === 'settings') {
      ctx.save();
      this._drawSettingsView(ctx, L, t);
      ctx.restore();
    }

    // 3d. Adventure pages — env overlay + sub-tab content
    this._scrollHitRects = [];
    if (this._viewMode === 'page' && sec?.kind === 'adventure' && this._state) {
      const advState = this._state.adventure;
      const cpid = this._state.currentPageId;
      ctx.save();
      ctx.translate(L.x, L.y);
      // Action page: env overlay beneath scroll, then the scroll itself
      if (cpid === 'adv_action') {
        drawAdventureEnv(ctx, advState.env, L.width, L.height, t);
        this._scrollHitRects = drawActionScroll(ctx, advState, L.width, L.height, advState.clockMs);
        // Offset hit rects to viewport coords (they're returned in page-local)
        for (const hr of this._scrollHitRects) {
          hr.x += L.x;
          hr.y += L.y;
        }
      }
      // Bookmarks page
      if (cpid === 'adv_bookmarks') {
        drawBookmarksPage(ctx, advState.bookmarks, L.width, L.height);
      }
      // Character sheet
      if (cpid === 'adv_character') {
        drawCharacterSheet(ctx, advState, L.width, L.height);
      }
      // Dialog history
      if (cpid === 'adv_history') {
        drawDialogHistory(ctx, advState.dialogLog, L.width, L.height);
      }
      ctx.restore();
    }

    // 4. Ink strokes (journal pages + codex wand strokes + settings selection)
    if (this._viewMode === 'page' || this._viewMode === 'codex' || this._viewMode === 'settings') {
      this._ink.draw(L.x, L.y);
    }

    // 4-wipe. Margin-wipe fragments. Procedural eviction animation
    // for ink that lived inside a region a utility just claimed
    // (e.g. the stylus sidebar). Page-local coords; ctx is
    // translated to match before delegating to the layer.
    if (this._marginWipe.isActive && this._viewMode === 'page') {
      ctx.save();
      ctx.translate(L.x, L.y);
      this._marginWipe.draw(ctx, this._ink, performance.now());
      ctx.restore();
    }

    // 4-eraser. One stroked-path "ghost" per eraser drag. Lives
    // until the user pen-up's, then fades out over a fixed window.
    // Single stroke per ghost = no per-sample alpha stacking even
    // on a slow circling drag.
    if (this._eraserGhosts.length > 0 && this._viewMode === 'page') {
      ctx.save();
      ctx.translate(L.x, L.y);
      this._drawEraserTrail(ctx);
      ctx.restore();
    }

    // 4-meta. Hover-link hint: a thin square around the single
    // anchor that would be hooked if the user starts drawing right
    // now. Snap targets are otherwise invisible (an internal index
    // for the link automation, not a permanent visual layer).
    if (this._viewMode === 'page' && !this._activeStrokeId) {
      this._drawHoverSnapHint(ctx, L);
    }

    // 4-snap. Snap indicator — crosshair at the nearest hookable
    // target while a stroke is in progress (live = bright, start-
    // snap = subtle).
    if (this._snapIndicator && this._activeStrokeId) {
      this._drawSnapIndicator(ctx, L);
    }
    if (this._snapStart && this._activeStrokeId) {
      this._drawSnapIndicator(ctx, L, this._snapStart);
    }

    // 4-ghost. Shape hint ghost preview over pending ink
    if (this._shapeHintPending.size > 0 && this._viewMode === 'page') {
      this._drawShapeGhost(ctx, L, t);
    }

    // 4a. Arcane analysis animation (glow / resolve / blend overlay)
    if (this._arcaneAnim && this._viewMode === 'page') {
      this._tickArcaneAnim(ctx, L.x, L.y);
    }

    // 4b. Block decorations (page-local coordinates, journal pages only)
    if (this._viewMode === 'page' && currentPage && currentPage.kind !== 'folder-landing') {
      ctx.save();
      ctx.translate(L.x, L.y);
      // Filter blocks by layer visibility
      const blocks = [...currentPage.blocks.values()].filter(b => this._state.isBlockVisible(b));
      const notes  = [...currentPage.marginalia.values()];

      const arrows: WvRelationArrow[] = [];
      for (const a of currentPage.arrows.values()) {
        const from = currentPage.blocks.get(a.fromId);
        const to   = currentPage.blocks.get(a.toId);
        if (from && to) {
          arrows.push({
            id: a.id, type: a.type, confirmed: a.confirmed, label: a.label,
            fromBbox: from.bbox, toBbox: to.bbox,
          });
        }
      }

      this._blockRenderer.frameNow = this._frameNow;
      this._blockRenderer.lockFocus = this._state.lockFocus;
      this._blockRenderer.draw(blocks, this._state.selection, notes, ctx, t, arrows);
      ctx.restore();
    }

    // Margin decorations — graphics first (themed ornaments occupying
    // the margin band), then utility (functional marks snug to the
    // gutter, drawn on top so they stay readable).
    const decoMargins = currentPage?.decorations?.margins;
    if (this._viewMode === 'page' && decoMargins?.graphics) {
      ctx.save();
      getMarginGraphic(decoMargins.graphics).draw(ctx, L);
      ctx.restore();
    }
    if (this._viewMode === 'page' && decoMargins?.utility) {
      ctx.save();
      getMarginUtility(decoMargins.utility).draw(ctx, L, {
        justification: currentPage?.decorations?.body?.justification,
      });
      ctx.restore();
    }

    // Frame — drawn topmost so it clearly delimits the body content rect.
    if (this._viewMode === 'page' && currentPage?.decorations?.frame) {
      ctx.save();
      getFrameDecoration(currentPage.decorations.frame).draw(ctx, L);
      ctx.restore();
    }

    // 4b-landing. Folder landing pages — title + 2-line description + the
    // folder's own content index. This replaces the old global Contents
    // codex page; each folder is now its own section-like landing view.
    this._landingHitRects = [];
    if (this._viewMode === 'page' && currentPage?.kind === 'folder-landing') {
      ctx.save();
      this._drawFolderLanding(ctx, L, currentPage);
      ctx.restore();
    }

    // 4b-astral. Meta info (passive text-detection shadows for now) lives
    // on the astral layer. The wand toggles _astralVisible, so casting is
    // also the canon "show meta" gesture. Skipped on landing pages — they
    // have their own info already.
    if (this._viewMode === 'page'
        && currentPage
        && currentPage.kind !== 'folder-landing'
        && this._astralVisible) {
      ctx.save();
      ctx.translate(L.x, L.y);
      this._drawAstralOverlay(ctx, currentPage.id);
      ctx.restore();
    }

    // 4c. Wizard scroll (right margin)
    if (this._wizardScroll.visible) {
      ctx.save();
      this._wizardScroll.draw(ctx, L, t);
      ctx.restore();
    }

    // 4c2. (NPC presence is now shown via activated arcane block glyphs)

    // 4c3. Agent streaming text animation (word-by-word reveal)
    if (this._agentStreamAnim && this._viewMode === 'page') {
      this._tickAgentStreamAnim(ctx, L, t);
    }

    // 4d. Post-process grain — composited OVER all page content so
    //     ink, blocks, and everything looks absorbed into parchment.
    this._texture.draw(L, sec?.color ?? null, this._settings.grainIntensity);

    // 5. Focus veil (canvas-level, across full viewport)
    const focus = this._state?.focus;
    if (focus?.active && this._viewMode === 'page') {
      this._state.tickFocusAnim();
      ctx.save();
      ctx.fillStyle = `rgba(20, 16, 10, ${focus.animT * 0.55})`;
      ctx.fillRect(0, 0, w, h);

      if (typeof focus.region === 'object' && focus.region !== null && 'bbox' in focus.region) {
        const r = (focus.region as { bbox: { x: number; y: number; width: number; height: number } }).bbox;
        ctx.clearRect(L.x + r.x - 2, L.y + r.y - 2, r.width + 4, r.height + 4);
        ctx.strokeStyle = 'rgba(200, 180, 130, 0.55)';
        ctx.lineWidth = 1.5;
        ctx.setLineDash([6, 4]);
        ctx.lineDashOffset = -(t * 20 % 10);
        ctx.strokeRect(L.x + r.x - 2, L.y + r.y - 2, r.width + 4, r.height + 4);
      }
      ctx.restore();
    }

    // 6. Spell feedback (on top of blocks)
    this._spell.draw(L.x, L.y);

    // 6-flux: tick flux engine (30 Hz). The strand-graph visualization
    // (glow/dash lines following the user's wand strokes) is gated on
    // the Debug overlay setting — for normal use the cast feel comes
    // from the spell-layer ink trace + per-tap rings, not the
    // engineering view of the field. The engine itself still runs so
    // resolution/fizzle logic continues working.
    const fluxPhase = this._fluxState.phase;
    const fluxAlive = fluxPhase !== 'idle' && fluxPhase !== 'dead';
    const showStrandGraph = this._settings.debugOverlay;
    if (fluxAlive || this._strandGraph._live) {
      const fluxNow = performance.now();
      const fluxDt = fluxNow - this._lastFluxTick;
      if (fluxDt >= 33) {                       // ~30 Hz
        tickFlux(this._strandGraph, this._fluxState, Math.min(fluxDt, 100));
        this._lastFluxTick = fluxNow;

        // Fizzle completed → last-chance resolution before cancelling
        if (this._fluxState.phase === 'dead' && !this._fluxResolved) {
          // Give the classifier one final shot before declaring misfire
          this._tryFluxResolution();
          if (!this._fluxResolved) {
            clearTimeout(this._wandCastTimeout);
            this._spell.finalize(false);
            if (this._isWandInvoked) {
              this._cancelWand();
            }
          }
        }

        // Live resolution — only between strokes (pen lifted), not mid-draw
        if (this._strandGraph.loops.length > 0 && !this._spell.penDown && !this._fluxResolved) {
          this._tryLiveFluxResolution();
        }
      }
      // Refresh rectangular cache for any new loops
      for (const loop of this._strandGraph.loops) {
        if (!this._rectLoopCache.has(loop.id as number)) {
          this._rectLoopCache.set(loop.id as number, isRectangularLoop(this._strandGraph, loop));
        }
      }
      if (showStrandGraph) {
        drawStrandGraph(ctx, this._strandGraph, this._fluxState, L.x, L.y, fluxNow, this._rectLoopCache);
      }
    }

    // 6a. Spell name toast
    if (this._spellToast) {
      const TOAST_DURATION = 1600;
      const age = performance.now() - this._spellToast.birthMs;
      if (age < TOAST_DURATION) {
        const t = age / TOAST_DURATION;
        const fadeIn = Math.min(t * 6, 1);
        const fadeOut = Math.max(0, 1 - (t - 0.6) / 0.4);
        const alpha = Math.min(fadeIn, fadeOut) * 0.85;
        const rise = t * 18;
        const tx = L.x + this._spellToast.x;
        const ty = L.y + this._spellToast.y - rise;

        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.font = 'bold 13px "Palatino Linotype", Georgia, serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillStyle = '#c8a84a';
        ctx.fillText(this._spellToast.name, tx, ty);
        ctx.restore();
      } else {
        this._spellToast = null;
      }
    }

    // 6b. Tap ring feedback
    this._tapRingLayer.draw(ctx, L.x, L.y);

    // 6b1. Copy flash feedback — bright flicker on copied region
    if (this._copyFlash) {
      const FLASH_DUR = 400;
      const flashAge = performance.now() - this._copyFlash.birthMs;
      if (flashAge < FLASH_DUR) {
        const ft = flashAge / FLASH_DUR;
        // Sharp flash then fade: peak at t=0.15
        const flashAlpha = ft < 0.15 ? ft / 0.15 : Math.max(0, 1 - (ft - 0.15) / 0.85);
        const b = this._copyFlash.bbox;
        ctx.save();
        ctx.globalAlpha = flashAlpha * 0.55;
        // Bright fill
        ctx.fillStyle = '#fffbe0';
        ctx.fillRect(L.x + b.x - 4, L.y + b.y - 4, b.width + 8, b.height + 8);
        // Bright border
        ctx.globalAlpha = flashAlpha * 0.9;
        ctx.strokeStyle = '#e8c840';
        ctx.lineWidth = 2.5;
        ctx.strokeRect(L.x + b.x - 4, L.y + b.y - 4, b.width + 8, b.height + 8);
        ctx.restore();
      } else {
        this._copyFlash = null;
      }
    }

    // 6b2. Debug flash overlay
    if (this._debugFlashes.length > 0) {
      const FLASH_DURATION = 4000;
      const nowFlash = performance.now();
      this._debugFlashes = this._debugFlashes.filter(f => nowFlash - f.birthMs < FLASH_DURATION);
      ctx.save();
      // Draw shapes at their location
      for (const f of this._debugFlashes) {
        const age = nowFlash - f.birthMs;
        const alpha = Math.max(0, 1 - age / FLASH_DURATION) * 0.85;
        const px = L.x + f.x;
        const py = L.y + f.y;
        ctx.globalAlpha = alpha;
        ctx.fillStyle = f.color;
        ctx.strokeStyle = f.color;
        ctx.lineWidth = 2;
        if (f.shape === 'circle') {
          ctx.beginPath();
          ctx.arc(px, py, 8, 0, Math.PI * 2);
          ctx.fill();
        } else if (f.shape === 'rect' && f.w && f.h) {
          ctx.strokeRect(px, py, f.w, f.h);
        } else if (f.shape === 'x') {
          ctx.beginPath();
          ctx.moveTo(px - 8, py - 8); ctx.lineTo(px + 8, py + 8);
          ctx.moveTo(px + 8, py - 8); ctx.lineTo(px - 8, py + 8);
          ctx.stroke();
        }
      }
      // Draw labels as a stacked column (left side) so they don't overlap
      let labelY = L.y + 20;
      ctx.textBaseline = 'top';
      for (const f of this._debugFlashes) {
        const age = nowFlash - f.birthMs;
        const alpha = Math.max(0, 1 - age / FLASH_DURATION) * 0.92;
        ctx.globalAlpha = alpha;
        // Background strip for readability
        ctx.fillStyle = 'rgba(10,8,4,0.7)';
        ctx.fillRect(L.x + 4, labelY - 1, 260, 15);
        // Colored dot + text
        ctx.fillStyle = f.color;
        ctx.beginPath();
        ctx.arc(L.x + 12, labelY + 6, 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.font = '11px monospace';
        ctx.fillText(f.label, L.x + 20, labelY);
        labelY += 16;
      }
      ctx.restore();
    }

    // 6c. Pop dialog (inline page-scrawled menu)
    this._popDialog.draw(ctx, L.x, L.y);

    // 6c2. Selection widgets (page header + section header + settings wonky rings)
    this._pageHeaderRegion.draw(ctx, L.x, L.y);
    this._sectionHeaderRegion.draw(ctx, L.x, L.y);
    if (this._viewMode === 'settings') {
      this._settingsRegion.draw(ctx, L.x, L.y);
    }

    // 6c3. Dissolve animations (red flash / smoke / fade)
    this._dissolves.draw(ctx);

    // 6c4. Tap effects (ring on every tap; burst on cadence resolve etc.)
    this._tapFx.draw(ctx);

    // 6c4b. Stylus sidebar (Batch 7). Permanent left-margin palette,
    //       toggleable via the stylus-summon cadence or the bound
    //       side-button shortcut. Layout is rebuilt every frame from
    //       the active tool params so selection outlines track live.
    if (this._stylusSidebarVisible
        && this._viewMode === 'page'
        && currentPage?.kind !== 'folder-landing') {
      this._stylusSidebarLayout = buildSidebarLayout(L, {
        tool:      this._toolRegistry.activeId,
        color:     this._penStyle.color,
        size:      this._penStyle.size,
        opacity:   this._penStyle.opacity,
        shape:     this._settings.shapeMode,
        autoLatex: this._settings.autoLatex,
        visualFx:  this._settings.visualFx,
        lockFocus: this._state?.lockFocus ?? false,
      });
      drawStylusSidebar(ctx, L, this._stylusSidebarLayout);
    } else {
      this._stylusSidebarLayout = null;
    }

    // Debug menu (SL cadence) — drawn last so it sits above the
    // sidebar and any page chrome.
    if (this._debugDialog.isOpen()) {
      this._debugDialog.draw(ctx, { w, h });
    }

    // 6c5. Cadence chain HUD — tiny letter readout near the most
    //      recent tap so the user can see what the engine classified
    //      (helps debug rhythm during S/L learning). Hidden when no
    //      chain is active.
    this._drawCadenceHud(ctx);

    // 6c6. Stylus state HUD (debug) — shows what the OS is delivering
    //      so we can tell whether a Slim Pen tail-click ever reaches
    //      the browser, what pointerType the events arrive as, etc.
    this._drawStylusHud(ctx);

    // 6d. Text input overlay
    if (this._textInput.active) {
      this._textInput.draw(ctx, L.x, L.y);
    }

    // 6e. Handwriting composition — hint ring deployment + cursor
    if (this._viewMode === 'page') {
      // Deploy a one-shot tap ring when a new hint arrives
      const hint = this._textCompose.hint;
      if (hint && hint.birth !== this._lastHintRingBirth) {
        this._lastHintRingBirth = hint.birth;
        this._tapRingLayer.deployOneShot(
          hint.ringX, hint.ringY, HINT_GLOW_COLOR, 3000, '__text_hint__',
          HINT_RING_RADIUS,
        );
      }
      // Draw detected text label next to the hint ring
      if (hint) {
        const age = (performance.now() - hint.birth) / 1000;
        if (age > 3) {
          // Ring expired without tap — dismiss hint
          this._textCompose.dismissHint();
        } else {
          ctx.save();
          ctx.translate(L.x, L.y);
          const alpha = Math.max(0, 1 - age / 3) * 0.95;
          ctx.globalAlpha = alpha;
          ctx.font = `${Math.round(hint.fontSize * 0.75)}px Georgia, serif`;
          ctx.fillStyle = HINT_GLOW_COLOR;
          ctx.textAlign = 'right';
          ctx.textBaseline = 'middle';
          ctx.fillText(hint.text, hint.ringX - 20, hint.ringY);
          ctx.restore();
        }
      }
      // Draw composition cursor when session is active
      if (this._textCompose.active && currentPage) {
        const cs = this._textCompose.session!;
        const block = currentPage.blocks.get(cs.blockId);
        if (block && block.kind === 'text') {
          const tb = block as import('./notebook_state').WvTextBlock;
          ctx.save();
          ctx.translate(L.x, L.y);
          ctx.font = `${tb.fontSize ?? 16}px Georgia, serif`;
          const textBefore = (tb.content ?? '').slice(0, cs.cursor);
          const cursorX = tb.bbox.x + 4 + ctx.measureText(textBefore).width;
          const cursorY = tb.bbox.y + 2;
          const cursorH = (tb.fontSize ?? 16) + 4;
          // Blinking (530ms period)
          const blink = Math.floor(t * 1000 / 530) % 2 === 0;
          if (blink) {
            ctx.strokeStyle = '#3a2510';
            ctx.lineWidth = 1.5;
            ctx.globalAlpha = 0.8;
            ctx.beginPath();
            ctx.moveTo(cursorX, cursorY);
            ctx.lineTo(cursorX, cursorY + cursorH);
            ctx.stroke();
          }
          // Subtle underline showing composition is active
          ctx.strokeStyle = '#8a6830';
          ctx.lineWidth = 1;
          ctx.globalAlpha = 0.35;
          ctx.setLineDash([4, 3]);
          ctx.beginPath();
          ctx.moveTo(tb.bbox.x, tb.bbox.y + cursorH + 4);
          ctx.lineTo(tb.bbox.x + tb.bbox.width, tb.bbox.y + cursorH + 4);
          ctx.stroke();
          ctx.restore();
        }
      }
    }

    // 7. Corner bookmarks — Codex (left) + Settings (right), no section tabs
    if (this._state && this._state.pageCount > 0) {
      const pages = this._state.visiblePages();
      const currentSec = this._state.currentSection();
      // Settings leftmost, then Codex. Journal-backed pages expose "New
      // page" on the side rail; local notebooks need a corner-bookmark
      // fallback because they have no journal sibling context.
      const cornerTabs: import('./page_renderer').BookmarkTab[] = [
        {
          label: 'Settings',
          tier: 'major',
          active: this._viewMode === 'settings',
          index: -3,
          kind: 'settings',
          align: 'left',
        },
        {
          label: 'Codex',
          tier: 'major',
          active: this._viewMode === 'codex',
          index: -2,
          kind: 'codex',
          align: 'left',
        },
      ];
      const hasJournalPageSidebar = !!this._journalNavContext
        && this._journalNavContext.items.length > 0;
      if (this._viewMode === 'page' && !hasJournalPageSidebar) {
        cornerTabs.push({
          label: 'New page',
          tier: 'major',
          active: false,
          index: -1,
          kind: 'newPage',
          align: 'right',
        });
      }

      this._tabHitRects = this._page.drawTabBar(L, cornerTabs, this._tabBarHeight);

      // 7b. Book spine (left side)
      this._page.drawBookSpine(L);

      // 7c. Stacked page edges (right/bottom — book depth)
      this._page.drawStackedEdges(L, pages.length, this._state.pageIndex);

      // 7d. Page sidebar — sticky-marker navigation tabs on the right edge.
      // Reflects folders and pages at the same level as the current page,
      // plus utility tabs for "level up" and "new page" at the top.
      if (this._viewMode === 'page') {
        const nav = this._journalNavContext;
        if (nav && nav.items.length > 0) {
          const sidebarTabs: PageSidebarTab[] = [];

          // Back is always visible. At root (no parent / grandparent)
          // it still navigates — the click handler falls back to the
          // last visited page so we don't dead-end. This means the
          // user can always exit settings/codex back to where they
          // were, regardless of folder depth.
          sidebarTabs.push({
            label: nav.parentTitle || 'Up',
            active: false,
            index: -10,
            kind: 'back',
          });

          // Sibling items in tree order. The "newpage" (add) tab pins
          // to the very bottom of the rail — putting it inline next to
          // the active sibling made it shuffle position with each
          // navigation, which read as visual noise rather than a
          // useful affordance.
          let sibIdx = 0;
          for (const item of nav.items) {
            const broken = !item.isFolder && !!item.pageId
              && this._failedPageIds.has(item.pageId);
            sidebarTabs.push({
              label: item.title,
              active: item.isActive,
              index: sibIdx++,
              kind: item.isFolder ? 'folder' : (broken ? 'broken' : undefined),
              pageId: item.pageId,
              nodePath: item.id,
            });
          }
          sidebarTabs.push({
            label: 'New',
            active: false,
            index: -9,
            kind: 'newpage',
          });

          // Delete-page tab — bottom-pinned, only shown for real
          // pages (not folder landings) and only when there's more
          // than one page on the rail so we can't delete the last
          // remaining navigable page.
          const curPg = this._state.currentPage();
          const isFolderLanding = curPg?.kind === 'folder-landing';
          const hasMultiplePages = this._state.pageCount > 1;
          if (curPg && !isFolderLanding && hasMultiplePages) {
            sidebarTabs.push({
              label: 'Delete',
              active: false,
              index: -8,
              kind: 'delete',
              pageId: curPg.id,
              bottomPin: true,
            });
          }

          this._pageSidebarHitRects = this._page.drawPageSidebar(L, sidebarTabs);
        } else {
          this._pageSidebarHitRects = [];
        }

        // 7e. Page header (sticky name + color at top) — fold pages use fixed colors
        const curPage = this._state.currentPage();
        const curSec = this._state.currentSection();
        const pageCustomizable = curPage?.customizable !== false && curSec?.customizable !== false;

        // Sheet paginator: if the page's primary block has a multi-subpage
        // content, promote the paginator to the page header so the user
        // navigates "as a sheet of pages" at the top, not through a block
        // strip at the bottom.
        let sheetInfo: { index: number; count: number; blockId: string } | undefined;
        if (curPage) {
          for (const blk of curPage.blocks.values()) {
            const ext = blk as unknown as { _role?: string; _subpageCount?: number; _subpageIndex?: number };
            if (ext._role === 'primary' && (ext._subpageCount ?? 1) > 1) {
              sheetInfo = {
                index: ext._subpageIndex ?? 0,
                count: ext._subpageCount ?? 1,
                blockId: blk.id,
              };
              break;
            }
          }
        }

        if (curPage && !curPage.isSectionHeader && !this._state.isFoldPage()) {
          const hideSwatches = !pageCustomizable;
          const headerResult = this._page.drawPageHeader(L, curPage.title || '', curPage.color, undefined, hideSwatches, sheetInfo);
          this._pageHeaderColorSwatches = hideSwatches ? [] : headerResult.colorSwatches;
          this._pageHeaderTitleRect = pageCustomizable ? headerResult.titleRect : null;
          this._sheetArrowHitRects = headerResult.sheetArrows;
          if (!hideSwatches) this._syncPageHeaderRegion(curPage.color);
        } else if (curPage && !curPage.isSectionHeader && this._state.isFoldPage()) {
          // Fold pages: show title but no color swatches, no rename
          const headerResult = this._page.drawPageHeader(L, curPage.title || '', curPage.color, undefined, true, sheetInfo);
          this._pageHeaderColorSwatches = [];
          this._pageHeaderTitleRect = null; // prevent rename
          this._sheetArrowHitRects = headerResult.sheetArrows;
        } else {
          this._pageHeaderColorSwatches = [];
          this._pageHeaderTitleRect = null;
          this._sheetArrowHitRects = [];
        }

        // 7f. Section header page rendering
        if (curPage?.isSectionHeader && currentSec) {
          const isFold = currentSec.kind === 'fold';
          const secCustomizable = !isFold && currentSec.customizable !== false;

          {
            if (isFold) {
              // Fold: full-page door replaces the normal section header
              this._page.drawFoldDoor(L, currentSec.color, currentSec.title);
              this._sectionHeaderHitData = null;
            } else {
              const headerData = this._page.drawSectionHeaderPage(
                L,
                currentSec.title,
                currentSec.color,
                SECTION_PALETTE,
                secCustomizable,
              );
              this._sectionHeaderHitData = headerData;
              if (secCustomizable) this._syncSectionHeaderRegion(currentSec.color);
            }
          }
        } else {
          this._sectionHeaderHitData = null;
        }
      } else {
        // Codex / Settings no longer draw a side tab rail — the old
        // chapter-as-tab UI is retired in favour of a unified tree view
        // and (future) free-placement bookmarks.
        this._pageSidebarHitRects = [];
        this._pageHeaderColorSwatches = [];
        this._sectionHeaderHitData = null;
      }
    }

    // 8. Page turn hint
    if (this._pageTurnProgress > 0) {
      this._page.drawPageTurnHint(L, this._pageTurnProgress, this._pageTurnDirection);
    }

    // 9. Rebuild unified UI hit rect array from per-category arrays
    this._rebuildUiHitRects();

    // Commit LaTeX overlay — hide blocks no longer visible this frame.
    if (_latexOverlay) _latexOverlay.commit();

    this._rafHandle = requestAnimationFrame(this._renderLoop);
  };

  /** Merge per-category UI hit rect arrays into the single flat _uiHitRects. */
  private _rebuildUiHitRects(): void {
    const out: UiHitRect[] = [];
    for (const t of this._tabHitRects) {
      out.push({ x: t.x, y: t.y, w: t.w, h: t.h, target: { kind: 'tab', data: { index: t.index, kind: t.kind } } });
    }
    for (const n of this._codexNavHitRects) {
      out.push({ x: n.x, y: n.y, w: n.w, h: n.h, target: { kind: 'codexNav', data: { dir: n.dir } } });
    }
    for (const s of this._pageSidebarHitRects) {
      out.push({ x: s.x, y: s.y, w: s.w, h: s.h, target: { kind: 'sidebar', data: { index: s.index, kind: s.kind, pageId: s.pageId, nodePath: s.nodePath } } });
    }
    for (const r of this._scrollHitRects) {
      out.push({ x: r.x, y: r.y, w: r.w, h: r.h, target: { kind: 'scroll', data: r } });
    }
    // Contents-page row clicks (only meaningful while viewing codex page 0,
    // but the renderer only populates hit rects on the frame it's drawn, so
    // this is self-gating).
    if (this._viewMode === 'codex' && this._groupPageIndex === 0) {
      for (const h of this._codex.contentsHitRects) {
        out.push({ x: h.x, y: h.y, w: h.w, h: h.h, target: { kind: 'contentsRow', data: { pageId: h.pageId } } });
      }
    }
    // Folder-landing row clicks. Self-gating like the codex Contents rects:
    // _drawFolderLanding only populates _landingHitRects on frames where it
    // actually drew, so this is only "live" while the user is on a landing.
    for (const h of this._landingHitRects) {
      out.push({ x: h.x, y: h.y, w: h.w, h: h.h, target: { kind: 'contentsRow', data: { pageId: h.pageId } } });
    }
    // Per-block subpage arrow hit rects from the markdown paginator.  Only
    // meaningful while viewing a page — skipped in codex/settings modes.
    if (this._viewMode === 'page') {
      const blockRenderer = this._blockRenderer as unknown as {
        subpageHitRects?: Array<{ blockId: string; dir: -1 | 1; x: number; y: number; w: number; h: number }>;
      };
      const rects = blockRenderer.subpageHitRects;
      if (rects) {
        for (const h of rects) {
          out.push({
            x: h.x, y: h.y, w: h.w, h: h.h,
            target: { kind: 'subpageArrow', data: { blockId: h.blockId, dir: h.dir } },
          });
        }
      }
      // Sheet paginator arrows in the page header — same dispatch as the
      // per-block subpage arrows, just rendered at the top instead of the
      // bottom of the block.
      for (const h of this._sheetArrowHitRects) {
        out.push({
          x: h.x, y: h.y, w: h.w, h: h.h,
          target: { kind: 'subpageArrow', data: { blockId: h.blockId, dir: h.dir } },
        });
      }
    }
    this._uiHitRects = out;
  }
}

// ── Module helpers ──────────────────────────────────────────────────────

/** Wrap a description string into at most `maxLines` lines that each fit in
 *  `maxW`. The last line gets ellipsised if more text remains. Used by the
 *  folder-landing renderer to lay out the two-line subtitle without having
 *  to ship a full text-wrapping engine. */
function wrapDescriptionLines(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxW: number,
  maxLines: number,
): string[] {
  if (!text) return [];
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = '';
  for (const w of words) {
    const test = current ? current + ' ' + w : w;
    if (ctx.measureText(test).width > maxW) {
      if (current) lines.push(current);
      if (lines.length >= maxLines) break;
      current = w;
    } else {
      current = test;
    }
  }
  if (current && lines.length < maxLines) lines.push(current);

  // Ellipsis on the last line if there are still un-rendered words.
  const renderedWordCount = lines.join(' ').split(/\s+/).length;
  if (renderedWordCount < words.length && lines.length > 0) {
    const last = lines[lines.length - 1];
    let trimmed = last;
    while (trimmed.length > 0 && ctx.measureText(trimmed + '…').width > maxW) {
      trimmed = trimmed.slice(0, -1);
    }
    lines[lines.length - 1] = (trimmed.trimEnd() || last) + '…';
  }
  return lines;
}

/** Truncate a single line with ellipsis to fit in `maxW`. */
function truncateForWidth(ctx: CanvasRenderingContext2D, text: string, maxW: number): string {
  if (ctx.measureText(text).width <= maxW) return text;
  for (let i = text.length - 1; i > 0; i--) {
    const trial = text.slice(0, i) + '…';
    if (ctx.measureText(trial).width <= maxW) return trial;
  }
  return '…';
}
